import { sessionManager } from "@/broadcastChannel/sessionManager";
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "@/components/ui/use-toast";
import FieldInput from "@/dashboard/components/fundings/FieldInput";
import { useFetchDataV2 } from "@/hooks/useFetchDataV2";
import usePostMultipart from "@/hooks/usePostMultipart";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/libs/utils";
import {
  AlertTriangle,
  ArrowRightLeft,
  ChevronDown,
  ExternalLink,
  Info,
  Link2,
  Loader2,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  SynthesesCalendarEvent,
  SynthesesReminderItem,
} from "./SynthesesRightRail";
import SynthesesProjectImpactIndicators from "./SynthesesProjectImpactIndicators";
import {
  getSynthesesMetricToneClasses,
  type SynthesesMetricTone,
} from "./synthesesMetricTones";
import useLocalDraftAutosave from "./useLocalDraftAutosave";
import {
  type SynthesesGroupedContractsDataUpdater,
  SynthesesGroupedContractNode,
  SynthesesGroupedContractsResponse,
  SynthesesGroupedFinanceurItem,
} from "./useSynthesesGroupedContractsRead";
import useSynthesesRemoteAutosave from "./useSynthesesRemoteAutosave";
import {
  mergeSynthesesContractsInGroupedData,
  moveLandPaymentRowsInGroupedData,
  removeLandPaymentRowsFromGroupedData,
  removeSynthesesContractsFromGroupedData,
  replaceLandPaymentRowsInGroupedData,
  replaceReconciledLandPaymentRowsInGroupedData,
  replaceLandPaymentStatusRowsInGroupedData,
  upsertSynthesesContractInGroupedData,
} from "./synthesesGroupedContractsCacheUtils";

function formatEuro(value: number) {
  return `${Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} €`;
}

function formatLocalAmount(
  value: number,
  contract: SynthesesGroupedContractNode,
) {
  const number = Number(value || 0).toLocaleString("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  const code = String(contract.currency?.code || "").toUpperCase();
  const symbol = String(contract.currency?.symbol || "").trim();

  if ("EUR" === code) return `${number} €`;
  if (symbol) return `${number} ${symbol}`;
  if (code) return `${number} ${code}`;

  return number;
}

function formatDate(value: string | null) {
  if (!value) return "Date non renseignée";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Date invalide";

  return new Intl.DateTimeFormat("fr-FR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

function SectionHint({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-5 w-5 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-700",
            className,
          )}
        >
          <Info className="h-3.5 w-3.5" />
        </button>
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="max-w-[300px] rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-left text-xs text-slate-600 shadow-xl"
      >
        {children}
      </TooltipContent>
    </Tooltip>
  );
}

function getProjectDashboardHref(projectId: number | null | undefined) {
  const normalizedId = Number(projectId || 0);
  if (!Number.isFinite(normalizedId) || normalizedId <= 0) return null;
  return `/dashboard/project/${normalizedId}`;
}

function getDesiredFundingOptionLabel(option: ProjectDesiredFundingOption) {
  return String(option.title || "").trim() || `Financement souhaité #${option.id}`;
}

function getDesiredFundingOptionAmountEuro(option: ProjectDesiredFundingOption) {
  const convertedAmount = Number(option.convertedAmount ?? 0);
  if (Number.isFinite(convertedAmount) && convertedAmount > 0) {
    return Number(convertedAmount.toFixed(2));
  }

  const amount = Number(option.amount || 0);
  return Number.isFinite(amount) ? Number(amount.toFixed(2)) : 0;
}

function getDesiredFundingOptionCurrencyLabel(
  option: ProjectDesiredFundingOption,
) {
  const symbol = toTrimmedText(option.currencySymbol);
  if (symbol) return symbol;

  const code = toTrimmedText(option.currencyCode).toUpperCase();
  if (code === "EUR") return "€";
  if (code) return code;

  return "€";
}

function extractTimelineYear(value: string | null | undefined) {
  const raw = toTrimmedText(value);
  if (!raw) return null;
  const date = new Date(raw);
  if (!Number.isNaN(date.getTime())) {
    return String(date.getFullYear());
  }
  const match = raw.match(/^(\d{4})/);
  return match?.[1] ?? null;
}

function extractTimelineMonthIndex(value: string | null | undefined) {
  const raw = toTrimmedText(value);
  if (!raw) return null;
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return null;
  return date.getMonth();
}

function createFinanceurTimelineMonthlyBucket(): FinanceurTimelineMonthlyBucket {
  return {
    expectedPaymentsEuro: 0,
    receivedPaymentsEuro: 0,
  };
}

function createFinanceurTimelineYearSummary(
  year: string,
): FinanceurTimelineYearSummary {
  return {
    year,
    expectedPaymentsEuro: 0,
    receivedPaymentsEuro: 0,
    months: {},
  };
}

function aggregateFinanceurTimelineYears(
  years: FinanceurTimelineYearSummary[],
): FinanceurTimelineYearSummary | null {
  if (!Array.isArray(years) || years.length === 0) return null;

  const aggregate = createFinanceurTimelineYearSummary("all");

  years.forEach((yearNode) => {
    aggregate.expectedPaymentsEuro += Number(yearNode.expectedPaymentsEuro || 0);
    aggregate.receivedPaymentsEuro += Number(yearNode.receivedPaymentsEuro || 0);

    MONTH_COLUMNS.forEach((month) => {
      const monthNode = yearNode.months[month.index];
      if (!monthNode) return;

      if (!aggregate.months[month.index]) {
        aggregate.months[month.index] = createFinanceurTimelineMonthlyBucket();
      }

      aggregate.months[month.index].expectedPaymentsEuro += Number(
        monthNode.expectedPaymentsEuro || 0,
      );
      aggregate.months[month.index].receivedPaymentsEuro += Number(
        monthNode.receivedPaymentsEuro || 0,
      );
    });
  });

  return {
    ...aggregate,
    expectedPaymentsEuro: Number(aggregate.expectedPaymentsEuro.toFixed(2)),
    receivedPaymentsEuro: Number(aggregate.receivedPaymentsEuro.toFixed(2)),
  };
}

function getCurrentTimelineYearString() {
  return String(new Date().getFullYear());
}

function buildFinanceurTimelineValues(
  yearSummary: FinanceurTimelineYearSummary,
  key: keyof FinanceurTimelineMonthlyBucket,
) {
  return MONTH_COLUMNS.map((month) =>
    Number((yearSummary.months[month.index]?.[key] || 0).toFixed(2)),
  );
}

function buildFinanceurTimelineYears(
  financeur: SynthesesGroupedFinanceurItem | null,
) {
  if (!financeur) return [];

  const yearMap = new Map<string, FinanceurTimelineYearSummary>();

  financeur.contracts.forEach((contract) => {
    const contractDateYear = extractTimelineYear(contract.contractDate);

    contract.expectedPayments.forEach((event) => {
      const year = extractTimelineYear(event.at) || contractDateYear;
      if (!year) return;

      if (!yearMap.has(year)) {
        yearMap.set(year, createFinanceurTimelineYearSummary(year));
      }
      const yearNode = yearMap.get(year)!;
      yearNode.expectedPaymentsEuro += Number(event.amountEuro || 0);

      const monthIndex = extractTimelineMonthIndex(event.at);
      if (monthIndex !== null) {
        if (!yearNode.months[monthIndex]) {
          yearNode.months[monthIndex] = createFinanceurTimelineMonthlyBucket();
        }
        yearNode.months[monthIndex].expectedPaymentsEuro += Number(
          event.amountEuro || 0,
        );
      }
    });

    contract.paidPayments.forEach((event) => {
      const year = extractTimelineYear(event.at) || contractDateYear;
      if (!year) return;

      if (!yearMap.has(year)) {
        yearMap.set(year, createFinanceurTimelineYearSummary(year));
      }
      const yearNode = yearMap.get(year)!;
      yearNode.receivedPaymentsEuro += Number(event.amountEuro || 0);

      const monthIndex = extractTimelineMonthIndex(event.at);
      if (monthIndex !== null) {
        if (!yearNode.months[monthIndex]) {
          yearNode.months[monthIndex] = createFinanceurTimelineMonthlyBucket();
        }
        yearNode.months[monthIndex].receivedPaymentsEuro += Number(
          event.amountEuro || 0,
        );
      }
    });
  });

  return Array.from(yearMap.values())
    .map((yearNode) => ({
      ...yearNode,
      expectedPaymentsEuro: Number(yearNode.expectedPaymentsEuro.toFixed(2)),
      receivedPaymentsEuro: Number(yearNode.receivedPaymentsEuro.toFixed(2)),
    }))
    .sort((a, b) => Number(a.year) - Number(b.year));
}

function buildFinanceurContractYears(
  financeur: SynthesesGroupedFinanceurItem | null,
) {
  if (!financeur) return [];

  const years = new Set<number>();
  financeur.contracts.forEach((contract) => {
    buildContractDeclaredYears(contract).forEach((year) => {
      years.add(year);
    });
  });

  const sortedYears = Array.from(years).sort((a, b) => a - b);
  if (sortedYears.length > 0) {
    return sortedYears.map((year) => String(year));
  }

  return [getCurrentTimelineYearString()];
}

type FinanceurCatalogItem = {
  id?: number | null;
  structureName?: string | null;
  fullname?: string | null;
  name?: string | null;
  title?: string | null;
  email?: string | null;
};

type FinanceurTimelineMonthlyBucket = {
  expectedPaymentsEuro: number;
  receivedPaymentsEuro: number;
};

type FinanceurTimelineYearSummary = {
  year: string;
  expectedPaymentsEuro: number;
  receivedPaymentsEuro: number;
  months: Record<number, FinanceurTimelineMonthlyBucket>;
};

type FinanceurRouteTarget = {
  financeurId: string | null;
  financeurName: string | null;
  synthesesContractId: number | null;
  contractNodeId: string | null;
  projectFilterKey: string | null;
  projectName: string | null;
  action: string | null;
  targetLineId: number | null;
  targetProjectId: number | null;
};

type ContractProjectFilterOption = {
  key: string;
  label: string;
};

const FINANCEUR_NAVIGATION_TARGET_STORAGE_KEY =
  "syntheses:financeurs:navigation-target";
const FINANCEUR_ROUTE_TARGET_ACTION_DESIRED_FUNDING_ALLOCATION =
  "desired-funding-allocation";

const MONTH_COLUMNS: Array<{ index: number; label: string }> = [
  { index: 0, label: "Jan" },
  { index: 1, label: "Fev" },
  { index: 2, label: "Mar" },
  { index: 3, label: "Avr" },
  { index: 4, label: "Mai" },
  { index: 5, label: "Juin" },
  { index: 6, label: "Juil" },
  { index: 7, label: "Aou" },
  { index: 8, label: "Sep" },
  { index: 9, label: "Oct" },
  { index: 10, label: "Nov" },
  { index: 11, label: "Dec" },
];

const MONTH_LABEL_COLUMN_CLASS = "w-[128px] shrink-0";
const MONTH_CELLS_ROW_CLASS =
  "flex flex-nowrap gap-1 [&>div]:!w-[62px] [&>div]:!min-w-[62px] [&>div]:!max-w-[62px] [&>div]:!flex-none";

function toTrimmedText(value: unknown) {
  return String(value ?? "").trim();
}

function formatLandPaymentRecipientLabel(recipient: any) {
  const organizationName = toTrimmedText(recipient?.organisation?.name);
  const fullName =
    toTrimmedText(recipient?.fullname) ||
    [recipient?.firstname, recipient?.lastname]
      .map((part) => toTrimmedText(part))
      .filter(Boolean)
      .join(" ");
  const name = toTrimmedText(recipient?.name);
  const email = toTrimmedText(recipient?.email);

  return fullName || name || email || organizationName || "Intervenant";
}

function buildLandPaymentRecipientOptions(
  recipients: unknown,
): LandPaymentRecipientOption[] {
  const optionsById = new Map<number, LandPaymentRecipientOption>();
  const list = Array.isArray(recipients) ? recipients : [];

  list.forEach((recipient: any) => {
    const recipientId = Number(recipient?.id || 0);
    if (!Number.isFinite(recipientId) || recipientId <= 0) return;

    optionsById.set(recipientId, {
      id: recipientId,
      label: formatLandPaymentRecipientLabel(recipient),
    });
  });

  return Array.from(optionsById.values()).sort((a, b) =>
    a.label.localeCompare(b.label, "fr"),
  );
}

function getLandPaymentRecipientId(source: any): number | null {
  const recipientId = Number(
    source?.recipientSubcontractorId ??
      source?.recipientSubcontractor?.id ??
      source?.landPayment?.recipientSubcontractorId ??
      source?.landPayment?.recipientSubcontractor?.id ??
      0,
  );

  return Number.isFinite(recipientId) && recipientId > 0 ? recipientId : null;
}

function getLandPaymentRecipientLabel(source: ProjectLinePaymentPreview) {
  const recipient = source.recipientSubcontractor;
  if (recipient) {
    const label = formatLandPaymentRecipientLabel(recipient);
    if (toTrimmedText(label)) return label;
  }

  const recipientId = getLandPaymentRecipientId(source);
  return recipientId ? `Intervenant #${recipientId}` : "";
}

function getLandPaymentRecipientTimelineGroupKey(
  recipientSubcontractorId: number | null | undefined,
) {
  const normalizedRecipientId = Number(recipientSubcontractorId || 0);
  return Number.isFinite(normalizedRecipientId) && normalizedRecipientId > 0
    ? `recipient-${normalizedRecipientId}`
    : "recipient-unassigned";
}

function buildTerrainTimelineCellPrefix(
  lineKey: string,
  recipientSubcontractorId: number | null | undefined,
) {
  return `${lineKey}-${getLandPaymentRecipientTimelineGroupKey(recipientSubcontractorId)}`;
}

function buildLandPaymentRecipientTimelineGroups(
  payments: ProjectLinePaymentPreview[],
  recipientOptions: LandPaymentRecipientOption[] = [],
): LandPaymentRecipientTimelineGroup[] {
  const groups = new Map<string, LandPaymentRecipientTimelineGroup>();

  recipientOptions.forEach((option) => {
    const key = getLandPaymentRecipientTimelineGroupKey(option.id);
    groups.set(key, {
      key,
      label: `Versements à ${option.label}`,
      recipientSubcontractorId: option.id,
      payments: [],
    });
  });

  payments.forEach((payment) => {
    const recipientId = getLandPaymentRecipientId(payment);
    const key = getLandPaymentRecipientTimelineGroupKey(recipientId);

    if (!groups.has(key)) {
      const recipientLabel = getLandPaymentRecipientLabel(payment);
      groups.set(key, {
        key,
        label: recipientLabel
          ? `Versements à ${recipientLabel}`
          : "Versements à l'intervenant non renseigné",
        recipientSubcontractorId: recipientId,
        payments: [],
      });
    }

    groups.get(key)?.payments.push(payment);
  });

  const sortedGroups = Array.from(groups.values()).sort((a, b) => {
    if (a.key === "recipient-unassigned") return 1;
    if (b.key === "recipient-unassigned") return -1;
    return a.label.localeCompare(b.label, "fr");
  });

  if (sortedGroups.length > 0) {
    return sortedGroups;
  }

  return [
    {
      key: "recipient-unassigned",
      label: "Versements à l'intervenant",
      recipientSubcontractorId: null,
      payments: [],
    },
  ];
}

function buildProjectVisualCatalog(
  programsResponse: ProgramsCatalogResponse | null | undefined,
) {
  const catalog = new Map<number, ProjectVisualCatalogItem>();
  const programs = Array.isArray(programsResponse) ? programsResponse : [];

  programs.forEach((program) => {
    const programName = toTrimmedText(program?.name);
    const projects = Array.isArray(program?.projects) ? program.projects : [];

    projects.forEach((project) => {
      const projectId = Number(project?.id || 0);
      if (!Number.isFinite(projectId) || projectId <= 0 || catalog.has(projectId)) {
        return;
      }

      const pictureUri =
        toTrimmedText(project?.defaultImage?.uri) ||
        toTrimmedText(project?.type?.image?.uri) ||
        null;

      catalog.set(projectId, {
        id: projectId,
        name: toTrimmedText(project?.name) || `Projet ${projectId}`,
        programName,
        pictureUri,
      });
    });
  });

  return catalog;
}

function resolveProjectPictureHref(pictureUri: string | null | undefined) {
  const mediaBase = String(import.meta.env.VITE_MEDIA_BASE || "")
    .trim()
    .replace(/\/+$/, "");
  const cleanedUri = toTrimmedText(pictureUri);
  if (mediaBase && cleanedUri) {
    return `${mediaBase}${cleanedUri}`;
  }

  return "/pexels-pixabay-235925.jpg";
}

function getFinanceurListKey(
  id: number | null | undefined,
  name: string | null | undefined,
) {
  const normalizedId = Number(id);
  if (Number.isFinite(normalizedId) && normalizedId > 0) {
    return `id:${normalizedId}`;
  }

  return `name:${toTrimmedText(name).toLocaleLowerCase()}`;
}

function getProjectFilterKey(
  projectId: number | null | undefined,
  projectName: string | null | undefined,
) {
  const normalizedProjectId = Number(projectId || 0);
  if (Number.isFinite(normalizedProjectId) && normalizedProjectId > 0) {
    return `id:${normalizedProjectId}`;
  }

  const normalizedProjectName = toTrimmedText(projectName).toLocaleLowerCase();
  return normalizedProjectName ? `name:${normalizedProjectName}` : "";
}

function collectContractProjectFilterOptions(
  contract: SynthesesGroupedContractNode,
): ContractProjectFilterOption[] {
  const options = new Map<string, ContractProjectFilterOption>();

  const registerProject = (
    projectId: number | null | undefined,
    projectName: string | null | undefined,
  ) => {
    const key = getProjectFilterKey(projectId, projectName);
    const label = toTrimmedText(projectName);
    if (!key || !label || options.has(key)) return;

    options.set(key, {
      key,
      label,
    });
  };

  registerProject(contract.project?.id, contract.project?.name);

  (Array.isArray(contract.projectTotals) ? contract.projectTotals : []).forEach(
    (projectTotal) => {
      registerProject(projectTotal?.projectId, projectTotal?.projectName);
    },
  );

  (Array.isArray(contract.lineRows) ? contract.lineRows : []).forEach((lineRow) => {
    registerProject(lineRow?.projectId, lineRow?.projectName);
  });

  return Array.from(options.values());
}

function collectPendingContractProjectFilterOptions(
  contract: PendingGroupContractDraft,
): ContractProjectFilterOption[] {
  const options = new Map<string, ContractProjectFilterOption>();

  contract.yearGroups.forEach((group) => {
    group.projectRows.forEach((row) => {
      const key = getProjectFilterKey(row.projectId, row.projectName);
      const label = toTrimmedText(row.projectName);
      if (!key || !label || options.has(key)) return;

      options.set(key, {
        key,
        label,
      });
    });
  });

  return Array.from(options.values());
}

function contractMatchesProjectFilter(
  contract: SynthesesGroupedContractNode,
  projectFilterKey: string,
) {
  if (!projectFilterKey || projectFilterKey === "all") return true;

  return collectContractProjectFilterOptions(contract).some(
    (option) => option.key === projectFilterKey,
  );
}

function pendingContractMatchesProjectFilter(
  contract: PendingGroupContractDraft,
  projectFilterKey: string,
) {
  if (!projectFilterKey || projectFilterKey === "all") return true;

  return collectPendingContractProjectFilterOptions(contract).some(
    (option) => option.key === projectFilterKey,
  );
}

const PROJECT_FILTER_TONES = [
  {
    dotClassName: "bg-sky-500",
    inactiveClassName:
      "border-sky-200 bg-sky-50 text-sky-700 hover:border-sky-300 hover:bg-sky-100",
    activeClassName:
      "border-sky-500 bg-sky-600 text-white shadow-sm shadow-sky-200",
  },
  {
    dotClassName: "bg-emerald-500",
    inactiveClassName:
      "border-emerald-200 bg-emerald-50 text-emerald-700 hover:border-emerald-300 hover:bg-emerald-100",
    activeClassName:
      "border-emerald-500 bg-emerald-600 text-white shadow-sm shadow-emerald-200",
  },
  {
    dotClassName: "bg-amber-500",
    inactiveClassName:
      "border-amber-200 bg-amber-50 text-amber-700 hover:border-amber-300 hover:bg-amber-100",
    activeClassName:
      "border-amber-400 bg-amber-500 text-white shadow-sm shadow-amber-200",
  },
  {
    dotClassName: "bg-rose-500",
    inactiveClassName:
      "border-rose-200 bg-rose-50 text-rose-700 hover:border-rose-300 hover:bg-rose-100",
    activeClassName:
      "border-rose-500 bg-rose-600 text-white shadow-sm shadow-rose-200",
  },
  {
    dotClassName: "bg-indigo-500",
    inactiveClassName:
      "border-indigo-200 bg-indigo-50 text-indigo-700 hover:border-indigo-300 hover:bg-indigo-100",
    activeClassName:
      "border-indigo-500 bg-indigo-600 text-white shadow-sm shadow-indigo-200",
  },
  {
    dotClassName: "bg-teal-500",
    inactiveClassName:
      "border-teal-200 bg-teal-50 text-teal-700 hover:border-teal-300 hover:bg-teal-100",
    activeClassName:
      "border-teal-500 bg-teal-600 text-white shadow-sm shadow-teal-200",
  },
] as const;

function getProjectFilterTone(projectFilterKey: string) {
  if (!projectFilterKey) return PROJECT_FILTER_TONES[0];

  let hash = 0;
  for (let index = 0; index < projectFilterKey.length; index += 1) {
    hash = (hash * 31 + projectFilterKey.charCodeAt(index)) >>> 0;
  }

  return PROJECT_FILTER_TONES[hash % PROJECT_FILTER_TONES.length];
}

function getContractCollapseKey(
  contract: SynthesesGroupedContractNode,
  index: number,
) {
  const contractId = extractSynthesesContractId(contract);
  if (Number.isFinite(contractId) && contractId > 0) {
    return `contract:${contractId}`;
  }

  const realFundingId = Number(contract.realFundingId || 0);
  if (Number.isFinite(realFundingId) && realFundingId > 0) {
    return `realfunding:${realFundingId}`;
  }

  return [
    "fallback",
    index,
    toTrimmedText(contract.title),
    toTrimmedText(contract.contractDate),
  ].join(":");
}

function getFinanceurCatalogName(item: FinanceurCatalogItem) {
  return toTrimmedText(
    item?.structureName || item?.fullname || item?.name || item?.title || "",
  );
}

function isVerdeoFinanceurCandidate(
  name: string | null | undefined,
  email: string | null | undefined,
) {
  return (
    toTrimmedText(email).toLocaleLowerCase() === "financer@verdeo.fr" ||
    toTrimmedText(name).toLocaleLowerCase() === "verdeo"
  );
}

function buildMergedFinanceurs(
  groupedFinanceurs: SynthesesGroupedFinanceurItem[],
  financeurCatalog: FinanceurCatalogItem[] | null | undefined,
) {
  const merged = new Map<string, SynthesesGroupedFinanceurItem>();

  groupedFinanceurs.forEach((financeur) => {
    const normalizedName = toTrimmedText(financeur.name) || "Financeur";
    const normalizedEmail = toTrimmedText(financeur.email) || null;
    merged.set(getFinanceurListKey(financeur.id, normalizedName), {
      ...financeur,
      name: normalizedName,
      email: normalizedEmail,
      isVerdeo:
        financeur.isVerdeo ||
        isVerdeoFinanceurCandidate(normalizedName, normalizedEmail),
    });
  });

  (Array.isArray(financeurCatalog) ? financeurCatalog : []).forEach((item) => {
    const name = getFinanceurCatalogName(item);
    if (!name) return;

    const numericId = Number(item?.id);
    const id =
      Number.isFinite(numericId) && numericId > 0 ? numericId : null;
    const email = toTrimmedText(item?.email) || null;
    const key = getFinanceurListKey(id, name);
    const existing = merged.get(key);

    if (existing) {
      merged.set(key, {
        ...existing,
        name: existing.name || name,
        email: existing.email || email,
        isVerdeo:
          existing.isVerdeo ||
          isVerdeoFinanceurCandidate(existing.name || name, existing.email || email),
      });
      return;
    }

    merged.set(key, {
      id,
      name,
      email,
      isVerdeo: isVerdeoFinanceurCandidate(name, email),
      contracts: [],
      totals: {
        contractAmountEuro: 0,
        allocationsEuro: 0,
        expectedPaymentsEuro: 0,
        receivedPaymentsEuro: 0,
        landPaymentsEuro: 0,
      },
    });
  });

  return Array.from(merged.values()).sort((a, b) =>
    toTrimmedText(a.name).localeCompare(toTrimmedText(b.name), "fr"),
  );
}

export function buildReminders(
  financeur: SynthesesGroupedFinanceurItem | null,
): SynthesesReminderItem[] {
  if (!financeur) return [];

  const reminders: SynthesesReminderItem[] = [];

  financeur.contracts.forEach((contract, index) => {
    const contractLabel = contract.title || `Contrat ${index + 1}`;
    const targetSynthesesContractId = extractSynthesesContractId(contract);

    if (!contract.contractDate) {
      reminders.push({
        id: `${contract.id}-missing-date`,
        level: "medium",
        title: `${contractLabel}: date contrat manquante`,
        description:
          "Renseignez la date du contrat pour fiabiliser le suivi temporel.",
        targetNodeId: String(contract.id || "").trim() || null,
        targetSynthesesContractId,
      });
    }

    if (contract.dateAmountFallback) {
      reminders.push({
        id: `${contract.id}-fallback-date`,
        level: "medium",
        title: `${contractLabel}: date de référence utilisée`,
        description:
          "La vraie date du contrat n'est pas encore renseignée. Une date de référence est utilisée pour figer le taux de change.",
        targetNodeId: String(contract.id || "").trim() || null,
        targetSynthesesContractId,
      });
    }

    const unpaidExpected = Number(
      (
        contract.totals.expectedPaymentsEuro -
        contract.totals.receivedPaymentsEuro
      ).toFixed(2),
    );
    if (unpaidExpected > 0.01) {
      reminders.push({
        id: `${contract.id}-pending-paid`,
        level: "low",
        title: `${contractLabel}: versements attendus en attente`,
        description: `${formatEuro(unpaidExpected)} prévus non encore reçus.`,
        targetNodeId: String(contract.id || "").trim() || null,
        targetSynthesesContractId,
      });
    }
  });

  return reminders.slice(0, 20);
}

export function buildCalendarEvents(
  financeur: SynthesesGroupedFinanceurItem | null,
): SynthesesCalendarEvent[] {
  if (!financeur) return [];

  const events: SynthesesCalendarEvent[] = [];
  const today = new Date();
  const todayStart = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );

  const isFutureOrToday = (value: string | null | undefined) => {
    if (!value) return false;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return false;
    const dateStart = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return dateStart >= todayStart;
  };

  financeur.contracts.forEach((contract, index) => {
    const contractLabel = contract.title || `Contrat ${index + 1}`;
    const contractNodeId = String(contract.id || "").trim() || null;
    const targetSynthesesContractId = extractSynthesesContractId(contract);

    if (contract.contractDate) {
      const contractPending = isFutureOrToday(contract.contractDate);
      events.push({
        id: `${contract.id}-signed`,
        date: contract.contractDate,
        label: `${contractLabel} • Contrat signé`,
        count: 1,
        isPending: contractPending,
        completionLabel: contractPending ? null : "Signé",
        targetNodeId: contractNodeId,
        targetSynthesesContractId,
      });
    }

    contract.expectedPayments.forEach((payment, paymentIndex) => {
      if (!payment.at) return;
      const expectedPending = !payment.datePaid;
      events.push({
        id: `${contract.id}-expected-${payment.id ?? paymentIndex}`,
        date: payment.at,
        label: `${contractLabel} • Versement financeur`,
        count: 1,
        isPending: expectedPending,
        completionLabel: expectedPending ? null : "Reçu",
        targetNodeId: contractNodeId,
        targetSynthesesContractId,
      });
    });

    contract.paidPayments.forEach((payment, paymentIndex) => {
      if (!payment.at) return;
      events.push({
        id: `${contract.id}-paid-${payment.id ?? paymentIndex}`,
        date: payment.at,
        label: `${contractLabel} • Versement financeur`,
        count: 1,
        isPending: false,
        completionLabel: "Reçu",
        targetNodeId: contractNodeId,
        targetSynthesesContractId,
      });
    });

    contract.landPayments.forEach((payment, paymentIndex) => {
      const status = Number(payment.status || payment.landPaymentStatus || 0);
      const landPaymentDate =
        status >= 3
          ? payment.dateVerified || payment.dateMeasure || payment.at
          : status === 2
            ? payment.dateMeasure || payment.at
            : payment.at || payment.dateMeasure || payment.dateVerified;
      if (!landPaymentDate) return;
      events.push({
        id: `${contract.id}-land-${payment.id ?? paymentIndex}`,
        date: landPaymentDate,
        label: `${contractLabel} • Versement à l'intervenant`,
        count: 1,
        isPending: status === 1,
        completionLabel:
          status >= 3 ? "Certifié" : status === 2 ? "Réalisé" : null,
        targetNodeId: contractNodeId,
        targetSynthesesContractId,
        completionMedia:
          status >= 3 && payment.pjVerified
            ? {
                label: getTerrainMediaLabel(payment.pjVerified),
                href: getTerrainMediaHref(payment.pjVerified) || null,
              }
            : null,
      });
    });
  });

  return events;
}

type PaymentRow = {
  id: number | null;
  projectIndicatorId?: number | null;
  landPaymentId?: number | null;
  landPaymentStatus?: number | null;
  realFundingId?: number | null;
  synthesesContractId?: number | null;
  synthesesContractLineId?: number | null;
  lineId?: number | null;
  lineTitle?: string | null;
  projectId?: number | null;
  projectName?: string | null;
  at: string | null;
  amountEuro: number;
  source?: string | null;
  status?: number | null;
  dateMeasure?: string | null;
  dateVerified?: string | null;
  pjVerified?: TerrainMediaRef | null;
  recipientSubcontractorId?: number | null;
  recipientSubcontractor?: unknown;
};

type TerrainMediaRef = {
  id: number | null;
  name: string | null;
  uri?: string | null;
  path?: string | null;
};

type LandPaymentRecipientOption = {
  id: number;
  label: string;
};

type LandPaymentRecipientTimelineGroup = {
  key: string;
  label: string;
  recipientSubcontractorId: number | null;
  payments: ProjectLinePaymentPreview[];
};

type ProgramsCatalogResponse = Array<{
  id?: number;
  name?: string;
  projects?: Array<{
    id?: number;
    name?: string;
    defaultImage?: {
      uri?: string | null;
    } | null;
    type?: {
      image?: {
        uri?: string | null;
      } | null;
    } | null;
  }>;
}>;

type ProjectVisualCatalogItem = {
  id: number;
  name: string;
  programName: string;
  pictureUri: string | null;
};

type ProjectDesiredFundingOption = {
  id: number;
  title: string;
  amount: number;
  convertedAmount: number | null;
  currencyCode: string | null;
  currencySymbol: string | null;
};

type ProjectDesiredFundingCatalogEntry = {
  projectId: number;
  desiredFundings: ProjectDesiredFundingOption[];
};

type ProjectLandPaymentRecipientCatalogEntry = {
  projectId: number;
  recipients: LandPaymentRecipientOption[];
};

type ProjectAmountColumn = {
  id: string;
  projectId?: number | null;
  projectName: string;
  amountEuro: number;
  contractCount: number;
};

type DraftExpectedPayment = {
  id: string;
  amount: string;
  expectedAt: string;
  status: 1 | 2;
  receivedAt: string;
};

type DesiredFundingAllocationDraft = {
  desiredFundingId: number;
  amountEuro: string;
};

type GroupContractProjectRowDraft = {
  id: string;
  lineId: number | null;
  realFundingId: number | null;
  projectId: number | null;
  projectName: string;
  title: string;
  amountEuro: string;
  desiredFundingIds: number[];
  desiredFundingAllocations: DesiredFundingAllocationDraft[];
  initialDesiredFundingIds: number[];
  desiredFundingSelectionTouched: boolean;
  expectedPayments: DraftExpectedPayment[];
  initialExpectedPaymentIds: number[];
};

type GroupContractYearGroupDraft = {
  id: string;
  year: string;
  projectRows: GroupContractProjectRowDraft[];
};

type GroupContractFormDraft = {
  title: string;
  contractDate: string;
  amountEuro: string;
  notes: string;
  expectedPayments: DraftExpectedPayment[];
  initialExpectedPaymentIds: number[];
  yearGroups: GroupContractYearGroupDraft[];
};

type PendingGroupContractDraft = GroupContractFormDraft & {
  id: string;
  financeurId: number | null;
  financeurName: string;
  createdAt: string;
};

type FinanceurGroupedContractCreationDraft = {
  isCreating: boolean;
  financeurId: number | null;
  financeurName: string;
  form: GroupContractFormDraft | null;
  pending: PendingGroupContractDraft[];
};

type FinanceurContractFormStep = 1 | 2 | 3 | 4;

const FINANCEUR_CONTRACT_FORM_STEPS: Array<{
  id: FinanceurContractFormStep;
  label: string;
  description: string;
}> = [
  {
    id: 1,
    label: "Contrat",
    description: "Identifier le contrat signé avec le financeur.",
  },
  {
    id: 2,
    label: "Actions financées",
    description: "Répartir le contrat par année, projet et action financée.",
  },
  {
    id: 3,
    label: "Versements",
    description: "Ajouter les versements prévus ou déjà reçus.",
  },
  {
    id: 4,
    label: "Récapitulatif",
    description: "Contrôler les données avant l'enregistrement.",
  },
];

const FINANCEUR_CONTRACT_FORM_STEP_GUIDANCE: Record<
  FinanceurContractFormStep,
  { title: string; description: string; items: string[] }
> = {
  1: {
    title: "Informations à recopier depuis le contrat signé",
    description:
      "Cette étape identifie le contrat. Utilisez les informations officielles du document signé pour éviter les doublons et faciliter le suivi.",
    items: [
      "Le financeur est l'organisation sélectionnée dans la colonne de gauche.",
      "La date du contrat est la date de signature ou d'obtention du financement.",
      "Le montant correspond au total du contrat signé, avant la répartition par actions financées.",
    ],
  },
  2: {
    title: "Que signifie une action financée ?",
    description:
      "Une action financée est une partie concrète du projet couverte par le contrat : travaux, achat, accompagnement, restauration, mission terrain, etc.",
    items: [
      "Créez une action par usage important si le contrat finance plusieurs choses.",
      "Le montant d'une action correspond uniquement à la part du contrat affectée à cette action.",
      "Si l'action correspond à un financement souhaité déjà renseigné dans le projet, associez-le pour garder le lien entre souhaité et obtenu.",
    ],
  },
  3: {
    title: "Comment saisir les versements ?",
    description:
      "Un versement correspond à une échéance du contrat. Il peut être prévu, puis marqué comme reçu lorsque l'argent arrive réellement.",
    items: [
      "Ajoutez un versement pour chaque échéance prévue avec le financeur.",
      "Gardez le statut Non reçu tant que le paiement n'est pas encaissé.",
      "Passez le statut à Reçu et renseignez la date de réception quand le versement arrive.",
    ],
  },
  4: {
    title: "Dernière vérification avant sauvegarde",
    description:
      "Relisez les informations avant d'enregistrer. Une erreur ici impacte les contributions déjà obtenues et le suivi de financements.",
    items: [
      "Vérifiez le financeur, la date, l'intitulé et le montant global.",
      "Vérifiez que les actions financées correspondent bien au contrat.",
      "Vérifiez que les versements sont cohérents avec l'échéancier du contrat.",
    ],
  },
};

type EditingPersistedContractState = {
  sourceType: string;
  contractId: number | null;
  realFundingIds: number[];
  contractNodeId: string;
  contractCollapseKey: string;
  contractLabel: string;
};

type ContractExpensePostDisplayRow = {
  id: number | null;
  title: string;
  amountLocal: number | null;
  amountEuro: number;
  currencyCode: string | null;
  desiredFundingSourceId: number | null;
  obtainedDate: string | null;
};

type ContractLineDisplayRow = {
  key: string;
  synthesesContractId: number | null;
  lineId: number | null;
  realFundingId: number | null;
  year: number;
  projectId: number | null;
  projectName: string;
  title: string;
  amountEuro: number;
  expensePostId: number | null;
  amountLocal: number | null;
  currencyCode: string | null;
  obtainedDate: string | null;
  desiredFundingSourceId: number | null;
  desiredFundingAllocations: ContractExpensePostDisplayRow[];
};

type ContractProjectDisplayRow = {
  key: string;
  projectId: number | null;
  projectName: string;
  amountEuro: number;
  lines: ContractLineDisplayRow[];
};

type ProjectLinePaymentPreview = {
  id: string;
  at: string | null;
  amountEuro: number;
  synthesesContractId?: number | null;
  synthesesContractLineId?: number | null;
  lineId?: number | null;
  realFundingId?: number | null;
  lineTitle?: string | null;
  projectId?: number | null;
  projectName?: string | null;
  projectIndicatorId?: number | null;
  landPaymentId?: number | null;
  landPaymentStatus?: number | null;
  source?: string | null;
  status?: number | null;
  dateMeasure?: string | null;
  dateVerified?: string | null;
  pjVerified?: TerrainMediaRef | null;
  recipientSubcontractorId?: number | null;
  recipientSubcontractor?: unknown;
};

type TerrainReconcileState = {
  lineKey: string;
  lineLabel: string;
  synthesesContractId: number | null;
  synthesesContractLineId: number | null;
  realFundingId: number | null;
  landPaymentId: number | null;
  landPaymentStatus: number | null;
  plannedRows: ProjectLinePaymentPreview[];
  realizedRows: ProjectLinePaymentPreview[];
  certifiedRows: ProjectLinePaymentPreview[];
  selectedEstimatedId: number | null;
  selectedMeasuredId: number | null;
  selectedVerifiedId: number | null;
};

type TerrainQuickLinkState = {
  projectKey: string;
  label: string;
  targetLandPaymentId: number | null;
  targetLandPaymentStatus: number | null;
  targetPayment: ProjectLinePaymentPreview | null;
  targetLandPaymentRows: ProjectLinePaymentPreview[];
  selectedEstimatedId: number | null;
  selectedMeasuredId: number | null;
  selectedVerifiedId: number | null;
  selectedEstimatedPayment: ProjectLinePaymentPreview | null;
  selectedMeasuredPayment: ProjectLinePaymentPreview | null;
  selectedVerifiedPayment: ProjectLinePaymentPreview | null;
  error: string | null;
};

type TerrainLinkVisualInfo = {
  title: string;
  rows: string[];
  cellClassName: string;
};

type TerrainCellMoveAction = {
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
};

type TerrainCellTooltipInfo = {
  title: string;
  rows: string[];
  warning?: string | null;
};

type TerrainMoveTargetOption = {
  lineId: number;
  label: string;
};

type TerrainMoveState = {
  lineKey: string;
  paymentRowId: string;
  landPaymentId: number;
  selectedTargetLineId: number | null;
  targetOptions: TerrainMoveTargetOption[];
};

type TerrainPaymentEditorState = {
  lineKey: string;
  lineLabel: string;
  synthesesContractId: number | null;
  synthesesContractLineId: number | null;
  realFundingId: number | null;
  projectId?: number | null;
  projectName?: string | null;
  lineTitle?: string | null;
  landPaymentId: number | null;
  status: number;
  amountEuro: string;
  expectedAt: string;
  dateMeasure: string;
  dateVerified: string;
  pjVerified: TerrainMediaRef | null;
  recipientSubcontractorId: number | null;
};

type TerrainCertifiedIndicatorOption = {
  id: number;
  label: string;
  disabled: boolean;
};

type TerrainCertifiedLineOption = {
  lineKey: string;
  label: string;
  synthesesContractId: number | null;
  synthesesContractLineId: number | null;
  realFundingId: number | null;
  amountEuro: number;
};

type TerrainCertifiedEditorState = {
  lineKey: string;
  lineLabel: string;
  cellKey: string;
  synthesesContractId: number | null;
  synthesesContractLineId: number | null;
  realFundingId: number | null;
  projectId: number | null;
  landPaymentId: number | null;
  year: number;
  monthIndex: number;
  amountEuro: string;
  selectedLineKey: string;
  targetLineOptions: TerrainCertifiedLineOption[];
  pjVerified: TerrainMediaRef | null;
  estimatedIndicatorId: number | null;
  measuredIndicatorId: number | null;
  estimatedOptions: TerrainCertifiedIndicatorOption[];
  measuredOptions: TerrainCertifiedIndicatorOption[];
  recipientSubcontractorId: number | null;
};

type ContractLineMoveTargetOption = {
  contractId: number;
  label: string;
};

type ActiveContractLineMoveState = {
  sourceContractId: number;
  sourceContractLabel: string;
  lineId: number;
  lineKey: string;
  year: number;
  projectName: string;
  amountEuro: number;
  targetContractId: string;
};

type ActiveContractsMergeState = {
  targetContractId: string;
  sourceContractIds: string[];
};

const SYNTHESES_SECONDARY_BUTTON_CLASS =
  "inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50";
const SYNTHESES_PRIMARY_BUTTON_CLASS =
  "inline-flex items-center rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700";
const SYNTHESES_INPUT_CLASS =
  "w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2";

function TerrainMovePanel({
  terrainMoveState,
  terrainMoveError,
  movingTerrainPayment,
  activeTerrainMoveSummary,
  onCancel,
  onChangeTarget,
  onConfirm,
}: {
  terrainMoveState: TerrainMoveState | null;
  terrainMoveError: string | null;
  movingTerrainPayment: boolean;
  activeTerrainMoveSummary: Array<{
    status: number;
    label: string;
    total: number;
  }>;
  onCancel: () => void;
  onChangeTarget: (targetLineId: number | null) => void;
  onConfirm: () => void;
}) {
  if (!terrainMoveState) return null;

  return (
    <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-1">
          <div className="flex items-center gap-1.5">
            <div className="text-sm font-medium text-slate-900">
              Déplacement du versement à l'intervenant
            </div>
            <SectionHint>
              <div className="space-y-1.5">
                <p>Le déplacement agit sur le versement à l'intervenant complet.</p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>les statuts déjà reliés suivent vers la ligne cible ;</li>
                  <li>vous choisissez une autre ligne du même projet ;</li>
                  <li>la ligne source ne conserve plus ce versement après validation.</li>
                </ul>
              </div>
            </SectionHint>
          </div>
          <p className="max-w-2xl text-xs leading-relaxed text-slate-600">
            Choisissez le contrat cible du même projet. Le prévu, le réalisé et le certifié
            déjà reliés seront déplacés ensemble.
          </p>
        </div>
        <button
          type="button"
          className={SYNTHESES_SECONDARY_BUTTON_CLASS}
          onClick={onCancel}
          disabled={movingTerrainPayment}
        >
          Annuler
        </button>
      </div>

      {activeTerrainMoveSummary.length > 0 ? (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          {activeTerrainMoveSummary.map((item) => (
            <span
              key={`timeline-move-summary-${item.status}`}
              className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[11px] font-medium text-amber-800"
            >
              {item.label} {formatEuro(item.total)}
            </span>
          ))}
        </div>
      ) : null}

      {terrainMoveError ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {terrainMoveError}
        </div>
      ) : null}

      <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
        <label className="space-y-1.5 text-sm">
          <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
            Contrat cible
          </span>
          <select
            value={terrainMoveState.selectedTargetLineId ?? ""}
            onChange={(event) => {
              const value = Number(event.target.value);
              onChangeTarget(Number.isFinite(value) && value > 0 ? value : null);
            }}
            className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
          >
            <option value="">Choisir un contrat</option>
            {(terrainMoveState.targetOptions ?? []).map((option) => (
              <option key={option.lineId} value={option.lineId}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          className={SYNTHESES_PRIMARY_BUTTON_CLASS}
          onClick={onConfirm}
          disabled={
            movingTerrainPayment
            || !isPositiveId(terrainMoveState.selectedTargetLineId)
          }
        >
          {movingTerrainPayment ? "Déplacement..." : "Déplacer le versement"}
        </button>
      </div>
    </div>
  );
}

const GROUPED_CONTRACT_CREATION_STORAGE_KEY =
  "syntheses:financeurs:grouped-contract-create:draft:v2";

function buildSaveContractEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/contracts/save`;
}

function buildDeleteContractEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/contracts/delete`;
}

function buildMoveContractLineEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/contracts/move-line`;
}

function buildMergeContractsEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/contracts/merge`;
}

function buildReconcileTerrainEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/land-payments/reconcile`;
}

function buildMoveTerrainEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/syntheses/land-payments/move`;
}

function buildSaveLandPaymentEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/land-payment/save`;
}

function buildProjectIndicatorEndpoint(verified = false) {
  const configuredApiBase = String(
    import.meta.env.VITE_API_ENDPOINT ||
    (import.meta.env.VITE_IP_API
      ? `${String(import.meta.env.VITE_IP_API).replace(/\/+$/, "")}/api`
      : ""),
  )
    .trim()
    .replace(/\/+$/, "");
  if (!configuredApiBase) return "";

  return `${configuredApiBase}/project/indicator${verified ? "/verify" : ""}?responseType=status`;
}

function buildDeleteLandPaymentEndpoint() {
  const apiBase = String(import.meta.env.VITE_IP_API || "")
    .trim()
    .replace(/\/+$/, "");
  if (!apiBase) return "";

  return `${apiBase}/api/land-payment/delete`;
}

function createDraftId(prefix: string) {
  const randomPart = Math.random().toString(36).slice(2, 8);
  return `${prefix}-${Date.now()}-${randomPart}`;
}

function normalizeAmountInput(value: string) {
  return String(value || "")
    .replace(/\s+/g, "")
    .replace(",", ".")
    .trim();
}

function parseAmountInput(value: string): number | null {
  const normalized = normalizeAmountInput(value);
  if (!normalized) return null;
  const amount = Number(normalized);
  if (!Number.isFinite(amount)) return null;
  return Number(amount.toFixed(2));
}

function buildAutomaticDesiredFundingAllocationMap(
  options: ProjectDesiredFundingOption[],
  targetAmountEuro: number,
) {
  const normalizedTargetAmountEuro = Number(targetAmountEuro || 0);
  if (
    !Number.isFinite(normalizedTargetAmountEuro) ||
    normalizedTargetAmountEuro <= 0
  ) {
    return new Map<number, number>();
  }

  const targetAmountCents = Math.round(normalizedTargetAmountEuro * 100);
  if (targetAmountCents <= 0) {
    return new Map<number, number>();
  }

  const entries = options
    .map((option) => ({
      desiredFundingId: Number(option.id || 0),
      amountEuro: getDesiredFundingOptionAmountEuro(option),
      amountCents: Math.round(getDesiredFundingOptionAmountEuro(option) * 100),
    }))
    .filter(
      (entry) =>
        Number.isFinite(entry.desiredFundingId) &&
        entry.desiredFundingId > 0 &&
        Number.isFinite(entry.amountEuro) &&
        entry.amountEuro > 0 &&
        entry.amountCents > 0,
    );

  if (entries.length === 0) {
    return new Map<number, number>();
  }

  const selectedTotalCents = entries.reduce(
    (sum, entry) => sum + entry.amountCents,
    0,
  );
  if (selectedTotalCents <= 0) {
    return new Map<number, number>();
  }

  if (selectedTotalCents <= targetAmountCents) {
    return new Map(
      entries.map((entry) => [
        entry.desiredFundingId,
        Number((entry.amountCents / 100).toFixed(2)),
      ]),
    );
  }

  let remainingCents = Math.min(targetAmountCents, selectedTotalCents);
  const lastIndex = entries.length - 1;
  const allocationMap = new Map<number, number>();

  entries.forEach((entry, index) => {
    if (remainingCents <= 0) {
      return;
    }

    let allocatedCents = entry.amountCents;
    if (index === lastIndex) {
      allocatedCents = Math.min(entry.amountCents, remainingCents);
    } else {
      allocatedCents = Math.round(
        (entry.amountCents / selectedTotalCents) * targetAmountCents,
      );
      allocatedCents = Math.min(entry.amountCents, remainingCents, allocatedCents);
    }

    allocatedCents = Math.max(0, allocatedCents);
    if (allocatedCents <= 0) {
      return;
    }

    allocationMap.set(
      entry.desiredFundingId,
      Number((allocatedCents / 100).toFixed(2)),
    );
    remainingCents = Math.max(0, remainingCents - allocatedCents);
  });

  return allocationMap;
}

function getYearFromIsoDate(value: string | null): string {
  if (!value) return String(new Date().getFullYear());
  const yearMatch = String(value).match(/^(\d{4})/);
  if (yearMatch?.[1]) return yearMatch[1];
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(new Date().getFullYear());
  return String(date.getFullYear());
}

function formatDateForInput(value: string | null | undefined): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch?.[1]) return isoMatch[1];
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function buildInitialExpectedPaymentDraft(): DraftExpectedPayment {
  return {
    id: createDraftId("expected-payment"),
    amount: "",
    expectedAt: new Date().toISOString().split("T")[0],
    status: 1,
    receivedAt: "",
  };
}

function normalizeExpectedPaymentDraft(value: any): DraftExpectedPayment {
  const status = Number(value?.status) === 2 ? 2 : 1;
  const expectedAt = formatDateForInput(value?.expectedAt ?? value?.at);
  const receivedAt = status === 2
    ? formatDateForInput(value?.receivedAt ?? value?.datePaid)
    : "";

  return {
    id: String(value?.id || createDraftId("expected-payment")),
    amount: String(value?.amount ?? value?.amountEuro ?? ""),
    expectedAt,
    status,
    receivedAt,
  };
}

function buildExpectedPaymentsForContract(
  contract: SynthesesGroupedContractNode,
): {
  expectedPayments: DraftExpectedPayment[];
  initialExpectedPaymentIds: number[];
} {
  const normalizedContractId = Number(contract.synthesesContractId || 0);
  const seenPaymentIds = new Set<number>();
  const expectedPayments = (Array.isArray(contract.expectedPayments)
    ? contract.expectedPayments
    : []
  )
    .filter((payment) => {
      if (normalizedContractId <= 0) return true;
      const paymentContractId = Number(payment?.synthesesContractId || 0);
      return paymentContractId <= 0 || paymentContractId === normalizedContractId;
    })
    .map((payment) => {
      const normalizedPayment = normalizeExpectedPaymentDraft({
        id: payment.id,
        amount: Number(payment.amountEuro || 0).toFixed(2),
        expectedAt: payment.at,
        status: Number(payment.status) === 2 ? 2 : 1,
        receivedAt: payment.datePaid,
      });
      return normalizedPayment;
    })
    .filter((payment) => {
      const paymentId = Number(payment.id);
      if (!Number.isFinite(paymentId) || paymentId <= 0) return true;
      if (seenPaymentIds.has(paymentId)) return false;
      seenPaymentIds.add(paymentId);
      return true;
    })
    .sort((a, b) => String(a.expectedAt || "").localeCompare(String(b.expectedAt || "")));

  return {
    expectedPayments,
    initialExpectedPaymentIds: expectedPayments
      .map((payment) => Number(payment.id))
      .filter((id) => Number.isFinite(id) && id > 0),
  };
}

function validateExpectedPaymentsList(
  payments: DraftExpectedPayment[],
): string | null {
  for (const payment of payments) {
    const amount = parseAmountInput(payment.amount);
    if (amount === null || amount <= 0) {
      return "Veuillez renseigner un montant valide pour chaque versement du contrat.";
    }
    if (!payment.expectedAt) {
      return "Veuillez renseigner une date prévue pour chaque versement du contrat.";
    }
    if (payment.status === 2 && !payment.receivedAt) {
      return "Veuillez renseigner la date de réception pour chaque versement reçu du contrat.";
    }
  }

  return null;
}

function buildInitialProjectRow(
  selectedFinanceur: SynthesesGroupedFinanceurItem | null,
): GroupContractProjectRowDraft {
  return {
    id: createDraftId("project-row"),
    lineId: null,
    realFundingId: null,
    projectId: null,
    projectName: "",
    title: "",
    amountEuro: "",
    desiredFundingIds: [],
    desiredFundingAllocations: [],
    initialDesiredFundingIds: [],
    desiredFundingSelectionTouched: false,
    expectedPayments: [],
    initialExpectedPaymentIds: [],
  };
}

function buildInitialYearGroup(
  selectedFinanceur: SynthesesGroupedFinanceurItem | null,
  yearValue: string,
): GroupContractYearGroupDraft {
  return {
    id: createDraftId("year-group"),
    year: yearValue,
    projectRows: [buildInitialProjectRow(selectedFinanceur)],
  };
}

function normalizeProjectRowDraft(row: any): GroupContractProjectRowDraft {
  const normalizedLineId = Number(row?.lineId);
  const normalizedRealFundingId = Number(row?.realFundingId);
  const normalizedProjectId = Number(row?.projectId);
  const expectedPayments = Array.isArray(row?.expectedPayments)
    ? row.expectedPayments.map((payment: any) => normalizeExpectedPaymentDraft(payment))
    : [];
  const initialExpectedPaymentIds = Array.isArray(row?.initialExpectedPaymentIds)
    ? row.initialExpectedPaymentIds
        .map((paymentId: any) => Number(paymentId))
        .filter((paymentId: number) => Number.isFinite(paymentId) && paymentId > 0)
    : expectedPayments
        .map((payment) => Number(payment.id))
        .filter((paymentId) => Number.isFinite(paymentId) && paymentId > 0);
  const desiredFundingIds = Array.isArray(row?.desiredFundingIds)
    ? row.desiredFundingIds
        .map((desiredFundingId: any) => Number(desiredFundingId))
        .filter(
          (desiredFundingId: number) =>
            Number.isFinite(desiredFundingId) && desiredFundingId > 0,
        )
    : [];
  const desiredFundingAllocations = Array.isArray(row?.desiredFundingAllocations)
    ? row.desiredFundingAllocations
        .map((allocation: any) => {
          const desiredFundingId = Number(
            allocation?.desiredFundingId ?? allocation?.id ?? 0,
          );
          if (!Number.isFinite(desiredFundingId) || desiredFundingId <= 0) {
            return null;
          }

          return {
            desiredFundingId,
            amountEuro: String(allocation?.amountEuro ?? allocation?.amount ?? ""),
          } satisfies DesiredFundingAllocationDraft;
        })
        .filter(
          (
            allocation,
          ): allocation is DesiredFundingAllocationDraft => !!allocation,
        )
    : [];
  const initialDesiredFundingIds = Array.isArray(row?.initialDesiredFundingIds)
    ? row.initialDesiredFundingIds
        .map((desiredFundingId: any) => Number(desiredFundingId))
        .filter(
          (desiredFundingId: number) =>
            Number.isFinite(desiredFundingId) && desiredFundingId > 0,
        )
    : desiredFundingIds;
  return {
    id: String(row?.id || createDraftId("project-row")),
    lineId:
      Number.isFinite(normalizedLineId) && normalizedLineId > 0
        ? normalizedLineId
        : null,
    realFundingId:
      Number.isFinite(normalizedRealFundingId) && normalizedRealFundingId > 0
        ? normalizedRealFundingId
        : null,
    projectId:
      Number.isFinite(normalizedProjectId) && normalizedProjectId > 0
        ? normalizedProjectId
        : null,
    projectName: String(row?.projectName || ""),
    title: String(row?.title ?? row?.lineTitle ?? ""),
    amountEuro: String(row?.amountEuro || ""),
    desiredFundingIds,
    desiredFundingAllocations,
    initialDesiredFundingIds,
    desiredFundingSelectionTouched: !!row?.desiredFundingSelectionTouched,
    expectedPayments,
    initialExpectedPaymentIds,
  };
}

function normalizeYearValue(yearValue: unknown, fallbackYear: string): string {
  const normalized = String(yearValue || "").trim();
  if ("" === normalized) return fallbackYear;
  if (/^\d{4}$/.test(normalized)) return normalized;
  // Keep partial/invalid user input editable; final validation happens on save.
  return normalized;
}

function getNextYearValue(
  yearGroups: GroupContractYearGroupDraft[],
  fallbackYear: string,
): string {
  const numericYears = yearGroups
    .map((group) => Number(String(group.year || "").trim()))
    .filter((year) => Number.isInteger(year) && year >= 1900 && year <= 2200);

  if (numericYears.length > 0) {
    return String(Math.max(...numericYears) + 1);
  }

  const fallback = Number(String(fallbackYear || "").trim());
  if (Number.isInteger(fallback) && fallback > 0) {
    return String(fallback + 1);
  }

  return fallbackYear;
}

function buildYearGroupsFromLegacyRows(
  rows: any[],
  fallbackYear: string,
): GroupContractYearGroupDraft[] {
  const grouped = new Map<string, GroupContractProjectRowDraft[]>();
  rows.forEach((row) => {
    const year = normalizeYearValue(row?.year, fallbackYear);
    const normalizedRow = normalizeProjectRowDraft(row);
    if (!grouped.has(year)) {
      grouped.set(year, []);
    }
    grouped.get(year)?.push(normalizedRow);
  });

  const yearGroups = Array.from(grouped.entries()).map(
    ([year, projectRows]) => ({
      id: createDraftId("year-group"),
      year,
      projectRows: projectRows.length
        ? projectRows
        : [normalizeProjectRowDraft({})],
    }),
  );

  return yearGroups.length
    ? yearGroups
    : [
        {
          id: createDraftId("year-group"),
          year: fallbackYear,
          projectRows: [normalizeProjectRowDraft({})],
        },
      ];
}

function normalizeYearGroupsDraft(
  value: any,
  fallbackYear: string,
): GroupContractYearGroupDraft[] {
  if (Array.isArray(value?.yearGroups)) {
    const groups = value.yearGroups.map((group: any) => {
      const projectRows = Array.isArray(group?.projectRows)
        ? group.projectRows.map((row: any) => normalizeProjectRowDraft(row))
        : [];
      return {
        id: String(group?.id || createDraftId("year-group")),
        year: normalizeYearValue(group?.year, fallbackYear),
        projectRows: projectRows.length
          ? projectRows
          : [normalizeProjectRowDraft({})],
      } satisfies GroupContractYearGroupDraft;
    });

    if (groups.length > 0) return groups;
  }

  if (Array.isArray(value?.projectRows)) {
    return buildYearGroupsFromLegacyRows(value.projectRows, fallbackYear);
  }

  return [
    {
      id: createDraftId("year-group"),
      year: fallbackYear,
      projectRows: [normalizeProjectRowDraft({})],
    },
  ];
}

function normalizeGroupContractFormDraft(value: any): GroupContractFormDraft {
  const contractDate = String(value?.contractDate || "");
  const fallbackYear = getYearFromIsoDate(contractDate || null);
  const normalizedYearGroups = normalizeYearGroupsDraft(value, fallbackYear);
  const rowExpectedPayments = normalizedYearGroups.flatMap((group) =>
    group.projectRows.flatMap((row) =>
      Array.isArray(row.expectedPayments) ? row.expectedPayments : [],
    ),
  );
  const rowInitialExpectedPaymentIds = normalizedYearGroups.flatMap((group) =>
    group.projectRows.flatMap((row) =>
      Array.isArray(row.initialExpectedPaymentIds)
        ? row.initialExpectedPaymentIds
        : [],
    ),
  );
  const expectedPayments = Array.isArray(value?.expectedPayments)
    ? value.expectedPayments.map((payment: any) =>
        normalizeExpectedPaymentDraft(payment),
      )
    : rowExpectedPayments;
  const initialExpectedPaymentIds = Array.isArray(value?.initialExpectedPaymentIds)
    ? value.initialExpectedPaymentIds
        .map((paymentId: any) => Number(paymentId))
        .filter((paymentId: number) => Number.isFinite(paymentId) && paymentId > 0)
    : Array.from(
        new Set(
          (rowInitialExpectedPaymentIds.length > 0
            ? rowInitialExpectedPaymentIds
            : expectedPayments.map((payment) => Number(payment.id))
          ).filter((paymentId) => Number.isFinite(paymentId) && paymentId > 0),
        ),
      );

  return {
    title: String(value?.title || ""),
    contractDate,
    amountEuro: String(value?.amountEuro || ""),
    notes: String(value?.notes || ""),
    expectedPayments,
    initialExpectedPaymentIds,
    yearGroups: normalizedYearGroups,
  };
}

function normalizePendingContractDraft(value: any): PendingGroupContractDraft {
  const normalizedForm = normalizeGroupContractFormDraft(value);
  return {
    ...normalizedForm,
    id: String(value?.id || createDraftId("pending-contract")),
    financeurId: Number.isFinite(Number(value?.financeurId))
      ? Number(value.financeurId)
      : null,
    financeurName: String(value?.financeurName || "").trim(),
    createdAt: String(value?.createdAt || new Date().toISOString()),
  };
}

function normalizeCreationDraftState(
  value: FinanceurGroupedContractCreationDraft,
): FinanceurGroupedContractCreationDraft {
  const pending = Array.isArray(value?.pending)
    ? value.pending.map((item) => normalizePendingContractDraft(item))
    : [];
  const form = value?.form ? normalizeGroupContractFormDraft(value.form) : null;

  return {
    isCreating: !!value?.isCreating,
    financeurId: Number.isFinite(Number(value?.financeurId))
      ? Number(value.financeurId)
      : null,
    financeurName: String(value?.financeurName || ""),
    form,
    pending,
  };
}

function normalizeContractDateInput(value: string | null | undefined): string {
  const raw = String(value || "").trim();
  if (!raw) return "";
  const isoMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoMatch?.[1]) return isoMatch[1];
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function extractSynthesesContractId(
  contract: SynthesesGroupedContractNode,
): number | null {
  const directId = Number(contract.synthesesContractId);
  if (Number.isFinite(directId) && directId > 0) return directId;

  const match = String(contract.id || "").match(/^synth-contract-(\d+)$/);
  if (!match?.[1]) return null;
  const parsed = Number(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function contractMatchesRouteTarget(
  contract: SynthesesGroupedContractNode,
  target: FinanceurRouteTarget | null,
): boolean {
  if (!target) return false;

  if (
    target.synthesesContractId
    && extractSynthesesContractId(contract) === target.synthesesContractId
  ) {
    return true;
  }

  if (
    target.contractNodeId
    && String(contract.id || "").trim() === target.contractNodeId
  ) {
    return true;
  }

  return false;
}

function financeurMatchesRouteTargetIdentity(
  financeur: SynthesesGroupedFinanceurItem,
  target: FinanceurRouteTarget | null,
): boolean {
  if (!target) return false;

  if (target.financeurId && String(financeur.id ?? "") === target.financeurId) {
    return true;
  }

  if (
    target.financeurName
    && toTrimmedText(financeur.name).toLocaleLowerCase()
      === target.financeurName.toLocaleLowerCase()
  ) {
    return true;
  }

  return false;
}

function financeurMatchesRouteTargetProjectFilter(
  financeur: SynthesesGroupedFinanceurItem,
  target: FinanceurRouteTarget | null,
): boolean {
  if (!target?.projectFilterKey || target.projectFilterKey === "all") {
    return false;
  }

  return financeur.contracts.some((contract) =>
    contractMatchesProjectFilter(contract, target.projectFilterKey || ""),
  );
}

function findFinanceurForRouteTarget(
  financeurs: SynthesesGroupedFinanceurItem[],
  target: FinanceurRouteTarget | null,
): SynthesesGroupedFinanceurItem | null {
  if (!target) return null;

  const candidatesWithContract = financeurs.filter((item) =>
    item.contracts.some((contract) => contractMatchesRouteTarget(contract, target)),
  );
  if (candidatesWithContract.length > 0) {
    return (
      candidatesWithContract.find((item) =>
        financeurMatchesRouteTargetIdentity(item, target),
      ) || candidatesWithContract[0]
    );
  }

  const candidatesWithProject = financeurs.filter((item) =>
    financeurMatchesRouteTargetProjectFilter(item, target),
  );
  if (candidatesWithProject.length > 0) {
    return (
      candidatesWithProject.find((item) =>
        financeurMatchesRouteTargetIdentity(item, target),
      )
      || candidatesWithProject.find(
        (item) =>
          item.isVerdeo === true
          || item.contracts.some((contract) => contract.isVerdeo === true),
      )
      || candidatesWithProject[0]
    );
  }

  if (target.contractNodeId || target.synthesesContractId) {
    return null;
  }

  return (
    financeurs.find((item) => financeurMatchesRouteTargetIdentity(item, target))
    || null
  );
}

function findContractIndexForRouteTarget(
  contracts: SynthesesGroupedContractNode[],
  target: FinanceurRouteTarget | null,
): number {
  if (!target) return -1;
  return contracts.findIndex((contract) => contractMatchesRouteTarget(contract, target));
}

function financeurRouteTargetRequestsDesiredFundingAllocation(
  target: FinanceurRouteTarget | null,
): boolean {
  return (
    toTrimmedText(target?.action) ===
    FINANCEUR_ROUTE_TARGET_ACTION_DESIRED_FUNDING_ALLOCATION
  );
}

function buildDesiredFundingRouteTargetSelector(
  target: FinanceurRouteTarget | null,
): string | null {
  if (!financeurRouteTargetRequestsDesiredFundingAllocation(target)) {
    return null;
  }

  if (Number.isFinite(Number(target?.targetLineId || 0)) && Number(target?.targetLineId || 0) > 0) {
    return `[data-creation-desired-funding-line-id="${Number(target?.targetLineId || 0)}"]`;
  }

  if (
    Number.isFinite(Number(target?.targetProjectId || 0)) &&
    Number(target?.targetProjectId || 0) > 0
  ) {
    return `[data-creation-desired-funding-project-id="${Number(target?.targetProjectId || 0)}"]`;
  }

  return null;
}

function normalizeFinanceurRouteTarget(
  value: unknown,
): FinanceurRouteTarget | null {
  if (!value || typeof value !== "object") return null;

  const raw = value as Record<string, unknown>;
  const financeurId = toTrimmedText(raw.financeurId);
  const financeurName = toTrimmedText(raw.financeurName);
  const synthesesContractId = Number(raw.synthesesContractId || 0);
  const contractNodeId = toTrimmedText(raw.contractNodeId);
  const projectFilterKey = toTrimmedText(raw.projectFilterKey);
  const projectName = toTrimmedText(raw.projectName);
  const action = toTrimmedText(raw.action);
  const targetLineId = Number(raw.targetLineId || 0);
  const targetProjectId = Number(raw.targetProjectId || 0);

  if (
    !contractNodeId &&
    !(synthesesContractId > 0) &&
    !financeurId &&
    !financeurName &&
    !projectFilterKey &&
    !action
  ) {
    return null;
  }

  return {
    financeurId: financeurId || null,
    financeurName: financeurName || null,
    synthesesContractId: synthesesContractId > 0 ? synthesesContractId : null,
    contractNodeId: contractNodeId || null,
    projectFilterKey: projectFilterKey || null,
    projectName: projectName || null,
    action: action || null,
    targetLineId: targetLineId > 0 ? targetLineId : null,
    targetProjectId: targetProjectId > 0 ? targetProjectId : null,
  };
}

function extractRealFundingIds(
  contract: SynthesesGroupedContractNode,
): number[] {
  const ids = Array.isArray(contract.realFundingIds)
    ? contract.realFundingIds
    : [];
  const normalized = ids
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value) && value > 0)
    .map((value) => Math.trunc(value));
  return Array.from(new Set(normalized)).sort((a, b) => a - b);
}

type PersistedContractDeletionInfo =
  | {
      canManage: true;
      payload:
        | { contractId: number }
        | {
            sourceType: string;
            realFundingIds: number[];
          };
    }
  | { canManage: false; payload: null };

function getPersistedContractDeletionInfo(
  contract: SynthesesGroupedContractNode,
): PersistedContractDeletionInfo {
  if (contract.isVerdeo === true) {
    return { canManage: false, payload: null };
  }

  const sourceType = String(contract.sourceType || "").trim();
  const isSynthesesContract = sourceType === "syntheses_contract";
  const isLegacyContract =
    sourceType === "legacy_real_funding_group" ||
    sourceType === "legacy_real_funding";

  if (isSynthesesContract) {
    const contractId = extractSynthesesContractId(contract);
    if (Number.isFinite(Number(contractId)) && Number(contractId || 0) > 0) {
      return {
        canManage: true,
        payload: { contractId: Number(contractId) },
      };
    }
    return { canManage: false, payload: null };
  }

  if (isLegacyContract) {
    const legacyRealFundingIds = extractRealFundingIds(contract);
    if (legacyRealFundingIds.length > 0) {
      return {
        canManage: true,
        payload: {
          sourceType,
          realFundingIds: legacyRealFundingIds,
        },
      };
    }
  }

  return { canManage: false, payload: null };
}

function getMergeableSynthesesContractId(
  contract: SynthesesGroupedContractNode,
): number | null {
  if (contract.isVerdeo === true) return null;
  if (String(contract.sourceType || "").trim() !== "syntheses_contract") {
    return null;
  }

  const contractId = extractSynthesesContractId(contract);
  if (!Number.isFinite(Number(contractId)) || Number(contractId || 0) <= 0) {
    return null;
  }

  return Number(contractId);
}

function getContractLineRowsCount(contract: SynthesesGroupedContractNode) {
  const rows = Array.isArray(contract.lineRows) ? contract.lineRows : [];
  if (rows.length > 0) return rows.length;

  const projectTotals = Array.isArray(contract.projectTotals)
    ? contract.projectTotals
    : [];
  if (projectTotals.length > 0) return projectTotals.length;

  return Number(contract.realFundingId || 0) > 0 ? 1 : 0;
}

function getContractMergeOptionLabel(
  contract: SynthesesGroupedContractNode,
  index: number,
) {
  const title = toTrimmedText(contract.title) || `Contrat ${index + 1}`;
  return `${title} • ${formatDate(contract.contractDate)}`;
}

function isPersistedContractBeingEdited(
  contract: SynthesesGroupedContractNode,
  editing: EditingPersistedContractState | null,
): boolean {
  if (!editing) return false;

  const sourceType = String(contract.sourceType || "").trim();
  const isSynthesesContract = sourceType === "syntheses_contract";
  const isLegacyContract =
    sourceType === "legacy_real_funding_group" ||
    sourceType === "legacy_real_funding";

  if (isSynthesesContract) {
    if (editing.sourceType !== "syntheses_contract") return false;
    const contractId = extractSynthesesContractId(contract);
    return (
      Number.isFinite(Number(contractId)) &&
      Number(contractId || 0) > 0 &&
      Number(contractId || 0) === Number(editing.contractId || 0)
    );
  }

  if (isLegacyContract) {
    if (
      editing.sourceType !== "legacy_real_funding_group" &&
      editing.sourceType !== "legacy_real_funding"
    ) {
      return false;
    }
    const legacyIds = extractRealFundingIds(contract);
    if (legacyIds.length === 0) return false;
    return editing.realFundingIds.some((id) => legacyIds.includes(id));
  }

  return false;
}

function buildYearGroupsFromSavedContract(
  contract: SynthesesGroupedContractNode,
): GroupContractYearGroupDraft[] {
  const lineRows = Array.isArray(contract.lineRows) ? contract.lineRows : [];
  const sourceType = String(contract.sourceType || "").trim();
  const isLegacyContract =
    sourceType === "legacy_real_funding_group" ||
    sourceType === "legacy_real_funding";

  if (isLegacyContract) {
    const groupedLegacy = new Map<string, GroupContractProjectRowDraft[]>();
    lineRows.forEach((lineRow) => {
      const yearValue = String(Number(lineRow?.year || 0) || "").trim();
      if (!/^\d{4}$/.test(yearValue)) return;
      if (!groupedLegacy.has(yearValue)) {
        groupedLegacy.set(yearValue, []);
      }

      const projectId =
        Number.isFinite(Number(lineRow?.projectId)) &&
        Number(lineRow?.projectId) > 0
          ? Number(lineRow?.projectId)
          : null;
      const projectName = String(lineRow?.projectName || "").trim();
      if (!projectName && null === projectId) return;
      const rowRealFundingId =
        Number.isFinite(Number(lineRow?.realFundingId)) &&
        Number(lineRow?.realFundingId) > 0
          ? Number(lineRow?.realFundingId)
          : null;

      groupedLegacy.get(yearValue)?.push({
        id: createDraftId("project-row"),
        lineId:
          Number.isFinite(Number(lineRow?.lineId)) &&
          Number(lineRow?.lineId) > 0
            ? Number(lineRow?.lineId)
            : null,
        realFundingId: rowRealFundingId,
        projectId,
        projectName,
        title: String(lineRow?.title || contract.title || "").trim(),
        amountEuro: Number(lineRow?.amountEuro || 0).toFixed(2),
        desiredFundingIds: [],
        desiredFundingAllocations: [],
        initialDesiredFundingIds: Array.isArray(lineRow?.desiredFundingSourceIds)
          ? lineRow.desiredFundingSourceIds
              .map((desiredFundingId: any) => Number(desiredFundingId))
              .filter(
                (desiredFundingId: number) =>
                  Number.isFinite(desiredFundingId) && desiredFundingId > 0,
              )
          : [],
        desiredFundingSelectionTouched: false,
        expectedPayments: [],
        initialExpectedPaymentIds: [],
      });
    });

    const legacyYearGroups = Array.from(groupedLegacy.entries())
      .sort((a, b) => Number(a[0]) - Number(b[0]))
      .map(([year, rows]) => ({
        id: createDraftId("year-group"),
        year,
        projectRows: rows.length ? rows : [normalizeProjectRowDraft({})],
      }));

    if (legacyYearGroups.length > 0) {
      return legacyYearGroups;
    }
  }

  const grouped = new Map<string, GroupContractProjectRowDraft[]>();

  lineRows.forEach((lineRow) => {
    const yearValue = String(Number(lineRow?.year || 0) || "").trim();
    if (!/^\d{4}$/.test(yearValue)) return;
    if (!grouped.has(yearValue)) {
      grouped.set(yearValue, []);
    }

    const projectId =
      Number.isFinite(Number(lineRow?.projectId)) &&
      Number(lineRow?.projectId) > 0
        ? Number(lineRow?.projectId)
        : null;
    const projectName = String(lineRow?.projectName || "").trim();
    if (!projectName && null === projectId) return;
    const rowRealFundingId =
      Number.isFinite(Number(lineRow?.realFundingId)) &&
      Number(lineRow?.realFundingId) > 0
        ? Number(lineRow?.realFundingId)
        : null;
    grouped.get(yearValue)?.push({
      id: createDraftId("project-row"),
      lineId:
        Number.isFinite(Number(lineRow?.lineId)) && Number(lineRow?.lineId) > 0
          ? Number(lineRow?.lineId)
          : null,
      realFundingId: rowRealFundingId,
      projectId,
      projectName,
      title: String(lineRow?.title || contract.title || "").trim(),
      amountEuro: Number(lineRow?.amountEuro || 0).toFixed(2),
      desiredFundingIds: [],
      desiredFundingAllocations: [],
      initialDesiredFundingIds: Array.isArray(lineRow?.desiredFundingSourceIds)
        ? lineRow.desiredFundingSourceIds
            .map((desiredFundingId: any) => Number(desiredFundingId))
            .filter(
              (desiredFundingId: number) =>
                Number.isFinite(desiredFundingId) && desiredFundingId > 0,
            )
        : [],
      desiredFundingSelectionTouched: false,
      expectedPayments: [],
      initialExpectedPaymentIds: [],
    });
  });

  const yearGroups = Array.from(grouped.entries())
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([year, rows]) => ({
      id: createDraftId("year-group"),
      year,
      projectRows: rows.length ? rows : [normalizeProjectRowDraft({})],
    }));

  if (yearGroups.length > 0) {
    return yearGroups;
  }

  const fallbackYear = getYearFromIsoDate(contract.contractDate || null);
  return [
    {
      id: createDraftId("year-group"),
      year: fallbackYear,
      projectRows: [buildInitialProjectRow(null)],
    },
  ];
}

function buildFormDraftFromSavedContract(
  contract: SynthesesGroupedContractNode,
  index: number,
): GroupContractFormDraft {
  const expectedPaymentsInfo = buildExpectedPaymentsForContract(contract);
  return {
    title: String(contract.title || `Contrat ${index + 1}`).trim(),
    contractDate: normalizeContractDateInput(contract.contractDate),
    amountEuro: Number(contract.amountEuro || 0).toFixed(2),
    notes: String(contract.notes || "").trim(),
    expectedPayments: expectedPaymentsInfo.expectedPayments,
    initialExpectedPaymentIds: expectedPaymentsInfo.initialExpectedPaymentIds,
    yearGroups: buildYearGroupsFromSavedContract(contract),
  };
}

function buildFormDraftFromSaveResponse(
  contractPayload: any,
  selectedFinanceur: SynthesesGroupedFinanceurItem | null,
): GroupContractFormDraft {
  const lineRows = Array.isArray(contractPayload?.lines)
    ? contractPayload.lines
    : [];
  const grouped = new Map<string, GroupContractProjectRowDraft[]>();

  lineRows.forEach((lineRow: any) => {
    const year = String(Number(lineRow?.year || 0) || "").trim();
    if (!/^\d{4}$/.test(year)) return;
    if (!grouped.has(year)) grouped.set(year, []);

    const projectId =
      Number.isFinite(Number(lineRow?.projectId)) &&
      Number(lineRow?.projectId) > 0
        ? Number(lineRow?.projectId)
        : null;
    const projectName = String(lineRow?.projectName || "").trim();
    if (!projectName && null === projectId) return;

    const lineId = Number(lineRow?.lineId ?? lineRow?.id);
    const generatedRealFundingId = Number(
      lineRow?.realFundingId ?? lineRow?.generatedRealFundingId,
    );
    grouped.get(year)?.push({
      id: createDraftId("project-row"),
      lineId: Number.isFinite(lineId) && lineId > 0 ? lineId : null,
      realFundingId:
        Number.isFinite(generatedRealFundingId) && generatedRealFundingId > 0
          ? generatedRealFundingId
          : null,
      projectId,
      projectName,
      title: String(lineRow?.title || contractPayload?.title || "").trim(),
      amountEuro: Number(lineRow?.amountEuro || 0).toFixed(2),
      desiredFundingIds: [],
      desiredFundingAllocations: [],
      initialDesiredFundingIds: Array.isArray(lineRow?.desiredFundingSourceIds)
        ? lineRow.desiredFundingSourceIds
            .map((desiredFundingId: any) => Number(desiredFundingId))
            .filter(
              (desiredFundingId: number) =>
                Number.isFinite(desiredFundingId) && desiredFundingId > 0,
            )
        : [],
      desiredFundingSelectionTouched: false,
      expectedPayments: [],
      initialExpectedPaymentIds: [],
    });
  });

  const fallbackYear = getYearFromIsoDate(
    contractPayload?.contractDate || null,
  );
  const yearGroups = Array.from(grouped.entries())
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([year, rows]) => ({
      id: createDraftId("year-group"),
      year,
      projectRows: rows.length
        ? rows
        : [buildInitialProjectRow(selectedFinanceur)],
    }));

  return {
    title: String(contractPayload?.title || "Contrat").trim() || "Contrat",
    contractDate: normalizeContractDateInput(contractPayload?.contractDate),
    amountEuro: Number(contractPayload?.amountEuro || 0).toFixed(2),
    notes: String(contractPayload?.notes || "").trim(),
    expectedPayments: Array.isArray(contractPayload?.expectedPayments)
      ? contractPayload.expectedPayments.map((payment: any) =>
          normalizeExpectedPaymentDraft({
            id: payment?.id,
            amount: Number(payment?.amountEuro ?? payment?.amountLocal ?? 0).toFixed(2),
            expectedAt: payment?.at,
            status: Number(payment?.status) === 2 ? 2 : 1,
            receivedAt: payment?.datePaid,
          }),
        )
      : [],
    initialExpectedPaymentIds: Array.isArray(contractPayload?.expectedPayments)
      ? contractPayload.expectedPayments
          .map((payment: any) => Number(payment?.id || 0))
          .filter((paymentId: number) => Number.isFinite(paymentId) && paymentId > 0)
      : [],
    yearGroups:
      yearGroups.length > 0
        ? yearGroups
        : [buildInitialYearGroup(selectedFinanceur, fallbackYear)],
  };
}

function buildProjectTotalsFromLineRows(
  lineRows: NonNullable<SynthesesGroupedContractNode["lineRows"]>,
) {
  const grouped = new Map<
    string,
    {
      projectId: number | null;
      projectName: string | null;
      amountEuro: number;
      contractCount: number;
    }
  >();

  lineRows.forEach((lineRow) => {
    const projectId = Number(lineRow.projectId || 0);
    const projectName = String(lineRow.projectName || "").trim();
    const key = projectId > 0 ? `project-${projectId}` : `project-${projectName}`;
    const amountEuro = Number(lineRow.amountEuro || 0);
    const current = grouped.get(key);
    if (current) {
      current.amountEuro = Number((current.amountEuro + amountEuro).toFixed(2));
      current.contractCount += 1;
      return;
    }

    grouped.set(key, {
      projectId: projectId > 0 ? projectId : null,
      projectName,
      amountEuro: Number(amountEuro.toFixed(2)),
      contractCount: 1,
    });
  });

  return Array.from(grouped.values());
}

function buildOptimisticExpectedPaymentRows({
  contractId,
  expectedPayments: draftExpectedPayments,
}: {
  contractId: number | null;
  expectedPayments: DraftExpectedPayment[];
}): {
  expectedPayments: SynthesesGroupedContractNode["expectedPayments"];
  paidPayments: SynthesesGroupedContractNode["paidPayments"];
} {
  const expectedPayments: SynthesesGroupedContractNode["expectedPayments"] = [];
  const paidPayments: SynthesesGroupedContractNode["paidPayments"] = [];

  draftExpectedPayments.forEach((payment, paymentIndex) => {
    const amountEuro = parseAmountInput(payment.amount);
    if (amountEuro === null || amountEuro <= 0 || !payment.expectedAt) {
      return;
    }

    const persistedId = Number(payment.id);
    const paymentId = Number.isFinite(persistedId) && persistedId > 0
      ? persistedId
      : -Number(`${Date.now()}${paymentIndex}`.slice(0, 12));
    const roundedAmountEuro = Number(amountEuro.toFixed(2));
    const basePayment = {
      id: paymentId,
      realFundingId: null,
      synthesesContractId: contractId,
      synthesesContractLineId: null,
      lineId: null,
      lineTitle: null,
      projectId: null,
      projectName: null,
      at: payment.expectedAt,
      amountLocal: roundedAmountEuro,
      amountEuro: roundedAmountEuro,
      status: payment.status,
      datePaid: payment.status === 2 ? payment.receivedAt || payment.expectedAt : null,
    };

    expectedPayments.push(basePayment);
    if (payment.status === 2) {
      paidPayments.push({
        id: paymentId,
        realFundingId: null,
        synthesesContractId: contractId,
        synthesesContractLineId: null,
        lineId: null,
        lineTitle: null,
        projectId: null,
        projectName: null,
        at: payment.receivedAt || payment.expectedAt,
        amountLocal: roundedAmountEuro,
        amountEuro: roundedAmountEuro,
      });
    }
  });

  return { expectedPayments, paidPayments };
}

function buildOptimisticContractNode({
  draftId,
  contractId,
  sourceType,
  realFundingIds,
  financeur,
  title,
  contractDate,
  amountEuro,
  notes,
  expectedPayments,
  yearGroups,
  existingContract,
  responseContract,
}: {
  draftId: string | null;
  contractId?: number | null;
  sourceType?: string | null;
  realFundingIds?: number[];
  financeur: SynthesesGroupedFinanceurItem;
  title: string;
  contractDate: string;
  amountEuro: string;
  notes: string;
  expectedPayments: DraftExpectedPayment[];
  yearGroups: GroupContractYearGroupDraft[];
  existingContract?: SynthesesGroupedContractNode | null;
  responseContract?: any;
}): SynthesesGroupedContractNode {
  const responseContractId = Number(responseContract?.id || 0);
  const normalizedContractId =
    Number.isFinite(responseContractId) && responseContractId > 0
      ? responseContractId
      : Number(contractId || 0);
  const synthesesContractId =
    Number.isFinite(normalizedContractId) && normalizedContractId > 0
      ? normalizedContractId
      : null;
  const responseLines = Array.isArray(responseContract?.lines)
    ? responseContract.lines
    : [];
  const lineRows = responseLines.length > 0
    ? responseLines.map((lineRow: any) => ({
        lineId: Number(lineRow?.lineId ?? lineRow?.id ?? 0) > 0
          ? Number(lineRow?.lineId ?? lineRow?.id)
          : null,
        realFundingId: Number(lineRow?.realFundingId ?? lineRow?.generatedRealFundingId ?? 0) > 0
          ? Number(lineRow?.realFundingId ?? lineRow?.generatedRealFundingId)
          : null,
        year: Number(lineRow?.year || 0),
        projectId: Number(lineRow?.projectId || 0) > 0
          ? Number(lineRow?.projectId)
          : null,
        projectName: String(lineRow?.projectName || "").trim(),
        title: String(lineRow?.title || title || "").trim(),
        amountEuro: Number(lineRow?.amountEuro || 0),
        desiredFundingSourceIds: Array.isArray(lineRow?.desiredFundingSourceIds)
          ? lineRow.desiredFundingSourceIds
          : [],
        desiredFundingAllocations: normalizeDesiredFundingAllocationRows(
          lineRow?.desiredFundingAllocations,
        ),
        expensePosts: normalizeExpensePostRows(lineRow?.expensePosts),
      }))
    : yearGroups.flatMap((group) =>
        group.projectRows.map((row) => ({
          lineId: row.lineId,
          realFundingId: row.realFundingId,
          year: Number(group.year || 0),
          projectId: row.projectId,
          projectName: row.projectName,
          title: row.title || title,
          amountEuro: Number(parseAmountInput(row.amountEuro) || 0),
          desiredFundingSourceIds: row.initialDesiredFundingIds,
          desiredFundingAllocations: [],
          expensePosts: [],
        })),
      );
  const primaryLine = lineRows[0] || null;
  const optimisticExpectedPaymentRows = buildOptimisticExpectedPaymentRows({
    contractId: synthesesContractId,
    expectedPayments,
  });
  const hasResponseExpectedPayments = !!responseContract && Array.isArray(responseContract.expectedPayments);
  const responseExpectedPayments: SynthesesGroupedContractNode["expectedPayments"] =
    hasResponseExpectedPayments
      ? responseContract.expectedPayments.map((payment: any) => ({
          id: Number(payment?.id || 0) > 0 ? Number(payment.id) : null,
          realFundingId: Number(payment?.realFundingId || 0) > 0
            ? Number(payment.realFundingId)
            : null,
          synthesesContractId: Number(payment?.synthesesContractId || synthesesContractId || 0) > 0
            ? Number(payment?.synthesesContractId || synthesesContractId)
            : null,
          synthesesContractLineId: Number(payment?.synthesesContractLineId || payment?.lineId || 0) > 0
            ? Number(payment?.synthesesContractLineId || payment?.lineId)
            : null,
          lineId: Number(payment?.lineId || payment?.synthesesContractLineId || 0) > 0
            ? Number(payment?.lineId || payment?.synthesesContractLineId)
            : null,
          lineTitle: typeof payment?.lineTitle === "string" ? payment.lineTitle : null,
          projectId: Number(payment?.projectId || 0) > 0 ? Number(payment.projectId) : null,
          projectName: typeof payment?.projectName === "string" ? payment.projectName : null,
          at: payment?.at || null,
          amountLocal: Number(Number(payment?.amountLocal ?? payment?.amountEuro ?? 0).toFixed(2)),
          amountEuro: Number(Number(payment?.amountEuro ?? payment?.amountLocal ?? 0).toFixed(2)),
          status: Number.isFinite(Number(payment?.status)) ? Number(payment.status) : null,
          datePaid: payment?.datePaid || null,
        }))
      : [];
  const responsePaidPayments: SynthesesGroupedContractNode["paidPayments"] =
    hasResponseExpectedPayments && Array.isArray(responseContract.paidPayments)
      ? responseContract.paidPayments.map((payment: any) => ({
          id: Number(payment?.id || 0) > 0 ? Number(payment.id) : null,
          realFundingId: Number(payment?.realFundingId || 0) > 0
            ? Number(payment.realFundingId)
            : null,
          synthesesContractId: Number(payment?.synthesesContractId || synthesesContractId || 0) > 0
            ? Number(payment?.synthesesContractId || synthesesContractId)
            : null,
          synthesesContractLineId: Number(payment?.synthesesContractLineId || payment?.lineId || 0) > 0
            ? Number(payment?.synthesesContractLineId || payment?.lineId)
            : null,
          lineId: Number(payment?.lineId || payment?.synthesesContractLineId || 0) > 0
            ? Number(payment?.lineId || payment?.synthesesContractLineId)
            : null,
          lineTitle: typeof payment?.lineTitle === "string" ? payment.lineTitle : null,
          projectId: Number(payment?.projectId || 0) > 0 ? Number(payment.projectId) : null,
          projectName: typeof payment?.projectName === "string" ? payment.projectName : null,
          at: payment?.at || null,
          amountLocal: Number(Number(payment?.amountLocal ?? payment?.amountEuro ?? 0).toFixed(2)),
          amountEuro: Number(Number(payment?.amountEuro ?? payment?.amountLocal ?? 0).toFixed(2)),
        }))
      : [];
  const expectedPaymentRows = hasResponseExpectedPayments
    ? {
        expectedPayments: responseExpectedPayments,
        paidPayments: responsePaidPayments,
      }
    : optimisticExpectedPaymentRows;
  const normalizedAmountEuro = Number(parseAmountInput(amountEuro) || 0);
  const nodeId = synthesesContractId
    ? `synth-contract-${synthesesContractId}`
    : existingContract?.id || `optimistic-contract-${draftId || Date.now()}`;

  return {
    id: nodeId,
    synthesesContractId,
    title: String(responseContract?.title || title || "Contrat").trim(),
    notes: String(responseContract?.notes ?? notes ?? "").trim(),
    source: String(responseContract?.source || "manual"),
    sourceType: String(responseContract?.sourceType || sourceType || "syntheses_contract"),
    realFundingId: Number(primaryLine?.realFundingId || 0) > 0
      ? Number(primaryLine?.realFundingId)
      : existingContract?.realFundingId ?? null,
    realFundingIds: Array.isArray(responseContract?.generatedRealFundingIds)
      ? responseContract.generatedRealFundingIds
      : (Array.isArray(realFundingIds) && realFundingIds.length > 0
        ? realFundingIds
        : existingContract?.realFundingIds ?? []),
    legacyContractsCount: existingContract?.legacyContractsCount,
    years: Array.from(
      new Set(lineRows.map((lineRow) => Number(lineRow.year || 0)).filter((year) => year > 0)),
    ).sort((a, b) => a - b),
    isVerdeo: false,
    contractDate: normalizeContractDateInput(responseContract?.contractDate || contractDate),
    contractDateRaw: normalizeContractDateInput(responseContract?.contractDate || contractDate),
    dateAmountFallback: false,
    amountLocal: Number(normalizedAmountEuro.toFixed(2)),
    amountEuro: Number(normalizedAmountEuro.toFixed(2)),
    currency: existingContract?.currency || {
      id: null,
      code: "EUR",
      name: "Euro",
      symbol: "€",
    },
    oldRate: existingContract?.oldRate || 1,
    dateOldRate: existingContract?.dateOldRate || null,
    project: {
      id: primaryLine?.projectId ?? null,
      name: primaryLine?.projectName ?? null,
      program: existingContract?.project?.program || {
        id: null,
        name: null,
      },
    },
    projectTotals: buildProjectTotalsFromLineRows(lineRows),
    lineRows,
    financier: {
      id: financeur.id,
      name: financeur.name,
      email: financeur.email,
      isVerdeo: financeur.isVerdeo,
    },
    allocations: existingContract?.allocations || [],
    expectedPayments: expectedPaymentRows.expectedPayments,
    paidPayments: expectedPaymentRows.paidPayments,
    landPayments: existingContract?.landPayments || [],
    totals: {
      allocationsEuro: Number(existingContract?.totals?.allocationsEuro || 0),
      expectedPaymentsEuro: Number(
        expectedPaymentRows.expectedPayments.reduce(
          (sum, payment) => sum + Number(payment.amountEuro || 0),
          0,
        ).toFixed(2),
      ),
      receivedPaymentsEuro: Number(
        expectedPaymentRows.paidPayments.reduce(
          (sum, payment) => sum + Number(payment.amountEuro || 0),
          0,
        ).toFixed(2),
      ),
      landPaymentsEuro: Number(existingContract?.totals?.landPaymentsEuro || 0),
    },
  };
}

function findContractInGroupedData(
  data: SynthesesGroupedContractsResponse | null,
  {
    contractId,
    sourceType,
    realFundingIds,
  }: {
    contractId?: number | null;
    sourceType?: string | null;
    realFundingIds?: number[];
  },
) {
  const normalizedContractId = Number(contractId || 0);
  const normalizedSourceType = String(sourceType || "").trim();
  const normalizedRealFundingIds = Array.isArray(realFundingIds)
    ? realFundingIds.map((id) => Number(id)).filter((id) => id > 0)
    : [];

  for (const financeur of data?.items || []) {
    for (const contract of financeur.contracts || []) {
      if (
        normalizedContractId > 0 &&
        Number(contract.synthesesContractId || 0) === normalizedContractId
      ) {
        return contract;
      }

      if (
        normalizedRealFundingIds.length > 0 &&
        (
          normalizedSourceType === "legacy_real_funding" ||
          normalizedSourceType === "legacy_real_funding_group"
        )
      ) {
        const contractRealFundingIds = extractRealFundingIds(contract);
        if (normalizedRealFundingIds.some((id) => contractRealFundingIds.includes(id))) {
          return contract;
        }
      }
    }
  }

  return null;
}

function buildInitialGroupContractForm(
  selectedFinanceur: SynthesesGroupedFinanceurItem | null,
  nextContractNumber: number,
): GroupContractFormDraft {
  const firstYear = getYearFromIsoDate(
    selectedFinanceur?.contracts?.[0]?.contractDate || null,
  );
  return {
    title: `Contrat ${nextContractNumber}`,
    contractDate: "",
    amountEuro: "",
    notes: "",
    expectedPayments: [],
    initialExpectedPaymentIds: [],
    yearGroups: [buildInitialYearGroup(selectedFinanceur, firstYear)],
  };
}

function formatCreationAutosaveLabel({
  localStatus,
  endpointConfigured,
  remoteStatus,
}: {
  localStatus: "idle" | "saving" | "saved" | "error";
  endpointConfigured: boolean;
  remoteStatus: "disabled" | "syncing" | "synced" | "error";
}) {
  if (localStatus === "error") return "Erreur sauvegarde locale";
  if (localStatus === "saving" || remoteStatus === "syncing") {
    return "Brouillon en sauvegarde…";
  }
  if (remoteStatus === "error") return "Erreur de synchronisation";
  if (!endpointConfigured || remoteStatus === "disabled") {
    return "Brouillon local sauvegardé";
  }
  return "Brouillon synchronisé";
}

function getYear(value: string | null): number | null {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return date.getFullYear();
}

function isPositiveId(value: unknown) {
  return Number.isFinite(Number(value)) && Number(value) > 0;
}

function sumPaymentPreviews(rows: ProjectLinePaymentPreview[]) {
  return Number(
    rows.reduce((sum, row) => sum + Number(row.amountEuro || 0), 0).toFixed(2),
  );
}

function filterPaymentPreviewsByStatus(
  rows: ProjectLinePaymentPreview[],
  status: number,
) {
  return rows.filter((row) => Number(row.status || 0) === status);
}

function dedupePaymentPreviewsByMonthYear(rows: ProjectLinePaymentPreview[]) {
  const seen = new Set<string>();

  return rows.filter((row) => {
    const date = toTrimmedText(row.at);
    if (!date) {
      return true;
    }

    const monthYearKey = date.slice(0, 7);
    if (!/^\d{4}-\d{2}$/.test(monthYearKey)) {
      return true;
    }

    if (seen.has(monthYearKey)) {
      return false;
    }

    seen.add(monthYearKey);
    return true;
  });
}

function getLandStatusLabel(status: number) {
  if (status === 1) return "Prévu";
  if (status === 2) return "Réalisé";
  if (status === 3) return "Certifié";
  return "Autre";
}

function getLandPaymentSourceClasses(source: string | null | undefined) {
  if (source === "project_indicator") {
    return "border-sky-200 bg-sky-50/35";
  }

  return "border-emerald-200 bg-emerald-50/30";
}

function canLinkExistingLandPaymentRow(
  payment: ProjectLinePaymentPreview,
  plannedRows: ProjectLinePaymentPreview[],
  realizedRows: ProjectLinePaymentPreview[],
  certifiedRows: ProjectLinePaymentPreview[],
  allLandPaymentRows: ProjectLinePaymentPreview[],
) {
  if (payment.source !== "land_payment" || !isPositiveId(payment.landPaymentId)) {
    return false;
  }

  const status = Number(payment.landPaymentStatus || payment.status || 0);
  if (Number(payment.status || 0) !== status) {
    return false;
  }

  const linkedStatuses = new Set(
    allLandPaymentRows
      .filter(
        (row) =>
          row.source === "land_payment"
          && Number(row.landPaymentId || 0) === Number(payment.landPaymentId || 0),
      )
      .map((row) => Number(row.status || 0))
      .filter((rowStatus) => rowStatus > 0),
  );

  if (status === 1) {
    return (
      (realizedRows.length > 0 && !linkedStatuses.has(2))
      || (certifiedRows.length > 0 && !linkedStatuses.has(3))
    );
  }
  if (status === 2) {
    return (
      (plannedRows.length > 0 && !linkedStatuses.has(1))
      || (certifiedRows.length > 0 && !linkedStatuses.has(3))
    );
  }
  if (status === 3) {
    return (
      (plannedRows.length > 0 && !linkedStatuses.has(1))
      || (realizedRows.length > 0 && !linkedStatuses.has(2))
    );
  }

  return false;
}

function canMoveLandPaymentRow(
  payment: ProjectLinePaymentPreview,
  targetOptions: TerrainMoveTargetOption[],
  allLandPaymentRows: ProjectLinePaymentPreview[],
) {
  if (payment.source !== "land_payment" || !isPositiveId(payment.landPaymentId)) {
    return false;
  }

  if (targetOptions.length === 0) {
    return false;
  }

  const sameLandPaymentRows = allLandPaymentRows.filter(
    (row) =>
      row.source === "land_payment"
      && Number(row.landPaymentId || 0) === Number(payment.landPaymentId || 0),
  );
  const landPaymentStatus = Number(payment.landPaymentStatus || payment.status || 0);
  const primaryRow = sameLandPaymentRows.find(
    (row) => Number(row.status || 0) === landPaymentStatus,
  );

  if (primaryRow) {
    return primaryRow.id === payment.id;
  }

  return sameLandPaymentRows[0]?.id === payment.id;
}

function canMoveTimelineLandPaymentRow(
  payment: ProjectLinePaymentPreview,
  targetOptions: TerrainMoveTargetOption[],
) {
  return isPositiveId(payment.landPaymentId) && targetOptions.length > 0;
}

function isTerrainMoveTargetRow(
  payment: ProjectLinePaymentPreview,
  terrainMoveState: TerrainMoveState | null,
) {
  if (
    !terrainMoveState
    || !isPositiveId(terrainMoveState.landPaymentId)
    || !toTrimmedText(terrainMoveState.paymentRowId)
  ) {
    return false;
  }

  return payment.id === terrainMoveState.paymentRowId;
}

function isTerrainMoveIncludedRow(
  payment: ProjectLinePaymentPreview,
  terrainMoveState: TerrainMoveState | null,
) {
  if (!terrainMoveState || !isPositiveId(terrainMoveState.landPaymentId)) {
    return false;
  }

  return Number(payment.landPaymentId || 0) === Number(terrainMoveState.landPaymentId || 0);
}

function isTerrainReconcileTargetRow(
  payment: ProjectLinePaymentPreview,
  terrainReconcileState: TerrainReconcileState | null,
) {
  if (!terrainReconcileState || !isPositiveId(terrainReconcileState.landPaymentId)) {
    return false;
  }

  return (
    Number(payment.landPaymentId || 0) === Number(terrainReconcileState.landPaymentId || 0)
    && Number(payment.status || 0) === Number(terrainReconcileState.landPaymentStatus || 0)
  );
}

function canSelectTerrainPaymentInReconcile(
  status: number,
  payment: ProjectLinePaymentPreview,
  terrainReconcileState: TerrainReconcileState | null,
) {
  if (
    !terrainReconcileState
    || payment.source !== "project_indicator"
    || !isPositiveId(payment.projectIndicatorId)
  ) {
    return false;
  }

  const targetStatus = Number(terrainReconcileState.landPaymentStatus || 0);
  if (isPositiveId(terrainReconcileState.landPaymentId)) {
    if (targetStatus === 1) {
      return status === 2 || status === 3;
    }
    if (targetStatus === 2) {
      return status === 1 || status === 3;
    }
    if (targetStatus === 3) {
      return status === 1 || status === 2;
    }

    return false;
  }

  return status === 1 || status === 2 || status === 3;
}

function canSubmitTerrainReconcile(terrainReconcileState: TerrainReconcileState | null) {
  if (!terrainReconcileState) return false;

  const targetStatus = Number(terrainReconcileState.landPaymentStatus || 0);
  if (isPositiveId(terrainReconcileState.landPaymentId)) {
    if (targetStatus === 1) {
      return isPositiveId(terrainReconcileState.selectedMeasuredId);
    }
    if (targetStatus === 2) {
      return (
        isPositiveId(terrainReconcileState.selectedEstimatedId)
        || isPositiveId(terrainReconcileState.selectedVerifiedId)
      );
    }
    if (targetStatus === 3) {
      return (
        isPositiveId(terrainReconcileState.selectedEstimatedId)
        || isPositiveId(terrainReconcileState.selectedMeasuredId)
      );
    }

    return false;
  }

  return (
    isPositiveId(terrainReconcileState.selectedEstimatedId)
    && isPositiveId(terrainReconcileState.selectedMeasuredId)
  );
}

function getTerrainReconcileInstruction(terrainReconcileState: TerrainReconcileState | null) {
  const targetStatus = Number(terrainReconcileState?.landPaymentStatus || 0);
  if (isPositiveId(terrainReconcileState?.landPaymentId) && targetStatus === 1) {
    return "Sélectionnez un réalisé, et éventuellement un certifié, pour compléter ce versement à l'intervenant prévu.";
  }
  if (isPositiveId(terrainReconcileState?.landPaymentId) && targetStatus === 2) {
    return "Sélectionnez un prévu et/ou un certifié pour compléter ce versement à l'intervenant réalisé.";
  }
  if (isPositiveId(terrainReconcileState?.landPaymentId) && targetStatus === 3) {
    return "Sélectionnez un prévu et/ou un réalisé pour compléter ce versement à l'intervenant certifié.";
  }

  return "Sélectionnez directement dans les listes un prévu, un réalisé, et éventuellement un certifié.";
}

function getTerrainReconcileSubmitLabel(
  terrainReconcileState: TerrainReconcileState | null,
  savingTerrainReconcile: boolean,
) {
  const hasTargetLandPayment = isPositiveId(terrainReconcileState?.landPaymentId);
  if (savingTerrainReconcile) {
    return hasTargetLandPayment ? "Mise à jour..." : "Création...";
  }

  return hasTargetLandPayment
    ? "Mettre à jour le versement à l'intervenant"
    : "Créer le versement à l'intervenant";
}

function getTerrainQuickLinkSelectedId(
  state: TerrainQuickLinkState | null,
  status: 1 | 2 | 3,
) {
  if (!state) return null;
  if (status === 1) return state.selectedEstimatedId;
  if (status === 2) return state.selectedMeasuredId;
  return state.selectedVerifiedId;
}

function isTerrainQuickLinkSelectedPayment(
  state: TerrainQuickLinkState | null,
  payment: ProjectLinePaymentPreview | null,
  status: 1 | 2 | 3,
) {
  if (!state || !payment) return false;
  const paymentLandPaymentId = Number(payment.landPaymentId || 0);
  if (
    isPositiveId(state.targetLandPaymentId) &&
    isPositiveId(paymentLandPaymentId) &&
    Number(state.targetLandPaymentId) === paymentLandPaymentId
  ) {
    if (state.targetPayment?.id) {
      return state.targetPayment.id === payment.id;
    }

    return Number(state.targetLandPaymentStatus || 0) === status;
  }

  const selectedId = getTerrainQuickLinkSelectedId(state, status);
  return (
    isPositiveId(selectedId) &&
    Number(payment.projectIndicatorId || 0) === Number(selectedId)
  );
}

function getTerrainQuickLinkContextRows(state: TerrainQuickLinkState) {
  const selectedRows = [
    state.selectedEstimatedPayment,
    state.selectedMeasuredPayment,
    state.selectedVerifiedPayment,
  ].filter((row): row is ProjectLinePaymentPreview => !!row);
  const selectedStatuses = new Set(
    selectedRows
      .map((row) => Number(row.status || 0))
      .filter((status) => [1, 2, 3].includes(status)),
  );
  const targetRows = (Array.isArray(state.targetLandPaymentRows)
    ? state.targetLandPaymentRows
    : []
  ).filter((row) => !selectedStatuses.has(Number(row.status || 0)));
  const targetPayment =
    state.targetPayment && !selectedStatuses.has(Number(state.targetPayment.status || 0))
      ? state.targetPayment
      : null;
  const rows = [
    ...targetRows,
    targetPayment,
    ...selectedRows,
  ].filter((row): row is ProjectLinePaymentPreview => !!row);

  const uniqueRows = new Map<string, ProjectLinePaymentPreview>();
  rows.forEach((row) => {
    const status = Number(row.status || 0);
    const projectIndicatorId = Number(row.projectIndicatorId || 0);
    const key =
      row.landPaymentId && Number(row.landPaymentId) > 0
        ? `land-${row.landPaymentId}-${status}-${projectIndicatorId || row.id}`
        : projectIndicatorId > 0
          ? `indicator-${projectIndicatorId}`
          : `${row.id}-${status}`;
    uniqueRows.set(key, row);
  });

  return Array.from(uniqueRows.values());
}

function getTerrainQuickLinkSelectedIndicatorIds(state: TerrainQuickLinkState) {
  return [
    state.selectedEstimatedId,
    state.selectedMeasuredId,
    state.selectedVerifiedId,
    ...getTerrainQuickLinkContextRows(state).map((row) => row.projectIndicatorId),
  ]
    .map((id) => Number(id || 0))
    .filter((id, index, items) =>
      Number.isFinite(id) && id > 0 && items.indexOf(id) === index,
    );
}

function getTerrainQuickLinkSelectedSourceLandPaymentIds(state: TerrainQuickLinkState) {
  const targetLandPaymentId = Number(state.targetLandPaymentId || 0);

  return [
    state.selectedEstimatedPayment?.landPaymentId,
    state.selectedMeasuredPayment?.landPaymentId,
    state.selectedVerifiedPayment?.landPaymentId,
  ]
    .map((id) => Number(id || 0))
    .filter((id, index, items) =>
      Number.isFinite(id) &&
      id > 0 &&
      id !== targetLandPaymentId &&
      items.indexOf(id) === index,
    );
}

function buildOptimisticReconciledLandPaymentRows({
  state,
  landPaymentId,
  realFundingId,
}: {
  state: TerrainQuickLinkState;
  landPaymentId: number;
  realFundingId: number;
}): SynthesesGroupedContractNode["landPayments"] {
  const contextRows = getTerrainQuickLinkContextRows(state);
  const finalStatus = Math.max(
    1,
    Number(state.targetLandPaymentStatus || 0),
    ...contextRows.map((row) => Number(row.status || 0)),
  );
  const recipientSource =
    contextRows.find((row) => isPositiveId(getLandPaymentRecipientId(row))) ||
    contextRows.find((row) => !!row.recipientSubcontractor) ||
    null;
  const recipientSubcontractorId = recipientSource
    ? getLandPaymentRecipientId(recipientSource)
    : null;
  const recipientSubcontractor = recipientSource?.recipientSubcontractor ?? null;
  const pjVerified =
    contextRows.find((row) => Number(row.status || 0) === 3 && row.pjVerified)?.pjVerified ||
    contextRows.find((row) => row.pjVerified)?.pjVerified ||
    null;

  const rowsByKey = new Map<string, SynthesesGroupedContractNode["landPayments"][number]>();

  contextRows.forEach((row) => {
    const rowStatus = Number(row.status || 0);
    if (![1, 2, 3].includes(rowStatus)) return;

    const projectIndicatorId = Number(row.projectIndicatorId || 0);
    const key = projectIndicatorId > 0
      ? `indicator-${projectIndicatorId}`
      : `row-${row.id}-${rowStatus}`;
    const amountEuro = Number(row.amountEuro || 0);
    if (!Number.isFinite(amountEuro) || amountEuro <= 0) return;

    rowsByKey.set(key, {
      id: projectIndicatorId > 0
        ? -projectIndicatorId
        : -Math.abs(Number(landPaymentId || 0) * 10 + rowStatus),
      projectIndicatorId: projectIndicatorId > 0 ? projectIndicatorId : null,
      landPaymentId,
      landPaymentStatus: finalStatus,
      realFundingId: Number(row.realFundingId || realFundingId || 0) > 0
        ? Number(row.realFundingId || realFundingId)
        : null,
      synthesesContractId:
        Number(row.synthesesContractId || 0) > 0 ? Number(row.synthesesContractId) : null,
      synthesesContractLineId:
        Number(row.synthesesContractLineId || row.lineId || 0) > 0
          ? Number(row.synthesesContractLineId || row.lineId)
          : null,
      lineId:
        Number(row.lineId || row.synthesesContractLineId || 0) > 0
          ? Number(row.lineId || row.synthesesContractLineId)
          : null,
      lineTitle: row.lineTitle || null,
      projectId: Number(row.projectId || 0) > 0 ? Number(row.projectId) : null,
      projectName: row.projectName || null,
      at: row.at,
      amountLocal: Number(amountEuro.toFixed(2)),
      amountEuro: Number(amountEuro.toFixed(2)),
      source: "land_payment",
      status: rowStatus,
      dateMeasure: rowStatus === 2 ? row.at : row.dateMeasure || null,
      dateVerified: rowStatus === 3 ? row.at : row.dateVerified || null,
      pjVerified,
      recipientSubcontractorId,
      recipientSubcontractor,
    });
  });

  return Array.from(rowsByKey.values()).sort((a, b) =>
    toTrimmedText(a.at).localeCompare(toTrimmedText(b.at)),
  );
}

function canSubmitTerrainQuickLink(state: TerrainQuickLinkState | null) {
  if (!state) return false;

  const targetStatus = Number(state.targetLandPaymentStatus || 0);
  if (isPositiveId(state.targetLandPaymentId)) {
    if (targetStatus === 1) {
      return isPositiveId(state.selectedMeasuredId);
    }
    if (targetStatus === 2) {
      return (
        isPositiveId(state.selectedEstimatedId) ||
        isPositiveId(state.selectedVerifiedId)
      );
    }
    if (targetStatus === 3) {
      return (
        isPositiveId(state.selectedEstimatedId) ||
        isPositiveId(state.selectedMeasuredId)
      );
    }
    return false;
  }

  return (
    isPositiveId(state.selectedEstimatedId) &&
    isPositiveId(state.selectedMeasuredId)
  );
}

function getTerrainQuickLinkSummary(state: TerrainQuickLinkState | null) {
  if (!state) return "";
  const parts = [
    isPositiveId(state.selectedEstimatedId) ||
    (state.targetLandPaymentStatus === 1 && isPositiveId(state.targetLandPaymentId))
      ? "Prévu sélectionné"
      : "Prévu manquant",
    isPositiveId(state.selectedMeasuredId) ||
    (state.targetLandPaymentStatus === 2 && isPositiveId(state.targetLandPaymentId))
      ? "Mesuré sélectionné"
      : "Mesuré manquant",
    isPositiveId(state.selectedVerifiedId) ||
    (state.targetLandPaymentStatus === 3 && isPositiveId(state.targetLandPaymentId))
      ? "Certifié sélectionné"
      : "Certifié optionnel",
  ];

  return parts.join(" • ");
}

function getTerrainPaymentVisualKey(payment: ProjectLinePaymentPreview | null | undefined) {
  if (!payment) return null;
  const id = toTrimmedText(payment.id);
  if (id) return `row-${id}`;

  const projectIndicatorId = Number(payment.projectIndicatorId || 0);
  if (Number.isFinite(projectIndicatorId) && projectIndicatorId > 0) {
    return `indicator-${projectIndicatorId}`;
  }

  const landPaymentId = Number(payment.landPaymentId || 0);
  const status = Number(payment.status || 0);
  const at = toTrimmedText(payment.at);
  if (Number.isFinite(landPaymentId) && landPaymentId > 0) {
    return `land-${landPaymentId}-${status}-${at}`;
  }

  return null;
}

const TERRAIN_LINK_TONES = [
  {
    cellClassName: "outline outline-2 outline-offset-1 outline-sky-400",
  },
  {
    cellClassName: "outline outline-2 outline-offset-1 outline-emerald-400",
  },
  {
    cellClassName: "outline outline-2 outline-offset-1 outline-amber-400",
  },
  {
    cellClassName: "outline outline-2 outline-offset-1 outline-fuchsia-400",
  },
];

const TERRAIN_PENDING_LINK_INFO: TerrainLinkVisualInfo = {
  title: "Liaison en cours",
  rows: ["Validez pour rattacher ces versements ensemble."],
  cellClassName: "outline outline-2 outline-offset-1 outline-slate-800",
};

function formatTerrainLinkRowSummary(row: ProjectLinePaymentPreview) {
  return `${getLandStatusLabel(Number(row.status || 0))} : ${formatEuro(
    Number(row.amountEuro || 0),
  )} · ${formatDate(toTrimmedText(row.at) || null)}`;
}

function buildTerrainLinkVisualMap(
  rows: ProjectLinePaymentPreview[],
  quickLinkState: TerrainQuickLinkState | null = null,
) {
  const visualMap = new Map<string, TerrainLinkVisualInfo>();
  const groups = new Map<number, ProjectLinePaymentPreview[]>();

  rows.forEach((row) => {
    const landPaymentId = Number(row.landPaymentId || 0);
    if (!Number.isFinite(landPaymentId) || landPaymentId <= 0) return;

    const groupRows = groups.get(landPaymentId) || [];
    groupRows.push(row);
    groups.set(landPaymentId, groupRows);
  });

  let linkIndex = 0;

  Array.from(groups.entries())
    .sort(([, aRows], [, bRows]) => {
      const aFirstDate = aRows.map((row) => toTrimmedText(row.at)).sort()[0] || "";
      const bFirstDate = bRows.map((row) => toTrimmedText(row.at)).sort()[0] || "";
      return aFirstDate.localeCompare(bFirstDate);
    })
    .forEach(([landPaymentId, groupRows]) => {
      const uniqueStatuses = new Set(
        groupRows
          .map((row) => Number(row.status || 0))
          .filter((status) => [1, 2, 3].includes(status)),
      );
      if (uniqueStatuses.size < 2) return;

      const tone = TERRAIN_LINK_TONES[linkIndex % TERRAIN_LINK_TONES.length];
      linkIndex += 1;
      const rowsByStatus = new Map<number, ProjectLinePaymentPreview>();
      groupRows
        .slice()
        .sort((a, b) => {
          const statusDiff = Number(a.status || 0) - Number(b.status || 0);
          if (statusDiff !== 0) return statusDiff;
          return toTrimmedText(a.at).localeCompare(toTrimmedText(b.at));
        })
        .forEach((row) => {
          const status = Number(row.status || 0);
          if (!rowsByStatus.has(status)) {
            rowsByStatus.set(status, row);
          }
        });
      const summary = Array.from(rowsByStatus.values())
        .map(formatTerrainLinkRowSummary)
        .filter(Boolean);
      const info: TerrainLinkVisualInfo = {
        title: `Versement à l'intervenant #${landPaymentId}`,
        rows: summary,
        cellClassName: tone.cellClassName,
      };

      groupRows.forEach((row) => {
        const visualKey = getTerrainPaymentVisualKey(row);
        if (visualKey) {
          visualMap.set(visualKey, info);
        }
      });
    });

  if (quickLinkState) {
    const pendingRows = getTerrainQuickLinkContextRows(quickLinkState);
    if (pendingRows.length > 1) {
      pendingRows.forEach((row) => {
        const visualKey = getTerrainPaymentVisualKey(row);
        if (visualKey) {
          visualMap.set(visualKey, TERRAIN_PENDING_LINK_INFO);
        }
      });
    }
  }

  return visualMap;
}

function getTerrainLinkVisualInfo(
  visualMap: Map<string, TerrainLinkVisualInfo>,
  payment: ProjectLinePaymentPreview | null,
) {
  const visualKey = getTerrainPaymentVisualKey(payment);
  return visualKey ? visualMap.get(visualKey) || null : null;
}

function sumRowsAmountForYear(rows: PaymentRow[], year: number) {
  return rows.reduce((sum, row) => {
    return getYear(row.at) === year ? sum + Number(row.amountEuro || 0) : sum;
  }, 0);
}

function sumRowsAmount(rows: PaymentRow[]) {
  return Number(
    rows.reduce((sum, row) => sum + Number(row.amountEuro || 0), 0).toFixed(2),
  );
}

function buildContractYears(contract: SynthesesGroupedContractNode): number[] {
  const years = new Set<number>();
  const registerYear = (value: unknown) => {
    const year = Number(value);
    if (Number.isFinite(year) && year > 0) {
      years.add(year);
    }
  };

  if (Array.isArray(contract.years)) {
    contract.years.forEach((year) => registerYear(year));
  }

  if (Array.isArray(contract.lineRows)) {
    contract.lineRows.forEach((lineRow) => registerYear(lineRow?.year));
  }

  registerYear(extractTimelineYear(contract.contractDate));

  const sortedYears = Array.from(years).sort((a, b) => a - b);
  if (sortedYears.length > 0) {
    return sortedYears;
  }

  return [new Date().getFullYear()];
}

function getContractAmountEuroForYear(
  contract: SynthesesGroupedContractNode,
  year: number,
) {
  const yearLineRows = (Array.isArray(contract.lineRows) ? contract.lineRows : [])
    .map((lineRow) => ({
      year: Number(lineRow?.year),
      amountEuro: Number(lineRow?.amountEuro || 0),
    }))
    .filter((row) => Number.isFinite(row.year) && row.year === year);
  if (yearLineRows.length > 0) {
    return yearLineRows.reduce(
      (sum, row) => sum + Number(row.amountEuro || 0),
      0,
    );
  }

  const years = buildContractYears(contract);
  if (years.length <= 1 && years.includes(year)) {
    return Number(contract.amountEuro || 0);
  }

  return 0;
}

function buildContractDeclaredYears(
  contract: SynthesesGroupedContractNode,
) {
  return buildContractYears(contract);
}

function pendingContractHasYear(
  draftContract: PendingGroupContractDraft,
  year: number,
) {
  const years = new Set<number>();

  (Array.isArray(draftContract.yearGroups) ? draftContract.yearGroups : []).forEach(
    (group) => {
      const groupYear = Number(group?.year || 0);
      if (Number.isFinite(groupYear) && groupYear > 0) {
        years.add(groupYear);
      }
    },
  );

  const draftYear = Number(extractTimelineYear(draftContract.contractDate) || 0);
  if (Number.isFinite(draftYear) && draftYear > 0) {
    years.add(draftYear);
  }

  return years.has(year);
}

function buildProjectAmountColumns(
  contract: SynthesesGroupedContractNode,
  limit = 3,
): ProjectAmountColumn[] {
  const grouped = new Map<
    string,
    {
      id: string;
      projectId: number | null;
      projectName: string;
      amountEuro: number;
      contractCount: number;
    }
  >();

  const projectTotals = Array.isArray(contract.projectTotals)
    ? contract.projectTotals
    : [];

  projectTotals.forEach((projectTotal, index) => {
    const projectName = String(projectTotal.projectName || "").trim();
    if (!projectName) return;
    const projectId = Number(projectTotal.projectId);
    const key = Number.isFinite(projectId)
      ? `project-${projectId}`
      : `project-${projectName.toLowerCase()}-${index}`;
    const current = grouped.get(key);

    if (current) {
      current.amountEuro += Number(projectTotal.amountEuro || 0);
      current.contractCount += Number(projectTotal.contractCount || 0) || 1;
      return;
    }

    grouped.set(key, {
      id: `${contract.id}-project-col-${projectTotal.projectId ?? index}`,
      projectId: Number.isFinite(projectId) ? projectId : null,
      projectName,
      amountEuro: Number(projectTotal.amountEuro || 0),
      contractCount: Number(projectTotal.contractCount || 0) || 1,
    });
  });

  const columns = Array.from(grouped.values()).sort(
    (a, b) => b.amountEuro - a.amountEuro,
  );
  if (columns.length > 0) {
    return limit > 0 ? columns.slice(0, limit) : columns;
  }

  return [
    {
      id: `${contract.id}-project-col-empty`,
      projectId: Number(contract.project.id || 0) || null,
      projectName: String(contract.project.name || "").trim(),
      amountEuro: Number(contract.amountEuro || 0),
      contractCount: Number(contract.legacyContractsCount || 0) || 1,
    },
  ];
}

function buildProjectAmountColumnsForYear(
  contract: SynthesesGroupedContractNode,
  year: number,
  fallbackColumns: ProjectAmountColumn[],
  totalYears: number,
  limit = 3,
): ProjectAmountColumn[] {
  const lineRows = Array.isArray(contract.lineRows) ? contract.lineRows : [];
  const grouped = new Map<
    string,
    {
      id: string;
      projectId: number | null;
      projectName: string;
      amountEuro: number;
      contractCount: number;
    }
  >();

  lineRows.forEach((lineRow, index) => {
    const lineYear = Number(lineRow?.year);
    if (!Number.isFinite(lineYear) || lineYear !== year) return;

    const projectId = Number(lineRow?.projectId);
    const projectNameRaw = String(lineRow?.projectName || "").trim();
    const amountEuro = Number(lineRow?.amountEuro || 0);
    if (!Number.isFinite(amountEuro)) return;

    const projectName =
      projectNameRaw ||
      (Number.isFinite(projectId) && projectId > 0
        ? `Projet ${projectId}`
        : "");
    if (!projectName) return;

    const key =
      Number.isFinite(projectId) && projectId > 0
        ? `project-${projectId}`
        : `project-${projectName.toLowerCase()}-${index}`;
    const current = grouped.get(key);
    if (current) {
      current.amountEuro += amountEuro;
      current.contractCount += 1;
      return;
    }

    grouped.set(key, {
      id: `${contract.id}-project-col-year-${year}-${projectId ?? index}`,
      projectId: Number.isFinite(projectId) && projectId > 0 ? projectId : null,
      projectName,
      amountEuro,
      contractCount: 1,
    });
  });

  const columns = Array.from(grouped.values()).sort(
    (a, b) => b.amountEuro - a.amountEuro,
  );
  if (columns.length > 0) {
    return limit > 0 ? columns.slice(0, limit) : columns;
  }

  // Legacy/sparse payload fallback: only reuse global amounts when there is a
  // single year, otherwise prefer showing no project amount for this year.
  if (totalYears <= 1) {
    return fallbackColumns;
  }

  return [];
}

function normalizeExpensePostRows(
  value: unknown,
): ContractExpensePostDisplayRow[] {
  if (!Array.isArray(value)) return [];

  return value
    .map((allocation: any) => {
      const amountEuro = Number(allocation?.amountEuro ?? 0);
      if (!Number.isFinite(amountEuro) || amountEuro <= 0) return null;

      const id = Number(allocation?.id ?? 0);
      const desiredFundingSourceId = Number(
        allocation?.desiredFundingSourceId ?? 0,
      );
      const amountLocal = Number(allocation?.amountLocal ?? NaN);

      return {
        id: Number.isFinite(id) && id > 0 ? id : null,
        title:
          toTrimmedText(allocation?.title) ||
          (Number.isFinite(desiredFundingSourceId) && desiredFundingSourceId > 0
            ? `Poste de dépense #${desiredFundingSourceId}`
            : "Poste de dépense"),
        amountLocal:
          Number.isFinite(amountLocal) && amountLocal > 0 ? amountLocal : null,
        amountEuro,
        currencyCode: toTrimmedText(allocation?.currencyCode) || null,
        desiredFundingSourceId:
          Number.isFinite(desiredFundingSourceId) && desiredFundingSourceId > 0
            ? desiredFundingSourceId
            : null,
        obtainedDate: toTrimmedText(allocation?.obtainedDate) || null,
      } satisfies ContractExpensePostDisplayRow;
    })
    .filter(
      (
        allocation,
      ): allocation is ContractExpensePostDisplayRow => !!allocation,
    );
}

function normalizeDesiredFundingAllocationRows(
  value: unknown,
): ContractExpensePostDisplayRow[] {
  return normalizeExpensePostRows(value);
}

function buildContractLineRowsForYear(
  contract: SynthesesGroupedContractNode,
  year: number,
): ContractLineDisplayRow[] {
  const lineRows = Array.isArray(contract.lineRows) ? contract.lineRows : [];

  return lineRows
    .flatMap((lineRow, index) => {
      const normalizedYear = Number(lineRow?.year);
      const normalizedLineId = Number(lineRow?.lineId);
      const normalizedRealFundingId = Number(lineRow?.realFundingId);
      const normalizedContractId = Number(contract.synthesesContractId || 0);
      const normalizedProjectId = Number(lineRow?.projectId);
      const projectName = String(lineRow?.projectName || "").trim();
      const title = String(lineRow?.title || "").trim();
      const amountEuro = Number(lineRow?.amountEuro || 0);
      const desiredFundingAllocations = normalizeDesiredFundingAllocationRows(
        lineRow?.desiredFundingAllocations,
      );
      const expensePosts = normalizeExpensePostRows(
        lineRow?.expensePosts,
      );
      const postRows =
        expensePosts.length > 0 ? expensePosts : desiredFundingAllocations;

      const baseRow: ContractLineDisplayRow = {
        key: `${contract.id}-line-${normalizedLineId || normalizedRealFundingId || index}`,
        synthesesContractId:
          Number.isFinite(normalizedContractId) && normalizedContractId > 0
            ? normalizedContractId
            : null,
        lineId:
          Number.isFinite(normalizedLineId) && normalizedLineId > 0
            ? normalizedLineId
            : null,
        realFundingId:
          Number.isFinite(normalizedRealFundingId) && normalizedRealFundingId > 0
            ? normalizedRealFundingId
            : null,
        year: Number.isFinite(normalizedYear) ? normalizedYear : 0,
        projectId:
          Number.isFinite(normalizedProjectId) && normalizedProjectId > 0
            ? normalizedProjectId
            : null,
        projectName,
        title,
        amountEuro: Number.isFinite(amountEuro) ? amountEuro : 0,
        expensePostId: null,
        amountLocal: null,
        currencyCode: null,
        obtainedDate: null,
        desiredFundingSourceId: null,
        desiredFundingAllocations,
      };

      if (postRows.length === 0) {
        return [baseRow];
      }

      return postRows.map((post, postIndex) => ({
        ...baseRow,
        key: `${baseRow.key}-post-${post.id ?? post.desiredFundingSourceId ?? postIndex}`,
        title: post.title || baseRow.title,
        amountEuro: Number(post.amountEuro || 0),
        expensePostId: post.id,
        amountLocal: post.amountLocal,
        currencyCode: post.currencyCode,
        obtainedDate: post.obtainedDate,
        desiredFundingSourceId: post.desiredFundingSourceId,
      }));
    })
    .filter((row) => {
      if (!row.projectName) return false;
      if (row.year === year) return true;

      return (
        contract.expectedPayments.some(
          (payment) =>
            paymentMatchesLineContext(payment, {
              synthesesContractId: row.synthesesContractId,
              synthesesContractLineId: row.lineId,
              realFundingId: row.realFundingId,
            }) &&
            getYear(payment.at) === year,
        ) ||
        contract.paidPayments.some(
          (payment) =>
            paymentMatchesLineContext(payment, {
              synthesesContractId: row.synthesesContractId,
              synthesesContractLineId: row.lineId,
              realFundingId: row.realFundingId,
            }) &&
            getYear(payment.at) === year,
        ) ||
        contract.landPayments.some(
          (payment) =>
            paymentMatchesLineContext(payment, {
              synthesesContractId: row.synthesesContractId,
              synthesesContractLineId: row.lineId,
              realFundingId: row.realFundingId,
            }) &&
            getYear(payment.at) === year,
        )
      );
    })
    .sort((a, b) => {
      const nameSort = a.projectName.localeCompare(b.projectName, "fr");
      if (nameSort !== 0) return nameSort;
      return Number(a.lineId || 0) - Number(b.lineId || 0);
    });
}

function buildContractProjectRowsFromLines(
  lineRows: ContractLineDisplayRow[],
): ContractProjectDisplayRow[] {
  const grouped = new Map<string, ContractProjectDisplayRow>();

  lineRows.forEach((line) => {
    const projectName = String(line.projectName || "").trim();
    if (!projectName) return;

    const key =
      Number(line.projectId || 0) > 0
        ? `project-${Number(line.projectId)}`
        : `project-${projectName.toLowerCase()}`;
    const current = grouped.get(key);
    if (current) {
      current.amountEuro += Number(line.amountEuro || 0);
      current.lines.push(line);
      return;
    }

    grouped.set(key, {
      key,
      projectId: line.projectId,
      projectName,
      amountEuro: Number(line.amountEuro || 0),
      lines: [line],
    });
  });

  return Array.from(grouped.values())
    .map((projectRow) => ({
      ...projectRow,
      amountEuro: Number(projectRow.amountEuro.toFixed(2)),
      lines: [...projectRow.lines].sort((a, b) => {
        const titleSort = (a.title || a.projectName).localeCompare(
          b.title || b.projectName,
          "fr",
        );
        if (titleSort !== 0) return titleSort;
        return Number(a.lineId || 0) - Number(b.lineId || 0);
      }),
    }))
    .sort((a, b) => {
      const nameSort = a.projectName.localeCompare(b.projectName, "fr");
      if (nameSort !== 0) return nameSort;
      return b.amountEuro - a.amountEuro;
    });
}

function buildPaymentsForYear(rows: PaymentRow[], year: number) {
  return rows.filter((row) => getYear(row.at) === year);
}

type PaymentLineContext = {
  synthesesContractId?: number | null;
  synthesesContractLineId?: number | null;
  realFundingId?: number | null;
};

function getPaymentContractLineId(payment: PaymentRow) {
  const lineId = Number(payment.synthesesContractLineId ?? payment.lineId ?? 0);
  return Number.isFinite(lineId) && lineId > 0 ? lineId : null;
}

function getPaymentContractId(payment: PaymentRow) {
  const contractId = Number(payment.synthesesContractId ?? 0);
  return Number.isFinite(contractId) && contractId > 0 ? contractId : null;
}

function paymentMatchesLineContext(
  payment: PaymentRow,
  context: PaymentLineContext,
) {
  const targetLineId = Number(context.synthesesContractLineId ?? 0);
  const paymentLineId = getPaymentContractLineId(payment);
  if (Number.isFinite(targetLineId) && targetLineId > 0 && paymentLineId) {
    return paymentLineId === targetLineId;
  }

  const targetRealFundingId = Number(context.realFundingId ?? 0);
  const paymentRealFundingId = Number(payment.realFundingId ?? 0);
  if (
    Number.isFinite(targetRealFundingId)
    && targetRealFundingId > 0
    && Number.isFinite(paymentRealFundingId)
    && paymentRealFundingId > 0
  ) {
    return paymentRealFundingId === targetRealFundingId;
  }

  if (Number.isFinite(targetLineId) && targetLineId > 0) {
    return false;
  }

  const targetContractId = Number(context.synthesesContractId ?? 0);
  const paymentContractId = getPaymentContractId(payment);
  return Number.isFinite(targetContractId) && targetContractId > 0
    ? paymentContractId === targetContractId
    : false;
}

function buildPaymentsForLine(
  payments: PaymentRow[],
  realFundingId: number | null,
  keyPrefix: string,
  context: PaymentLineContext = {},
): ProjectLinePaymentPreview[] {
  const matchContext = {
    ...context,
    realFundingId: context.realFundingId ?? realFundingId,
  };
  if (
    !payments.length
    || (
      !matchContext.realFundingId
      && !matchContext.synthesesContractLineId
      && !matchContext.synthesesContractId
    )
  ) return [];

  return payments
    .filter((payment) => paymentMatchesLineContext(payment, matchContext))
	    .map((payment, index) => ({
	      id: `${keyPrefix}-${payment.id ?? index}`,
	      at: payment.at,
	      amountEuro: Number(Number(payment.amountEuro || 0).toFixed(2)),
	      synthesesContractId: getPaymentContractId(payment),
	      synthesesContractLineId: getPaymentContractLineId(payment),
	      lineId: getPaymentContractLineId(payment),
	      realFundingId: Number(payment.realFundingId || 0) > 0 ? Number(payment.realFundingId) : null,
	      lineTitle: typeof payment.lineTitle === "string" ? payment.lineTitle : null,
      projectId: Number(payment.projectId || 0) > 0 ? Number(payment.projectId) : null,
      projectName: typeof payment.projectName === "string" ? payment.projectName : null,
      projectIndicatorId: Number(payment.projectIndicatorId || 0) > 0
        ? Number(payment.projectIndicatorId)
        : null,
      landPaymentId: Number(payment.landPaymentId || 0) > 0
        ? Number(payment.landPaymentId)
        : null,
      landPaymentStatus: Number.isFinite(Number(payment.landPaymentStatus))
        ? Number(payment.landPaymentStatus)
        : null,
      source: typeof payment.source === "string" ? payment.source : null,
      status: Number.isFinite(Number(payment.status)) ? Number(payment.status) : null,
      dateMeasure: typeof payment.dateMeasure === "string" ? payment.dateMeasure : null,
      dateVerified: typeof payment.dateVerified === "string" ? payment.dateVerified : null,
      pjVerified: payment.pjVerified && Number(payment.pjVerified.id || 0) > 0
        ? payment.pjVerified
        : null,
      recipientSubcontractorId: getLandPaymentRecipientId(payment),
      recipientSubcontractor: payment.recipientSubcontractor ?? null,
    }))
    .filter((payment) => payment.amountEuro > 0);
}

function buildContractProjectLandPaymentPreviews(
  contract: SynthesesGroupedContractNode,
  projectId: number | null | undefined,
): ProjectLinePaymentPreview[] {
  const normalizedProjectId = Number(projectId || 0);
  if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
    return [];
  }

  return (Array.isArray(contract.lineRows) ? contract.lineRows : [])
    .filter((lineRow) => Number(lineRow.projectId || 0) === normalizedProjectId)
    .flatMap((lineRow) =>
      buildPaymentsForLine(
        contract.landPayments || [],
        lineRow.realFundingId,
        `${lineRow.key}-project-land-sync`,
        {
          synthesesContractId: lineRow.synthesesContractId,
          synthesesContractLineId: lineRow.lineId,
          realFundingId: lineRow.realFundingId,
        },
      ),
    )
    .sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
}

function areTerrainCertifiedIndicatorOptionsEqual(
  left: TerrainCertifiedIndicatorOption[],
  right: TerrainCertifiedIndicatorOption[],
) {
  if (left.length !== right.length) return false;

  return left.every((option, index) => {
    const nextOption = right[index];

    return (
      option.id === nextOption?.id &&
      option.label === nextOption?.label &&
      option.disabled === nextOption?.disabled
    );
  });
}

function buildPaymentPreviews(
  payments: PaymentRow[],
  keyPrefix: string,
): ProjectLinePaymentPreview[] {
  return (Array.isArray(payments) ? payments : [])
	    .map((payment, index) => ({
	      id: `${keyPrefix}-${payment.id ?? index}`,
	      at: payment.at,
	      amountEuro: Number(Number(payment.amountEuro || 0).toFixed(2)),
	      synthesesContractId: getPaymentContractId(payment),
	      synthesesContractLineId: getPaymentContractLineId(payment),
	      lineId: getPaymentContractLineId(payment),
	      realFundingId: Number(payment.realFundingId || 0) > 0 ? Number(payment.realFundingId) : null,
	      lineTitle: typeof payment.lineTitle === "string" ? payment.lineTitle : null,
      projectId: Number(payment.projectId || 0) > 0 ? Number(payment.projectId) : null,
      projectName: typeof payment.projectName === "string" ? payment.projectName : null,
      projectIndicatorId: Number(payment.projectIndicatorId || 0) > 0
        ? Number(payment.projectIndicatorId)
        : null,
      landPaymentId: Number(payment.landPaymentId || 0) > 0
        ? Number(payment.landPaymentId)
        : null,
      landPaymentStatus: Number.isFinite(Number(payment.landPaymentStatus))
        ? Number(payment.landPaymentStatus)
        : null,
      source: typeof payment.source === "string" ? payment.source : null,
      status: Number.isFinite(Number(payment.status)) ? Number(payment.status) : null,
      dateMeasure: typeof payment.dateMeasure === "string" ? payment.dateMeasure : null,
      dateVerified: typeof payment.dateVerified === "string" ? payment.dateVerified : null,
      pjVerified: payment.pjVerified && Number(payment.pjVerified.id || 0) > 0
        ? payment.pjVerified
        : null,
      recipientSubcontractorId: getLandPaymentRecipientId(payment),
      recipientSubcontractor: payment.recipientSubcontractor ?? null,
    }))
    .filter((payment) => payment.amountEuro > 0);
}

function normalizeTerrainDateInput(value: string | null | undefined) {
  return typeof value === "string" ? value.slice(0, 10) : "";
}

function formatTerrainAmountInput(value: number | null | undefined) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) return "";
  return String(Number(parsed.toFixed(2)));
}

function buildOptimisticLandPaymentRows({
  landPaymentId,
  amountEuro,
  status,
  expectedAt,
  dateMeasure,
  dateVerified,
  realFundingId,
  synthesesContractId,
  synthesesContractLineId,
  projectId,
  projectName,
  lineTitle,
  pjVerified,
  recipientSubcontractorId,
}: {
  landPaymentId: number | null;
  amountEuro: number;
  status: number;
  expectedAt: string;
  dateMeasure?: string | null;
  dateVerified?: string | null;
  realFundingId?: number | null;
  synthesesContractId?: number | null;
  synthesesContractLineId?: number | null;
  projectId?: number | null;
  projectName?: string | null;
  lineTitle?: string | null;
  pjVerified?: TerrainMediaRef | null;
  recipientSubcontractorId?: number | null;
}): SynthesesGroupedContractNode["landPayments"] {
  const normalizedAmount = Number(amountEuro || 0);
  if (!Number.isFinite(normalizedAmount) || normalizedAmount <= 0) return [];

  const normalizedStatus = Math.max(1, Math.min(3, Number(status || 1)));
  const rawLandPaymentId = Number(landPaymentId || 0);
  const normalizedLandPaymentId =
    Number.isFinite(rawLandPaymentId) && rawLandPaymentId !== 0
      ? rawLandPaymentId
      : -Date.now();
  const tempBaseId = normalizedLandPaymentId;
  const rows: SynthesesGroupedContractNode["landPayments"] = [];
  const pushRow = (rowStatus: number, at: string | null | undefined) => {
    if (!at) return;
    rows.push({
      id: normalizedLandPaymentId ? -Math.abs(tempBaseId * 10 + rowStatus) : tempBaseId - rowStatus,
      projectIndicatorId: null,
      landPaymentId: normalizedLandPaymentId,
      landPaymentStatus: normalizedStatus,
      realFundingId: Number(realFundingId || 0) > 0 ? Number(realFundingId) : null,
      synthesesContractId:
        Number(synthesesContractId || 0) > 0 ? Number(synthesesContractId) : null,
      synthesesContractLineId:
        Number(synthesesContractLineId || 0) > 0
          ? Number(synthesesContractLineId)
          : null,
      lineId:
        Number(synthesesContractLineId || 0) > 0
          ? Number(synthesesContractLineId)
          : null,
      lineTitle: lineTitle || null,
      projectId: Number(projectId || 0) > 0 ? Number(projectId) : null,
      projectName: projectName || null,
      at,
      amountLocal: Number(normalizedAmount.toFixed(2)),
      amountEuro: Number(normalizedAmount.toFixed(2)),
      source: "land_payment",
      status: rowStatus,
      dateMeasure: rowStatus === 2 ? at : dateMeasure || null,
      dateVerified: rowStatus === 3 ? at : dateVerified || null,
      pjVerified: pjVerified || null,
      recipientSubcontractorId:
        Number(recipientSubcontractorId || 0) > 0
          ? Number(recipientSubcontractorId)
          : null,
      recipientSubcontractor: null,
    });
  };

  pushRow(1, expectedAt);
  if (normalizedStatus >= 2) {
    pushRow(2, dateMeasure || expectedAt);
  }
  if (normalizedStatus >= 3) {
    pushRow(3, dateVerified || dateMeasure || expectedAt);
  }

  return rows;
}

function getDefaultTerrainDateForYear(year: string | number | null | undefined) {
  const normalizedYear = String(year ?? "").trim();
  if (/^\d{4}$/.test(normalizedYear)) {
    return `${normalizedYear}-01-01`;
  }

  return new Date().toISOString().slice(0, 10);
}

function getTerrainMediaHref(media: TerrainMediaRef | null | undefined) {
  const mediaUri = typeof media?.uri === "string" ? media.uri.trim() : "";
  if (mediaUri) {
    if (/^https?:\/\//i.test(mediaUri)) return mediaUri;
    return `${import.meta.env.VITE_MEDIA_BASE}${mediaUri}`;
  }

  const mediaPath = typeof media?.path === "string" ? media.path.trim() : "";
  if (mediaPath) {
    return `${import.meta.env.VITE_IP_API}/media/${mediaPath}`;
  }

  return "";
}

function getTerrainMediaLabel(media: TerrainMediaRef | null | undefined) {
  if (typeof media?.name === "string" && media.name.trim()) {
    return media.name;
  }

  const mediaUri = typeof media?.uri === "string" ? media.uri.trim() : "";
  if (mediaUri) {
    return mediaUri.split("/").pop() || "justificatif";
  }

  const mediaPath = typeof media?.path === "string" ? media.path.trim() : "";
  if (mediaPath) {
    return mediaPath.split("/").pop() || "justificatif";
  }

  return "justificatif";
}

function canManageExistingLandPaymentRow(
  payment: ProjectLinePaymentPreview,
  allLandPaymentRows: ProjectLinePaymentPreview[],
) {
  if (payment.source !== "land_payment" || !isPositiveId(payment.landPaymentId)) {
    return false;
  }

  const sameLandPaymentRows = allLandPaymentRows.filter(
    (row) =>
      row.source === "land_payment"
      && Number(row.landPaymentId || 0) === Number(payment.landPaymentId || 0),
  );
  if (sameLandPaymentRows.length === 0) {
    return false;
  }

  const landPaymentStatus = Number(payment.landPaymentStatus || payment.status || 0);
  const primaryRow = sameLandPaymentRows.find(
    (row) => Number(row.status || 0) === landPaymentStatus,
  );

  return (primaryRow ?? sameLandPaymentRows[0])?.id === payment.id;
}

function buildTerrainPaymentEditorDraft(
  rows: ProjectLinePaymentPreview[],
  payment: ProjectLinePaymentPreview | null | undefined,
  fallbackYear: number | null | undefined,
) {
  const landPaymentId = isPositiveId(payment?.landPaymentId)
    ? Number(payment?.landPaymentId)
    : null;
  const relatedRows = null === landPaymentId
    ? []
    : rows.filter(
        (row) =>
          row.source === "land_payment"
          && Number(row.landPaymentId || 0) === landPaymentId,
      );
  const relevantRows = relatedRows.length > 0
    ? relatedRows
    : (payment ? [payment] : []);
  const status = Number(payment?.landPaymentStatus || payment?.status || 1) || 1;
  const primaryRow = relevantRows.find((row) => Number(row.status || 0) === status)
    ?? relevantRows[0]
    ?? payment
    ?? null;
  const plannedRow = relevantRows.find((row) => Number(row.status || 0) === 1)
    ?? primaryRow;
  const realizedRow = relevantRows.find((row) => Number(row.status || 0) === 2)
    ?? primaryRow;
  const certifiedRow = relevantRows.find((row) => Number(row.status || 0) === 3)
    ?? primaryRow;

  return {
    landPaymentId,
    status,
    amountEuro: formatTerrainAmountInput(primaryRow?.amountEuro ?? payment?.amountEuro ?? 0),
    expectedAt:
      normalizeTerrainDateInput(plannedRow?.at || primaryRow?.at) ||
      getDefaultTerrainDateForYear(fallbackYear),
    dateMeasure:
      normalizeTerrainDateInput(realizedRow?.dateMeasure || realizedRow?.at),
    dateVerified:
      normalizeTerrainDateInput(certifiedRow?.dateVerified || certifiedRow?.at),
    pjVerified: certifiedRow?.pjVerified ?? primaryRow?.pjVerified ?? payment?.pjVerified ?? null,
    recipientSubcontractorId:
      getLandPaymentRecipientId(primaryRow) ??
      getLandPaymentRecipientId(payment) ??
      null,
  };
}

function formatTerrainIndicatorOption(row: ProjectLinePaymentPreview) {
  const monthIndex = extractTimelineMonthIndex(row.at);
  const monthLabel = monthIndex === null
    ? "Mois non renseigné"
    : MONTH_COLUMNS.find((month) => month.index === monthIndex)?.label || "Mois";
  const yearLabel = extractTimelineYear(row.at) || "Année non renseignée";

  return `${monthLabel} ${yearLabel} • ${formatEuro(row.amountEuro)}`;
}

function buildTerrainCertifiedLineOptions(
  rows: ContractLineDisplayRow[],
): TerrainCertifiedLineOption[] {
  const seen = new Set<string>();

  return rows
    .filter((row) => {
      if (!row?.key || seen.has(row.key)) return false;
      seen.add(row.key);

      return (
        isPositiveId(row.realFundingId) ||
        isPositiveId(row.lineId)
      );
    })
    .map((row, index) => {
      const title = toTrimmedText(row.title) || `Poste ${index + 1}`;

      return {
        lineKey: row.key,
        label: `${title} • ${formatEuro(row.amountEuro)}`,
        synthesesContractId: row.synthesesContractId,
        synthesesContractLineId: row.lineId,
        realFundingId: row.realFundingId,
        amountEuro: Number(row.amountEuro || 0),
      };
	    });
}

function buildTerrainMoveTargetOptions(
  financeurContracts: SynthesesGroupedContractNode[],
  sourceRow: ContractLineDisplayRow | null | undefined,
): TerrainMoveTargetOption[] {
  if (!sourceRow) return [];

  const sourceLineId = Number(sourceRow.lineId || 0);
  const sourceProjectId = Number(sourceRow.projectId || 0);
  if (!Number.isFinite(sourceProjectId) || sourceProjectId <= 0) return [];

  return Array.from(
    new Map(
      (financeurContracts || [])
        .flatMap((targetContract) =>
          (Array.isArray(targetContract.lineRows)
            ? targetContract.lineRows
            : [])
            .map((targetLine) => {
              const targetLineId = Number(targetLine.lineId || 0);
              const targetProjectId = Number(targetLine.projectId || 0);
              const targetContractLabel =
                toTrimmedText(targetContract.title) || "Contrat";

              return {
                key: targetLineId,
                projectId: targetProjectId,
                isVerdeo: targetContract.isVerdeo === true,
                value: {
                  lineId: targetLineId,
                  label: [
                    targetContractLabel,
                    formatEuro(Number(targetLine.amountEuro || 0)),
                  ]
                    .filter(Boolean)
                    .join(" • "),
                } satisfies TerrainMoveTargetOption,
              };
            }),
        )
        .filter(
          (option) =>
            option.key > 0
            && option.key !== sourceLineId
            && option.projectId > 0
            && option.projectId === sourceProjectId
            && option.isVerdeo !== true,
        )
        .map((option) => [option.key, option.value]),
    ).values(),
  );
}

function findTerrainLineRowForPayment(
  rows: ContractLineDisplayRow[],
  payment: ProjectLinePaymentPreview | null | undefined,
) {
  if (!payment) return null;

  const paymentLineId = Number(payment.synthesesContractLineId || payment.lineId || 0);
  if (Number.isFinite(paymentLineId) && paymentLineId > 0) {
    const rowByLine = rows.find(
      (row) => Number(row.lineId || 0) === paymentLineId,
    );
    if (rowByLine) return rowByLine;
  }

  const paymentRealFundingId = Number(payment.realFundingId || 0);
  if (Number.isFinite(paymentRealFundingId) && paymentRealFundingId > 0) {
    const rowByRealFunding = rows.find(
      (row) => Number(row.realFundingId || 0) === paymentRealFundingId,
    );
    if (rowByRealFunding) return rowByRealFunding;
  }

  return null;
}

function isTerrainIndicatorLinkedToAnotherCertified(
  row: ProjectLinePaymentPreview,
  rows: ProjectLinePaymentPreview[],
  currentLandPaymentId: number | null,
  currentCertifiedIndicatorId: number | null,
) {
  const landPaymentId = Number(row.landPaymentId || 0);
  if (!Number.isFinite(landPaymentId) || landPaymentId <= 0) {
    return false;
  }
  if (currentLandPaymentId && landPaymentId === currentLandPaymentId) {
    return false;
  }

  return rows.some(
    (candidate) =>
      Number(candidate.landPaymentId || 0) === landPaymentId &&
      Number(candidate.status || 0) === 3 &&
      Number(candidate.projectIndicatorId || 0) > 0 &&
      Number(candidate.projectIndicatorId || 0) !== Number(currentCertifiedIndicatorId || 0),
  );
}

function buildTerrainCertifiedIndicatorOptions(
  rows: ProjectLinePaymentPreview[],
  status: 1 | 2,
  currentLandPaymentId: number | null,
  currentCertifiedIndicatorId: number | null,
): TerrainCertifiedIndicatorOption[] {
  const seen = new Set<number>();

  return rows
    .filter(
      (row) =>
        Number(row.status || 0) === status &&
        Number(row.projectIndicatorId || 0) > 0,
    )
    .filter((row) => {
      const id = Number(row.projectIndicatorId || 0);
      if (seen.has(id)) return false;
      seen.add(id);
      return true;
    })
    .sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")))
    .map((row) => ({
      id: Number(row.projectIndicatorId || 0),
      label: formatTerrainIndicatorOption(row),
      disabled: isTerrainIndicatorLinkedToAnotherCertified(
        row,
        rows,
        currentLandPaymentId,
        currentCertifiedIndicatorId,
      ),
    }));
}

function findTerrainLinkedIndicatorId(
  rows: ProjectLinePaymentPreview[],
  status: 1 | 2,
  landPaymentId: number | null,
) {
  if (!landPaymentId) return null;

  const row = rows.find(
    (candidate) =>
      Number(candidate.landPaymentId || 0) === landPaymentId &&
      Number(candidate.status || 0) === status &&
      Number(candidate.projectIndicatorId || 0) > 0,
  );

  return row ? Number(row.projectIndicatorId || 0) : null;
}

function TopAmountCell({
  title,
  value,
  subtitle,
  tone = "slate",
}: {
  title: string;
  value: string;
  subtitle: string;
  tone?: SynthesesMetricTone;
}) {
  const safeTitle = String(title || "").trim();
  const safeSubtitle = String(subtitle || "").trim();
  const toneClasses = getSynthesesMetricToneClasses(tone);

  return (
    <div
      className={`rounded-xl border px-3 py-2 shadow-[0_1px_0_rgba(15,23,42,0.03)] ${toneClasses.card}`}
    >
      {safeTitle ? (
        <div
          className={`text-[11px] font-semibold uppercase tracking-wide ${toneClasses.label}`}
        >
          {safeTitle}
        </div>
      ) : null}
      <div className={`mt-1 text-sm font-semibold ${toneClasses.value}`}>
        {value}
      </div>
      {safeSubtitle ? (
        <div className={`mt-0.5 truncate text-[11px] ${toneClasses.meta}`}>
          {safeSubtitle}
        </div>
      ) : null}
    </div>
  );
}

function CustomHorizontalScroller({
  children,
  compact = false,
}: {
  children: ReactNode;
  compact?: boolean;
}) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);
  const dragStateRef = useRef<{
    pointerId: number;
    startClientX: number;
    startScrollLeft: number;
  } | null>(null);
  const [metrics, setMetrics] = useState({
    viewportWidth: 0,
    contentWidth: 0,
    maxScrollLeft: 0,
  });
  const [scrollLeft, setScrollLeft] = useState(0);

  useEffect(() => {
    const viewportNode = viewportRef.current;
    const contentNode = contentRef.current;
    if (!viewportNode || !contentNode) return;

    const updateMetrics = () => {
      const viewportWidth = viewportNode.clientWidth;
      const contentWidth = contentNode.scrollWidth;
      const maxScrollLeft = Math.max(0, contentWidth - viewportWidth);

      setMetrics({
        viewportWidth,
        contentWidth,
        maxScrollLeft,
      });
      setScrollLeft((current) => Math.min(current, maxScrollLeft));
    };

    updateMetrics();

    const observer = new ResizeObserver(() => {
      updateMetrics();
    });
    observer.observe(viewportNode);
    observer.observe(contentNode);

    return () => {
      observer.disconnect();
    };
  }, [children]);

  const syncScrollLeft = (nextScrollLeft: number) => {
    const viewportNode = viewportRef.current;
    const maxScrollLeft = metrics.maxScrollLeft;
    const clampedValue = Math.max(0, Math.min(nextScrollLeft, maxScrollLeft));

    if (viewportNode) {
      viewportNode.scrollLeft = clampedValue;
    }
    setScrollLeft(clampedValue);
  };

  const handleViewportScroll = () => {
    const viewportNode = viewportRef.current;
    if (!viewportNode) return;
    setScrollLeft(viewportNode.scrollLeft);
  };

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    const trackNode = trackRef.current;
    if (!trackNode || metrics.maxScrollLeft <= 0) return;

    const bounds = trackNode.getBoundingClientRect();
    const ratio = Math.max(
      0,
      Math.min(1, (event.clientX - bounds.left) / bounds.width),
    );
    syncScrollLeft(ratio * metrics.maxScrollLeft);
  };

  const handleThumbPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (metrics.maxScrollLeft <= 0) return;
    dragStateRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startScrollLeft: scrollLeft,
    };

    const releaseDrag = () => {
      dragStateRef.current = null;
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", releaseDrag);
      window.removeEventListener("pointercancel", releaseDrag);
    };

    const handlePointerMove = (moveEvent: PointerEvent) => {
      const state = dragStateRef.current;
      const trackNode = trackRef.current;
      if (!state || !trackNode) return;
      if (moveEvent.pointerId !== state.pointerId) return;

      const deltaX = moveEvent.clientX - state.startClientX;
      const trackWidth = trackNode.clientWidth;
      if (trackWidth <= 0) return;

      const nextScrollLeft =
        state.startScrollLeft + (deltaX / trackWidth) * metrics.maxScrollLeft;
      syncScrollLeft(nextScrollLeft);
    };

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", releaseDrag);
    window.addEventListener("pointercancel", releaseDrag);
  };

  const thumbWidthPercent =
    metrics.contentWidth > 0
      ? Math.max(
          12,
          Math.min(100, (metrics.viewportWidth / metrics.contentWidth) * 100),
        )
      : 100;
  const thumbTravelPercent = Math.max(0, 100 - thumbWidthPercent);
  const thumbLeftPercent =
    metrics.maxScrollLeft > 0
      ? (scrollLeft / metrics.maxScrollLeft) * thumbTravelPercent
      : 0;

  return (
    <div className="min-w-0 w-full max-w-full">
      <div
        ref={viewportRef}
        className={cn(
          "hide-native-scrollbar min-w-0 w-full max-w-full overflow-x-auto",
          compact ? "pb-1.5" : "pb-2",
        )}
        style={{
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
        onScroll={handleViewportScroll}
      >
        <div ref={contentRef} className="w-max">
          {children}
        </div>
      </div>

      {metrics.maxScrollLeft > 0 ? (
        <div className={cn(compact ? "px-0.5" : "px-1")}>
          <div
            ref={trackRef}
            className={cn(
              "relative w-full cursor-pointer rounded-full bg-slate-200/90",
              compact ? "h-2.5" : "h-3",
            )}
            onPointerDown={handleTrackPointerDown}
          >
            <div
              role="slider"
              aria-label="Défilement horizontal de la vue mensuelle"
              aria-valuemin={0}
              aria-valuemax={Math.round(metrics.maxScrollLeft)}
              aria-valuenow={Math.round(scrollLeft)}
              tabIndex={0}
              onPointerDown={handleThumbPointerDown}
              className={cn(
                "absolute rounded-full bg-slate-400 shadow-sm transition-colors hover:bg-slate-500",
                compact ? "top-[2px] h-[6px]" : "top-[2px] h-[8px]",
              )}
              style={{
                left: `${thumbLeftPercent}%`,
                width: `${thumbWidthPercent}%`,
              }}
            />
          </div>
        </div>
      ) : null}
    </div>
  );
}

function FinanceurMonthlyTimeline({
  yearSummary,
  embedded = false,
}: {
  yearSummary: FinanceurTimelineYearSummary | null;
  embedded?: boolean;
}) {
  const safeYearSummary =
    yearSummary || createFinanceurTimelineYearSummary(getCurrentTimelineYearString());

  const expectedArr = buildFinanceurTimelineValues(
    safeYearSummary,
    "expectedPaymentsEuro",
  );
  const receivedArr = buildFinanceurTimelineValues(
    safeYearSummary,
    "receivedPaymentsEuro",
  );

  return (
    <CustomHorizontalScroller compact={embedded}>
      <div className="min-w-[920px]">
        <div
          className={cn(
            embedded
              ? "rounded-xl border border-slate-200 bg-white px-2.5 py-2.5"
              : "rounded-2xl bg-[#F3FAF3] px-3 py-3",
          )}
        >
          <div className="mb-2 flex items-center gap-2 w-full">
            <div
              className={cn(
                MONTH_LABEL_COLUMN_CLASS,
                embedded
                  ? "text-[8px] font-semibold uppercase tracking-wide text-slate-500"
                  : "text-[9px] font-semibold uppercase tracking-wide text-slate-500",
              )}
            >
              Mois
            </div>
            <div className={MONTH_CELLS_ROW_CLASS}>
              {MONTH_COLUMNS.map((month) => (
                <div
                  key={`financeur-month-header-${month.index}`}
                  className={cn(
                    "rounded-md border border-slate-200 bg-slate-50 text-center font-semibold uppercase tracking-wide text-slate-600",
                    embedded ? "px-1 py-0.5 text-[8px]" : "px-1 py-1 text-[9px]",
                  )}
                >
                  {month.label}
                </div>
              ))}
            </div>
          </div>

          <div className="mb-2 rounded-xl border border-slate-200 bg-slate-50/80 px-2 py-2">
            <div className="flex items-center gap-2 w-full">
              <div
                className={cn(
                  MONTH_LABEL_COLUMN_CLASS,
                  "text-[11px] font-semibold text-slate-700",
                )}
              >
                Versements prévus
              </div>
              <div className={MONTH_CELLS_ROW_CLASS}>
                {expectedArr.map((value, index) => (
                  <FieldInput
                    key={`financeur-expected-${safeYearSummary.year}-${index}`}
                    type={5}
                    defaultValue={value || 0}
                    valueMultiple={String(value || 0)}
                    data={{
                      idFinanceur: 0,
                      year: Number(safeYearSummary.year) || 0,
                      month: index,
                      idDevise: 0,
                    }}
                    compact
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-2 py-2">
            <div className="flex items-center gap-2 w-full">
              <div
                className={cn(
                  MONTH_LABEL_COLUMN_CLASS,
                  "text-[11px] font-semibold text-slate-700",
                )}
              >
                Versements reçus
              </div>
              <div className={MONTH_CELLS_ROW_CLASS}>
                {receivedArr.map((value, index) => (
                  <FieldInput
                    key={`financeur-received-${safeYearSummary.year}-${index}`}
                    type={6}
                    defaultValue={value || 0}
                    valueMultiple={String(value || 0)}
                    data={{
                      idFinanceur: 0,
                      year: Number(safeYearSummary.year) || 0,
                      month: index,
                      idDevise: 0,
                    }}
                    compact
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </CustomHorizontalScroller>
  );
}

function FinanceurContractSelectorCard({
  contract,
  index,
  selectedHeaderYear,
  isSelected,
  isEditing,
  isSaving,
  onSelect,
}: {
  contract: SynthesesGroupedContractNode;
  index: number;
  selectedHeaderYear: number | null;
  isSelected: boolean;
  isEditing: boolean;
  isSaving: boolean;
  onSelect: () => void;
}) {
  const contractLabel = contract.title || `Contrat ${index + 1}`;
  const displayedAmount =
    selectedHeaderYear === null
      ? Number(contract.amountEuro || 0)
      : getContractAmountEuroForYear(contract, selectedHeaderYear);
  const receivedAmount =
    selectedHeaderYear === null
      ? Number(contract.totals.receivedPaymentsEuro || 0)
      : sumRowsAmountForYear(contract.paidPayments, selectedHeaderYear);
  const contractYears = buildContractYears(contract);
  const hasReceivedAmount = receivedAmount > 0;
  const contractProjects = collectContractProjectFilterOptions(contract);
  const contractProjectsCount = contractProjects.length;

  return (
    <div className="relative flex w-[240px] shrink-0 pb-3">
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(event) => {
          if (event.key !== "Enter" && event.key !== " ") return;
          event.preventDefault();
          onSelect();
        }}
        className={cn(
          "flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-[16px] border text-left shadow-sm transition focus:outline-none focus:ring-2 focus:ring-emerald-200",
          isSelected
            ? "border-emerald-300 bg-gradient-to-br from-emerald-50/80 via-white to-sky-50/80 ring-2 ring-emerald-100 shadow-md shadow-emerald-100/60"
            : "border-slate-200 bg-gradient-to-br from-white via-slate-50/50 to-sky-50/60 hover:border-sky-200 hover:shadow-md hover:shadow-sky-100/60",
        )}
      >
        <div
          className={cn(
            "h-1.5 w-full bg-gradient-to-r",
            isSelected
              ? "from-emerald-400 via-sky-400 to-amber-300"
              : "from-sky-300 via-slate-200 to-emerald-200",
          )}
        />

        <div className="space-y-2 px-3 pb-3 pt-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] text-sky-700">
              Contrat
            </span>
            {contract.isVerdeo ? (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                Verdeo
              </span>
            ) : null}
            {isEditing ? (
              <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                En édition
              </span>
            ) : null}
            {isSaving ? (
              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-700">
                <Loader2 className="h-3 w-3 animate-spin" />
                Sauvegarde
              </span>
            ) : null}
          </div>

          <div
            title={contractLabel}
            className="line-clamp-2 min-h-[40px] text-sm font-semibold leading-5 text-slate-900"
          >
            {contractLabel}
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0 text-xs text-slate-600">
              {formatDate(contract.contractDate)}
            </div>

            <div className="inline-flex max-w-[128px] shrink-0 items-center gap-1.5 rounded-full border border-emerald-100 bg-emerald-50/75 px-2 py-1 text-[11px] font-medium text-emerald-800">
              <span className="truncate">
                {contractProjectsCount > 0
                  ? `concerne ${contractProjectsCount} projet${contractProjectsCount > 1 ? "s" : ""}`
                  : "projet non renseigné"}
              </span>
              {contractProjectsCount > 0 ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span
                      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700"
                      onClick={(event) => event.stopPropagation()}
                    >
                      <Info className="h-3 w-3" />
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="max-w-[260px] text-xs">
                    <div className="space-y-1">
                      <div className="font-medium text-slate-900">
                        Projets concernés
                      </div>
                      <ul className="space-y-0.5 text-slate-600">
                        {contractProjects.map((project) => (
                          <li key={project.key} className="flex gap-1.5">
                            <span className="mt-[0.45em] h-1 w-1 shrink-0 rounded-full bg-emerald-500" />
                            <span>{project.label}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </TooltipContent>
                </Tooltip>
              ) : null}
            </div>
          </div>
        </div>

        <div className="mt-auto grid grid-cols-2 gap-2 border-t border-slate-200/80 bg-white/70 px-3 py-3">
          <div className="rounded-xl border border-sky-200 bg-sky-50/80 px-2.5 py-2">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-sky-700/80">
              Montant
            </div>
            <div className="mt-1 text-sm font-semibold text-sky-950">
              {formatEuro(displayedAmount)}
            </div>
          </div>
          <div
            className={cn(
              "rounded-xl border px-2.5 py-2",
              hasReceivedAmount
                ? "border-emerald-200 bg-emerald-50/85"
                : "border-amber-200 bg-amber-50/85",
            )}
          >
            <div
              className={cn(
                "text-[10px] font-semibold uppercase tracking-wide",
                hasReceivedAmount ? "text-emerald-700/80" : "text-amber-700/80",
              )}
            >
              Reçus
            </div>
            <div
              className={cn(
                "mt-1 text-sm font-semibold",
                hasReceivedAmount ? "text-emerald-950" : "text-amber-950",
              )}
            >
              {formatEuro(receivedAmount)}
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200/80 bg-slate-50/55 px-3 py-2 text-[11px] font-medium text-slate-600">
          {contractYears.length > 1
            ? `${contractYears[0]} - ${contractYears[contractYears.length - 1]}`
            : String(contractYears[0] || "Sans année")}
        </div>
      </div>

      {isSelected ? (
        <div className="pointer-events-none absolute bottom-[5px] left-1/2 z-10 h-4 w-4 -translate-x-1/2 rotate-45 rounded-[2px] border-b border-r border-emerald-300 bg-white" />
      ) : null}
      {isSaving ? (
        <div className="pointer-events-none absolute inset-x-2 top-2 rounded-full border border-amber-200 bg-amber-50/95 px-2 py-1 text-center text-[10px] font-semibold uppercase tracking-wide text-amber-700 shadow-sm">
          Sauvegarde en cours...
        </div>
      ) : null}
    </div>
  );
}

type ContractPaymentPanelTone = "expected" | "received";

const CONTRACT_PAYMENT_PANEL_TONES: Record<
  ContractPaymentPanelTone,
  {
    panel: string;
    title: string;
    total: string;
    row: string;
  }
> = {
  expected: {
    panel: "border-[#a1560b]/25 bg-[#a1560b]/10",
    title: "text-[#8b4a09]",
    total: "text-[#7a4108]",
    row: "border-[#a1560b]/20 bg-white/85",
  },
  received: {
    panel: "border-[#425e1a]/25 bg-[#425e1a]/10",
    title: "text-[#425e1a]",
    total: "text-[#344816]",
    row: "border-[#425e1a]/20 bg-white/85",
  },
};

function getPaymentContextLabel(payment: ProjectLinePaymentPreview) {
  return [
    toTrimmedText(payment.projectName),
    toTrimmedText(payment.lineTitle),
  ]
    .filter(Boolean)
    .join(" • ");
}

function ContractPaymentRows({
  rows,
  emptyLabel,
  showContext = false,
  rowClassName,
}: {
  rows: ProjectLinePaymentPreview[];
  emptyLabel: string;
  showContext?: boolean;
  rowClassName?: (payment: ProjectLinePaymentPreview) => string;
}) {
  if (rows.length === 0) {
    return <div className="text-[11px] text-slate-500">{emptyLabel}</div>;
  }

  return (
    <div className="space-y-1.5">
      {rows.map((payment) => {
        const contextLabel = showContext ? getPaymentContextLabel(payment) : "";

        return (
          <div
            key={payment.id}
            className={cn(
              "flex items-start justify-between gap-2 rounded-md border px-2 py-1.5",
              rowClassName?.(payment),
            )}
          >
            <div className="min-w-0">
              <div className="text-[11px] text-slate-600">
                {formatDate(payment.at)}
              </div>
              {contextLabel ? (
                <div className="mt-0.5 truncate text-[10px] text-slate-500">
                  {contextLabel}
                </div>
              ) : null}
            </div>
            <span className="shrink-0 text-[11px] font-medium text-slate-900">
              {formatEuro(payment.amountEuro)}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function ContractPaymentPanel({
  title,
  total,
  rows,
  emptyLabel,
  tone,
  showContext = false,
}: {
  title: string;
  total: number;
  rows: ProjectLinePaymentPreview[];
  emptyLabel: string;
  tone: ContractPaymentPanelTone;
  showContext?: boolean;
}) {
  const toneClasses = CONTRACT_PAYMENT_PANEL_TONES[tone];

  return (
    <div className={cn("rounded-xl border p-2.5", toneClasses.panel)}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <div
          className={cn(
            "text-[11px] font-semibold uppercase tracking-wide",
            toneClasses.title,
          )}
        >
          {title}
        </div>
        <div className={cn("text-xs font-semibold", toneClasses.total)}>
          {formatEuro(total)}
        </div>
      </div>
      <ContractPaymentRows
        rows={rows}
        emptyLabel={emptyLabel}
        showContext={showContext}
        rowClassName={() => toneClasses.row}
      />
    </div>
  );
}

function buildContractLandTimelineValues(
  rows: ProjectLinePaymentPreview[],
  status: number,
) {
  const buckets = new Map<number, number>();

  dedupePaymentPreviewsByMonthYear(
    rows.filter((row) => Number(row.status || 0) === status),
  ).forEach((row) => {
      const monthIndex = extractTimelineMonthIndex(row.at);
      if (monthIndex === null) return;

      buckets.set(
        monthIndex,
        Number(((buckets.get(monthIndex) || 0) + Number(row.amountEuro || 0)).toFixed(2)),
      );
    });

  return MONTH_COLUMNS.map((month) => buckets.get(month.index) || 0);
}

function buildContractPaymentTimelineValues(
  rows: ProjectLinePaymentPreview[],
  year: string | number | null | undefined,
) {
  const timelineYear = toTrimmedText(year);
  const buckets = new Map<number, number>();

  rows
    .filter((row) => {
      const rowYear = extractTimelineYear(row.at);
      return rowYear === timelineYear;
    })
    .forEach((row) => {
      const monthIndex = extractTimelineMonthIndex(row.at);
      if (monthIndex === null) return;

      buckets.set(
        monthIndex,
        Number(((buckets.get(monthIndex) || 0) + Number(row.amountEuro || 0)).toFixed(2)),
      );
    });

  return MONTH_COLUMNS.map((month) => buckets.get(month.index) || 0);
}

function buildPaymentTimelineYearOptions(
  rows: ProjectLinePaymentPreview[],
  fallbackYear?: number | string | null,
) {
  const years = new Set<string>();

  rows.forEach((row) => {
    const year = extractTimelineYear(row.at);
    if (year) years.add(year);
  });

  const normalizedFallbackYear = toTrimmedText(fallbackYear);
  if (/^\d{4}$/.test(normalizedFallbackYear)) {
    years.add(normalizedFallbackYear);
  }

  if (years.size === 0) {
    years.add(getCurrentTimelineYearString());
  }

  return Array.from(years).sort((a, b) => Number(a) - Number(b));
}

function buildTerrainTimelineCellKey(
  lineKey: string,
  status: number,
  year: number | string | null | undefined,
  monthIndex: number,
) {
  return `${lineKey}-${status}-${toTrimmedText(year)}-${monthIndex}`;
}

function findTerrainTimelineCellPayment(
  rows: ProjectLinePaymentPreview[],
  status: number,
  year: number,
  monthIndex: number,
) {
  return dedupePaymentPreviewsByMonthYear(
    rows.filter((row) => Number(row.status || 0) === status),
  ).find((row) => {
    const rowYear = Number(extractTimelineYear(row.at) || 0);
    const rowMonthIndex = extractTimelineMonthIndex(row.at);

    return rowYear === year && rowMonthIndex === monthIndex;
  }) ?? null;
}

function formatTerrainTimelineInputValue(value: number) {
  const amount = Number(value || 0);
  if (!Number.isFinite(amount) || amount <= 0) return "-";
  return amount.toLocaleString("fr-FR", {
    maximumFractionDigits: 2,
  });
}

function getTerrainUnlinkedWarningMessage(status: number) {
  if (status === 1) {
    return "Valeur ESG non liée : liez-la à un réalisé et un certifié, puis déplacez-la si le contrat actuel n'est pas le bon.";
  }
  if (status === 2) {
    return "Valeur ESG non liée : liez-la à un prévu et un certifié, puis déplacez-la si le contrat actuel n'est pas le bon.";
  }
  if (status === 3) {
    return "Valeur ESG non liée : liez-la à un prévu et un réalisé, puis déplacez-la si le contrat actuel n'est pas le bon.";
  }

  return "Valeur ESG non liée : liez-la aux autres étapes, puis déplacez-la si le contrat actuel n'est pas le bon.";
}

function buildTerrainCellTooltipInfo(
  payment: ProjectLinePaymentPreview | null,
  fallbackStatus: number,
  fallbackAmountEuro: number,
): TerrainCellTooltipInfo | null {
  const amount = Number(payment?.amountEuro ?? fallbackAmountEuro ?? 0);
  if (!payment && (!Number.isFinite(amount) || amount <= 0)) return null;

  const status = Number(payment?.status || fallbackStatus || 0);
  const statusLabel = getLandStatusLabel(status);
  const rows = [
    `Statut : ${statusLabel}`,
    `Montant : ${formatEuro(Number.isFinite(amount) ? amount : 0)}`,
  ];
  const date = toTrimmedText(payment?.at);
  if (date) {
    rows.push(`Date : ${formatDate(date)}`);
  }
  const recipientLabel = payment ? getLandPaymentRecipientLabel(payment) : "";
  if (recipientLabel) {
    rows.push(`Intervenant : ${recipientLabel}`);
  }
  const shouldShowLinkWarning = !!payment
    && !isPositiveId(payment.landPaymentId)
    && payment.source !== "land_payment";

  return {
    title: `Versement ${statusLabel.toLowerCase()} à l'intervenant`,
    rows,
    warning: shouldShowLinkWarning
      ? getTerrainUnlinkedWarningMessage(status)
      : null,
  };
}

function TerrainLinkTooltip({
  linkInfo,
  tooltipInfo,
  moveAction,
  children,
}: {
  linkInfo?: TerrainLinkVisualInfo | null;
  tooltipInfo?: TerrainCellTooltipInfo | null;
  moveAction?: TerrainCellMoveAction | null;
  children: ReactNode;
}) {
  const displayedTooltipInfo = linkInfo ?? tooltipInfo ?? null;
  if (!displayedTooltipInfo && !moveAction) return <>{children}</>;

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        {children}
      </TooltipTrigger>
      <TooltipContent
        side="top"
        className="pointer-events-auto w-80 max-w-[calc(100vw-2rem)] whitespace-normal text-left text-xs leading-relaxed"
      >
        <div className="space-y-2">
          {displayedTooltipInfo ? (
            <>
              <div className="font-semibold text-slate-900">{displayedTooltipInfo.title}</div>
              <div className="space-y-1 text-slate-700">
                {displayedTooltipInfo.rows.map((row, index) => (
                  <div key={`terrain-link-tooltip-row-${index}`}>
                    {row}
                  </div>
                ))}
              </div>
              {displayedTooltipInfo.warning ? (
                <div className="flex gap-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-[11px] leading-snug text-amber-800">
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                  <span>{displayedTooltipInfo.warning}</span>
                </div>
              ) : null}
            </>
          ) : (
            <div className="font-semibold text-slate-900">
              Versement à l'intervenant
            </div>
          )}
          {moveAction ? (
            <div className="border-t border-slate-100 pt-2">
              <button
                type="button"
                className={cn(
                  "inline-flex w-full items-center justify-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-semibold transition",
                  moveAction.active
                    ? "border-amber-300 bg-amber-100 text-amber-800 hover:bg-amber-200"
                    : "border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100",
                  moveAction.disabled
                    ? "cursor-not-allowed opacity-50"
                    : "",
                )}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (moveAction.disabled) return;
                  moveAction.onClick();
                }}
                disabled={moveAction.disabled}
              >
                {moveAction.active ? (
                  <>
                    <X className="h-3.5 w-3.5" />
                    Annuler le déplacement
                  </>
                ) : (
                  <>
                    <ArrowRightLeft className="h-3.5 w-3.5" />
                    Déplacer dans un autre contrat
                  </>
                )}
              </button>
            </div>
          ) : null}
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

function parseTerrainTimelineInputValue(value: string) {
  const normalized = String(value || "")
    .replace(/\s/g, "")
    .replace(",", ".")
    .trim();
  if (!normalized || normalized === "-") return null;

  const amount = Number(normalized);
  if (!Number.isFinite(amount) || amount <= 0) return null;

  return Number(amount.toFixed(2));
}

function EditableTerrainTimelineInput({
  type,
  value,
  disabled,
  saving,
  linkInfo,
  tooltipInfo,
  moveAction,
  onCommit,
}: {
  type: 1 | 2 | 3;
  value: number;
  disabled: boolean;
  saving: boolean;
  linkInfo?: TerrainLinkVisualInfo | null;
  tooltipInfo?: TerrainCellTooltipInfo | null;
  moveAction?: TerrainCellMoveAction | null;
  onCommit: (amountEuro: number | null) => Promise<void> | void;
}) {
  const [displayedValue, setDisplayedValue] = useState(
    () => formatTerrainTimelineInputValue(value),
  );
  const [isEditing, setIsEditing] = useState(false);
  const oldValueRef = useRef<string>("");

  useEffect(() => {
    if (isEditing) return;
    setDisplayedValue(formatTerrainTimelineInputValue(value));
  }, [isEditing, value]);

  const isEmpty = parseTerrainTimelineInputValue(displayedValue) === null;
  const inputClassName = cn(
    "h-7 w-full rounded-md p-1.5 text-center text-[11px] leading-3 outline-none transition focus-visible:ring-0 focus-visible:ring-transparent",
    type === 1
      ? "bg-verdeoblue text-white focus-visible:border-verdeoblue focus-visible:text-white"
      : type === 2
        ? "bg-verdeogreen text-white focus-visible:border-verdeogreen focus-visible:text-white"
        : "bg-verdeoorange text-white focus-visible:border-verdeoorange focus-visible:text-white",
    isEmpty && type === 1
      ? "border border-verdeoblue bg-background text-verdeoblue"
      : "",
    isEmpty && type === 2
      ? "border border-verdeogreen bg-background text-verdeogreen"
      : "",
    isEmpty && type === 3
      ? "border border-verdeoorange bg-background text-verdeoorange"
      : "",
    disabled ? "pointer-events-none opacity-50" : "",
    linkInfo?.cellClassName || "",
  );

  return (
	    <TerrainLinkTooltip linkInfo={linkInfo} tooltipInfo={tooltipInfo} moveAction={moveAction}>
	      <div className="relative flex-1 px-0.5 pt-0.5">
	      <input
	        value={displayedValue}
	        onFocus={() => {
	          if (disabled) return;
	          oldValueRef.current = displayedValue;
	          setIsEditing(true);
	          if (displayedValue === "-") {
            setDisplayedValue("");
          }
        }}
        onChange={(event) => {
          const nextValue = event.target.value;
          if (
            nextValue === "" ||
            nextValue === "-" ||
            /^\d*([,.]\d*)?$/.test(nextValue.replace(/\s/g, ""))
          ) {
            setDisplayedValue(nextValue);
          }
	        }}
	        onBlur={() => {
	          if (disabled) return;
	          setIsEditing(false);

          const amount = parseTerrainTimelineInputValue(displayedValue);
          if (amount === null) {
            if (
              displayedValue === oldValueRef.current ||
              (displayedValue === "" && oldValueRef.current === "-")
            ) {
              setDisplayedValue("-");
              return;
            }

            setDisplayedValue("-");
            void onCommit(null);
            return;
          }

          const previousAmount = parseTerrainTimelineInputValue(oldValueRef.current);
          if (previousAmount !== null && Math.abs(previousAmount - amount) < 0.005) {
            setDisplayedValue(formatTerrainTimelineInputValue(value));
            return;
          }

          void onCommit(amount);
	        }}
	        disabled={disabled}
	        aria-busy={saving}
	        className={inputClassName}
	      />
	      </div>
	    </TerrainLinkTooltip>
  );
}

function CertifiedTerrainTimelineButton({
  value,
  disabled,
  saving,
  linkInfo,
  tooltipInfo,
  moveAction,
  onOpen,
}: {
  value: number;
  disabled: boolean;
  saving: boolean;
  linkInfo?: TerrainLinkVisualInfo | null;
  tooltipInfo?: TerrainCellTooltipInfo | null;
  moveAction?: TerrainCellMoveAction | null;
  onOpen: () => void;
}) {
  const isEmpty = !Number.isFinite(Number(value)) || Number(value) <= 0;

  return (
    <TerrainLinkTooltip linkInfo={linkInfo} tooltipInfo={tooltipInfo} moveAction={moveAction}>
      <div className="relative flex-1 px-0.5 pt-0.5">
	      <button
		        type="button"
		        onClick={onOpen}
		        disabled={disabled}
		        aria-busy={saving}
		        className={cn(
	          "h-7 w-full rounded-md p-1.5 text-center text-[11px] leading-3 outline-none transition focus-visible:ring-0 focus-visible:ring-transparent",
	          isEmpty
	            ? "border border-verdeoorange bg-background text-verdeoorange"
	            : "bg-verdeoorange text-background hover:bg-verdeoorange/90",
	          disabled ? "pointer-events-none opacity-50" : "",
	          linkInfo?.cellClassName || "",
	        )}
		      >
		        {isEmpty ? "-" : formatTerrainTimelineInputValue(value)}
		      </button>
	      </div>
	    </TerrainLinkTooltip>
  );
}

function QuickLinkTerrainTimelineButton({
  type,
  value,
  payment,
  selected,
  disabled,
  linkInfo,
  tooltipInfo,
  moveAction,
  onPick,
}: {
  type: 1 | 2 | 3;
  value: number;
  payment: ProjectLinePaymentPreview | null;
  selected: boolean;
  disabled: boolean;
  linkInfo?: TerrainLinkVisualInfo | null;
  tooltipInfo?: TerrainCellTooltipInfo | null;
  moveAction?: TerrainCellMoveAction | null;
  onPick: () => void;
}) {
  const isEmpty = !Number.isFinite(Number(value)) || Number(value) <= 0;
  const toneClassName =
    type === 1
      ? "border-verdeoblue text-verdeoblue"
      : type === 2
        ? "border-verdeogreen text-verdeogreen"
        : "border-verdeoorange text-verdeoorange";
  const selectedClassName =
    type === 1
      ? "border-verdeoblue bg-verdeoblue text-white ring-verdeoblue/25"
      : type === 2
        ? "border-verdeogreen bg-verdeogreen text-white ring-verdeogreen/25"
        : "border-verdeoorange bg-verdeoorange text-white ring-verdeoorange/25";
  const canPick =
    !disabled &&
    !isEmpty &&
    !!payment &&
    (isPositiveId(payment.projectIndicatorId) || isPositiveId(payment.landPaymentId));

  return (
    <TerrainLinkTooltip linkInfo={linkInfo} tooltipInfo={tooltipInfo} moveAction={moveAction}>
      <div className="relative flex-1 px-0.5 pt-0.5">
      <button
        type="button"
        onClick={onPick}
        disabled={!canPick}
        className={cn(
          "h-7 w-full rounded-md border bg-white p-1.5 text-center text-[11px] leading-3 outline-none transition focus-visible:ring-2 focus-visible:ring-offset-0",
          toneClassName,
          selected ? `font-semibold ring-2 ${selectedClassName}` : "",
          linkInfo?.cellClassName || "",
          !canPick ? "cursor-not-allowed opacity-35" : "hover:bg-slate-50",
        )}
        title={
          canPick
            ? "Sélectionner ce versement pour la liaison rapide"
            : "Aucun indicateur sélectionnable sur cette cellule"
        }
      >
        {isEmpty ? "-" : formatTerrainTimelineInputValue(value)}
      </button>
      </div>
    </TerrainLinkTooltip>
  );
}

function ReadOnlyTerrainTimelineCell({
  type,
  value,
  timelineYear,
  monthIndex,
  linkInfo,
  tooltipInfo,
  moveAction,
}: {
  type: 1 | 2 | 3;
  value: number;
  timelineYear: number;
  monthIndex: number;
  linkInfo?: TerrainLinkVisualInfo | null;
  tooltipInfo?: TerrainCellTooltipInfo | null;
  moveAction?: TerrainCellMoveAction | null;
}) {
  return (
    <TerrainLinkTooltip linkInfo={linkInfo} tooltipInfo={tooltipInfo} moveAction={moveAction}>
      <div className="relative flex-1 px-0.5 pt-0.5">
      <div className={cn("rounded-md", linkInfo?.cellClassName || "")}>
        <FieldInput
          type={type}
          defaultValue={value || 0}
          valueMultiple={String(value || 0)}
          data={{
            idFinanceur: 0,
            year: timelineYear,
            month: monthIndex,
            idDevise: 0,
          }}
          compact
        />
      </div>
      </div>
    </TerrainLinkTooltip>
  );
}

type TerrainTimelineCommitPayload = {
  status: 1 | 2 | 3;
  year: number;
  monthIndex: number;
  amountEuro: number | null;
  payment: ProjectLinePaymentPreview | null;
  recipientSubcontractorId?: number | null;
};

function ContractLandPaymentMonthlyTimeline({
  landPayments,
  linkSourceLandPayments = landPayments,
  year,
  editable = false,
  disabled = false,
  savingCellKeys = [],
  cellKeyPrefix = "terrain-timeline",
  quickLinkMode = false,
  quickLinkState = null,
  canMovePayment,
  isMovePaymentActive,
  onCommitCell,
  onOpenCertifiedCell,
  onPickQuickLinkCell,
  onMovePayment,
}: {
  landPayments: ProjectLinePaymentPreview[];
  linkSourceLandPayments?: ProjectLinePaymentPreview[];
  year?: string | number | null;
  editable?: boolean;
  disabled?: boolean;
  savingCellKeys?: string[];
  cellKeyPrefix?: string;
  quickLinkMode?: boolean;
  quickLinkState?: TerrainQuickLinkState | null;
  canMovePayment?: (payment: ProjectLinePaymentPreview) => boolean;
  isMovePaymentActive?: (payment: ProjectLinePaymentPreview) => boolean;
  onCommitCell?: (payload: TerrainTimelineCommitPayload) => Promise<void> | void;
  onOpenCertifiedCell?: (payload: TerrainTimelineCommitPayload) => void;
  onPickQuickLinkCell?: (
    payload: TerrainTimelineCommitPayload,
  ) => void;
  onMovePayment?: (payment: ProjectLinePaymentPreview) => void;
}) {
  const plannedValues = buildContractLandTimelineValues(landPayments, 1);
  const realizedValues = buildContractLandTimelineValues(landPayments, 2);
  const certifiedValues = buildContractLandTimelineValues(landPayments, 3);
  const timelineYear = Number(toTrimmedText(year) || 0);
  const linkVisualMap = useMemo(
    () => buildTerrainLinkVisualMap(linkSourceLandPayments, quickLinkState),
    [linkSourceLandPayments, quickLinkState],
  );

  return (
    <CustomHorizontalScroller compact>
      <div className="min-w-[980px] rounded-3xl border border-[#dbead7] bg-[#F7FFF5] px-4 py-4">
        <div className="mb-2 flex w-full items-center gap-2">
          <div
            className={cn(
              MONTH_LABEL_COLUMN_CLASS,
              "text-[8px] font-semibold uppercase tracking-wide text-slate-500",
            )}
          >
            Mois
          </div>
          <div className={MONTH_CELLS_ROW_CLASS}>
            {MONTH_COLUMNS.map((month) => (
              <div
                key={`contract-land-timeline-month-${month.index}`}
                className="rounded-md border border-slate-200 bg-white px-1 py-0.5 text-center text-[8px] font-semibold uppercase tracking-wide text-slate-600"
              >
                {month.label}
              </div>
            ))}
          </div>
        </div>

        {[
          {
            key: "planned",
            label: "Versements prévus",
            values: plannedValues,
            type: 1 as const,
          },
          {
            key: "realized",
            label: "Versements réalisés",
            values: realizedValues,
            type: 2 as const,
          },
          {
            key: "certified",
            label: "Versements certifiés",
            values: certifiedValues,
            type: 3 as const,
          },
        ].map((row, rowIndex) => (
          <div
            key={`contract-land-timeline-${row.key}`}
            className={cn(
              "rounded-2xl bg-white/75 px-2 py-1.5",
              rowIndex < 2 ? "mb-1" : "",
            )}
          >
            <div className="flex w-full items-center gap-1.5">
              <div
                className={cn(
                  MONTH_LABEL_COLUMN_CLASS,
                  "text-[11px] font-medium text-[#586071]",
                )}
              >
                {row.label}
              </div>
              <div className={MONTH_CELLS_ROW_CLASS}>
                {row.values.map((value, index) => {
                  const payment = findTerrainTimelineCellPayment(
                    landPayments,
                    row.type,
                    timelineYear,
                    index,
                  );
                  const cellKey = buildTerrainTimelineCellKey(
                    cellKeyPrefix,
                    row.type,
                    timelineYear,
                    index,
                  );
	                  const linkInfo = getTerrainLinkVisualInfo(linkVisualMap, payment);
	                  const tooltipInfo = buildTerrainCellTooltipInfo(
	                    payment,
	                    row.type,
	                    Number(value || 0),
	                  );
	                  const canMoveThisPayment = !!payment
	                    && Number(value || 0) > 0
	                    && !!onMovePayment
	                    && !!canMovePayment?.(payment);
	                  const moveAction = canMoveThisPayment && payment
	                    ? {
	                        active: !!isMovePaymentActive?.(payment),
	                        disabled,
	                        onClick: () => onMovePayment(payment),
	                      } satisfies TerrainCellMoveAction
	                    : null;

	                  if (quickLinkMode && onPickQuickLinkCell) {
	                    return (
	                      <QuickLinkTerrainTimelineButton
                        key={`contract-land-timeline-${row.key}-${index}`}
                        type={row.type}
                        value={value || 0}
                        payment={payment}
	                        disabled={disabled}
	                        linkInfo={linkInfo}
	                        tooltipInfo={tooltipInfo}
	                        moveAction={moveAction}
	                        selected={isTerrainQuickLinkSelectedPayment(
	                          quickLinkState,
	                          payment,
                          row.type,
                        )}
                        onPick={() =>
                          onPickQuickLinkCell({
                            status: row.type,
                            year: timelineYear,
                            monthIndex: index,
                            amountEuro: value || null,
                            payment,
                          })
                        }
                      />
                    );
                  }

                  if (editable && row.type === 3 && onOpenCertifiedCell) {
                    return (
                      <CertifiedTerrainTimelineButton
                        key={`contract-land-timeline-${row.key}-${index}`}
                        value={value || 0}
	                        disabled={disabled}
	                        saving={savingCellKeys.includes(cellKey)}
	                        linkInfo={linkInfo}
	                        tooltipInfo={tooltipInfo}
	                        moveAction={moveAction}
	                        onOpen={() =>
	                          onOpenCertifiedCell({
                            status: row.type,
                            year: timelineYear,
                            monthIndex: index,
                            amountEuro: value || null,
                            payment,
                          })
                        }
                      />
                    );
                  }

                  return editable && onCommitCell ? (
                    <EditableTerrainTimelineInput
                      key={`contract-land-timeline-${row.key}-${index}`}
                      type={row.type}
                      value={value || 0}
	                      disabled={disabled}
	                      saving={savingCellKeys.includes(cellKey)}
	                      linkInfo={linkInfo}
	                      tooltipInfo={tooltipInfo}
	                      moveAction={moveAction}
	                      onCommit={(amountEuro) =>
	                        onCommitCell({
                          status: row.type,
                          year: timelineYear,
                          monthIndex: index,
                          amountEuro,
                          payment,
                        })
                      }
                    />
                  ) : (
                    <ReadOnlyTerrainTimelineCell
                      key={`contract-land-timeline-${row.key}-${index}`}
                      type={row.type}
                      value={value || 0}
	                      timelineYear={timelineYear}
	                      monthIndex={index}
	                      linkInfo={linkInfo}
	                      tooltipInfo={tooltipInfo}
	                      moveAction={moveAction}
	                    />
	                  );
                })}
              </div>
            </div>
          </div>
        ))}
      </div>
    </CustomHorizontalScroller>
  );
}

function ContractPaymentsOverview({
  expectedPayments,
  paidPayments,
  fallbackYear,
}: {
  expectedPayments: ProjectLinePaymentPreview[];
  paidPayments: ProjectLinePaymentPreview[];
  fallbackYear?: number | string | null;
}) {
  const timelineRows = useMemo(
    () => [...expectedPayments, ...paidPayments],
    [expectedPayments, paidPayments],
  );
  const timelineYears = useMemo(
    () => buildPaymentTimelineYearOptions(timelineRows, fallbackYear),
    [fallbackYear, timelineRows],
  );
  const [selectedTimelineYear, setSelectedTimelineYear] = useState(
    () => {
      const normalizedFallbackYear = toTrimmedText(fallbackYear);
      return timelineYears.includes(normalizedFallbackYear)
        ? normalizedFallbackYear
        : timelineYears[timelineYears.length - 1] || getCurrentTimelineYearString();
    },
  );

  useEffect(() => {
    const normalizedFallbackYear = toTrimmedText(fallbackYear);

    setSelectedTimelineYear((current) => {
      if (timelineYears.includes(current)) {
        return current;
      }
      if (timelineYears.includes(normalizedFallbackYear)) {
        return normalizedFallbackYear;
      }

      return timelineYears[timelineYears.length - 1] || getCurrentTimelineYearString();
    });
  }, [fallbackYear, timelineYears]);

  const expectedValues = useMemo(
    () => buildContractPaymentTimelineValues(expectedPayments, selectedTimelineYear),
    [expectedPayments, selectedTimelineYear],
  );
  const receivedValues = useMemo(
    () => buildContractPaymentTimelineValues(paidPayments, selectedTimelineYear),
    [paidPayments, selectedTimelineYear],
  );
  const expectedYearTotal = sumPaymentPreviews(
    expectedPayments.filter(
      (payment) => extractTimelineYear(payment.at) === selectedTimelineYear,
    ),
  );
  const receivedYearTotal = sumPaymentPreviews(
    paidPayments.filter(
      (payment) => extractTimelineYear(payment.at) === selectedTimelineYear,
    ),
  );

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
            Versements du contrat
          </div>
          <div className="mt-1 text-xs text-slate-500">
            Ces versements sont rattachés au contrat financeur. Les versements à
            l&apos;intervenant sont affichés dans le projet concerné.
          </div>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-1.5">
          {timelineYears.map((year) => (
            <button
              key={`contract-payments-timeline-year-${year}`}
              type="button"
              onClick={() => setSelectedTimelineYear(year)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
                selectedTimelineYear === year
                  ? "border-verdeoblue bg-verdeoblue text-white shadow-sm"
                  : "border-slate-200 bg-slate-50 text-slate-600 hover:border-verdeoblue/30 hover:text-slate-900",
              )}
            >
              {year}
            </button>
          ))}
        </div>
      </div>

      <CustomHorizontalScroller compact>
        <div className="min-w-[920px] rounded-3xl border border-slate-200 bg-slate-50/80 px-4 py-4">
          <div className="mb-2 flex w-full items-center gap-2">
            <div
              className={cn(
                MONTH_LABEL_COLUMN_CLASS,
                "text-[8px] font-semibold uppercase tracking-wide text-slate-500",
              )}
            >
              Mois
            </div>
            <div className={MONTH_CELLS_ROW_CLASS}>
              {MONTH_COLUMNS.map((month) => (
                <div
                  key={`contract-payments-month-${selectedTimelineYear}-${month.index}`}
                  className="rounded-md border border-slate-200 bg-white px-1 py-0.5 text-center text-[8px] font-semibold uppercase tracking-wide text-slate-600"
                >
                  {month.label}
                </div>
              ))}
            </div>
          </div>

          {[
            {
              key: "expected",
              label: "Versements prévus",
              values: expectedValues,
              type: 5,
              total: expectedYearTotal,
              rowClassName: "border-[#a1560b]/20 bg-[#a1560b]/8",
              labelClassName: "text-[#8b4a09]",
            },
            {
              key: "received",
              label: "Versements reçus",
              values: receivedValues,
              type: 6,
              total: receivedYearTotal,
              rowClassName: "border-[#425e1a]/20 bg-[#425e1a]/8",
              labelClassName: "text-[#425e1a]",
            },
          ].map((row, rowIndex) => (
            <div
              key={`contract-payments-row-${row.key}-${selectedTimelineYear}`}
              className={cn(
                "rounded-2xl border px-2 py-1.5",
                row.rowClassName,
                rowIndex === 0 ? "mb-1.5" : "",
              )}
            >
              <div className="flex w-full items-center gap-1.5">
                <div className={cn(MONTH_LABEL_COLUMN_CLASS, "min-w-0")}>
                  <div
                    className={cn(
                      "truncate text-[11px] font-semibold",
                      row.labelClassName,
                    )}
                  >
                    {row.label}
                  </div>
                  <div className="mt-0.5 text-[10px] font-medium text-slate-500">
                    {formatEuro(row.total)}
                  </div>
                </div>
                <div className={MONTH_CELLS_ROW_CLASS}>
                  {row.values.map((value, index) => (
                    <FieldInput
                      key={`contract-payments-${row.key}-${selectedTimelineYear}-${index}`}
                      type={row.type}
                      defaultValue={value || 0}
                      valueMultiple={String(value || 0)}
                      data={{
                        idFinanceur: 0,
                        year: Number(selectedTimelineYear) || 0,
                        month: index,
                        idDevise: 0,
                      }}
                      compact
                    />
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </CustomHorizontalScroller>
    </div>
  );
}

export function ProjectLandPaymentsTimelinePanel({
  title = "Versements aux intervenants",
  landPayments,
  projectId = null,
  recipientOptions = [],
  fallbackYear,
  children,
  editable = false,
  disabled = false,
  savingCellKeys = [],
  cellKeyPrefix = "terrain-timeline",
  error = null,
  quickLinkMode = false,
  quickLinkState = null,
  canMovePayment,
  isMovePaymentActive,
  onEnsureRecipientOptions,
  onCommitCell,
  onOpenCertifiedCell,
  onPickQuickLinkCell,
  onMovePayment,
}: {
  title?: string;
  landPayments: ProjectLinePaymentPreview[];
  projectId?: number | null;
  recipientOptions?: LandPaymentRecipientOption[];
  fallbackYear?: number | string | null;
  children?: ReactNode;
  editable?: boolean;
  disabled?: boolean;
  savingCellKeys?: string[];
  cellKeyPrefix?: string;
  error?: string | null;
  quickLinkMode?: boolean;
  quickLinkState?: TerrainQuickLinkState | null;
  canMovePayment?: (payment: ProjectLinePaymentPreview) => boolean;
  isMovePaymentActive?: (payment: ProjectLinePaymentPreview) => boolean;
  onEnsureRecipientOptions?: (projectId: number) => void | Promise<LandPaymentRecipientOption[]>;
  onCommitCell?: (payload: TerrainTimelineCommitPayload) => Promise<void> | void;
  onOpenCertifiedCell?: (payload: TerrainTimelineCommitPayload) => void;
  onPickQuickLinkCell?: (payload: TerrainTimelineCommitPayload) => void;
  onMovePayment?: (payment: ProjectLinePaymentPreview) => void;
}) {
  useEffect(() => {
    const normalizedProjectId = Number(projectId || 0);
    if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) return;

    void onEnsureRecipientOptions?.(normalizedProjectId);
  }, [onEnsureRecipientOptions, projectId]);

  const timelineYears = useMemo(
    () => buildPaymentTimelineYearOptions(landPayments, fallbackYear),
    [fallbackYear, landPayments],
  );
  const [selectedTimelineYear, setSelectedTimelineYear] = useState(
    () => timelineYears[timelineYears.length - 1] || getCurrentTimelineYearString(),
  );

  useEffect(() => {
    setSelectedTimelineYear((current) =>
      timelineYears.includes(current)
        ? current
        : timelineYears[timelineYears.length - 1] || getCurrentTimelineYearString(),
    );
  }, [timelineYears]);

  const displayedLandPayments = useMemo(
    () =>
      landPayments.filter((payment) => {
        const paymentYear =
          extractTimelineYear(payment.at) || toTrimmedText(fallbackYear);
        return paymentYear === selectedTimelineYear;
      }),
    [fallbackYear, landPayments, selectedTimelineYear],
  );
  const landPaymentsRealized = dedupePaymentPreviewsByMonthYear(
    filterPaymentPreviewsByStatus(displayedLandPayments, 2),
  );
  const landPaymentsRealizedTotal = sumPaymentPreviews(landPaymentsRealized);
	  const recipientGroups = useMemo(
	    () => buildLandPaymentRecipientTimelineGroups(displayedLandPayments, recipientOptions),
	    [displayedLandPayments, recipientOptions],
	  );
	  const allRecipientGroupsByKey = useMemo(
	    () =>
	      new Map(
	        buildLandPaymentRecipientTimelineGroups(landPayments, recipientOptions).map(
	          (group) => [group.key, group],
	        ),
	      ),
	    [landPayments, recipientOptions],
	  );

	  return (
	    <div className="rounded-2xl border border-verdeoblue/20 bg-verdeoblue/8 p-3">
	      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
	        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-800">
            {title}
          </div>
	        </div>
        <div className="ml-auto flex flex-wrap items-center justify-end gap-1.5">
          <div className="rounded-full border border-white/70 bg-white px-2.5 py-1 text-xs font-semibold text-slate-900">
            {formatEuro(landPaymentsRealizedTotal)} réalisé
          </div>
          {timelineYears.map((year) => (
            <button
              key={`project-land-timeline-year-${year}`}
              type="button"
              onClick={() => setSelectedTimelineYear(year)}
              className={cn(
                "rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
                selectedTimelineYear === year
                  ? "border-verdeoblue bg-verdeoblue text-white shadow-sm"
                  : "border-white/80 bg-white text-slate-600 hover:border-verdeoblue/30 hover:text-slate-900",
              )}
            >
              {year}
            </button>
          ))}
        </div>
      </div>
      {children ? <div className="mt-3">{children}</div> : null}
      {error ? (
        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
          {error}
        </div>
      ) : null}
	      <div className="space-y-3">
	        {recipientGroups.map((group) => {
	          const linkSourcePayments =
	            allRecipientGroupsByKey.get(group.key)?.payments || group.payments;
	          const groupCellKeyPrefix = buildTerrainTimelineCellPrefix(
	            cellKeyPrefix,
	            group.recipientSubcontractorId,
	          );
	          const isGroupSaving = savingCellKeys.some((savingCellKey) =>
	            savingCellKey.startsWith(`${groupCellKeyPrefix}-`),
	          );
	          const groupRealizedTotal = sumPaymentPreviews(
	            dedupePaymentPreviewsByMonthYear(
	              filterPaymentPreviewsByStatus(group.payments, 2),
            ),
          );

          return (
            <div
              key={`land-payment-recipient-group-${group.key}`}
              className="rounded-2xl border border-white/80 bg-white/70 p-2.5 shadow-sm"
	            >
	              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
	                <div className="flex flex-wrap items-center gap-2">
	                  <div className="text-xs font-semibold text-slate-900">
	                    {group.label}
	                  </div>
	                  {isGroupSaving ? (
	                    <span className="inline-flex items-center gap-1 rounded-full border border-emerald-100 bg-white px-2 py-0.5 text-[10px] font-medium text-emerald-700 shadow-sm">
	                      <Loader2 className="h-3 w-3 animate-spin" />
	                      Sauvegarde...
	                    </span>
	                  ) : null}
	                </div>
                <div className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-700">
                  {formatEuro(groupRealizedTotal)} réalisé
                </div>
              </div>

	              <ContractLandPaymentMonthlyTimeline
	                landPayments={group.payments}
	                linkSourceLandPayments={linkSourcePayments}
	                year={selectedTimelineYear}
                editable={editable}
                disabled={disabled}
	                savingCellKeys={savingCellKeys}
	                cellKeyPrefix={groupCellKeyPrefix}
	                quickLinkMode={quickLinkMode}
	                quickLinkState={quickLinkState}
	                canMovePayment={canMovePayment}
	                isMovePaymentActive={isMovePaymentActive}
	                onCommitCell={
	                  onCommitCell
                    ? (payload) =>
                        onCommitCell({
                          ...payload,
                          recipientSubcontractorId: group.recipientSubcontractorId,
                        })
                    : undefined
                }
                onOpenCertifiedCell={
                  onOpenCertifiedCell
                    ? (payload) =>
                        onOpenCertifiedCell({
                          ...payload,
                          recipientSubcontractorId: group.recipientSubcontractorId,
                        })
                    : undefined
                }
	                onPickQuickLinkCell={
	                  onPickQuickLinkCell
	                    ? (payload) =>
                        onPickQuickLinkCell({
                          ...payload,
                          recipientSubcontractorId: group.recipientSubcontractorId,
	                        })
	                    : undefined
	                }
	                onMovePayment={onMovePayment}
	              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function ContractCard({
  contract,
  financeurContracts,
  index,
  isExpanded,
  selectedYear,
  projectVisualCatalog,
  onToggleExpanded,
  onSelectYear,
  canManage,
  isDeleting,
  actionsDisabled,
  isEditing,
  isSaving,
  moveTargets,
  activeLineMove,
  movePendingLineKey,
  moveError,
  onEdit,
  onDelete,
  onStartMoveLine,
  onChangeMoveTarget,
  onCancelMoveLine,
  onConfirmMoveLine,
  onRefreshContracts,
  onMutateData,
  onRestoreData,
  collapsible = true,
}: {
  contract: SynthesesGroupedContractNode;
  financeurContracts: SynthesesGroupedContractNode[];
  index: number;
  isExpanded: boolean;
  selectedYear?: number | null;
  projectVisualCatalog: Map<number, ProjectVisualCatalogItem>;
  onToggleExpanded?: () => void;
  onSelectYear: (year: number) => void;
  canManage: boolean;
  isDeleting: boolean;
  actionsDisabled: boolean;
  isEditing: boolean;
  isSaving: boolean;
  moveTargets: ContractLineMoveTargetOption[];
  activeLineMove: ActiveContractLineMoveState | null;
  movePendingLineKey: string | null;
  moveError: string | null;
  onEdit: (contract: SynthesesGroupedContractNode, index: number) => void;
  onDelete: (contract: SynthesesGroupedContractNode) => void;
  onStartMoveLine: (
    contract: SynthesesGroupedContractNode,
    index: number,
    row: ContractLineDisplayRow,
  ) => void;
  onChangeMoveTarget: (targetContractId: string) => void;
  onCancelMoveLine: () => void;
  onConfirmMoveLine: () => void;
  onRefreshContracts?: (() => Promise<unknown>) | (() => void);
  onMutateData?: (
    updater: SynthesesGroupedContractsDataUpdater,
  ) => SynthesesGroupedContractsResponse | null;
  onRestoreData?: (snapshot: SynthesesGroupedContractsResponse | null) => void;
  collapsible?: boolean;
}) {
  const { postMultipart } = usePostMultipart<FormData>();
  const { fetchData: fetchProjectRecipientData } = useFetchDataV2<any>("", {
    browserCache: true,
    browserCacheNamespace: "syntheses-land-payment-recipients",
  });
  const contractLabel = contract.title || `Contrat ${index + 1}`;
  const allYears = buildContractYears(contract);
  const years = selectedYear
    ? allYears.filter((year) => year === selectedYear)
    : allYears;
  const displayContractAmountEuro =
    selectedYear !== null && selectedYear !== undefined
      ? getContractAmountEuroForYear(contract, selectedYear)
      : Number(contract.amountEuro || 0);
  const summaryExpectedEuro =
    contract.expectedPayments.length > 0
      ? sumRowsAmount(contract.expectedPayments)
      : Number(contract.totals.expectedPaymentsEuro || 0);
  const summaryReceivedEuro =
    contract.paidPayments.length > 0
      ? sumRowsAmount(contract.paidPayments)
      : Number(contract.totals.receivedPaymentsEuro || 0);
  const realizedLandPayments = contract.landPayments.filter(
    (payment) => Number(payment.status || 0) === 2,
  );
  const summaryLandEuro =
    realizedLandPayments.length > 0
      ? sumRowsAmount(realizedLandPayments)
      : Number(contract.totals.landPaymentsEuro || 0);
  const contractExpectedPayments = buildPaymentPreviews(
    contract.expectedPayments,
    `${contract.id}-contract-expected`,
  ).sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
  const contractPaidPayments = buildPaymentPreviews(
    contract.paidPayments,
    `${contract.id}-contract-paid`,
  ).sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
  const summaryAllocationsEuro =
    selectedYear !== null && selectedYear !== undefined
      ? 0
      : Number(contract.totals.allocationsEuro || 0);
  const showAllocations = contract.isVerdeo !== true;
  const canMoveLines = canManage && !actionsDisabled && moveTargets.length > 0;
  const [terrainReconcileState, setTerrainReconcileState] =
    useState<TerrainReconcileState | null>(null);
  const [terrainReconcileError, setTerrainReconcileError] = useState<string | null>(null);
  const [savingTerrainReconcile, setSavingTerrainReconcile] = useState(false);
  const [terrainQuickLinkState, setTerrainQuickLinkState] =
    useState<TerrainQuickLinkState | null>(null);
  const [savingTerrainQuickLink, setSavingTerrainQuickLink] = useState(false);
  const [terrainPaymentEditorState, setTerrainPaymentEditorState] =
    useState<TerrainPaymentEditorState | null>(null);
  const [terrainPaymentEditorError, setTerrainPaymentEditorError] = useState<string | null>(null);
  const [savingTerrainPayment, setSavingTerrainPayment] = useState(false);
  const [terrainCertifiedEditorState, setTerrainCertifiedEditorState] =
    useState<TerrainCertifiedEditorState | null>(null);
  const [terrainCertifiedEditorError, setTerrainCertifiedEditorError] =
    useState<string | null>(null);
  const [savingTerrainCertified, setSavingTerrainCertified] = useState(false);
  const [deletingTerrainPaymentId, setDeletingTerrainPaymentId] = useState<number | null>(null);
  const [terrainMoveState, setTerrainMoveState] = useState<TerrainMoveState | null>(null);
  const [terrainMoveError, setTerrainMoveError] = useState<string | null>(null);
  const [movingTerrainPayment, setMovingTerrainPayment] = useState(false);
  const [savingTerrainTimelineCellKeys, setSavingTerrainTimelineCellKeys] =
    useState<string[]>([]);
  const [terrainTimelineError, setTerrainTimelineError] = useState<string | null>(null);
  const [
    landPaymentRecipientCatalogByProjectId,
    setLandPaymentRecipientCatalogByProjectId,
  ] = useState<Record<number, ProjectLandPaymentRecipientCatalogEntry>>({});
  const [loadingRecipientProjectIds, setLoadingRecipientProjectIds] = useState<
    Record<number, boolean>
  >({});
  const markTerrainTimelineCellSaving = useCallback((cellKey: string) => {
    setSavingTerrainTimelineCellKeys((current) => [...current, cellKey]);
  }, []);
  const unmarkTerrainTimelineCellSaving = useCallback((cellKey: string) => {
    setSavingTerrainTimelineCellKeys((current) => {
      const index = current.indexOf(cellKey);
      if (index < 0) return current;

      return [
        ...current.slice(0, index),
        ...current.slice(index + 1),
      ];
    });
  }, []);
  const [selectedLineKeyByYear, setSelectedLineKeyByYear] = useState<
    Record<string, string>
  >({});
  const reconcileTerrainEndpoint = useMemo(() => buildReconcileTerrainEndpoint(), []);
  const moveTerrainEndpoint = useMemo(() => buildMoveTerrainEndpoint(), []);
  const saveLandPaymentEndpoint = useMemo(() => buildSaveLandPaymentEndpoint(), []);
  const saveProjectIndicatorEndpoint = useMemo(
    () => buildProjectIndicatorEndpoint(false),
    [],
  );
  const saveProjectIndicatorVerifyEndpoint = useMemo(
    () => buildProjectIndicatorEndpoint(true),
    [],
  );
  const deleteLandPaymentEndpoint = useMemo(() => buildDeleteLandPaymentEndpoint(), []);

  const scrollToContractMovePanel = useCallback((lineKey: string) => {
    window.setTimeout(() => {
      const panel = document.querySelector<HTMLElement>(
        `[data-contract-line-move-panel="${lineKey}"]`,
      );
      if (!panel) return;
      panel.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }, 80);
  }, []);

  const refreshContracts = useCallback(async () => {
    if (onRefreshContracts) {
      await Promise.resolve(onRefreshContracts());
    }
  }, [onRefreshContracts]);

  const getRecipientOptionsForProject = useCallback(
    (projectId: number | null | undefined) => {
      const normalizedProjectId = Number(projectId || 0);
      if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
        return [];
      }

      return (
        landPaymentRecipientCatalogByProjectId[normalizedProjectId]?.recipients ||
        []
      );
    },
    [landPaymentRecipientCatalogByProjectId],
  );

  const loadRecipientOptionsForProject = useCallback(
    async (projectId: number | null | undefined) => {
      const normalizedProjectId = Number(projectId || 0);
      if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
        return [];
      }

      const cached =
        landPaymentRecipientCatalogByProjectId[normalizedProjectId]?.recipients;
      if (cached) {
        return cached;
      }

      if (loadingRecipientProjectIds[normalizedProjectId]) {
        return [];
      }

      const apiBase = String(import.meta.env.VITE_API_ENDPOINT || "")
        .trim()
        .replace(/\/+$/, "");
      if (!apiBase) return [];

      setLoadingRecipientProjectIds((current) => ({
        ...current,
        [normalizedProjectId]: true,
      }));

      try {
        const payload = await fetchProjectRecipientData(
          `${apiBase}/project/${normalizedProjectId}?excludes=indicator,reporting`,
        );
        const recipients = buildLandPaymentRecipientOptions(
          payload?.fundLocalSubcontractors,
        );

        setLandPaymentRecipientCatalogByProjectId((current) => ({
          ...current,
          [normalizedProjectId]: {
            projectId: normalizedProjectId,
            recipients,
          },
        }));

        return recipients;
      } finally {
        setLoadingRecipientProjectIds((current) => ({
          ...current,
          [normalizedProjectId]: false,
        }));
      }
    },
    [
      fetchProjectRecipientData,
      landPaymentRecipientCatalogByProjectId,
      loadingRecipientProjectIds,
    ],
  );

  useEffect(() => {
    const projectId = Number(terrainPaymentEditorState?.projectId || 0);
    if (!Number.isFinite(projectId) || projectId <= 0) return;

    void loadRecipientOptionsForProject(projectId);
  }, [loadRecipientOptionsForProject, terrainPaymentEditorState?.projectId]);

  useEffect(() => {
    const projectId = Number(terrainCertifiedEditorState?.projectId || 0);
    if (!Number.isFinite(projectId) || projectId <= 0) return;

    void loadRecipientOptionsForProject(projectId);
  }, [loadRecipientOptionsForProject, terrainCertifiedEditorState?.projectId]);

  useEffect(() => {
    if (!terrainPaymentEditorState) return;
    if (isPositiveId(terrainPaymentEditorState.recipientSubcontractorId)) return;

    const options = getRecipientOptionsForProject(terrainPaymentEditorState.projectId);
    if (options.length !== 1) return;

    setTerrainPaymentEditorState((current) => {
      if (!current || isPositiveId(current.recipientSubcontractorId)) {
        return current;
      }

      return {
        ...current,
        recipientSubcontractorId: options[0].id,
      };
    });
  }, [
    getRecipientOptionsForProject,
    terrainPaymentEditorState,
  ]);

  useEffect(() => {
    if (!terrainCertifiedEditorState) return;
    if (isPositiveId(terrainCertifiedEditorState.recipientSubcontractorId)) return;

    const options = getRecipientOptionsForProject(terrainCertifiedEditorState.projectId);
    if (options.length !== 1) return;

    setTerrainCertifiedEditorState((current) => {
      if (!current || isPositiveId(current.recipientSubcontractorId)) {
        return current;
      }

      return {
        ...current,
        recipientSubcontractorId: options[0].id,
      };
    });
  }, [
    getRecipientOptionsForProject,
    terrainCertifiedEditorState,
  ]);

  useEffect(() => {
    setTerrainCertifiedEditorState((current) => {
      if (!current) return current;

      const currentLandPaymentId = isPositiveId(current.landPaymentId)
        ? Number(current.landPaymentId)
        : null;
      const currentRows = buildContractProjectLandPaymentPreviews(
        contract,
        current.projectId,
      );
      const currentCertifiedIndicatorId =
        currentLandPaymentId === null
          ? null
          : currentRows.find(
              (row) =>
                Number(row.landPaymentId || 0) === currentLandPaymentId &&
                Number(row.status || 0) === 3 &&
                Number(row.projectIndicatorId || 0) > 0,
            )?.projectIndicatorId ?? null;
      const estimatedOptions = buildTerrainCertifiedIndicatorOptions(
        currentRows,
        1,
        currentLandPaymentId,
        currentCertifiedIndicatorId,
      );
      const measuredOptions = buildTerrainCertifiedIndicatorOptions(
        currentRows,
        2,
        currentLandPaymentId,
        currentCertifiedIndicatorId,
      );
      const linkedEstimatedIndicatorId =
        findTerrainLinkedIndicatorId(currentRows, 1, currentLandPaymentId);
      const linkedMeasuredIndicatorId =
        findTerrainLinkedIndicatorId(currentRows, 2, currentLandPaymentId);
      const estimatedIndicatorId =
        current.estimatedIndicatorId ?? linkedEstimatedIndicatorId ?? null;
      const measuredIndicatorId =
        current.measuredIndicatorId ?? linkedMeasuredIndicatorId ?? null;

      if (
        areTerrainCertifiedIndicatorOptionsEqual(current.estimatedOptions, estimatedOptions) &&
        areTerrainCertifiedIndicatorOptionsEqual(current.measuredOptions, measuredOptions) &&
        Number(current.estimatedIndicatorId || 0) === Number(estimatedIndicatorId || 0) &&
        Number(current.measuredIndicatorId || 0) === Number(measuredIndicatorId || 0)
      ) {
        return current;
      }

      return {
        ...current,
        estimatedIndicatorId,
        measuredIndicatorId,
        estimatedOptions,
        measuredOptions,
      };
    });
  }, [
    contract,
  ]);

  const reconcileTerrainPayloadToApi = useCallback(
    async (payload: {
      realFundingId: number;
      landPaymentId: number | null;
      estimatedIndicatorId: number | null;
      measuredIndicatorId: number | null;
      verifiedIndicatorId: number | null;
      estimatedLandPaymentId?: number | null;
      measuredLandPaymentId?: number | null;
      verifiedLandPaymentId?: number | null;
    }) => {
      if (!reconcileTerrainEndpoint) {
        throw new Error("Le rapprochement terrain n'est pas configuré.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(reconcileTerrainEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }
      return body;
    },
    [reconcileTerrainEndpoint],
  );

  const moveTerrainPayloadToApi = useCallback(
    async (payload: {
      landPaymentId: number;
      targetLineId: number;
    }) => {
      if (!moveTerrainEndpoint) {
        throw new Error("Le déplacement terrain n'est pas configuré.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(moveTerrainEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }
      return body;
    },
    [moveTerrainEndpoint],
  );

  const saveTerrainPaymentToApi = useCallback(
    async (payload: {
      realfunding?: number;
      synthesesContractId?: number;
      synthesesContractLineId?: number;
      amount: number;
      expectedAt: string;
      status: number;
      dateMeasure?: string;
      dateVerified?: string;
      toEdit?: number;
      recipientSubcontractorId?: number;
    }) => {
      if (!saveLandPaymentEndpoint) {
        throw new Error("La sauvegarde du versement à l'intervenant n'est pas configurée.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(saveLandPaymentEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }
      return body;
    },
    [saveLandPaymentEndpoint],
  );

  const saveTerrainIndicatorToApi = useCallback(
    async (
      payload: Record<string, unknown>,
      verified: boolean,
    ) => {
      const endpoint = verified
        ? saveProjectIndicatorVerifyEndpoint
        : saveProjectIndicatorEndpoint;
      if (!endpoint) {
        throw new Error("La sauvegarde via le suivi de performance ESG n'est pas configurée.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok || body?.status === false) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }

      return body;
    },
    [saveProjectIndicatorEndpoint, saveProjectIndicatorVerifyEndpoint],
  );

  const deleteTerrainPaymentToApi = useCallback(
    async (landPaymentId: number) => {
      if (!deleteLandPaymentEndpoint) {
        throw new Error("La suppression du versement à l'intervenant n'est pas configurée.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(`${deleteLandPaymentEndpoint}/${landPaymentId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({}),
      });

      const body = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(String(body?.message || body?.error || `HTTP ${response.status}`));
      }
      return body;
    },
    [deleteLandPaymentEndpoint],
  );

  const handleOpenTerrainPaymentCreate = useCallback(
    (row: ContractLineDisplayRow, status: number, fallbackYear: number | null) => {
      if (!row.realFundingId && !row.lineId) {
        setTerrainPaymentEditorError("Impossible de retrouver la ligne à l'intervenant.");
        return;
      }

      const defaultDate = getDefaultTerrainDateForYear(fallbackYear);
      const cachedRecipientOptions = getRecipientOptionsForProject(row.projectId);

      setTerrainQuickLinkState(null);
      setTerrainReconcileState(null);
      setTerrainReconcileError(null);
      setTerrainMoveState(null);
      setTerrainMoveError(null);
      setTerrainPaymentEditorError(null);
      setTerrainPaymentEditorState({
        lineKey: row.key,
        lineLabel: `${row.projectName} • ${contractLabel}`,
        synthesesContractId: row.synthesesContractId,
        synthesesContractLineId: row.lineId,
        realFundingId: row.realFundingId,
        projectId: row.projectId,
        projectName: row.projectName,
        lineTitle: row.title,
        landPaymentId: null,
        status,
        amountEuro: "",
        expectedAt: defaultDate,
        dateMeasure: status >= 2 ? defaultDate : "",
        dateVerified: status >= 3 ? defaultDate : "",
        pjVerified: null,
        recipientSubcontractorId:
          cachedRecipientOptions.length === 1 ? cachedRecipientOptions[0].id : null,
      });
      void loadRecipientOptionsForProject(row.projectId);
    },
    [contractLabel, getRecipientOptionsForProject, loadRecipientOptionsForProject],
  );

  const handleOpenTerrainPaymentEdit = useCallback(
    (
      row: ContractLineDisplayRow,
      payment: ProjectLinePaymentPreview,
      allRows: ProjectLinePaymentPreview[],
      fallbackYear: number | null,
    ) => {
      if (!row.realFundingId && !row.lineId) {
        setTerrainPaymentEditorError("Impossible de retrouver la ligne à l'intervenant.");
        return;
      }

      const draft = buildTerrainPaymentEditorDraft(allRows, payment, fallbackYear);
      const cachedRecipientOptions = getRecipientOptionsForProject(row.projectId);

      setTerrainQuickLinkState(null);
      setTerrainReconcileState(null);
      setTerrainReconcileError(null);
      setTerrainMoveState(null);
      setTerrainMoveError(null);
      setTerrainPaymentEditorError(null);
      setTerrainPaymentEditorState({
        lineKey: row.key,
        lineLabel: `${row.projectName} • ${contractLabel}`,
        synthesesContractId: row.synthesesContractId,
        synthesesContractLineId: row.lineId,
        realFundingId: row.realFundingId,
        projectId: row.projectId,
        projectName: row.projectName,
        lineTitle: row.title,
        landPaymentId: draft.landPaymentId,
        status: draft.status,
        amountEuro: draft.amountEuro,
        expectedAt: draft.expectedAt,
        dateMeasure: draft.dateMeasure,
        dateVerified: draft.dateVerified,
        pjVerified: draft.pjVerified,
        recipientSubcontractorId:
          draft.recipientSubcontractorId ??
          (cachedRecipientOptions.length === 1 ? cachedRecipientOptions[0].id : null),
      });
      void loadRecipientOptionsForProject(row.projectId);
    },
    [contractLabel, getRecipientOptionsForProject, loadRecipientOptionsForProject],
  );

  const handleChangeTerrainPaymentEditor = useCallback(
    (patch: Partial<TerrainPaymentEditorState>) => {
      setTerrainPaymentEditorError(null);
      setTerrainPaymentEditorState((current) => {
        if (!current) return current;
        return { ...current, ...patch };
      });
    },
    [],
  );

  const handleUploadTerrainPaymentProof = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append("type", "12");
      formData.append("subType", "20");
      formData.append("files[]", file);

      const medias = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data: formData,
      });

      if (!Array.isArray(medias) || !medias[0]?.id) {
        throw new Error(String(medias?.message || "Erreur lors de l'upload du justificatif."));
      }

      setTerrainPaymentEditorState((current) => {
        if (!current) return current;
        return {
          ...current,
          pjVerified: medias[0],
        };
      });
    },
    [postMultipart],
  );

  const handleCancelTerrainPaymentEditor = useCallback(() => {
    setTerrainPaymentEditorState(null);
    setTerrainPaymentEditorError(null);
  }, []);

  const handleOpenTerrainCertifiedTimelineEditor = useCallback(
    (
      row: ContractLineDisplayRow,
      allRows: ProjectLinePaymentPreview[],
      payload: TerrainTimelineCommitPayload,
      targetLineRows: ContractLineDisplayRow[] = [],
    ) => {
      if (actionsDisabled || !canManage) return;

      const realFundingId = Number(row.realFundingId || 0);
      const synthesesContractId = Number(row.synthesesContractId || 0);
      const synthesesContractLineId = Number(row.lineId || 0);
      const projectId = Number(row.projectId || payload.payment?.projectId || 0);
      const landPaymentId = Number(payload.payment?.landPaymentId || 0) > 0
        ? Number(payload.payment?.landPaymentId)
        : null;
      const currentCertifiedIndicatorId = Number(payload.payment?.projectIndicatorId || 0) > 0
        ? Number(payload.payment?.projectIndicatorId)
        : null;
      const relatedRows = landPaymentId
        ? allRows.filter(
            (paymentRow) => Number(paymentRow.landPaymentId || 0) === landPaymentId,
          )
        : [];
      const certifiedRow = relatedRows.find((paymentRow) => Number(paymentRow.status || 0) === 3)
        ?? payload.payment
        ?? null;
	      const targetLineOptions = buildTerrainCertifiedLineOptions(
	        targetLineRows.length > 0 ? targetLineRows : [row],
	      );
	      const cachedRecipientOptions = getRecipientOptionsForProject(projectId);
	      const payloadRecipientSubcontractorId = isPositiveId(
	        payload.recipientSubcontractorId,
	      )
	        ? Number(payload.recipientSubcontractorId)
	        : null;
	      const initialRecipientSubcontractorId =
	        getLandPaymentRecipientId(certifiedRow) ??
	        getLandPaymentRecipientId(payload.payment) ??
	        payloadRecipientSubcontractorId ??
	        (cachedRecipientOptions.length === 1 ? cachedRecipientOptions[0].id : null);
	      const cellKey = buildTerrainTimelineCellKey(
	        buildTerrainTimelineCellPrefix(row.key, initialRecipientSubcontractorId),
	        3,
	        payload.year,
	        payload.monthIndex,
      );

      if (
        (!Number.isFinite(realFundingId) || realFundingId <= 0)
        && (!Number.isFinite(synthesesContractLineId) || synthesesContractLineId <= 0)
      ) {
        setTerrainTimelineError("La ligne à l'intervenant est introuvable.");
        return;
      }
      if (!Number.isFinite(projectId) || projectId <= 0) {
        setTerrainTimelineError("Le projet du versement à l'intervenant est introuvable.");
        return;
      }

      setTerrainPaymentEditorState(null);
      setTerrainPaymentEditorError(null);
      setTerrainQuickLinkState(null);
      setTerrainReconcileState(null);
      setTerrainReconcileError(null);
      setTerrainMoveState(null);
      setTerrainMoveError(null);
      setTerrainTimelineError(null);
      setTerrainCertifiedEditorError(null);
      setTerrainCertifiedEditorState({
        lineKey: row.key,
        lineLabel: `${row.projectName} • ${contractLabel}`,
        cellKey,
        synthesesContractId,
        synthesesContractLineId,
        realFundingId,
        projectId,
        landPaymentId,
        year: Number(payload.year),
        monthIndex: payload.monthIndex,
        amountEuro: formatTerrainAmountInput(certifiedRow?.amountEuro ?? payload.amountEuro ?? 0),
        selectedLineKey: row.key,
        targetLineOptions,
        pjVerified: certifiedRow?.pjVerified ?? payload.payment?.pjVerified ?? null,
        estimatedIndicatorId: findTerrainLinkedIndicatorId(allRows, 1, landPaymentId),
        measuredIndicatorId: findTerrainLinkedIndicatorId(allRows, 2, landPaymentId),
        estimatedOptions: buildTerrainCertifiedIndicatorOptions(
          allRows,
          1,
          landPaymentId,
          currentCertifiedIndicatorId,
        ),
        measuredOptions: buildTerrainCertifiedIndicatorOptions(
          allRows,
          2,
          landPaymentId,
          currentCertifiedIndicatorId,
        ),
	        recipientSubcontractorId: initialRecipientSubcontractorId,
	      });
      void loadRecipientOptionsForProject(projectId);
    },
    [
      actionsDisabled,
      canManage,
      contractLabel,
      getRecipientOptionsForProject,
      loadRecipientOptionsForProject,
    ],
  );

  const handleChangeTerrainCertifiedEditor = useCallback(
    (patch: Partial<TerrainCertifiedEditorState>) => {
      setTerrainCertifiedEditorError(null);
      setTerrainCertifiedEditorState((current) => {
        if (!current) return current;
        return { ...current, ...patch };
      });
    },
    [],
  );

  const handleUploadTerrainCertifiedProof = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append("type", "12");
      formData.append("subType", "20");
      formData.append("files[]", file);

      const medias = await postMultipart({
        url: `${import.meta.env.VITE_IP_API}/api/media/temp/upload`,
        data: formData,
      });

      if (!Array.isArray(medias) || !medias[0]?.id) {
        throw new Error(String(medias?.message || "Erreur lors de l'upload du justificatif."));
      }

      setTerrainCertifiedEditorState((current) => {
        if (!current) return current;
        return {
          ...current,
          pjVerified: medias[0],
        };
      });
    },
    [postMultipart],
  );

  const handleCancelTerrainCertifiedEditor = useCallback(() => {
    setTerrainCertifiedEditorState(null);
    setTerrainCertifiedEditorError(null);
  }, []);

  const handleConfirmTerrainCertifiedEditor = useCallback(async () => {
    if (!terrainCertifiedEditorState) return;

    const selectedTargetLine =
      terrainCertifiedEditorState.targetLineOptions.find(
        (option) => option.lineKey === terrainCertifiedEditorState.selectedLineKey,
      ) ||
      terrainCertifiedEditorState.targetLineOptions[0] ||
      null;
    const realFundingId = Number(
      selectedTargetLine?.realFundingId ?? terrainCertifiedEditorState.realFundingId ?? 0,
    );
    const synthesesContractId = Number(
      selectedTargetLine?.synthesesContractId ??
      terrainCertifiedEditorState.synthesesContractId ??
      0,
    );
    const synthesesContractLineId = Number(
      selectedTargetLine?.synthesesContractLineId ??
      terrainCertifiedEditorState.synthesesContractLineId ??
      0,
    );
    const projectId = Number(terrainCertifiedEditorState.projectId || 0);
    const isSameTargetLine =
      !!selectedTargetLine &&
      selectedTargetLine.lineKey === terrainCertifiedEditorState.lineKey;
    const landPaymentId = isSameTargetLine
      ? Number(terrainCertifiedEditorState.landPaymentId || 0)
      : 0;
    const normalizedAmount = String(terrainCertifiedEditorState.amountEuro || "")
      .replace(/\s/g, "")
      .replace(",", ".")
      .trim();
    const shouldDeleteValue = normalizedAmount === "" || normalizedAmount === "-";
    const amount = shouldDeleteValue ? null : Number(normalizedAmount);
    const recipientOptions = getRecipientOptionsForProject(projectId);
	    const recipientSubcontractorId = isPositiveId(
	      terrainCertifiedEditorState.recipientSubcontractorId,
	    )
	      ? Number(terrainCertifiedEditorState.recipientSubcontractorId)
	      : null;
	    const certifiedSavingCellKey = buildTerrainTimelineCellKey(
	      buildTerrainTimelineCellPrefix(
	        terrainCertifiedEditorState.lineKey,
	        recipientSubcontractorId,
	      ),
	      3,
	      terrainCertifiedEditorState.year,
	      terrainCertifiedEditorState.monthIndex,
	    );

    if (
      (!Number.isFinite(realFundingId) || realFundingId <= 0)
      && (!Number.isFinite(synthesesContractLineId) || synthesesContractLineId <= 0)
    ) {
      setTerrainCertifiedEditorError("Choisissez le poste de dépenses à lier au certifié.");
      return;
    }
    if (!Number.isFinite(projectId) || projectId <= 0) {
      setTerrainCertifiedEditorError("Le projet du versement à l'intervenant est introuvable.");
      return;
    }
    if (!shouldDeleteValue && (!Number.isFinite(Number(amount)) || Number(amount) <= 0)) {
      setTerrainCertifiedEditorError("Renseignez un montant strictement positif.");
      return;
    }
    if (!shouldDeleteValue && !isPositiveId(terrainCertifiedEditorState.pjVerified?.id)) {
      setTerrainCertifiedEditorError("Ajoutez la pièce jointe de vérification.");
      return;
    }
    if (!shouldDeleteValue && recipientOptions.length > 0 && !recipientSubcontractorId) {
      setTerrainCertifiedEditorError("Choisissez l'intervenant destinataire.");
      return;
    }
    if (
      !shouldDeleteValue &&
      (!isPositiveId(terrainCertifiedEditorState.estimatedIndicatorId) ||
        !isPositiveId(terrainCertifiedEditorState.measuredIndicatorId))
    ) {
      setTerrainCertifiedEditorError(
        "Veuillez d'abord renseigner puis sélectionner un prévu et un réalisé avant d'enregistrer le certifié.",
      );
      return;
    }

    const certifiedIsoDate = `${Number(terrainCertifiedEditorState.year)}-${String(
      terrainCertifiedEditorState.monthIndex + 1,
    ).padStart(2, "0")}-01`;
    const certifiedAmountEuro = Number(Number(amount || 0).toFixed(2));
    const recipientOption = recipientSubcontractorId
      ? recipientOptions.find((option) => option.id === recipientSubcontractorId)
      : null;
    const optimisticRecipient = recipientSubcontractorId
      ? {
          id: recipientSubcontractorId,
          name: recipientOption?.label || `Intervenant #${recipientSubcontractorId}`,
        }
      : null;
    const contractMatcher = {
      contractNodeId: contract.id,
      synthesesContractId: extractSynthesesContractId(contract),
    };
    const optimisticLandPaymentId = landPaymentId > 0 ? landPaymentId : -Date.now();
    const projectLandPaymentRowsForLink = buildContractProjectLandPaymentPreviews(
      contract,
      projectId,
    );
    const selectedEstimatedIndicatorId = Number(
      terrainCertifiedEditorState.estimatedIndicatorId || 0,
    );
    const selectedMeasuredIndicatorId = Number(
      terrainCertifiedEditorState.measuredIndicatorId || 0,
    );
    const selectedEstimatedRow = projectLandPaymentRowsForLink.find(
      (row) => Number(row.projectIndicatorId || 0) === selectedEstimatedIndicatorId,
    );
    const selectedMeasuredRow = projectLandPaymentRowsForLink.find(
      (row) => Number(row.projectIndicatorId || 0) === selectedMeasuredIndicatorId,
    );
    const optimisticCertifiedRows = shouldDeleteValue
      ? []
      : buildOptimisticLandPaymentRows({
          landPaymentId: optimisticLandPaymentId,
          amountEuro: certifiedAmountEuro,
          status: 3,
          expectedAt: certifiedIsoDate,
          dateMeasure: certifiedIsoDate,
          dateVerified: certifiedIsoDate,
          realFundingId,
          synthesesContractId,
          synthesesContractLineId,
          projectId,
          projectName: terrainCertifiedEditorState.lineLabel,
          lineTitle: selectedTargetLine?.label || null,
          pjVerified: terrainCertifiedEditorState.pjVerified || null,
          recipientSubcontractorId,
        })
          .filter((paymentRow) => Number(paymentRow.status || 0) === 3)
          .map((paymentRow) => ({
            ...paymentRow,
            recipientSubcontractor: optimisticRecipient,
          }));
    const selectedSiblingRowsForOptimisticLink = shouldDeleteValue
      ? []
      : [selectedEstimatedRow, selectedMeasuredRow]
          .filter((row): row is ProjectLinePaymentPreview => !!row)
          .map((row) => ({
            ...row,
            id: isPositiveId(row.projectIndicatorId)
              ? -Number(row.projectIndicatorId)
              : row.id,
            landPaymentId: optimisticLandPaymentId,
            landPaymentStatus: 3,
            realFundingId,
            synthesesContractId,
            synthesesContractLineId,
            lineId: synthesesContractLineId,
            lineTitle: selectedTargetLine?.label || row.lineTitle || null,
            source: "land_payment",
            recipientSubcontractorId,
            recipientSubcontractor: optimisticRecipient ?? row.recipientSubcontractor ?? null,
          }));
    const selectedCertifiedLinkIndicatorIds = [
      selectedEstimatedIndicatorId,
      selectedMeasuredIndicatorId,
    ].filter((indicatorId) => Number.isFinite(indicatorId) && indicatorId > 0);
    const selectedCertifiedSourceLandPaymentIds = [
      selectedEstimatedRow?.landPaymentId,
      selectedMeasuredRow?.landPaymentId,
      landPaymentId,
    ]
      .map((id) => Number(id || 0))
      .filter((id, index, ids) =>
        Number.isFinite(id) &&
        id > 0 &&
        id !== optimisticLandPaymentId &&
        ids.indexOf(id) === index,
      );
    const optimisticLinkedCertifiedRows = [
      ...selectedSiblingRowsForOptimisticLink,
      ...optimisticCertifiedRows,
    ];
    const applyOptimisticCertifiedLandPaymentUpdate = () => {
      if (!onMutateData) {
        return null;
      }

      if (shouldDeleteValue) {
        if (landPaymentId <= 0) {
          return null;
        }

        return onMutateData((current) =>
          replaceLandPaymentStatusRowsInGroupedData(
            current,
            contractMatcher,
            landPaymentId,
            3,
            [],
          ),
        );
      }

      return onMutateData((current) =>
        replaceReconciledLandPaymentRowsInGroupedData(
          current,
          contractMatcher,
          optimisticLandPaymentId,
          selectedCertifiedLinkIndicatorIds,
          optimisticLinkedCertifiedRows,
          selectedCertifiedSourceLandPaymentIds,
        ),
      );
    };

	    setSavingTerrainCertified(true);
	    markTerrainTimelineCellSaving(certifiedSavingCellKey);
    setTerrainCertifiedEditorError(null);
    const didApplyOptimisticCertifiedLandPayment =
      !!onMutateData &&
      (shouldDeleteValue ? landPaymentId > 0 : true);
    const optimisticCertifiedLandPaymentSnapshot =
      applyOptimisticCertifiedLandPaymentUpdate();

    setTerrainCertifiedEditorState(null);

    void saveTerrainIndicatorToApi(
      {
        project: projectId,
        indicator: 1,
        year: Number(terrainCertifiedEditorState.year),
        month: terrainCertifiedEditorState.monthIndex,
        type: 3,
        value: shouldDeleteValue ? null : amount,
        devise: 0,
        ...(Number.isFinite(realFundingId) && realFundingId > 0
          ? { realFundingId, realfunding: realFundingId }
          : {}),
        ...(Number.isFinite(synthesesContractId) && synthesesContractId > 0
          ? { synthesesContractId }
          : {}),
        ...(Number.isFinite(synthesesContractLineId) && synthesesContractLineId > 0
          ? { synthesesContractLineId }
          : {}),
        ...(!shouldDeleteValue && recipientSubcontractorId
          ? { recipientSubcontractorId }
          : {}),
        pjs: shouldDeleteValue
          ? []
          : [Number(terrainCertifiedEditorState.pjVerified?.id || 0)].filter(
              (mediaId) => mediaId > 0,
            ),
        valueInserted: null,
        desiredFundings: [],
        obtainedFundings: [],
        ...(!shouldDeleteValue
          ? {
              ...(landPaymentId > 0
                ? {
                    landPaymentChoice: "existing",
                    targetLandPaymentId: landPaymentId,
                  }
                : {}),
              estimatedIndicatorId: Number(
                terrainCertifiedEditorState.estimatedIndicatorId || 0,
              ),
              measuredIndicatorId: Number(
                terrainCertifiedEditorState.measuredIndicatorId || 0,
              ),
            }
          : {}),
      },
      true,
    )
      .then((savedIndicator) => {
        const savedProjectIndicatorId = Number(
          savedIndicator?.projectIndicatorId || savedIndicator?.indicatorId || 0,
        );
        const savedLandPaymentId = Number(savedIndicator?.landPaymentId || 0);
        const savedLandPaymentStatus = Number(savedIndicator?.landPaymentStatus || 3);
        const persistedLinkedCertifiedRows = optimisticLinkedCertifiedRows.map((paymentRow) => {
          const isCertifiedRow = Number(paymentRow.status || 0) === 3;

          return {
            ...paymentRow,
            id:
              isCertifiedRow && savedProjectIndicatorId > 0
                ? -savedProjectIndicatorId
                : paymentRow.id,
            projectIndicatorId:
              isCertifiedRow && savedProjectIndicatorId > 0
                ? savedProjectIndicatorId
                : paymentRow.projectIndicatorId,
            landPaymentId:
              savedLandPaymentId > 0
                ? savedLandPaymentId
                : paymentRow.landPaymentId,
            landPaymentStatus:
              savedLandPaymentStatus > 0
                ? savedLandPaymentStatus
                : paymentRow.landPaymentStatus,
          };
        });
        const persistedCertifiedLinkIndicatorIds = [
          ...selectedCertifiedLinkIndicatorIds,
          savedProjectIndicatorId,
        ].filter((indicatorId) => Number.isFinite(indicatorId) && indicatorId > 0);

        if (
          !shouldDeleteValue &&
          !!onMutateData &&
          (savedProjectIndicatorId > 0 || savedLandPaymentId > 0)
        ) {
          onMutateData?.((current) =>
            replaceReconciledLandPaymentRowsInGroupedData(
              current,
              contractMatcher,
              optimisticLandPaymentId,
              persistedCertifiedLinkIndicatorIds,
              persistedLinkedCertifiedRows,
              selectedCertifiedSourceLandPaymentIds,
            ),
          );
        }

        void refreshContracts().then(() => {
          if (
            !shouldDeleteValue &&
            !!onMutateData &&
            (savedProjectIndicatorId > 0 || savedLandPaymentId > 0)
          ) {
            onMutateData?.((current) =>
              replaceReconciledLandPaymentRowsInGroupedData(
                current,
                contractMatcher,
                savedLandPaymentId > 0 ? savedLandPaymentId : optimisticLandPaymentId,
                persistedCertifiedLinkIndicatorIds,
                persistedLinkedCertifiedRows,
                selectedCertifiedSourceLandPaymentIds,
              ),
            );
          }
        });
      })
      .catch((error: unknown) => {
        if (didApplyOptimisticCertifiedLandPayment) {
          onRestoreData?.(optimisticCertifiedLandPaymentSnapshot);
        }
        const message =
          error instanceof Error
            ? error.message
            : "Erreur lors de la sauvegarde du versement certifié.";
        setTerrainCertifiedEditorError(message);
        toast({
          title: "La sauvegarde du versement certifié a échoué.",
          description: message,
        });
      })
	      .finally(() => {
	        setSavingTerrainCertified(false);
	        unmarkTerrainTimelineCellSaving(certifiedSavingCellKey);
	      });
  }, [
    contract,
    refreshContracts,
	    getRecipientOptionsForProject,
	    markTerrainTimelineCellSaving,
	    onMutateData,
	    onRestoreData,
	    saveTerrainIndicatorToApi,
	    terrainCertifiedEditorState,
	    unmarkTerrainTimelineCellSaving,
  ]);

  const handleConfirmTerrainPaymentEditor = useCallback(async () => {
    if (!terrainPaymentEditorState) {
      return;
    }

    const realFundingId = Number(terrainPaymentEditorState.realFundingId || 0);
    const synthesesContractId = Number(terrainPaymentEditorState.synthesesContractId || 0);
    const synthesesContractLineId = Number(terrainPaymentEditorState.synthesesContractLineId || 0);
    const amount = Number(
      String(terrainPaymentEditorState.amountEuro || "")
        .replace(/\s/g, "")
        .replace(",", "."),
    );
    const status = Number(terrainPaymentEditorState.status || 0);
    const recipientOptions = getRecipientOptionsForProject(
      terrainPaymentEditorState.projectId,
    );
    const recipientSubcontractorId = isPositiveId(
      terrainPaymentEditorState.recipientSubcontractorId,
    )
      ? Number(terrainPaymentEditorState.recipientSubcontractorId)
      : null;

    if (
      (!Number.isFinite(realFundingId) || realFundingId <= 0)
      && (!Number.isFinite(synthesesContractLineId) || synthesesContractLineId <= 0)
    ) {
      setTerrainPaymentEditorError("La ligne à l'intervenant est introuvable.");
      return;
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setTerrainPaymentEditorError("Renseignez un montant strictement positif.");
      return;
    }
    if (!terrainPaymentEditorState.expectedAt) {
      setTerrainPaymentEditorError("Renseignez la date prévue.");
      return;
    }
    if (status >= 2 && !terrainPaymentEditorState.dateMeasure) {
      setTerrainPaymentEditorError("Renseignez la date de réalisation.");
      return;
    }
    if (status >= 3 && !terrainPaymentEditorState.dateVerified) {
      setTerrainPaymentEditorError("Renseignez la date de certification.");
      return;
    }
    if (status >= 3 && !isPositiveId(terrainPaymentEditorState.pjVerified?.id)) {
      setTerrainPaymentEditorError("Ajoutez la pièce jointe de vérification.");
      return;
    }
    if (recipientOptions.length > 0 && !recipientSubcontractorId) {
      setTerrainPaymentEditorError("Choisissez l'intervenant destinataire.");
      return;
    }

    setSavingTerrainPayment(true);
    setTerrainPaymentEditorError(null);

    const optimisticLandPaymentId = isPositiveId(terrainPaymentEditorState.landPaymentId)
      ? Number(terrainPaymentEditorState.landPaymentId)
      : -Date.now();
    const optimisticRows = buildOptimisticLandPaymentRows({
      landPaymentId: optimisticLandPaymentId,
      amountEuro: amount,
      status,
      expectedAt: terrainPaymentEditorState.expectedAt,
      dateMeasure: terrainPaymentEditorState.dateMeasure,
      dateVerified: terrainPaymentEditorState.dateVerified,
      realFundingId,
      synthesesContractId,
      synthesesContractLineId,
      projectId: terrainPaymentEditorState.projectId,
      projectName: terrainPaymentEditorState.projectName,
      lineTitle: terrainPaymentEditorState.lineTitle,
      pjVerified: terrainPaymentEditorState.pjVerified,
      recipientSubcontractorId,
    });
    const didApplyOptimisticLandPayment = !!onMutateData;
    const optimisticLandPaymentSnapshot = onMutateData?.((current) =>
      replaceLandPaymentRowsInGroupedData(
        current,
        {
          contractNodeId: contract.id,
          synthesesContractId: extractSynthesesContractId(contract),
        },
        optimisticLandPaymentId,
        optimisticRows,
      ),
    ) ?? null;

    setTerrainPaymentEditorState(null);

    void saveTerrainPaymentToApi({
      ...(Number.isFinite(realFundingId) && realFundingId > 0
        ? { realfunding: realFundingId }
        : {}),
      ...(Number.isFinite(synthesesContractId) && synthesesContractId > 0
        ? { synthesesContractId }
        : {}),
      ...(Number.isFinite(synthesesContractLineId) && synthesesContractLineId > 0
        ? { synthesesContractLineId }
        : {}),
      amount,
      expectedAt: terrainPaymentEditorState.expectedAt,
      status,
      ...(recipientSubcontractorId ? { recipientSubcontractorId } : {}),
      ...(status >= 2 ? { dateMeasure: terrainPaymentEditorState.dateMeasure } : {}),
      ...(status >= 3
        ? {
            dateVerified: terrainPaymentEditorState.dateVerified,
            pjVerified: Number(terrainPaymentEditorState.pjVerified?.id),
          }
        : {}),
      ...(isPositiveId(terrainPaymentEditorState.landPaymentId)
        ? { toEdit: Number(terrainPaymentEditorState.landPaymentId) }
        : {}),
    })
      .then((savedLandPayment) => {
        const savedLandPaymentId = Number(savedLandPayment?.id || savedLandPayment?.landPaymentId || 0);
        if (Number.isFinite(savedLandPaymentId) && savedLandPaymentId > 0) {
          const finalRows = buildOptimisticLandPaymentRows({
            landPaymentId: savedLandPaymentId,
            amountEuro: amount,
            status,
            expectedAt: terrainPaymentEditorState.expectedAt,
            dateMeasure: terrainPaymentEditorState.dateMeasure,
            dateVerified: terrainPaymentEditorState.dateVerified,
            realFundingId,
            synthesesContractId,
            synthesesContractLineId,
            projectId: terrainPaymentEditorState.projectId,
            projectName: terrainPaymentEditorState.projectName,
            lineTitle: terrainPaymentEditorState.lineTitle,
            pjVerified: terrainPaymentEditorState.pjVerified,
            recipientSubcontractorId,
          });
          onMutateData?.((current) =>
            replaceLandPaymentRowsInGroupedData(
              current,
              {
                contractNodeId: contract.id,
                synthesesContractId: extractSynthesesContractId(contract),
              },
              optimisticLandPaymentId,
              finalRows,
            ),
          );
        }
        void refreshContracts();
      })
      .catch((error: unknown) => {
        if (didApplyOptimisticLandPayment) {
          onRestoreData?.(optimisticLandPaymentSnapshot);
        }
        const message =
          error instanceof Error
            ? error.message
            : "Erreur lors de la sauvegarde du versement à l'intervenant.";
        setTerrainPaymentEditorError(message);
        toast({
          title: "La sauvegarde du versement à l'intervenant a échoué.",
          description: message,
        });
      })
      .finally(() => {
        setSavingTerrainPayment(false);
      });
  }, [
    contract,
    onMutateData,
    onRestoreData,
    refreshContracts,
    getRecipientOptionsForProject,
    saveTerrainPaymentToApi,
    terrainPaymentEditorState,
  ]);

  const handleCommitTerrainTimelineCell = useCallback(
    async (
      row: ContractLineDisplayRow,
      allRows: ProjectLinePaymentPreview[],
      payload: TerrainTimelineCommitPayload,
    ) => {
      if (actionsDisabled || !canManage) return;

      const realFundingId = Number(row.realFundingId || 0);
      const synthesesContractId = Number(row.synthesesContractId || 0);
      const synthesesContractLineId = Number(row.lineId || 0);
      const projectId = Number(row.projectId || payload.payment?.projectId || 0);
      const shouldDeleteValue = payload.amountEuro === null;
      const amount = shouldDeleteValue ? null : Number(payload.amountEuro || 0);
      const existingDraft = payload.payment
        ? buildTerrainPaymentEditorDraft(allRows, payload.payment, payload.year)
        : null;
	      if (
	        (!Number.isFinite(realFundingId) || realFundingId <= 0)
        && (!Number.isFinite(synthesesContractLineId) || synthesesContractLineId <= 0)
      ) {
        setTerrainTimelineError("La ligne à l'intervenant est introuvable.");
        return;
      }
      if (!Number.isFinite(projectId) || projectId <= 0) {
        setTerrainTimelineError("Le projet du versement à l'intervenant est introuvable.");
        return;
      }
      if (
        !shouldDeleteValue &&
        (!Number.isFinite(Number(amount)) || Number(amount) <= 0)
      ) {
        setTerrainTimelineError("Renseignez un montant strictement positif.");
        return;
      }

      const recipientOptions = shouldDeleteValue
        ? []
        : await loadRecipientOptionsForProject(projectId);
      const payloadRecipientSubcontractorId = isPositiveId(
        payload.recipientSubcontractorId,
      )
        ? Number(payload.recipientSubcontractorId)
        : null;
	      const recipientSubcontractorId =
	        getLandPaymentRecipientId(existingDraft) ??
	        getLandPaymentRecipientId(payload.payment) ??
	        payloadRecipientSubcontractorId ??
	        (recipientOptions.length === 1 ? recipientOptions[0].id : null);
	      const cellKey = buildTerrainTimelineCellKey(
	        buildTerrainTimelineCellPrefix(row.key, recipientSubcontractorId),
	        payload.status,
	        payload.year,
	        payload.monthIndex,
	      );
      if (
        !shouldDeleteValue &&
        payload.status !== 3 &&
        recipientOptions.length > 0 &&
        !recipientSubcontractorId
      ) {
        const dateForCell = `${payload.year}-${String(payload.monthIndex + 1).padStart(2, "0")}-01`;
        setTerrainTimelineError(null);
        setTerrainPaymentEditorError(null);
        setTerrainPaymentEditorState({
          lineKey: row.key,
          lineLabel: `${row.projectName} • ${contractLabel}`,
          synthesesContractId,
          synthesesContractLineId,
          realFundingId,
          projectId,
          projectName: row.projectName,
          lineTitle: row.title,
          landPaymentId: existingDraft?.landPaymentId ?? null,
          status: payload.status,
          amountEuro: String(amount || ""),
          expectedAt:
            payload.status === 1
              ? dateForCell
              : existingDraft?.expectedAt || payload.payment?.at || dateForCell,
          dateMeasure:
            payload.status === 2
              ? dateForCell
              : existingDraft?.dateMeasure || payload.payment?.dateMeasure || "",
          dateVerified:
            existingDraft?.dateVerified || payload.payment?.dateVerified || "",
          pjVerified: existingDraft?.pjVerified || payload.payment?.pjVerified || null,
          recipientSubcontractorId: null,
        });
        return;
      }

      markTerrainTimelineCellSaving(cellKey);
      setTerrainTimelineError(null);
      setTerrainPaymentEditorState(null);
      setTerrainPaymentEditorError(null);
      setTerrainQuickLinkState(null);
      setTerrainReconcileState(null);
      setTerrainReconcileError(null);
      setTerrainQuickLinkState(null);
      setTerrainMoveState(null);
      setTerrainMoveError(null);

      const landPaymentId = Number(
        existingDraft?.landPaymentId ||
        payload.payment?.landPaymentId ||
        0,
      );
      const optimisticLandPaymentId = landPaymentId > 0 ? landPaymentId : -Date.now();
      const canOptimisticallyPatchTimeline = payload.status !== 3 || shouldDeleteValue;
      const optimisticTimelineRows = shouldDeleteValue
        ? []
        : buildOptimisticLandPaymentRows({
            landPaymentId: optimisticLandPaymentId,
            amountEuro: Number(amount || 0),
            status: payload.status,
            expectedAt:
              payload.status === 1
                ? `${payload.year}-${String(payload.monthIndex + 1).padStart(2, "0")}-01`
                : existingDraft?.expectedAt || payload.payment?.at || `${payload.year}-${String(payload.monthIndex + 1).padStart(2, "0")}-01`,
            dateMeasure:
              payload.status === 2
                ? `${payload.year}-${String(payload.monthIndex + 1).padStart(2, "0")}-01`
                : existingDraft?.dateMeasure || payload.payment?.dateMeasure || null,
            dateVerified:
              payload.status === 3
                ? `${payload.year}-${String(payload.monthIndex + 1).padStart(2, "0")}-01`
                : existingDraft?.dateVerified || payload.payment?.dateVerified || null,
            realFundingId,
            synthesesContractId,
            synthesesContractLineId,
            projectId,
            projectName: row.projectName,
            lineTitle: row.title,
            pjVerified: payload.payment?.pjVerified || existingDraft?.pjVerified || null,
            recipientSubcontractorId,
          }).filter((paymentRow) => Number(paymentRow.status || 0) === payload.status);
      const didApplyOptimisticTimeline =
        canOptimisticallyPatchTimeline && !!onMutateData;
      const optimisticTimelineSnapshot = canOptimisticallyPatchTimeline
        ? onMutateData?.((current) =>
            replaceLandPaymentStatusRowsInGroupedData(
              current,
              {
                contractNodeId: contract.id,
                synthesesContractId: extractSynthesesContractId(contract),
              },
              optimisticLandPaymentId,
              payload.status,
              optimisticTimelineRows,
            ),
          ) ?? null
        : null;

      try {
        const findIndicatorId = (status: 1 | 2) => {
          const sameLandPaymentRows = landPaymentId > 0
            ? allRows.filter(
                (paymentRow) =>
                  Number(paymentRow.landPaymentId || 0) === landPaymentId,
              )
            : [];
          const rowsToSearch = sameLandPaymentRows.length > 0
            ? sameLandPaymentRows
            : allRows;
          const indicatorRow = rowsToSearch.find(
            (paymentRow) =>
              Number(paymentRow.status || 0) === status &&
              Number(paymentRow.projectIndicatorId || 0) > 0,
          );

          return Number(indicatorRow?.projectIndicatorId || 0) || null;
        };
        const basePayload = {
          project: projectId,
          indicator: 1,
          year: Number(payload.year),
          month: payload.monthIndex,
          type: payload.status,
          value: shouldDeleteValue ? null : amount,
          devise: 0,
          ...(Number.isFinite(realFundingId) && realFundingId > 0
            ? { realFundingId, realfunding: realFundingId }
            : {}),
          ...(Number.isFinite(synthesesContractId) && synthesesContractId > 0
            ? { synthesesContractId }
            : {}),
          ...(Number.isFinite(synthesesContractLineId) && synthesesContractLineId > 0
            ? { synthesesContractLineId }
            : {}),
          ...(!shouldDeleteValue && recipientSubcontractorId
            ? { recipientSubcontractorId }
            : {}),
        };

        if (payload.status === 3) {
          if (shouldDeleteValue) {
            await saveTerrainIndicatorToApi(
              {
                ...basePayload,
                pjs: [],
                valueInserted: null,
                desiredFundings: [],
                obtainedFundings: [],
              },
              true,
            );
            void refreshContracts();
            return;
          }

          const estimatedIndicatorId = findIndicatorId(1);
          const measuredIndicatorId = findIndicatorId(2);
          if (!estimatedIndicatorId || !measuredIndicatorId) {
            setTerrainTimelineError(
              "Renseignez puis sélectionnez d'abord un prévu et un réalisé avant d'enregistrer le certifié.",
            );
            return;
          }

          await saveTerrainIndicatorToApi(
            {
              ...basePayload,
              pjs: [
                Number(
                  existingDraft?.pjVerified?.id ||
                  payload.payment?.pjVerified?.id ||
                  0,
                ),
              ].filter((mediaId) => mediaId > 0),
              valueInserted: null,
              desiredFundings: [],
              obtainedFundings: [],
              landPaymentChoice: landPaymentId > 0 ? "existing" : "new",
              ...(landPaymentId > 0 ? { targetLandPaymentId: landPaymentId } : {}),
              estimatedIndicatorId,
              measuredIndicatorId,
            },
            true,
          );
        } else {
          const savedIndicator = await saveTerrainIndicatorToApi(basePayload, false);
          const savedProjectIndicatorId = Number(
            savedIndicator?.projectIndicatorId || savedIndicator?.indicatorId || 0,
          );
          const savedLandPaymentId = Number(savedIndicator?.landPaymentId || 0);
          const savedLandPaymentStatus = Number(
            savedIndicator?.landPaymentStatus || payload.status,
          );

          if (
            !shouldDeleteValue &&
            didApplyOptimisticTimeline &&
            (savedProjectIndicatorId > 0 || savedLandPaymentId > 0)
          ) {
            const persistedTimelineRows = optimisticTimelineRows.map((paymentRow) => ({
              ...paymentRow,
              id:
                savedProjectIndicatorId > 0
                  ? -savedProjectIndicatorId
                  : paymentRow.id,
              projectIndicatorId:
                savedProjectIndicatorId > 0
                  ? savedProjectIndicatorId
                  : paymentRow.projectIndicatorId,
              landPaymentId:
                savedLandPaymentId > 0
                  ? savedLandPaymentId
                  : paymentRow.landPaymentId,
              landPaymentStatus:
                savedLandPaymentStatus > 0
                  ? savedLandPaymentStatus
                  : paymentRow.landPaymentStatus,
            }));

            onMutateData?.((current) =>
              replaceLandPaymentStatusRowsInGroupedData(
                current,
                {
                  contractNodeId: contract.id,
                  synthesesContractId: extractSynthesesContractId(contract),
                },
                optimisticLandPaymentId,
                payload.status,
                persistedTimelineRows,
              ),
            );
          }
        }
        void refreshContracts();
      } catch (error: unknown) {
        if (didApplyOptimisticTimeline) {
          onRestoreData?.(optimisticTimelineSnapshot);
        }
        setTerrainTimelineError(
          error instanceof Error
            ? error.message
            : "Erreur lors de la sauvegarde du versement à l'intervenant.",
        );
      } finally {
        unmarkTerrainTimelineCellSaving(cellKey);
      }
    },
    [
      actionsDisabled,
      canManage,
      contract,
      onMutateData,
      onRestoreData,
      refreshContracts,
	      contractLabel,
	      loadRecipientOptionsForProject,
	      markTerrainTimelineCellSaving,
	      saveTerrainIndicatorToApi,
	      unmarkTerrainTimelineCellSaving,
	    ],
  );

  const handleDeleteTerrainPayment = useCallback(
    async (payment: ProjectLinePaymentPreview) => {
      const landPaymentId = Number(payment.landPaymentId || 0);
      if (!Number.isFinite(landPaymentId) || landPaymentId <= 0) {
        return;
      }

      const confirmed = window.confirm(
        "Supprimer ce versement à l'intervenant et les statuts qui lui sont liés ?",
      );
      if (!confirmed) {
        return;
      }

      setDeletingTerrainPaymentId(landPaymentId);
      setTerrainPaymentEditorError(null);
      const didApplyOptimisticDelete = !!onMutateData;
      const optimisticDeleteSnapshot = onMutateData?.((current) =>
        removeLandPaymentRowsFromGroupedData(current, landPaymentId),
      ) ?? null;

      try {
        await deleteTerrainPaymentToApi(landPaymentId);
        void refreshContracts();
        setTerrainPaymentEditorState((current) => {
          if (!current) return current;
          return Number(current.landPaymentId || 0) === landPaymentId ? null : current;
        });
      } catch (error: unknown) {
        if (didApplyOptimisticDelete) {
          onRestoreData?.(optimisticDeleteSnapshot);
        }
        setTerrainPaymentEditorError(
          error instanceof Error
            ? error.message
            : "Erreur lors de la suppression du versement à l'intervenant.",
        );
      } finally {
        setDeletingTerrainPaymentId(null);
      }
    },
    [deleteTerrainPaymentToApi, onMutateData, onRestoreData, refreshContracts],
  );

  const handleOpenTerrainReconcile = useCallback(
    (
      row: ContractLineDisplayRow,
      plannedRows: ProjectLinePaymentPreview[],
      realizedRows: ProjectLinePaymentPreview[],
      certifiedRows: ProjectLinePaymentPreview[],
      targetLandPayment?: ProjectLinePaymentPreview | null,
    ) => {
      if (!row.realFundingId) {
        setTerrainReconcileError("Impossible de retrouver la ligne à l'intervenant.");
        return;
      }

      const targetLandPaymentId = isPositiveId(targetLandPayment?.landPaymentId)
        ? Number(targetLandPayment?.landPaymentId)
        : null;
      const targetLandPaymentStatus = isPositiveId(targetLandPayment?.landPaymentStatus)
        ? Number(targetLandPayment?.landPaymentStatus)
        : (
          isPositiveId(targetLandPayment?.status)
            ? Number(targetLandPayment?.status)
            : null
        );

      setTerrainReconcileError(null);
      setTerrainMoveState(null);
      setTerrainMoveError(null);
      setTerrainPaymentEditorState(null);
      setTerrainPaymentEditorError(null);
      const selectedEstimatedId =
        null === targetLandPaymentId
          ? (plannedRows[0]?.projectIndicatorId ?? null)
          : (
            targetLandPaymentStatus === 2 || targetLandPaymentStatus === 3
              ? (plannedRows.length === 1 ? (plannedRows[0]?.projectIndicatorId ?? null) : null)
              : null
          );
      const selectedMeasuredId =
        null === targetLandPaymentId || targetLandPaymentStatus === 1
          ? (realizedRows[0]?.projectIndicatorId ?? null)
          : (
            targetLandPaymentStatus === 3
              ? (realizedRows.length === 1 ? (realizedRows[0]?.projectIndicatorId ?? null) : null)
              : null
          );
      const selectedVerifiedId =
        null === targetLandPaymentId
        || targetLandPaymentStatus === 1
        || targetLandPaymentStatus === 2
          ? (certifiedRows.length === 1 ? (certifiedRows[0]?.projectIndicatorId ?? null) : null)
          : null;

      setTerrainReconcileState({
        lineKey: row.key,
        lineLabel: `${row.projectName} • ${contractLabel}`,
        synthesesContractId: row.synthesesContractId,
        synthesesContractLineId: row.lineId,
        realFundingId: row.realFundingId,
        landPaymentId: targetLandPaymentId,
        landPaymentStatus: targetLandPaymentStatus,
        plannedRows,
        realizedRows,
        certifiedRows,
        selectedEstimatedId,
        selectedMeasuredId,
        selectedVerifiedId,
      });
    },
    [contractLabel],
  );

  const handlePickTerrainReconcile = useCallback(
    (
      field: "selectedEstimatedId" | "selectedMeasuredId" | "selectedVerifiedId",
      indicatorId: number | null,
    ) => {
      setTerrainReconcileError(null);
      setTerrainReconcileState((current) => {
        if (!current) return current;

        return {
          ...current,
          [field]: indicatorId,
        };
      });
    },
    [],
  );

  const handleCancelTerrainReconcile = useCallback(() => {
    setTerrainReconcileState(null);
    setTerrainReconcileError(null);
  }, []);

  const handleStartTerrainQuickLink = useCallback(
    (projectKey: string, label: string) => {
      setTerrainReconcileState(null);
      setTerrainReconcileError(null);
      setTerrainPaymentEditorState(null);
      setTerrainPaymentEditorError(null);
      setTerrainCertifiedEditorState(null);
      setTerrainCertifiedEditorError(null);
      setTerrainMoveState(null);
      setTerrainMoveError(null);
      setTerrainTimelineError(null);
      setTerrainQuickLinkState((current) => {
        if (current?.projectKey === projectKey) {
          return null;
        }

        return {
          projectKey,
          label,
          targetLandPaymentId: null,
          targetLandPaymentStatus: null,
          targetPayment: null,
          targetLandPaymentRows: [],
          selectedEstimatedId: null,
          selectedMeasuredId: null,
          selectedVerifiedId: null,
          selectedEstimatedPayment: null,
          selectedMeasuredPayment: null,
          selectedVerifiedPayment: null,
          error: null,
        };
      });
    },
    [],
  );

  const handleCancelTerrainQuickLink = useCallback(() => {
    setTerrainQuickLinkState(null);
  }, []);

  const handlePickTerrainQuickLinkCell = useCallback(
    (
      projectKey: string,
      payload: TerrainTimelineCommitPayload,
      allRows: ProjectLinePaymentPreview[],
    ) => {
      const payment = payload.payment;
      if (!payment) return;

      const status = Number(payload.status || payment.status || 0) as 1 | 2 | 3;
      if (![1, 2, 3].includes(status)) return;

      const projectIndicatorId = Number(payment.projectIndicatorId || 0);
      const landPaymentId = Number(payment.landPaymentId || 0);
      const canUseIndicator = Number.isFinite(projectIndicatorId) && projectIndicatorId > 0;
      const canUseLandPayment = Number.isFinite(landPaymentId) && landPaymentId > 0;
      if (!canUseIndicator && !canUseLandPayment) return;

      setTerrainQuickLinkState((current) => {
        if (!current || current.projectKey !== projectKey) return current;

        const next: TerrainQuickLinkState = {
          ...current,
          error: null,
        };

        if (payment.source === "land_payment" && canUseLandPayment) {
          const isSameTarget =
            Number(current.targetLandPaymentId || 0) === landPaymentId;
          const isSameTargetRow =
            isSameTarget && current.targetPayment?.id === payment.id;
          const targetStatus = Number(payment.landPaymentStatus || status);

          if (isPositiveId(current.targetLandPaymentId) && !isSameTarget) {
            if (!canUseIndicator) {
              return {
                ...next,
                error: "Ce versement doit d'abord être lié à une valeur ESG pour être sélectionné.",
              };
            }

            if (status === 1) {
              const isSame = Number(current.selectedEstimatedId || 0) === projectIndicatorId;
              next.selectedEstimatedId = isSame ? null : projectIndicatorId;
              next.selectedEstimatedPayment = isSame ? null : payment;
            } else if (status === 2) {
              const isSame = Number(current.selectedMeasuredId || 0) === projectIndicatorId;
              next.selectedMeasuredId = isSame ? null : projectIndicatorId;
              next.selectedMeasuredPayment = isSame ? null : payment;
            } else {
              const isSame = Number(current.selectedVerifiedId || 0) === projectIndicatorId;
              next.selectedVerifiedId = isSame ? null : projectIndicatorId;
              next.selectedVerifiedPayment = isSame ? null : payment;
            }

            return next;
          }

          if (isSameTarget && !isSameTargetRow) {
            return next;
          }

          const targetLandPaymentRows = allRows.filter(
            (row) => Number(row.landPaymentId || 0) === landPaymentId,
          );

          next.targetLandPaymentId = isSameTargetRow ? null : landPaymentId;
          next.targetLandPaymentStatus = isSameTargetRow ? null : targetStatus;
          next.targetPayment = isSameTargetRow ? null : payment;
          next.targetLandPaymentRows = isSameTargetRow
            ? []
            : (targetLandPaymentRows.length > 0 ? targetLandPaymentRows : [payment]);

          return next;
        }

        if (!canUseIndicator) return next;

        if (
          (status === 1 || status === 2) &&
          isTerrainIndicatorLinkedToAnotherCertified(
            payment,
            allRows,
            current.targetLandPaymentId,
            current.selectedVerifiedId,
          )
        ) {
          return {
            ...next,
            error: "Cet indicateur est déjà lié à un autre certifié.",
          };
        }

        if (status === 1) {
          const isSame = Number(current.selectedEstimatedId || 0) === projectIndicatorId;
          next.selectedEstimatedId = isSame ? null : projectIndicatorId;
          next.selectedEstimatedPayment = isSame ? null : payment;
        } else if (status === 2) {
          const isSame = Number(current.selectedMeasuredId || 0) === projectIndicatorId;
          next.selectedMeasuredId = isSame ? null : projectIndicatorId;
          next.selectedMeasuredPayment = isSame ? null : payment;
        } else {
          const isSame = Number(current.selectedVerifiedId || 0) === projectIndicatorId;
          next.selectedVerifiedId = isSame ? null : projectIndicatorId;
          next.selectedVerifiedPayment = isSame ? null : payment;
        }

        return next;
      });
    },
    [],
  );

  const handleConfirmTerrainQuickLink = useCallback(async () => {
    if (!terrainQuickLinkState) return;

    if (!canSubmitTerrainQuickLink(terrainQuickLinkState)) {
      setTerrainQuickLinkState((current) =>
        current
          ? {
              ...current,
              error:
                "Sélectionnez au moins un prévu et un mesuré, ou complétez le statut manquant de la ligne choisie.",
            }
          : current,
      );
      return;
    }

    const contextRows = getTerrainQuickLinkContextRows(terrainQuickLinkState);
    const contextKeys = Array.from(
      new Set(
        contextRows
          .map((row) => {
            const lineId = Number(row.synthesesContractLineId || row.lineId || 0);
            if (Number.isFinite(lineId) && lineId > 0) return `line-${lineId}`;
            const realFundingId = Number(row.realFundingId || 0);
            if (Number.isFinite(realFundingId) && realFundingId > 0) {
              return `rf-${realFundingId}`;
            }
            return null;
          })
          .filter((key): key is string => !!key),
      ),
    );

    if (contextKeys.length > 1) {
      setTerrainQuickLinkState((current) =>
        current
          ? {
              ...current,
              error:
                "Choisissez des versements du même poste de dépenses pour la liaison rapide.",
            }
          : current,
      );
      return;
    }

    const realFundingId = contextRows.reduce((found, row) => {
      if (found) return found;
      const candidate = Number(row.realFundingId || 0);
      return Number.isFinite(candidate) && candidate > 0 ? candidate : 0;
    }, 0);

    if (!Number.isFinite(realFundingId) || realFundingId <= 0) {
      setTerrainQuickLinkState((current) =>
        current
          ? {
              ...current,
              error:
                "Impossible de retrouver le poste de dépenses lié à ces versements.",
            }
          : current,
      );
      return;
    }

    setSavingTerrainQuickLink(true);
    setTerrainQuickLinkState((current) =>
      current ? { ...current, error: null } : current,
    );

    const optimisticLandPaymentId = isPositiveId(terrainQuickLinkState.targetLandPaymentId)
      ? Number(terrainQuickLinkState.targetLandPaymentId)
      : -Date.now();
    const optimisticRows = buildOptimisticReconciledLandPaymentRows({
      state: terrainQuickLinkState,
      landPaymentId: optimisticLandPaymentId,
      realFundingId,
    });
    const selectedIndicatorIds = getTerrainQuickLinkSelectedIndicatorIds(terrainQuickLinkState);
    const selectedSourceLandPaymentIds =
      getTerrainQuickLinkSelectedSourceLandPaymentIds(terrainQuickLinkState);
    const didApplyOptimisticQuickLink = !!onMutateData && optimisticRows.length > 0;
    const optimisticQuickLinkSnapshot = didApplyOptimisticQuickLink
      ? onMutateData?.((current) =>
          replaceReconciledLandPaymentRowsInGroupedData(
            current,
            {
              contractNodeId: contract.id,
              synthesesContractId: extractSynthesesContractId(contract),
            },
            optimisticLandPaymentId,
            selectedIndicatorIds,
            optimisticRows,
            selectedSourceLandPaymentIds,
          ),
        ) ?? null
      : null;
    const submittedQuickLinkState = terrainQuickLinkState;
    if (didApplyOptimisticQuickLink) {
      setTerrainQuickLinkState(null);
    }

    try {
      const savedReconcile = await reconcileTerrainPayloadToApi({
        realFundingId,
        landPaymentId: isPositiveId(terrainQuickLinkState.targetLandPaymentId)
          ? Number(terrainQuickLinkState.targetLandPaymentId)
          : null,
        estimatedIndicatorId: isPositiveId(terrainQuickLinkState.selectedEstimatedId)
          ? Number(terrainQuickLinkState.selectedEstimatedId)
          : null,
        measuredIndicatorId: isPositiveId(terrainQuickLinkState.selectedMeasuredId)
          ? Number(terrainQuickLinkState.selectedMeasuredId)
          : null,
        verifiedIndicatorId: isPositiveId(terrainQuickLinkState.selectedVerifiedId)
          ? Number(terrainQuickLinkState.selectedVerifiedId)
          : null,
        estimatedLandPaymentId: isPositiveId(
          terrainQuickLinkState.selectedEstimatedPayment?.landPaymentId,
        )
          ? Number(terrainQuickLinkState.selectedEstimatedPayment?.landPaymentId)
          : null,
        measuredLandPaymentId: isPositiveId(
          terrainQuickLinkState.selectedMeasuredPayment?.landPaymentId,
        )
          ? Number(terrainQuickLinkState.selectedMeasuredPayment?.landPaymentId)
          : null,
        verifiedLandPaymentId: isPositiveId(
          terrainQuickLinkState.selectedVerifiedPayment?.landPaymentId,
        )
          ? Number(terrainQuickLinkState.selectedVerifiedPayment?.landPaymentId)
          : null,
      });
      const savedLandPaymentId = Number(savedReconcile?.landPaymentId || 0);
      if (
        didApplyOptimisticQuickLink &&
        Number.isFinite(savedLandPaymentId) &&
        savedLandPaymentId > 0
      ) {
        const persistedRows = optimisticRows.map((row) => ({
          ...row,
          landPaymentId: savedLandPaymentId,
        }));
        onMutateData?.((current) =>
          replaceReconciledLandPaymentRowsInGroupedData(
            current,
            {
              contractNodeId: contract.id,
              synthesesContractId: extractSynthesesContractId(contract),
            },
            optimisticLandPaymentId,
            selectedIndicatorIds,
            persistedRows,
            selectedSourceLandPaymentIds,
          ),
        );
      }
      void refreshContracts();
      setTerrainQuickLinkState(null);
    } catch (error: unknown) {
      if (didApplyOptimisticQuickLink) {
        onRestoreData?.(optimisticQuickLinkSnapshot);
      }
      setTerrainQuickLinkState((current) =>
        (current ?? submittedQuickLinkState)
          ? {
              ...(current ?? submittedQuickLinkState),
              error:
                error instanceof Error
                  ? error.message
                  : "Erreur lors de la liaison rapide.",
            }
          : current,
      );
    } finally {
      setSavingTerrainQuickLink(false);
    }
  }, [
    contract,
    reconcileTerrainPayloadToApi,
    refreshContracts,
    onMutateData,
    onRestoreData,
    terrainQuickLinkState,
  ]);

  const handleOpenTerrainMove = useCallback(
    (
      row: ContractLineDisplayRow,
      payment: ProjectLinePaymentPreview,
      targetOptions: TerrainMoveTargetOption[],
    ) => {
      const landPaymentId = Number(payment.landPaymentId || 0);

      if (!Number.isFinite(landPaymentId) || landPaymentId <= 0) {
        setTerrainMoveError("Impossible de retrouver le versement à l'intervenant.");
        return;
      }

      setTerrainReconcileState(null);
      setTerrainReconcileError(null);
      setTerrainQuickLinkState(null);
      setTerrainMoveError(null);
      setTerrainPaymentEditorState(null);
      setTerrainPaymentEditorError(null);
      setTerrainMoveState({
        lineKey: row.key,
        paymentRowId: payment.id,
        landPaymentId,
        selectedTargetLineId: targetOptions[0]?.lineId ?? null,
        targetOptions,
      });
    },
    [],
  );

  const handleChangeTerrainMoveTarget = useCallback((targetLineId: number | null) => {
    setTerrainMoveError(null);
    setTerrainMoveState((current) => {
      if (!current) return current;

      return {
        ...current,
        selectedTargetLineId: targetLineId,
      };
    });
  }, []);

  const handleCancelTerrainMove = useCallback(() => {
    setTerrainMoveState(null);
    setTerrainMoveError(null);
  }, []);

  const handleConfirmTerrainMove = useCallback(async () => {
    if (!terrainMoveState) {
      return;
    }

    const landPaymentId = Number(terrainMoveState.landPaymentId || 0);
    const targetLineId = Number(terrainMoveState.selectedTargetLineId || 0);

    if (!Number.isFinite(landPaymentId) || landPaymentId <= 0) {
      setTerrainMoveError("Le versement à l'intervenant est introuvable.");
      return;
    }
    if (!Number.isFinite(targetLineId) || targetLineId <= 0) {
      setTerrainMoveError("Choisissez la ligne cible.");
      return;
    }

	    setMovingTerrainPayment(true);
	    setTerrainMoveError(null);
	    const didApplyOptimisticMove = !!onMutateData;
	    const optimisticMoveSnapshot = onMutateData?.((current) =>
	      moveLandPaymentRowsInGroupedData(current, landPaymentId, targetLineId),
	    ) ?? null;
	    setTerrainMoveState(null);

	    try {
	      await moveTerrainPayloadToApi({
	        landPaymentId,
	        targetLineId,
	      });
	      void refreshContracts();
	    } catch (error: unknown) {
	      if (didApplyOptimisticMove) {
	        onRestoreData?.(optimisticMoveSnapshot);
	      }
	      const message =
	        error instanceof Error
	          ? error.message
	          : "Erreur lors du déplacement du versement à l'intervenant.";
	      setTerrainMoveState(terrainMoveState);
	      setTerrainMoveError(message);
	      toast({
	        title: "Le déplacement du versement à l'intervenant a échoué.",
	        description: message,
	      });
	    } finally {
	      setMovingTerrainPayment(false);
	    }
	  }, [
	    moveTerrainPayloadToApi,
	    onMutateData,
	    onRestoreData,
	    refreshContracts,
	    terrainMoveState,
	  ]);

  const handleConfirmTerrainReconcile = useCallback(async () => {
    if (!terrainReconcileState) {
      return;
    }

    const estimatedIndicatorId = Number(terrainReconcileState.selectedEstimatedId || 0);
    const measuredIndicatorId = Number(terrainReconcileState.selectedMeasuredId || 0);
    const verifiedIndicatorId = Number(terrainReconcileState.selectedVerifiedId || 0);
    const landPaymentId = Number(terrainReconcileState.landPaymentId || 0);
    const landPaymentStatus = Number(terrainReconcileState.landPaymentStatus || 0);

    if (!Number.isFinite(landPaymentId) || landPaymentId <= 0) {
      if (!Number.isFinite(estimatedIndicatorId) || estimatedIndicatorId <= 0) {
        setTerrainReconcileError("Choisissez un versement à l'intervenant prévu.");
        return;
      }
      if (!Number.isFinite(measuredIndicatorId) || measuredIndicatorId <= 0) {
        setTerrainReconcileError("Choisissez un versement à l'intervenant réalisé.");
        return;
      }
    } else if (landPaymentStatus === 1) {
      if (!Number.isFinite(measuredIndicatorId) || measuredIndicatorId <= 0) {
        setTerrainReconcileError("Choisissez un versement à l'intervenant réalisé.");
        return;
      }
    } else if (landPaymentStatus === 2) {
      if (
        (!Number.isFinite(estimatedIndicatorId) || estimatedIndicatorId <= 0)
        && (!Number.isFinite(verifiedIndicatorId) || verifiedIndicatorId <= 0)
      ) {
        setTerrainReconcileError("Choisissez un versement à l'intervenant prévu et/ou certifié.");
        return;
      }
    } else if (landPaymentStatus === 3) {
      if (
        (!Number.isFinite(estimatedIndicatorId) || estimatedIndicatorId <= 0)
        && (!Number.isFinite(measuredIndicatorId) || measuredIndicatorId <= 0)
      ) {
        setTerrainReconcileError("Choisissez un versement à l'intervenant prévu et/ou réalisé.");
        return;
      }
    }

    setSavingTerrainReconcile(true);
    setTerrainReconcileError(null);

    try {
      await reconcileTerrainPayloadToApi({
        realFundingId: Number(terrainReconcileState.realFundingId || 0),
        landPaymentId:
          Number.isFinite(landPaymentId) && landPaymentId > 0 ? landPaymentId : null,
        estimatedIndicatorId:
          Number.isFinite(estimatedIndicatorId) && estimatedIndicatorId > 0
            ? estimatedIndicatorId
            : null,
        measuredIndicatorId:
          Number.isFinite(measuredIndicatorId) && measuredIndicatorId > 0
            ? measuredIndicatorId
            : null,
        verifiedIndicatorId:
          Number.isFinite(verifiedIndicatorId) && verifiedIndicatorId > 0
            ? verifiedIndicatorId
            : null,
      });
      await refreshContracts();
      setTerrainReconcileState(null);
    } catch (error: unknown) {
      setTerrainReconcileError(
        error instanceof Error ? error.message : "Erreur lors du rapprochement.",
      );
    } finally {
      setSavingTerrainReconcile(false);
    }
  }, [reconcileTerrainPayloadToApi, refreshContracts, terrainReconcileState]);

  const terrainCertifiedRecipientOptions = terrainCertifiedEditorState
    ? getRecipientOptionsForProject(terrainCertifiedEditorState.projectId)
    : [];
  const isLoadingTerrainCertifiedRecipients = terrainCertifiedEditorState
    ? !!loadingRecipientProjectIds[Number(terrainCertifiedEditorState.projectId || 0)]
    : false;

  return (
    <div
      className={cn(
        "relative overflow-hidden rounded-2xl border border-slate-200 bg-white",
        isEditing ? "border-emerald-400 ring-1 ring-emerald-200" : "",
        isSaving ? "border-amber-300 ring-1 ring-amber-100" : "",
      )}
    >
      <Dialog
        open={terrainCertifiedEditorState !== null}
        onOpenChange={(open) => {
          if (open || savingTerrainCertified) return;
          handleCancelTerrainCertifiedEditor();
        }}
      >
        <DialogContent className="sm:max-w-[640px]">
          <DialogTitle className="text-xl font-semibold text-slate-900">
            Certifier le versement à l&apos;intervenant
          </DialogTitle>

          {terrainCertifiedEditorState ? (
            <div className="space-y-4">
              <div className="rounded-2xl border border-orange-100 bg-orange-50/70 px-4 py-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-orange-700">
                  Suivi de performance ESG
                </div>
                <div className="mt-1 text-sm text-slate-700">
                  {terrainCertifiedEditorState.lineLabel}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {MONTH_COLUMNS.find(
                    (month) => month.index === terrainCertifiedEditorState.monthIndex,
                  )?.label || "Mois"}{" "}
                  {terrainCertifiedEditorState.year}
                </div>
              </div>

              {terrainCertifiedEditorError ? (
                <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  {terrainCertifiedEditorError}
                </div>
              ) : null}

              <div className="grid gap-3 md:grid-cols-2">
                <label className="space-y-1.5 text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Montant certifié €
                  </span>
                  <input
                    value={terrainCertifiedEditorState.amountEuro}
                    onChange={(event) => {
                      const nextValue = event.target.value;
                      if (
                        nextValue === "" ||
                        nextValue === "-" ||
                        /^\d*([,.]\d*)?$/.test(nextValue.replace(/\s/g, ""))
                      ) {
                        handleChangeTerrainCertifiedEditor({ amountEuro: nextValue });
                      }
                    }}
                    className={SYNTHESES_INPUT_CLASS}
                    placeholder="-"
                  />
                  <span className="block text-[11px] text-slate-500">
                    Laissez vide ou saisissez - pour supprimer la valeur.
                  </span>
                </label>

                <div className="space-y-1.5 text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Pièce jointe
                  </span>
                  <div className="rounded-xl border border-slate-200 bg-slate-50/80 px-3 py-2">
                    <div className="text-xs text-slate-600">
                      Preuve de vérification
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <input
                        type="file"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (!file) return;
                          void handleUploadTerrainCertifiedProof(file).catch((error: unknown) => {
                            setTerrainCertifiedEditorError(
                              error instanceof Error
                                ? error.message
                                : "Erreur lors de l'upload du justificatif.",
                            );
                          });
                          event.currentTarget.value = "";
                        }}
                        className="block max-w-full text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-white file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-50"
                      />
                      {terrainCertifiedEditorState.pjVerified?.id ? (
                        <div className="flex items-center gap-2">
                          <a
                            href={getTerrainMediaHref(terrainCertifiedEditorState.pjVerified)}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-medium text-emerald-700 underline underline-offset-2"
                          >
                            {getTerrainMediaLabel(terrainCertifiedEditorState.pjVerified)}
                          </a>
                          <button
                            type="button"
                            className="text-xs font-medium text-rose-600 hover:text-rose-700"
                            onClick={() =>
                              handleChangeTerrainCertifiedEditor({ pjVerified: null })
                            }
                          >
                            Retirer
                          </button>
                        </div>
                      ) : (
                        <span className="text-xs text-slate-500">
                          Aucun justificatif ajouté
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-2xl border border-sky-100 bg-sky-50/70 p-4">
                <label className="space-y-1.5 text-sm">
                  <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                    Intervenant destinataire
                  </span>
                  <select
                    value={
                      terrainCertifiedEditorState.recipientSubcontractorId
                        ? String(terrainCertifiedEditorState.recipientSubcontractorId)
                        : ""
                    }
                    onChange={(event) =>
                      handleChangeTerrainCertifiedEditor({
                        recipientSubcontractorId: event.target.value
                          ? Number(event.target.value)
                          : null,
                      })
                    }
                    className={SYNTHESES_INPUT_CLASS}
                    disabled={isLoadingTerrainCertifiedRecipients}
                  >
                    <option value="">
                      {isLoadingTerrainCertifiedRecipients
                        ? "Chargement des intervenants..."
                        : "Choisir un intervenant"}
                    </option>
                    {terrainCertifiedRecipientOptions.map((option) => (
                      <option
                        key={`terrain-certified-recipient-${option.id}`}
                        value={String(option.id)}
                      >
                        {option.label}
                      </option>
                    ))}
                  </select>
                  {terrainCertifiedRecipientOptions.length === 0 &&
                  !isLoadingTerrainCertifiedRecipients ? (
                    <span className="block text-xs text-slate-500">
                      Aucun intervenant local financé n'est renseigné sur ce projet.
                    </span>
                  ) : null}
                </label>
              </div>

              <div className="rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                <div className="text-sm font-semibold text-slate-900">
                  Lier au poste de dépenses
                  <span className="ml-1 text-red-500">*</span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">
                  Choisissez le poste du contrat sur lequel ce certifié doit
                  apparaître. C&apos;est l&apos;équivalent de la liaison faite
                  depuis le suivi de performance ESG.
                </p>

                <div className="mt-3 space-y-2">
                  {terrainCertifiedEditorState.targetLineOptions.length > 0 ? (
                    terrainCertifiedEditorState.targetLineOptions.map((option) => {
                      const isSelected =
                        terrainCertifiedEditorState.selectedLineKey === option.lineKey;

                      return (
                        <button
                          key={`certified-target-line-${option.lineKey}`}
                          type="button"
                          onClick={() =>
                            handleChangeTerrainCertifiedEditor({
                              selectedLineKey: option.lineKey,
                            })
                          }
                          className={cn(
                            "flex w-full items-center justify-between gap-3 rounded-xl border px-3 py-2 text-left text-sm transition",
                            isSelected
                              ? "border-emerald-400 bg-white text-slate-950 ring-1 ring-emerald-200"
                              : "border-emerald-100 bg-white/70 text-slate-700 hover:border-emerald-300 hover:bg-white",
                          )}
                          aria-pressed={isSelected}
                        >
                          <span className="min-w-0 truncate">{option.label}</span>
                          <span
                            className={cn(
                              "h-2.5 w-2.5 shrink-0 rounded-full border",
                              isSelected
                                ? "border-emerald-600 bg-emerald-600"
                                : "border-slate-300 bg-white",
                            )}
                          />
                        </button>
                      );
                    })
                  ) : (
                    <div className="rounded-xl border border-rose-100 bg-white px-3 py-2 text-xs text-rose-700">
                      Aucun poste de dépenses disponible pour ce projet.
                    </div>
                  )}
                </div>
              </div>

              <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
                <div className="text-sm font-semibold text-slate-900">
                  Associer d&apos;autres indicateurs à ce poste
                  <span className="ml-1 text-red-500">*</span>
                </div>
                <p className="mt-1 text-xs leading-relaxed text-slate-600">
                  Comme dans le suivi de performance ESG, un certifié doit être
                  rattaché à un prévu et à un réalisé déjà saisis. Ils peuvent
                  être sur un autre mois.
                </p>

                <div className="mt-3 grid gap-3 md:grid-cols-2">
                  <label className="space-y-1.5 text-sm">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Prévu
                    </span>
                    <select
                      value={terrainCertifiedEditorState.estimatedIndicatorId ?? ""}
                      onChange={(event) =>
                        handleChangeTerrainCertifiedEditor({
                          estimatedIndicatorId: event.target.value
                            ? Number(event.target.value)
                            : null,
                        })
                      }
                      className={SYNTHESES_INPUT_CLASS}
                    >
                      <option value="">Choisir un prévu</option>
                      {terrainCertifiedEditorState.estimatedOptions.map((option) => (
                        <option
                          key={`certified-estimated-${option.id}`}
                          value={option.id}
                          disabled={option.disabled}
                        >
                          {option.label}
                          {option.disabled ? " - déjà lié à un autre certifié" : ""}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="space-y-1.5 text-sm">
                    <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                      Réalisé
                    </span>
                    <select
                      value={terrainCertifiedEditorState.measuredIndicatorId ?? ""}
                      onChange={(event) =>
                        handleChangeTerrainCertifiedEditor({
                          measuredIndicatorId: event.target.value
                            ? Number(event.target.value)
                            : null,
                        })
                      }
                      className={SYNTHESES_INPUT_CLASS}
                    >
                      <option value="">Choisir un réalisé</option>
                      {terrainCertifiedEditorState.measuredOptions.map((option) => (
                        <option
                          key={`certified-measured-${option.id}`}
                          value={option.id}
                          disabled={option.disabled}
                        >
                          {option.label}
                          {option.disabled ? " - déjà lié à un autre certifié" : ""}
                        </option>
                      ))}
                    </select>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  className={SYNTHESES_SECONDARY_BUTTON_CLASS}
                  onClick={handleCancelTerrainCertifiedEditor}
                  disabled={savingTerrainCertified}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className={SYNTHESES_PRIMARY_BUTTON_CLASS}
                  onClick={handleConfirmTerrainCertifiedEditor}
                  disabled={savingTerrainCertified}
                >
                  {savingTerrainCertified ? "Enregistrement..." : "Sauvegarder"}
                </button>
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <div className="px-4 pb-3 pt-4">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
          <div
            className={cn(
              "min-w-0 flex-1 space-y-2 text-left",
              collapsible ? "cursor-pointer" : "",
            )}
            {...(
              collapsible
                ? {
                    role: "button",
                    tabIndex: 0,
                    onClick: onToggleExpanded,
                    onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
                      if (event.key !== "Enter" && event.key !== " ") return;
                      event.preventDefault();
                      onToggleExpanded?.();
                    },
                    "aria-expanded": isExpanded,
                  }
                : {}
            )}
          >
            <div className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                Contrat
              </div>
              <div className="flex flex-wrap items-center gap-2">
                {collapsible ? (
                  <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-slate-500">
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 transition-transform",
                        isExpanded ? "rotate-0" : "-rotate-90",
                      )}
                    />
                  </span>
                ) : null}
                <div className="text-lg font-semibold text-slate-900">
                  {contractLabel}
                </div>
                {contract.isVerdeo ? (
                  <>
                    <span className="rounded-full border border-slate-300 bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-700">
                      Lecture seule
                    </span>
                    <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                      Via Verdeo
                    </span>
                  </>
                ) : null}
                {isEditing ? (
                  <span className="rounded-full border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                    En cours de modification
                  </span>
                ) : null}
                {isSaving ? (
                  <span className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    Sauvegarde en cours
                  </span>
                ) : null}
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
              <div className="inline-flex items-center gap-1.5">
                <span className="font-medium text-slate-700">Date du contrat</span>
                <span>{formatDate(contract.contractDate)}</span>
                {contract.dateAmountFallback ? (
                  <TooltipProvider delayDuration={120}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span
                          className="inline-flex cursor-help items-center text-amber-700"
                          aria-label="Date de secours utilisée"
                        >
                          <AlertTriangle className="h-3.5 w-3.5" />
                        </span>
                      </TooltipTrigger>
                      <TooltipContent
                        side="top"
                        align="start"
                        className="max-w-[280px] text-left text-xs leading-relaxed"
                      >
                        Date de secours utilisee: la vraie date du contrat
                        n&apos;est pas encore renseignee. Merci de renseigner la
                        date reelle du contrat pour figer le taux de change sur la
                        bonne date.
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                ) : null}
              </div>
            </div>
          </div>

          {canManage ? (
            <div className="flex shrink-0 flex-wrap items-center gap-2 xl:justify-end">
              <button
                type="button"
                className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-[11px] text-slate-700 hover:bg-slate-50"
                onClick={() => onEdit(contract, index)}
                disabled={isDeleting || actionsDisabled || isSaving}
              >
                <Pencil className="h-3.5 w-3.5" />
                Modifier
              </button>
              <button
                type="button"
                className={cn(
                  "inline-flex items-center gap-1 rounded-md border px-2.5 py-1.5 text-[11px]",
                  isDeleting
                    ? "cursor-not-allowed border-slate-300 bg-slate-100 text-slate-400"
                    : "border-rose-300 bg-white text-rose-700 hover:bg-rose-50",
                )}
                onClick={() => onDelete(contract)}
                disabled={isDeleting || actionsDisabled || isSaving}
              >
                <Trash2 className="h-3.5 w-3.5" />
                {isDeleting ? "Suppression..." : "Supprimer"}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {isExpanded ? (
        <div className="space-y-4 border-t border-slate-100 px-4 pb-4 pt-4">
        {allYears.length > 1 ? (
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-slate-50/70 p-2">
            <div className="flex min-w-max gap-2">
              {allYears.map((year) => (
                <button
                  key={`${contract.id}-year-${year}`}
                  type="button"
                  onClick={() => onSelectYear(year)}
                  className={cn(
                    "rounded-lg border px-3 py-1.5 text-xs font-medium transition",
                    selectedYear === year
                      ? "border-emerald-600 bg-emerald-600 text-white shadow-sm"
                      : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50",
                  )}
                >
                  {year}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        <div className="grid gap-2 md:grid-cols-4">
          <TopAmountCell
            title="Montant €"
            value={formatEuro(displayContractAmountEuro)}
            subtitle={formatLocalAmount(contract.amountLocal, contract)}
          />
          <TopAmountCell
            title="Versements prévus"
            value={formatEuro(summaryExpectedEuro)}
            subtitle="Échéances du contrat"
            tone="teal"
          />
          <TopAmountCell
            title="Versements reçus"
            value={formatEuro(summaryReceivedEuro)}
            subtitle="Encaissements du contrat"
            tone="violet"
          />
          <TopAmountCell
            title="Versements à l'intervenant"
            value={formatEuro(summaryLandEuro)}
            subtitle="Versements réalisés du contrat"
            tone="rose"
          />
        </div>

        <ContractPaymentsOverview
          expectedPayments={contractExpectedPayments}
          paidPayments={contractPaidPayments}
          fallbackYear={selectedYear}
        />

        <div className="rounded-xl border border-slate-200 bg-slate-100/80 p-3">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-600">
            Notes
          </div>
          <div className="text-xs text-slate-700">
            {String(contract.notes || "").trim() ? (
              <div className="whitespace-pre-wrap rounded-md border border-slate-200 bg-white p-2 text-slate-800">
                {contract.notes}
              </div>
            ) : null}
          </div>
        </div>

        <div className="space-y-3">
        {years.map((year) => {
          const yearLineRows = buildContractLineRowsForYear(contract, year);
          const yearProjectRows = buildContractProjectRowsFromLines(yearLineRows);
          const yearProjectRowsCount = yearProjectRows.length;
          const yearSelectionKey = String(year);
          const selectedYearProjectRow =
            yearProjectRows.find(
              (row) => row.key === selectedLineKeyByYear[yearSelectionKey],
            ) || yearProjectRows[0] || null;
          const selectedYearLineRow = selectedYearProjectRow?.lines[0] || null;

          return (
            <div
              key={`${contract.id}-${year}`}
              className="overflow-hidden rounded-xl border border-sky-100 bg-sky-50/40"
            >
              <div className="flex items-center justify-between border-b border-sky-100 bg-gradient-to-r from-sky-50/90 via-white to-white px-3 py-2.5">
                <div className="text-sm font-semibold text-slate-900">{year}</div>
              </div>

              <div className="p-3">
                {yearProjectRowsCount > 0 ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Projets dans {year}
                      </div>
                    </div>

                    <CustomHorizontalScroller compact>
                      <div className="flex min-w-max gap-3 pb-1">
                        {yearProjectRows.map((row) => {
                          const projectVisual =
                            Number(row.projectId || 0) > 0
                              ? projectVisualCatalog.get(Number(row.projectId || 0)) || null
                              : null;
                          const isSelectedProjectRow =
                            selectedYearProjectRow?.key === row.key;

                          return (
                            <div
                              key={`${row.key}-year-card-${year}`}
                              className="relative flex w-[220px] shrink-0 pb-3"
                            >
                              <div
                                role="button"
                                tabIndex={0}
                                onClick={() =>
                                  setSelectedLineKeyByYear((current) => ({
                                    ...current,
                                    [yearSelectionKey]: row.key,
                                  }))
                                }
                                onKeyDown={(event) => {
                                  if (event.key !== "Enter" && event.key !== " ") return;
                                  event.preventDefault();
                                  setSelectedLineKeyByYear((current) => ({
                                    ...current,
                                    [yearSelectionKey]: row.key,
                                  }));
                                }}
                                className={cn(
                                  "flex h-full w-full cursor-pointer flex-col overflow-hidden rounded-[15px] border bg-white text-left shadow-sm transition focus:outline-none focus:ring-2 focus:ring-emerald-200",
                                  isSelectedProjectRow
                                    ? "border-emerald-300 ring-2 ring-emerald-200"
                                    : "border-slate-200 hover:border-slate-300",
                                )}
                              >
                                <div className="relative h-[126px] overflow-hidden bg-slate-700">
                                  <img
                                    src={resolveProjectPictureHref(projectVisual?.pictureUri)}
                                    alt={row.projectName}
                                    className="size-full object-cover"
                                  />
                                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/10 to-transparent" />
                                  <img
                                    src="/folder.svg"
                                    alt=""
                                    className="absolute left-3 top-3 w-5 opacity-95"
                                  />
                                  <div className="absolute bottom-3 right-3 rounded-xl border border-white/80 bg-white/95 px-2.5 py-1.5 text-right shadow-sm backdrop-blur-sm">
                                    <div className="text-[9px] font-medium uppercase tracking-wide text-slate-500">
                                      Part du contrat
                                    </div>
                                    <div className="text-[11px] font-semibold text-slate-900">
                                      {formatEuro(row.amountEuro)}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex min-h-[78px] flex-1 flex-col justify-between space-y-1 px-3 py-3">
                                  <div className="flex items-start justify-between gap-2">
                                    <div
                                      title={row.projectName}
                                      className="line-clamp-2 text-sm font-medium leading-5 text-slate-900"
                                    >
                                      {row.projectName}
                                    </div>
                                    {getProjectDashboardHref(row.projectId) ? (
                                      <a
                                        href={getProjectDashboardHref(row.projectId) || undefined}
                                        target="_blank"
                                        rel="noreferrer noopener"
                                        onClick={(event) => event.stopPropagation()}
                                        className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
                                        title="Ouvrir le projet dans un nouvel onglet"
                                      >
                                        <ExternalLink className="h-3.5 w-3.5" />
                                      </a>
                                    ) : null}
                                  </div>
                                  <div className="text-xs text-slate-500">
                                    {row.lines.length} ligne{row.lines.length > 1 ? "s" : ""} de contrat
                                  </div>
                                </div>
                              </div>
                              {isSelectedProjectRow ? (
                                <div className="pointer-events-none absolute bottom-[5px] left-1/2 z-10 h-4 w-4 -translate-x-1/2 rotate-45 rounded-[2px] border-b border-r border-emerald-300 bg-white" />
                              ) : null}
                            </div>
                          );
                        })}
                      </div>
                    </CustomHorizontalScroller>
                  </div>
                ) : null}

                {selectedYearProjectRow ? (
                  <div className="space-y-3">
                    <div className="rounded-2xl border border-slate-200/80 bg-slate-50/80 p-3">
                      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                        <div>
                          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                            Liste des postes de dépenses
                          </div>
                          <div className="mt-1 text-sm font-semibold text-slate-900">
                            {selectedYearProjectRow.projectName}
                          </div>
                        </div>
                        <div className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600">
                          {selectedYearProjectRow.lines.length} poste{selectedYearProjectRow.lines.length > 1 ? "s" : ""}
                        </div>
                      </div>

                      <div className="space-y-2">
                        {selectedYearProjectRow.lines.map((row, lineIndex) => {
                          const lineLabel =
                            String(row.title || "").trim() ||
                            `Poste ${lineIndex + 1}`;

                          return (
                            <div key={row.key} className="space-y-2">
                              <div
                                className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:flex-row sm:items-center sm:justify-between"
                              >
                                <div className="min-w-0">
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                    Poste de dépense
                                  </div>
                                  <div
                                    title={lineLabel}
                                    className="mt-0.5 truncate text-sm font-semibold text-slate-900"
                                  >
                                    {lineLabel}
                                  </div>
                                </div>

                              <div className="flex shrink-0 flex-wrap items-center gap-2 sm:justify-end">
                                <div className="rounded-lg border border-sky-100 bg-sky-50/80 px-3 py-1.5 text-right">
                                  <div className="text-[10px] font-semibold uppercase tracking-wide text-sky-700/80">
                                    Montant
                                  </div>
                                  <div className="text-sm font-semibold text-sky-950">
                                    {formatEuro(row.amountEuro)}
                                  </div>
                                </div>

                              </div>
                            </div>
                          </div>
                        );
                      })}
	                    </div>
	                  </div>

                    {(() => {
                      const projectLandPayments = selectedYearProjectRow.lines
                        .flatMap((lineRow) =>
                          buildPaymentsForLine(
                            contract.landPayments,
                            lineRow.realFundingId,
                            `${lineRow.key}-project-land`,
                            {
                              synthesesContractId: lineRow.synthesesContractId,
                              synthesesContractLineId: lineRow.lineId,
                              realFundingId: lineRow.realFundingId,
                            },
                          ),
	                        )
	                        .sort((a, b) => String(a.at || "").localeCompare(String(b.at || "")));
	                      const fallbackTerrainLineRow = selectedYearProjectRow.lines[0] || null;
	                      const resolveTerrainLinePayments = (lineRow: ContractLineDisplayRow) =>
	                        buildPaymentsForLine(
	                          contract.landPayments,
	                          lineRow.realFundingId,
	                          `${lineRow.key}-project-land-edit`,
	                          {
	                            synthesesContractId: lineRow.synthesesContractId,
	                            synthesesContractLineId: lineRow.lineId,
	                            realFundingId: lineRow.realFundingId,
	                          },
	                        ).sort((a, b) =>
	                          String(a.at || "").localeCompare(String(b.at || "")),
	                        );
	                      const resolveTerrainLineRow = (
	                        payment: ProjectLinePaymentPreview | null,
	                      ) =>
	                        findTerrainLineRowForPayment(
	                          selectedYearProjectRow.lines,
	                          payment,
	                        ) || fallbackTerrainLineRow;
	                      const quickLinkProjectKey = `${contract.id}-${selectedYearProjectRow.key}-terrain-quick-link`;
	                      const isTerrainQuickLinkActive =
	                        terrainQuickLinkState?.projectKey === quickLinkProjectKey;
	                      const projectRecipientOptions = getRecipientOptionsForProject(
	                        selectedYearProjectRow.projectId,
	                      );
	                      const isProjectTerrainMoveActive = selectedYearProjectRow.lines.some(
	                        (lineRow) => terrainMoveState?.lineKey === lineRow.key,
	                      );
	                      const activeProjectTerrainMoveRows = isProjectTerrainMoveActive
	                        ? projectLandPayments.filter((payment) =>
	                            isTerrainMoveIncludedRow(payment, terrainMoveState),
	                          )
	                        : [];
	                      const activeProjectTerrainMoveSummary = [1, 2, 3]
	                        .map((status) => {
	                          const dedupedRows = dedupePaymentPreviewsByMonthYear(
	                            filterPaymentPreviewsByStatus(
	                              activeProjectTerrainMoveRows,
	                              status,
	                            ),
	                          );

	                          return {
	                            status,
	                            label: getLandStatusLabel(status),
	                            total: sumPaymentPreviews(dedupedRows),
	                          };
	                        })
	                        .filter((item) => item.total > 0);
	                      const getTimelineTerrainMoveTargetOptions = (
	                        payment: ProjectLinePaymentPreview,
	                      ) => {
	                        const sourceLineRow = resolveTerrainLineRow(payment);
	                        return buildTerrainMoveTargetOptions(
	                          financeurContracts,
	                          sourceLineRow,
	                        );
	                      };
	                      const canMoveTimelineTerrainPayment = (
	                        payment: ProjectLinePaymentPreview,
	                      ) =>
	                        !isTerrainQuickLinkActive
	                        && contract.isVerdeo !== true
	                        && canMoveTimelineLandPaymentRow(
	                          payment,
	                          getTimelineTerrainMoveTargetOptions(payment),
	                        );
	                      const handleMoveTimelineTerrainPayment = (
	                        payment: ProjectLinePaymentPreview,
	                      ) => {
	                        if (isTerrainMoveTargetRow(payment, terrainMoveState)) {
	                          handleCancelTerrainMove();
	                          return;
	                        }

	                        const sourceLineRow = resolveTerrainLineRow(payment);
	                        if (!sourceLineRow) {
	                          setTerrainMoveError("Impossible de retrouver la ligne source.");
	                          return;
	                        }

	                        handleOpenTerrainMove(
	                          sourceLineRow,
	                          payment,
	                          buildTerrainMoveTargetOptions(
	                            financeurContracts,
	                            sourceLineRow,
	                          ),
	                        );
	                      };

	                      return (
	                        <ProjectLandPaymentsTimelinePanel
	                          landPayments={projectLandPayments}
	                          projectId={selectedYearProjectRow.projectId}
	                          recipientOptions={projectRecipientOptions}
	                          fallbackYear={selectedYear ?? fallbackTerrainLineRow?.year ?? year}
	                          editable={!!fallbackTerrainLineRow && !isTerrainQuickLinkActive}
		                          disabled={
		                            actionsDisabled ||
		                            !canManage
		                          }
		                          savingCellKeys={savingTerrainTimelineCellKeys}
	                          cellKeyPrefix={fallbackTerrainLineRow?.key || "terrain-timeline"}
		                          error={terrainTimelineError}
		                          quickLinkMode={isTerrainQuickLinkActive}
		                          quickLinkState={terrainQuickLinkState}
		                          canMovePayment={canMoveTimelineTerrainPayment}
		                          isMovePaymentActive={(payment) =>
		                            isTerrainMoveTargetRow(payment, terrainMoveState)
		                          }
		                          onEnsureRecipientOptions={loadRecipientOptionsForProject}
	                          onPickQuickLinkCell={(payload) =>
	                            handlePickTerrainQuickLinkCell(
	                              quickLinkProjectKey,
	                              payload,
	                              projectLandPayments,
	                            )
	                          }
	                          onCommitCell={(payload) => {
	                            const targetTerrainLineRow = resolveTerrainLineRow(payload.payment);
	                            if (!targetTerrainLineRow) return undefined;

	                            return handleCommitTerrainTimelineCell(
	                              targetTerrainLineRow,
	                              resolveTerrainLinePayments(targetTerrainLineRow),
	                              payload,
	                            );
	                          }}
		                          onOpenCertifiedCell={(payload) => {
		                            const targetTerrainLineRow = resolveTerrainLineRow(payload.payment);
		                            if (!targetTerrainLineRow) return;

	                            handleOpenTerrainCertifiedTimelineEditor(
	                              targetTerrainLineRow,
	                              projectLandPayments,
	                              payload,
		                              selectedYearProjectRow.lines,
		                            );
		                          }}
		                          onMovePayment={handleMoveTimelineTerrainPayment}
		                        >
		                          <>
		                          <div className="rounded-2xl border border-slate-200 bg-white/85 px-3 py-2">
		                            <div className="flex flex-wrap items-center justify-between gap-2">
		                              <div>
	                                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">
	                                  Liaison rapide
	                                </div>
	                                <div className="mt-0.5 text-xs text-slate-600">
	                                  Sélectionnez un prévu, un mesuré et si besoin un certifié directement sur la timeline.
	                                </div>
	                              </div>
	                              <button
	                                type="button"
	                                className={cn(
	                                  "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition",
	                                  isTerrainQuickLinkActive
	                                    ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
	                                    : "border-verdeoblue/25 bg-verdeoblue/10 text-verdeoblue hover:bg-verdeoblue/15",
	                                )}
	                                onClick={() =>
	                                  isTerrainQuickLinkActive
	                                    ? handleCancelTerrainQuickLink()
	                                    : handleStartTerrainQuickLink(
	                                        quickLinkProjectKey,
	                                        selectedYearProjectRow.projectName,
	                                      )
	                                }
	                                disabled={actionsDisabled || !canManage || projectLandPayments.length === 0}
	                              >
	                                {isTerrainQuickLinkActive ? (
	                                  <>
	                                    <X className="h-3.5 w-3.5" />
	                                    Annuler la liaison
	                                  </>
	                                ) : (
	                                  <>
	                                    <Link2 className="h-3.5 w-3.5" />
	                                    Lier rapidement
	                                  </>
	                                )}
	                              </button>
	                            </div>

	                            {isTerrainQuickLinkActive ? (
	                              <div className="mt-3 rounded-xl border border-verdeoblue/20 bg-verdeoblue/5 px-3 py-2">
	                                <div className="flex flex-wrap items-center justify-between gap-2">
	                                  <div className="text-xs text-slate-700">
	                                    {getTerrainQuickLinkSummary(terrainQuickLinkState)}
	                                  </div>
	                                  <div className="flex flex-wrap items-center gap-2">
	                                    <button
	                                      type="button"
	                                      className={SYNTHESES_SECONDARY_BUTTON_CLASS}
	                                      onClick={handleCancelTerrainQuickLink}
	                                      disabled={savingTerrainQuickLink}
	                                    >
	                                      Annuler
	                                    </button>
	                                    <button
	                                      type="button"
	                                      className={SYNTHESES_PRIMARY_BUTTON_CLASS}
	                                      onClick={handleConfirmTerrainQuickLink}
	                                      disabled={
	                                        savingTerrainQuickLink ||
	                                        !canSubmitTerrainQuickLink(terrainQuickLinkState)
	                                      }
	                                    >
	                                      {savingTerrainQuickLink ? "Liaison..." : "Valider la liaison"}
	                                    </button>
	                                  </div>
	                                </div>
	                                {terrainQuickLinkState?.error ? (
	                                  <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
	                                    {terrainQuickLinkState.error}
	                                  </div>
	                                ) : null}
		                              </div>
		                            ) : null}
		                          </div>
		                          {isProjectTerrainMoveActive ? (
		                            <TerrainMovePanel
		                              terrainMoveState={terrainMoveState}
		                              terrainMoveError={terrainMoveError}
		                              movingTerrainPayment={movingTerrainPayment}
		                              activeTerrainMoveSummary={activeProjectTerrainMoveSummary}
		                              onCancel={handleCancelTerrainMove}
		                              onChangeTarget={handleChangeTerrainMoveTarget}
		                              onConfirm={handleConfirmTerrainMove}
		                            />
		                          ) : null}
		                          </>
		                        </ProjectLandPaymentsTimelinePanel>
	                      );
	                    })()}
	                  </div>
	                ) : null}

                {selectedYearLineRow ? (
                  <div className="hidden">
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        Détail du projet sélectionné
                      </div>
                      {canMoveLines && !!selectedYearLineRow.lineId ? (
                        <button
                          type="button"
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition",
                            movePendingLineKey === selectedYearLineRow.key
                              ? "cursor-not-allowed border-slate-300 bg-slate-100 text-slate-400"
                              : activeLineMove?.lineKey === selectedYearLineRow.key &&
                                  activeLineMove?.lineId === selectedYearLineRow.lineId
                                ? "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
                                : "border-amber-200 bg-white text-amber-800 hover:bg-amber-50",
                          )}
                          onClick={() => {
                            if (
                              activeLineMove?.lineKey === selectedYearLineRow.key &&
                              activeLineMove?.lineId === selectedYearLineRow.lineId
                            ) {
                              onCancelMoveLine();
                              return;
                            }

                            onStartMoveLine(contract, index, selectedYearLineRow);
                            scrollToContractMovePanel(selectedYearLineRow.key);
                          }}
                          disabled={movePendingLineKey === selectedYearLineRow.key}
                        >
                          <ArrowRightLeft className="h-3.5 w-3.5" />
                          {activeLineMove?.lineKey === selectedYearLineRow.key &&
                          activeLineMove?.lineId === selectedYearLineRow.lineId
                            ? "Annuler le déplacement"
                            : "Déplacer dans un autre contrat"}
                        </button>
                      ) : null}
                    </div>
                    <div className="space-y-2">
                    {(() => {
                    const row = selectedYearLineRow;
                    const isMovingThisRow =
                      activeLineMove?.lineKey === row.key &&
                      activeLineMove?.lineId === row.lineId;
                    const isSubmittingThisRow = movePendingLineKey === row.key;
                    const projectedExpectedPayments = buildPaymentsForLine(
                      contract.expectedPayments,
                      row.realFundingId,
                      `${row.key}-expected`,
                      {
                        synthesesContractId: row.synthesesContractId,
                        synthesesContractLineId: row.lineId,
                      },
                    );
                    const projectedPaidPayments = buildPaymentsForLine(
                      contract.paidPayments,
                      row.realFundingId,
                      `${row.key}-paid`,
                      {
                        synthesesContractId: row.synthesesContractId,
                        synthesesContractLineId: row.lineId,
                      },
                    );
                    const projectedLandPayments = buildPaymentsForLine(
                      contract.landPayments,
                      row.realFundingId,
                      `${row.key}-land`,
                      {
                        synthesesContractId: row.synthesesContractId,
                        synthesesContractLineId: row.lineId,
                      },
                    );
                    const landPaymentsPlanned = dedupePaymentPreviewsByMonthYear(
                      filterPaymentPreviewsByStatus(projectedLandPayments, 1),
                    );
                    const landPaymentsRealized = dedupePaymentPreviewsByMonthYear(
                      filterPaymentPreviewsByStatus(projectedLandPayments, 2),
                    );
                    const landPaymentsCertified = dedupePaymentPreviewsByMonthYear(
                      filterPaymentPreviewsByStatus(projectedLandPayments, 3),
                    );
                    const terrainReconcilePlannedRows = landPaymentsPlanned.filter(
                      (payment) =>
                        payment.source === "project_indicator"
                        && Number(payment.projectIndicatorId || 0) > 0,
                    );
                    const terrainReconcileRealizedRows = landPaymentsRealized.filter(
                      (payment) =>
                        payment.source === "project_indicator"
                        && Number(payment.projectIndicatorId || 0) > 0,
                    );
                    const terrainReconcileCertifiedRows = landPaymentsCertified.filter(
                      (payment) =>
                        payment.source === "project_indicator"
                        && Number(payment.projectIndicatorId || 0) > 0,
                    );
                    const canReconcileTerrain =
                      terrainReconcilePlannedRows.length > 0
                      && terrainReconcileRealizedRows.length > 0;
                    const isTerrainReconcileActive =
                      terrainReconcileState?.lineKey === row.key;
                    const terrainMoveTargetOptions = Array.from(
                      new Map(
                        (financeurContracts || [])
                          .flatMap((targetContract) =>
                            (Array.isArray(targetContract.lineRows)
                              ? targetContract.lineRows
                              : [])
                              .map((targetLine) => {
                                const targetLineId = Number(targetLine.lineId || 0);
                                const targetProjectId = Number(targetLine.projectId || 0);
                                const sourceProjectId = Number(row.projectId || 0);
                                const targetContractLabel =
                                  toTrimmedText(targetContract.title) || "Contrat";

                                return {
                                  key: targetLineId,
                                  projectId: targetProjectId,
                                  sourceProjectId,
                                  isVerdeo: targetContract.isVerdeo === true,
                                  value: {
                                    lineId: targetLineId,
                                    label: [
                                      targetContractLabel,
                                      formatEuro(Number(targetLine.amountEuro || 0)),
                                    ]
                                      .filter(Boolean)
                                      .join(" • "),
                                  } satisfies TerrainMoveTargetOption,
                                };
                              }),
                          )
                          .filter(
                            (option) =>
                              option.key > 0
                              && option.key !== Number(row.lineId || 0)
                              && option.projectId > 0
                              && option.projectId === option.sourceProjectId
                              && option.isVerdeo !== true
                          )
                          .map((option) => [option.key, option.value]),
                      ).values(),
                    );
                    const isTerrainMoveActive =
                      terrainMoveState?.lineKey === row.key;
                    const activeTerrainMoveRows = isTerrainMoveActive
                      ? projectedLandPayments.filter((payment) =>
                          isTerrainMoveIncludedRow(payment, terrainMoveState),
                        )
                      : [];
                    const activeTerrainMoveSummary = [1, 2, 3]
                      .map((status) => {
                        const dedupedRows = dedupePaymentPreviewsByMonthYear(
                          filterPaymentPreviewsByStatus(activeTerrainMoveRows, status),
                        );

                        return {
                          status,
                          label: getLandStatusLabel(status),
                          total: sumPaymentPreviews(dedupedRows),
                        };
                      })
                      .filter((item) => item.total > 0);
                    const selectedEstimatedId = isTerrainReconcileActive
                      ? terrainReconcileState?.selectedEstimatedId ?? null
                      : null;
                    const selectedMeasuredId = isTerrainReconcileActive
                      ? terrainReconcileState?.selectedMeasuredId ?? null
                      : null;
                    const selectedVerifiedId = isTerrainReconcileActive
                      ? terrainReconcileState?.selectedVerifiedId ?? null
                      : null;
                    const projectedExpectedTotal = projectedExpectedPayments.reduce(
                      (sum, payment) => sum + payment.amountEuro,
                      0,
                    );
                    const projectedPaidTotal = projectedPaidPayments.reduce(
                      (sum, payment) => sum + payment.amountEuro,
                      0,
                    );
                    const landPlannedTotal = sumPaymentPreviews(landPaymentsPlanned);
                    const landRealizedTotal = sumPaymentPreviews(landPaymentsRealized);
                    const landCertifiedTotal = sumPaymentPreviews(landPaymentsCertified);
                    const isTerrainPaymentEditorActive =
                      terrainPaymentEditorState?.lineKey === row.key;
                    const isEditingExistingTerrainPayment =
                      isTerrainPaymentEditorActive
                      && isPositiveId(terrainPaymentEditorState?.landPaymentId);
                    const terrainPaymentRecipientOptions = isTerrainPaymentEditorActive
                      ? getRecipientOptionsForProject(terrainPaymentEditorState?.projectId)
                      : [];
                    const isLoadingTerrainPaymentRecipients = isTerrainPaymentEditorActive
                      && !!loadingRecipientProjectIds[
                        Number(terrainPaymentEditorState?.projectId || 0)
                      ];

                    return (
                      <div
                        key={row.key}
                        className="space-y-3"
                      >
                        {projectedExpectedPayments.length > 0 ||
                        projectedPaidPayments.length > 0 ||
                        projectedLandPayments.length > 0 ? (
                          <div className="space-y-3">
                                <div className="grid gap-2 lg:grid-cols-2">
                                  <div className="rounded-xl border border-[#a1560b]/25 bg-[#a1560b]/10 p-2.5">
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#8b4a09]">
                                        Versements prévus
                                      </div>
                                      <div className="text-xs font-semibold text-[#7a4108]">
                                        {formatEuro(projectedExpectedTotal)}
                                      </div>
                                    </div>
                                    <div className="mt-2 space-y-1.5">
                                      {projectedExpectedPayments.length > 0 ? (
                                        projectedExpectedPayments.map((payment) => (
                                          <div
                                            key={payment.id}
                                            className="flex items-center justify-between gap-2 rounded-md border border-[#a1560b]/20 bg-white/80 px-2 py-1"
                                          >
                                            <span className="text-[11px] text-slate-600">
                                              {formatDate(payment.at)}
                                            </span>
                                            <span className="text-[11px] font-medium text-slate-900">
                                              {formatEuro(payment.amountEuro)}
                                            </span>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="text-[11px] text-slate-500">
                                          Aucun versement prévu.
                                        </div>
                                      )}
                                    </div>
                                  </div>

                                  <div className="rounded-xl border border-[#425e1a]/25 bg-[#425e1a]/10 p-2.5">
                                    <div className="flex items-center justify-between gap-2">
                                      <div className="text-[11px] font-semibold uppercase tracking-wide text-[#425e1a]">
                                        Versements reçus
                                      </div>
                                      <div className="text-xs font-semibold text-[#344816]">
                                        {formatEuro(projectedPaidTotal)}
                                      </div>
                                    </div>
                                    <div className="mt-2 space-y-1.5">
                                      {projectedPaidPayments.length > 0 ? (
                                        projectedPaidPayments.map((payment) => (
                                          <div
                                            key={payment.id}
                                            className="flex items-center justify-between gap-2 rounded-md border border-[#425e1a]/20 bg-white/80 px-2 py-1"
                                          >
                                            <span className="text-[11px] text-slate-600">
                                              {formatDate(payment.at)}
                                            </span>
                                            <span className="text-[11px] font-medium text-slate-900">
                                              {formatEuro(payment.amountEuro)}
                                            </span>
                                          </div>
                                        ))
                                      ) : (
                                        <div className="text-[11px] text-slate-500">
                                          Aucun versement reçu.
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <div className="rounded-xl border border-verdeoblue/25 bg-verdeoblue/8 p-3">
                                  <div className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5 text-[11px] tracking-wide text-slate-800">
                                      <span>Versements à l'intervenant</span>
                                      {canReconcileTerrain ? (
                                        <button
                                          type="button"
                                          className="ml-1 rounded-full border border-verdeoblue/25 bg-white px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-slate-700 hover:bg-verdeoblue/10"
                                          onClick={() =>
                                            isTerrainReconcileActive
                                              ? handleCancelTerrainReconcile()
                                              : handleOpenTerrainReconcile(
                                                  row,
                                                  terrainReconcilePlannedRows,
                                                  terrainReconcileRealizedRows,
                                                  terrainReconcileCertifiedRows,
                                                )
                                          }
                                        >
                                          {isTerrainReconcileActive ? "Annuler" : "Lier"}
                                        </button>
                                      ) : null}
                                      <Tooltip>
                                        <TooltipTrigger asChild>
                                          <span className="inline-flex h-4 w-4 cursor-help items-center justify-center rounded-full text-slate-400 hover:text-slate-600">
                                            <Info className="h-3 w-3" />
                                          </span>
                                        </TooltipTrigger>
                                        <TooltipContent
                                          side="top"
                                          align="start"
                                          className="max-w-[320px] text-left text-xs leading-relaxed"
                                        >
                                          <div className="space-y-2">
                                            <p>Ce bloc regroupe les versements à l'intervenant rattachés à cette ligne de contrat.</p>
                                            <ul className="list-disc space-y-1 pl-4">
                                              <li>
                                                Bordure bleue : information issue du suivi
                                                de performance ESG, pas encore consolidée
                                                dans une ligne terrain complète.
                                              </li>
                                              <li>
                                                Bordure verte : ligne déjà créée et gérée
                                                dans le suivi de financements.
                                              </li>
                                              <li>
                                                Les blocs prévu, réalisé et certifié peuvent
                                                représenter la même ligne terrain selon son
                                                avancement.
                                              </li>
                                            </ul>
                                            <p>Actions disponibles :</p>
                                            <ul className="list-disc space-y-1 pl-4">
                                              <li>
                                                Lier crée une nouvelle ligne terrain ou
                                                complète une ligne existante avec les
                                                statuts ESG manquants.
                                              </li>
                                              <li>
                                                Modifier ou supprimer agit sur le versement
                                                à l'intervenant complet, pas seulement sur la
                                                vignette visible.
                                              </li>
                                              <li>
                                                Déplacer rattache tout le versement à une
                                                autre ligne du contrat, avec ses statuts déjà
                                                reliés.
                                              </li>
                                            </ul>
                                          </div>
                                        </TooltipContent>
                                      </Tooltip>
                                    </div>
                                    <div className="text-xs font-semibold text-slate-900">
                                      {formatEuro(landPlannedTotal)}
                                    </div>
                                  </div>

                                  {isTerrainPaymentEditorActive ? (
                                    <div className="mt-3 rounded-xl border border-emerald-200 bg-emerald-50/80 px-3 py-3">
                                      <div className="flex flex-wrap items-start justify-between gap-3">
                                        <div className="space-y-1">
                                          <div className="flex flex-wrap items-center gap-2">
                                            <div className="text-sm font-medium text-slate-900">
                                              {isEditingExistingTerrainPayment
                                                ? "Modifier le versement à l'intervenant"
                                                : "Nouveau versement à l'intervenant"}
                                            </div>
                                            <span className="rounded-full border border-emerald-200 bg-white px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                                              {getLandStatusLabel(Number(terrainPaymentEditorState?.status || 1))}
                                            </span>
                                          </div>
                                          <p className="max-w-2xl text-xs leading-relaxed text-slate-600">
                                            {isEditingExistingTerrainPayment
                                              ? "La modification s'applique au versement complet et à ses statuts déjà liés."
                                              : "Renseignez le montant et les dates utiles. La pièce jointe devient obligatoire pour un versement certifié."}
                                          </p>
                                        </div>
                                        <button
                                          type="button"
                                          className={SYNTHESES_SECONDARY_BUTTON_CLASS}
                                          onClick={handleCancelTerrainPaymentEditor}
                                          disabled={savingTerrainPayment}
                                        >
                                          Annuler
                                        </button>
                                      </div>

                                      {terrainPaymentEditorError ? (
                                        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                                          {terrainPaymentEditorError}
                                        </div>
                                      ) : null}

                                      <div className="mt-3 rounded-xl border border-white/80 bg-white/90 p-3">
                                        <label className="mb-3 block space-y-1.5 text-sm">
                                          <span className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
                                            Intervenant destinataire
                                          </span>
                                          <select
                                            value={
                                              terrainPaymentEditorState?.recipientSubcontractorId
                                                ? String(terrainPaymentEditorState.recipientSubcontractorId)
                                                : ""
                                            }
                                            onChange={(event) =>
                                              handleChangeTerrainPaymentEditor({
                                                recipientSubcontractorId:
                                                  event.target.value
                                                    ? Number(event.target.value)
                                                    : null,
                                              })
                                            }
                                            className={SYNTHESES_INPUT_CLASS}
                                            disabled={isLoadingTerrainPaymentRecipients}
                                          >
                                            <option value="">
                                              {isLoadingTerrainPaymentRecipients
                                                ? "Chargement des intervenants..."
                                                : "Choisir un intervenant"}
                                            </option>
                                            {terrainPaymentRecipientOptions.map((option) => (
                                              <option
                                                key={`terrain-payment-recipient-${option.id}`}
                                                value={String(option.id)}
                                              >
                                                {option.label}
                                              </option>
                                            ))}
                                          </select>
                                          {terrainPaymentRecipientOptions.length === 0 &&
                                          !isLoadingTerrainPaymentRecipients ? (
                                            <span className="block text-xs text-slate-500">
                                              Aucun intervenant local financé n'est renseigné sur ce projet.
                                            </span>
                                          ) : null}
                                        </label>

                                        <div className="grid gap-3 md:grid-cols-2">
                                          <label className="space-y-1.5 text-sm">
                                            <span className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
                                              Montant €
                                            </span>
                                            <input
                                              type="number"
                                              min="0"
                                              step="0.01"
                                              value={terrainPaymentEditorState?.amountEuro ?? ""}
                                              onChange={(event) =>
                                                handleChangeTerrainPaymentEditor({
                                                  amountEuro: event.target.value,
                                                })
                                              }
                                              className={SYNTHESES_INPUT_CLASS}
                                            />
                                          </label>

                                          <label className="space-y-1.5 text-sm">
                                            <span className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
                                              Date prévue
                                            </span>
                                            <input
                                              type="date"
                                              value={terrainPaymentEditorState?.expectedAt ?? ""}
                                              onChange={(event) =>
                                                handleChangeTerrainPaymentEditor({
                                                  expectedAt: event.target.value,
                                                })
                                              }
                                              className={SYNTHESES_INPUT_CLASS}
                                            />
                                          </label>

                                        </div>

                                        {Number(terrainPaymentEditorState?.status || 0) >= 2 ? (
                                          <div className="mt-3 grid gap-3 md:grid-cols-2">
                                            <label className="space-y-1.5 text-sm">
                                              <span className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
                                                Date de réalisation
                                              </span>
                                              <input
                                                type="date"
                                                value={terrainPaymentEditorState?.dateMeasure ?? ""}
                                                onChange={(event) =>
                                                  handleChangeTerrainPaymentEditor({
                                                    dateMeasure: event.target.value,
                                                  })
                                                }
                                                className={SYNTHESES_INPUT_CLASS}
                                              />
                                            </label>

                                            {Number(terrainPaymentEditorState?.status || 0) >= 3 ? (
                                              <label className="space-y-1.5 text-sm">
                                                <span className="whitespace-nowrap text-xs font-medium uppercase tracking-wide text-slate-500">
                                                  Date de certification
                                                </span>
                                                <input
                                                  type="date"
                                                  value={terrainPaymentEditorState?.dateVerified ?? ""}
                                                  onChange={(event) =>
                                                    handleChangeTerrainPaymentEditor({
                                                      dateVerified: event.target.value,
                                                    })
                                                  }
                                                  className={SYNTHESES_INPUT_CLASS}
                                                />
                                              </label>
                                            ) : (
                                              <div />
                                            )}
                                          </div>
                                        ) : null}

                                        {Number(terrainPaymentEditorState?.status || 0) >= 3 ? (
                                          <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50/50 p-3">
                                            <div className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                              Pièce jointe
                                            </div>
                                            <div className="mt-1 text-sm text-slate-700">
                                              Preuve de vérification
                                            </div>
                                            <div className="mt-3 flex flex-wrap items-center gap-3">
                                              <input
                                                type="file"
                                                onChange={(event) => {
                                                  const file = event.target.files?.[0];
                                                  if (!file) return;
                                                  void handleUploadTerrainPaymentProof(file);
                                                  event.currentTarget.value = "";
                                                }}
                                                className="block text-xs text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-white file:px-3 file:py-2 file:text-xs file:font-medium file:text-slate-700 hover:file:bg-slate-50"
                                              />
                                              {terrainPaymentEditorState?.pjVerified?.id ? (
                                                <a
                                                  href={getTerrainMediaHref(terrainPaymentEditorState.pjVerified)}
                                                  target="_blank"
                                                  rel="noreferrer"
                                                  className="text-xs font-medium text-emerald-700 underline underline-offset-2"
                                                >
                                                  {getTerrainMediaLabel(terrainPaymentEditorState.pjVerified)}
                                                </a>
                                              ) : (
                                                <span className="text-xs text-slate-500">
                                                  Aucun justificatif ajouté
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        ) : null}
                                      </div>

                                      <div className="mt-3 flex justify-end">
                                        <button
                                          type="button"
                                          className={SYNTHESES_PRIMARY_BUTTON_CLASS}
                                          onClick={handleConfirmTerrainPaymentEditor}
                                          disabled={savingTerrainPayment}
                                        >
                                          {savingTerrainPayment
                                            ? "Enregistrement..."
                                            : isEditingExistingTerrainPayment
                                              ? "Enregistrer le versement"
                                              : "Ajouter le versement"}
                                        </button>
                                      </div>
                                    </div>
                                  ) : null}

                                  <div className="mt-3 grid gap-2 xl:grid-cols-3">
                                    {isTerrainMoveActive ? (
                                      <div className="xl:col-span-3 rounded-xl border border-amber-200 bg-amber-50/80 px-3 py-3">
                                        <div className="flex flex-wrap items-start justify-between gap-3">
                                          <div className="space-y-1">
                                            <div className="flex items-center gap-1.5">
                                              <div className="text-sm font-medium text-slate-900">
                                                Déplacement du versement à l'intervenant
                                              </div>
                                              <SectionHint>
                                                <div className="space-y-1.5">
                                                  <p>Le déplacement agit sur le versement à l'intervenant complet.</p>
                                                  <ul className="list-disc space-y-1 pl-4">
                                                    <li>les statuts déjà reliés suivent vers la ligne cible ;</li>
                                                    <li>vous choisissez uniquement une autre ligne du même contrat ;</li>
                                                    <li>la ligne source ne conserve plus ce versement après validation.</li>
                                                  </ul>
                                                </div>
                                              </SectionHint>
                                            </div>
                                            <p className="max-w-2xl text-xs leading-relaxed text-slate-600">
                                              Le déplacement concerne l’ensemble de ce versement à l'intervenant,
                                              avec toutes ses lignes déjà liées dans ce contrat
                                              (prévu, réalisé, certifié). Vous choisissez ici la
                                              ligne du contrat sur laquelle rattacher ce versement complet.
                                            </p>
                                          </div>
                                          <button
                                            type="button"
                                            className={SYNTHESES_SECONDARY_BUTTON_CLASS}
                                            onClick={handleCancelTerrainMove}
                                            disabled={movingTerrainPayment}
                                          >
                                            Annuler
                                          </button>
                                        </div>

                                        {activeTerrainMoveSummary.length > 0 ? (
                                          <div className="mt-3 flex flex-wrap items-center gap-2">
                                            {activeTerrainMoveSummary.map((item) => (
                                              <span
                                                key={`${row.key}-move-summary-${item.status}`}
                                                className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[11px] font-medium text-amber-800"
                                              >
                                                {item.label} {formatEuro(item.total)}
                                              </span>
                                            ))}
                                          </div>
                                        ) : null}

                                        {terrainMoveError ? (
                                          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                                            {terrainMoveError}
                                          </div>
                                        ) : null}

                                        <div className="mt-3 grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
                                          <label className="space-y-1.5 text-sm">
                                            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                              Ligne cible
                                            </span>
                                            <select
                                              value={terrainMoveState?.selectedTargetLineId ?? ""}
                                              onChange={(event) => {
                                                const value = Number(event.target.value);
                                                handleChangeTerrainMoveTarget(
                                                  Number.isFinite(value) && value > 0 ? value : null,
                                                );
                                              }}
                                              className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
                                            >
                                              <option value="">Choisir une ligne</option>
                                              {(terrainMoveState?.targetOptions ?? []).map((option) => (
                                                <option key={option.lineId} value={option.lineId}>
                                                  {option.label}
                                                </option>
                                              ))}
                                            </select>
                                          </label>
                                          <button
                                            type="button"
                                            className={SYNTHESES_PRIMARY_BUTTON_CLASS}
                                            onClick={handleConfirmTerrainMove}
                                            disabled={
                                              movingTerrainPayment
                                              || !isPositiveId(terrainMoveState?.selectedTargetLineId)
                                            }
                                          >
                                            {movingTerrainPayment ? "Déplacement..." : "Déplacer le versement"}
                                          </button>
                                        </div>
                                      </div>
                                    ) : null}

                                    {[
                                      {
                                        key: "planned",
                                        status: 1,
                                        label: getLandStatusLabel(1),
                                        rows: landPaymentsPlanned,
                                        total: landPlannedTotal,
                                      },
                                      {
                                        key: "realized",
                                        status: 2,
                                        label: getLandStatusLabel(2),
                                        rows: landPaymentsRealized,
                                        total: landRealizedTotal,
                                      },
                                      {
                                        key: "certified",
                                        status: 3,
                                        label: getLandStatusLabel(3),
                                        rows: landPaymentsCertified,
                                        total: landCertifiedTotal,
                                      },
                                    ].map((statusGroup) => (
                                      <div
                                        key={`${row.key}-land-${statusGroup.key}`}
                                        className="rounded-md border border-rose-100 bg-white p-2"
                                      >
                                        <div className="flex items-center justify-between gap-2">
                                          <div className="flex items-center gap-1.5">
                                            <div className="text-[11px] font-semibold uppercase tracking-wide text-rose-700">
                                              {statusGroup.label}
                                            </div>
                                            <button
                                              type="button"
                                              className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-rose-200 bg-white text-rose-700 hover:bg-rose-50"
                                              onClick={() =>
                                                isTerrainPaymentEditorActive
                                                && !isEditingExistingTerrainPayment
                                                && Number(terrainPaymentEditorState?.status || 0) === statusGroup.status
                                                  ? handleCancelTerrainPaymentEditor()
                                                  : handleOpenTerrainPaymentCreate(
                                                      row,
                                                      statusGroup.status,
                                                      selectedYear ?? row.year,
                                                    )
                                              }
                                              disabled={savingTerrainPayment || actionsDisabled}
                                              title={`Ajouter un versement ${statusGroup.label.toLowerCase()}`}
                                            >
                                              {isTerrainPaymentEditorActive
                                              && !isEditingExistingTerrainPayment
                                              && Number(terrainPaymentEditorState?.status || 0) === statusGroup.status ? (
                                                <X className="h-3.5 w-3.5" />
                                              ) : (
                                                <Plus className="h-3.5 w-3.5" />
                                              )}
                                            </button>
                                          </div>
                                          <div className="text-[11px] font-semibold text-slate-900">
                                            {formatEuro(statusGroup.total)}
                                          </div>
                                        </div>
                                        <div className="mt-2 space-y-1.5">
                                          {statusGroup.rows.length > 0 ? (
                                            statusGroup.rows.map((payment) => {
                                              const canSelectThisPayment = isTerrainReconcileActive
                                                && canSelectTerrainPaymentInReconcile(
                                                  statusGroup.status,
                                                  payment,
                                                  terrainReconcileState,
                                                );
                                              const isTargetLandPayment = isTerrainReconcileActive
                                                && isTerrainReconcileTargetRow(
                                                  payment,
                                                  terrainReconcileState,
                                                );
                                              const isMoveTargetLandPayment = isTerrainMoveActive
                                                && isTerrainMoveTargetRow(
                                                  payment,
                                                  terrainMoveState,
                                                );
                                              const isMoveIncludedLandPayment = isTerrainMoveActive
                                                && isTerrainMoveIncludedRow(
                                                  payment,
                                                  terrainMoveState,
                                                );
                                              const isSelectedPayment = (
                                                statusGroup.status === 1
                                                && selectedEstimatedId === payment.projectIndicatorId
                                              ) || (
                                                statusGroup.status === 2
                                                && selectedMeasuredId === payment.projectIndicatorId
                                              ) || (
                                                statusGroup.status === 3
                                                && selectedVerifiedId === payment.projectIndicatorId
                                              );
                                              const canLinkThisLandPayment =
                                                canLinkExistingLandPaymentRow(
                                                  payment,
                                                  terrainReconcilePlannedRows,
                                                  terrainReconcileRealizedRows,
                                                  terrainReconcileCertifiedRows,
                                                  projectedLandPayments,
                                                );
                                              const canManageThisLandPayment =
                                                canManageExistingLandPaymentRow(
                                                  payment,
                                                  projectedLandPayments,
                                                );
                                              const canMoveThisLandPayment =
                                                contract.isVerdeo !== true
                                                && canMoveLandPaymentRow(
                                                  payment,
                                                  terrainMoveTargetOptions,
                                                  projectedLandPayments,
                                                );
                                              const showMoveAction =
                                                canMoveThisLandPayment
                                                && (!isTerrainMoveActive || isMoveTargetLandPayment);
                                              const isEditingThisLandPayment =
                                                isTerrainPaymentEditorActive
                                                && isPositiveId(terrainPaymentEditorState?.landPaymentId)
                                                && Number(terrainPaymentEditorState?.landPaymentId || 0)
                                                  === Number(payment.landPaymentId || 0);
                                              const isDeletingThisLandPayment =
                                                Number(deletingTerrainPaymentId || 0)
                                                  === Number(payment.landPaymentId || 0);

                                              return (
                                                <div key={payment.id} className="space-y-2">
                                                  <div
                                                    role={canSelectThisPayment ? "button" : undefined}
                                                    tabIndex={canSelectThisPayment ? 0 : undefined}
                                                    className={cn(
                                                      "relative flex w-full items-center justify-between gap-2 rounded-md border px-2 py-1 text-left transition",
                                                      getLandPaymentSourceClasses(payment.source),
                                                      isTerrainReconcileActive ? "opacity-55" : "",
                                                      canSelectThisPayment ? "cursor-pointer hover:opacity-100" : "",
                                                      isSelectedPayment || isTargetLandPayment
                                                        ? "border-rose-400 ring-2 ring-rose-200 opacity-100"
                                                        : "",
                                                      isMoveIncludedLandPayment
                                                        ? "border-amber-400 ring-2 ring-amber-200 opacity-100"
                                                        : "",
                                                      isTerrainReconcileActive
                                                      && !canSelectThisPayment
                                                      && !isTargetLandPayment
                                                        ? "cursor-not-allowed opacity-35"
                                                        : "",
                                                    )}
                                                    onClick={() => {
                                                      if (!canSelectThisPayment) return;

                                                      if (statusGroup.status === 1) {
                                                        handlePickTerrainReconcile(
                                                          "selectedEstimatedId",
                                                          payment.projectIndicatorId ?? null,
                                                        );
                                                        return;
                                                      }
                                                      if (statusGroup.status === 2) {
                                                        handlePickTerrainReconcile(
                                                          "selectedMeasuredId",
                                                          payment.projectIndicatorId ?? null,
                                                        );
                                                        return;
                                                      }

                                                      handlePickTerrainReconcile(
                                                        "selectedVerifiedId",
                                                        selectedVerifiedId === payment.projectIndicatorId
                                                          ? null
                                                          : (payment.projectIndicatorId ?? null),
                                                      );
                                                    }}
                                                    onKeyDown={(event) => {
                                                      if (!canSelectThisPayment) return;
                                                      if (event.key !== "Enter" && event.key !== " ") return;
                                                      event.preventDefault();
                                                      (event.currentTarget as HTMLDivElement).click();
                                                    }}
                                                  >
                                                    <div className="min-w-0 text-[11px] text-slate-600">
                                                      {formatDate(payment.at)}
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                      <span className="text-[11px] font-medium text-slate-900">
                                                        {formatEuro(payment.amountEuro)}
                                                      </span>
                                                    </div>
                                                    {(canManageThisLandPayment || canLinkThisLandPayment || showMoveAction) ? (
                                                      <div className="absolute -bottom-2 -right-2 z-10 flex items-center gap-1">
                                                        {canManageThisLandPayment ? (
                                                          <>
                                                            <Tooltip>
                                                              <TooltipTrigger asChild>
                                                                <button
                                                                  type="button"
                                                                  className={cn(
                                                                    "inline-flex h-6 w-6 items-center justify-center rounded-full border shadow-sm transition",
                                                                    isEditingThisLandPayment
                                                                      ? "border-emerald-300 bg-emerald-100 text-emerald-700 hover:bg-emerald-200"
                                                                      : "border-emerald-200 bg-white text-emerald-700 hover:bg-emerald-50",
                                                                  )}
                                                                  onClick={(event) => {
                                                                    event.stopPropagation();

                                                                    if (isEditingThisLandPayment) {
                                                                      handleCancelTerrainPaymentEditor();
                                                                      return;
                                                                    }

                                                                    handleOpenTerrainPaymentEdit(
                                                                      row,
                                                                      payment,
                                                                      projectedLandPayments,
                                                                      selectedYear ?? row.year,
                                                                    );
                                                                  }}
                                                                  disabled={actionsDisabled}
                                                                >
                                                                  {isEditingThisLandPayment ? (
                                                                    <X className="h-3.5 w-3.5" />
                                                                  ) : (
                                                                    <Pencil className="h-3.5 w-3.5" />
                                                                  )}
                                                                </button>
                                                              </TooltipTrigger>
                                                              <TooltipContent side="top" className="text-xs">
                                                                {isEditingThisLandPayment
                                                                  ? "Fermer l'édition du versement à l'intervenant"
                                                                  : "Modifier ce versement à l'intervenant"}
                                                              </TooltipContent>
                                                            </Tooltip>
                                                            <Tooltip>
                                                              <TooltipTrigger asChild>
                                                                <button
                                                                  type="button"
                                                                  className={cn(
                                                                    "inline-flex h-6 w-6 items-center justify-center rounded-full border shadow-sm transition",
                                                                    isDeletingThisLandPayment || actionsDisabled
                                                                      ? "cursor-not-allowed border-slate-300 bg-slate-100 text-slate-400"
                                                                      : "border-rose-200 bg-white text-rose-700 hover:bg-rose-50",
                                                                  )}
                                                                  onClick={(event) => {
                                                                    event.stopPropagation();
                                                                    void handleDeleteTerrainPayment(payment);
                                                                  }}
                                                                  disabled={isDeletingThisLandPayment || actionsDisabled}
                                                                >
                                                                  <Trash2 className="h-3.5 w-3.5" />
                                                                </button>
                                                              </TooltipTrigger>
                                                              <TooltipContent side="top" className="text-xs">
                                                                {isDeletingThisLandPayment
                                                                  ? "Suppression..."
                                                                  : "Supprimer ce versement à l'intervenant"}
                                                              </TooltipContent>
                                                            </Tooltip>
                                                          </>
                                                        ) : null}
                                                        {showMoveAction ? (
                                                          <Tooltip>
                                                            <TooltipTrigger asChild>
                                                              <button
                                                                type="button"
                                                                className={cn(
                                                                  "inline-flex h-6 w-6 items-center justify-center rounded-full border shadow-sm transition",
                                                                  isMoveTargetLandPayment
                                                                    ? "border-amber-300 bg-amber-100 text-amber-700 hover:bg-amber-200"
                                                                    : "border-amber-200 bg-white text-amber-700 hover:bg-amber-50",
                                                                )}
                                                                onClick={(event) => {
                                                                  event.stopPropagation();

                                                                  if (isMoveTargetLandPayment) {
                                                                    handleCancelTerrainMove();
                                                                    return;
                                                                  }

                                                                  handleOpenTerrainMove(
                                                                    row,
                                                                    payment,
                                                                    terrainMoveTargetOptions,
                                                                  );
                                                                }}
                                                              >
                                                                {isMoveTargetLandPayment ? (
                                                                  <X className="h-3.5 w-3.5" />
                                                                ) : (
                                                                  <ArrowRightLeft className="h-3.5 w-3.5" />
                                                                )}
                                                              </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top" className="text-xs">
                                                              {isMoveTargetLandPayment
                                                                ? "Annuler le déplacement de ce versement à l'intervenant"
                                                                : "Déplacer ce versement à l'intervenant vers une autre ligne du même contrat"}
                                                            </TooltipContent>
                                                          </Tooltip>
                                                        ) : null}
                                                        {canLinkThisLandPayment ? (
                                                          <Tooltip>
                                                            <TooltipTrigger asChild>
                                                              <button
                                                                type="button"
                                                                className={cn(
                                                                  "inline-flex h-6 w-6 items-center justify-center rounded-full border shadow-sm transition",
                                                                  isTargetLandPayment
                                                                    ? "border-rose-300 bg-rose-100 text-rose-700 hover:bg-rose-200"
                                                                    : "border-rose-200 bg-white text-rose-700 hover:bg-rose-50",
                                                                )}
                                                                onClick={(event) => {
                                                                  event.stopPropagation();

                                                                  if (isTargetLandPayment) {
                                                                    handleCancelTerrainReconcile();
                                                                    return;
                                                                  }

                                                                  handleOpenTerrainReconcile(
                                                                    row,
                                                                    terrainReconcilePlannedRows,
                                                                    terrainReconcileRealizedRows,
                                                                    terrainReconcileCertifiedRows,
                                                                    payment,
                                                                  );
                                                                }}
                                                              >
                                                                {isTargetLandPayment ? (
                                                                  <X className="h-3.5 w-3.5" />
                                                                ) : (
                                                                  <Link2 className="h-3.5 w-3.5" />
                                                                )}
                                                              </button>
                                                            </TooltipTrigger>
                                                            <TooltipContent side="top" className="text-xs">
                                                              {isTargetLandPayment
                                                                ? "Annuler la sélection de ce versement à l'intervenant"
                                                                : "Compléter ce versement à l'intervenant avec des lignes ESG"}
                                                            </TooltipContent>
                                                          </Tooltip>
                                                        ) : null}
                                                      </div>
                                                    ) : null}
                                                  </div>
                                                </div>
                                              );
                                            })
                                          ) : (
                                            <div className="text-[11px] text-slate-500">
                                              Aucun versement {statusGroup.label.toLowerCase()}.
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                    ))}
                                  </div>

                                  {isTerrainReconcileActive ? (
                                    <div className="mt-3 rounded-md border border-rose-200 bg-white px-3 py-3">
                                      <div className="flex items-start justify-between gap-3">
                                        <div className="text-xs text-slate-600">
                                          {getTerrainReconcileInstruction(terrainReconcileState)}
                                        </div>
                                        <SectionHint className="shrink-0">
                                          <div className="space-y-1.5">
                                            <p>Cette étape sert à regrouper plusieurs indicateurs ESG dans une même ligne terrain.</p>
                                            <ul className="list-disc space-y-1 pl-4">
                                              <li>choisissez les statuts manquants sur les listes prévues, réalisées et certifiées ;</li>
                                              <li>la validation crée la ligne terrain si elle n'existe pas encore ;</li>
                                              <li>si une ligne existe déjà, elle est complétée sans créer de doublon.</li>
                                            </ul>
                                          </div>
                                        </SectionHint>
                                      </div>
                                      {terrainReconcileError ? (
                                        <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                                          {terrainReconcileError}
                                        </div>
                                      ) : null}
                                      <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
                                        <button
                                          type="button"
                                          className={SYNTHESES_SECONDARY_BUTTON_CLASS}
                                          onClick={handleCancelTerrainReconcile}
                                          disabled={savingTerrainReconcile}
                                        >
                                          Annuler
                                        </button>
                                        <button
                                          type="button"
                                          className={SYNTHESES_PRIMARY_BUTTON_CLASS}
                                          onClick={handleConfirmTerrainReconcile}
                                          disabled={
                                            savingTerrainReconcile
                                            || !canSubmitTerrainReconcile(terrainReconcileState)
                                          }
                                        >
                                          {getTerrainReconcileSubmitLabel(
                                            terrainReconcileState,
                                            savingTerrainReconcile,
                                          )}
                                        </button>
                                      </div>
                                    </div>
                                  ) : null}
                                </div>
                          </div>
                        ) : (
                          <div className="rounded-md border border-slate-100 bg-slate-50/80 px-2.5 py-2 text-[11px] text-slate-500">
                            Aucun versement prévu, reçu ou à l'intervenant sur cette ligne pour cette année.
                          </div>
                        )}

                        {isMovingThisRow ? (
                          <div
                            className="mt-2 rounded-md border border-emerald-200 bg-emerald-50/60 p-2"
                            data-contract-line-move-panel={row.key}
                          >
                            <div className="text-xs text-slate-700">
                              Déplacer cette ligne depuis{" "}
                              <span className="font-semibold">
                                {activeLineMove.sourceContractLabel}
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <select
                                value={activeLineMove.targetContractId}
                                onChange={(event) =>
                                  onChangeMoveTarget(event.target.value)
                                }
                                className="min-w-[220px] rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 outline-none ring-emerald-200 focus:ring-2"
                              >
                                <option value="">Choisir un contrat cible</option>
                                {moveTargets.map((option) => (
                                  <option
                                    key={`move-target-${option.contractId}`}
                                    value={String(option.contractId)}
                                  >
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                              <button
                                type="button"
                                className={cn(
                                  "rounded-md bg-emerald-600 px-2.5 py-1.5 text-xs font-medium text-white",
                                  isSubmittingThisRow
                                    ? "cursor-not-allowed opacity-70"
                                    : "hover:bg-emerald-700",
                                )}
                                onClick={onConfirmMoveLine}
                                disabled={
                                  isSubmittingThisRow ||
                                  !String(activeLineMove.targetContractId || "").trim()
                                }
                              >
                                Confirmer
                              </button>
                              <button
                                type="button"
                                className="rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 hover:bg-slate-50"
                                onClick={onCancelMoveLine}
                                disabled={isSubmittingThisRow}
                              >
                                Annuler
                              </button>
                            </div>
                            {moveError ? (
                              <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-2 text-xs text-rose-700">
                                {moveError}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    );
                    })()}
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
          );
        })}
        </div>

      </div>
      ) : null}
    </div>
  );
}

function SynthesesFinanceursReadOnlyBoard({
  data,
  loading,
  error,
  onRefreshData,
  onMutateData,
  onRestoreData,
}: {
  data: SynthesesGroupedContractsResponse | null;
  loading: boolean;
  error: string | null;
  onRefreshData?: (() => Promise<unknown>) | (() => void);
  onMutateData?: (
    updater: SynthesesGroupedContractsDataUpdater,
  ) => SynthesesGroupedContractsResponse | null;
  onRestoreData?: (snapshot: SynthesesGroupedContractsResponse | null) => void;
}) {
  const location = useLocation();
  const groupedFinanceurs = Array.isArray(data?.items) ? data.items : [];
  const financeursCatalogEndpoint = useMemo(() => {
    const apiBase = String(import.meta.env.VITE_API_ENDPOINT || "")
      .trim()
      .replace(/\/+$/, "");
    return apiBase ? `${apiBase}/financers` : "";
  }, []);
  const financeursCatalogQuery = useFetchDataV2<FinanceurCatalogItem[]>(
    financeursCatalogEndpoint,
    { browserCache: true, browserCacheNamespace: "syntheses" },
  );
  const programsCatalogEndpoint = useMemo(() => {
    const apiBase = String(import.meta.env.VITE_API_ENDPOINT || "")
      .trim()
      .replace(/\/+$/, "");
    return apiBase ? `${apiBase}/info/programs` : "";
  }, []);
  const programsCatalogQuery = useFetchDataV2<ProgramsCatalogResponse>(
    programsCatalogEndpoint,
    { browserCache: true, browserCacheNamespace: "syntheses" },
  );
  const { fetchData: fetchDesiredFundingProjectData } = useFetchDataV2<any>(
    "",
    {
      browserCache: true,
      browserCacheNamespace: "syntheses-project-catalog",
    },
  );
  const projectVisualCatalog = useMemo(
    () => buildProjectVisualCatalog(programsCatalogQuery.data),
    [programsCatalogQuery.data],
  );
  const financeurs = useMemo(
    () =>
      buildMergedFinanceurs(groupedFinanceurs, financeursCatalogQuery.data),
    [financeursCatalogQuery.data, groupedFinanceurs],
  );
  const [selectedFinanceurKey, setSelectedFinanceurKey] = useState<string | null>(null);
  const [financeurSearch, setFinanceurSearch] = useState("");
  const parsedRouteTarget = useMemo<FinanceurRouteTarget | null>(() => {
    const stateTarget = normalizeFinanceurRouteTarget(
      (location.state as { synthesesFinanceurTarget?: unknown } | null)
        ?.synthesesFinanceurTarget,
    );
    if (stateTarget) return stateTarget;

    const searchParams = new URLSearchParams(location.search);
    const searchTarget = normalizeFinanceurRouteTarget({
      financeurId: searchParams.get("targetFinanceurId"),
      financeurName: searchParams.get("targetFinanceurName"),
      synthesesContractId: searchParams.get("targetSynthesesContractId"),
      contractNodeId: searchParams.get("targetContractNodeId"),
      projectFilterKey: searchParams.get("targetProjectFilterKey"),
      projectName: searchParams.get("targetProjectName"),
      action: searchParams.get("targetAction"),
      targetLineId: searchParams.get("targetLineId"),
      targetProjectId: searchParams.get("targetProjectId"),
    });
    if (searchTarget) return searchTarget;

    if (typeof window === "undefined") return null;

    try {
      return normalizeFinanceurRouteTarget(
        JSON.parse(
          window.sessionStorage.getItem(
            FINANCEUR_NAVIGATION_TARGET_STORAGE_KEY,
          ) || "null",
        ),
      );
    } catch {
      return null;
    }
  }, [location.search, location.state]);
  const [pendingRouteTarget, setPendingRouteTarget] =
    useState<FinanceurRouteTarget | null>(parsedRouteTarget);
  const saveContractEndpoint = useMemo(() => buildSaveContractEndpoint(), []);
  const deleteContractEndpoint = useMemo(
    () => buildDeleteContractEndpoint(),
    [],
  );
  const moveContractLineEndpoint = useMemo(
    () => buildMoveContractLineEndpoint(),
    [],
  );
  const mergeContractsEndpoint = useMemo(
    () => buildMergeContractsEndpoint(),
    [],
  );
  const [desiredFundingCatalogByProjectId, setDesiredFundingCatalogByProjectId] =
    useState<Record<number, ProjectDesiredFundingCatalogEntry>>({});
  const [loadingDesiredFundingProjectIds, setLoadingDesiredFundingProjectIds] =
    useState<Record<number, boolean>>({});
  const [desiredFundingErrorsByProjectId, setDesiredFundingErrorsByProjectId] =
    useState<Record<number, string>>({});
  const normalizedFinanceurSearch = useMemo(
    () => toTrimmedText(financeurSearch).toLocaleLowerCase(),
    [financeurSearch],
  );
  const filteredFinanceurs = useMemo(() => {
    if (!normalizedFinanceurSearch) return financeurs;

    return financeurs.filter((financeur) => {
      const name = toTrimmedText(financeur.name).toLocaleLowerCase();
      const email = toTrimmedText(financeur.email).toLocaleLowerCase();
      return (
        name.includes(normalizedFinanceurSearch) ||
        email.includes(normalizedFinanceurSearch)
      );
    });
  }, [financeurs, normalizedFinanceurSearch]);
  const financeursLoading =
    loading || (!!financeursCatalogEndpoint && financeursCatalogQuery.loading);
  useEffect(() => {
    setPendingRouteTarget(parsedRouteTarget);
  }, [parsedRouteTarget]);
  useEffect(() => {
    if (!parsedRouteTarget || typeof window === "undefined") return;
    window.sessionStorage.removeItem(
      FINANCEUR_NAVIGATION_TARGET_STORAGE_KEY,
    );
  }, [parsedRouteTarget]);

  useEffect(() => {
    if (!pendingRouteTarget || financeursLoading) return;

    const targetFinanceur = findFinanceurForRouteTarget(
      financeurs,
      pendingRouteTarget,
    );
    if (!targetFinanceur) return;

    const targetFinanceurKey = getFinanceurListKey(
      targetFinanceur.id,
      targetFinanceur.name,
    );

    if (financeurSearch) {
      setFinanceurSearch("");
    }

    if (selectedFinanceurKey !== targetFinanceurKey) {
      setSelectedFinanceurKey(targetFinanceurKey);
    }
  }, [
    financeurs,
    financeursLoading,
    financeurSearch,
    pendingRouteTarget,
    selectedFinanceurKey,
  ]);

  useEffect(() => {
    if (pendingRouteTarget) {
      return;
    }

    if (!filteredFinanceurs.length) {
      setSelectedFinanceurKey(null);
      return;
    }

    const hasCurrent = filteredFinanceurs.some(
      (item) => getFinanceurListKey(item.id, item.name) === selectedFinanceurKey,
    );
    if (!hasCurrent) {
      setSelectedFinanceurKey(
        getFinanceurListKey(
          filteredFinanceurs[0].id,
          filteredFinanceurs[0].name,
        ),
      );
    }
  }, [
    filteredFinanceurs,
    pendingRouteTarget,
    selectedFinanceurKey,
  ]);

  const selectedFinanceur = useMemo(() => {
    return (
      financeurs.find(
        (item) => getFinanceurListKey(item.id, item.name) === selectedFinanceurKey,
      ) || null
    );
  }, [
    financeurs,
    selectedFinanceurKey,
  ]);
  const [selectedYearByContractId, setSelectedYearByContractId] = useState<
    Record<string, number>
  >({});
  const [expandedContractKeysByFinanceur, setExpandedContractKeysByFinanceur] = useState<
    Record<string, Record<string, boolean>>
  >({});
  const [selectedContractProjectFilterByFinanceurKey, setSelectedContractProjectFilterByFinanceurKey] =
    useState<Record<string, string>>({});
  const [selectedTimelineYearByFinanceurKey, setSelectedTimelineYearByFinanceurKey] =
    useState<Record<string, string>>({});
  const [selectedMonthlyTimelineYearByFinanceurKey, setSelectedMonthlyTimelineYearByFinanceurKey] =
    useState<Record<string, string>>({});
  const selectedFinanceurTimelineKey = useMemo(
    () => getFinanceurListKey(selectedFinanceur?.id, selectedFinanceur?.name),
    [selectedFinanceur?.id, selectedFinanceur?.name],
  );
  const selectedFinanceurTimelineYears = useMemo(
    () => buildFinanceurTimelineYears(selectedFinanceur),
    [selectedFinanceur],
  );
  const selectedFinanceurContractYears = useMemo(
    () => buildFinanceurContractYears(selectedFinanceur),
    [selectedFinanceur],
  );
  const visibleFinanceurTimelineYears = useMemo(
    () =>
      selectedFinanceurTimelineYears.length > 0
        ? selectedFinanceurTimelineYears
        : [createFinanceurTimelineYearSummary(getCurrentTimelineYearString())],
    [selectedFinanceurTimelineYears],
  );
  const selectedFinanceurMonthlyTimelineYears = useMemo(() => {
    if (selectedFinanceurTimelineYears.length > 0) {
      return selectedFinanceurTimelineYears.map((item) => item.year);
    }

    if (selectedFinanceurContractYears.length > 0) {
      return selectedFinanceurContractYears;
    }

    return [getCurrentTimelineYearString()];
  }, [selectedFinanceurContractYears, selectedFinanceurTimelineYears]);
  useEffect(() => {
    if (!selectedFinanceur) return;

    setSelectedTimelineYearByFinanceurKey((current) => {
      const currentValue = current[selectedFinanceurTimelineKey];
      if (
        currentValue === "all" ||
        selectedFinanceurContractYears.includes(String(currentValue || ""))
      ) {
        return current;
      }
      return {
        ...current,
        [selectedFinanceurTimelineKey]: "all",
      };
    });
  }, [
    selectedFinanceur,
    selectedFinanceurContractYears,
    selectedFinanceurTimelineKey,
  ]);
  useEffect(() => {
    if (!selectedFinanceur || selectedFinanceurMonthlyTimelineYears.length === 0) return;

    setSelectedMonthlyTimelineYearByFinanceurKey((current) => {
      const currentValue = current[selectedFinanceurTimelineKey];
      if (
        currentValue &&
        selectedFinanceurMonthlyTimelineYears.includes(String(currentValue))
      ) {
        return current;
      }

      const latestYear =
        selectedFinanceurMonthlyTimelineYears[
          selectedFinanceurMonthlyTimelineYears.length - 1
        ] || "";
      if (!latestYear) return current;

      return {
        ...current,
        [selectedFinanceurTimelineKey]: latestYear,
      };
    });
  }, [
    selectedFinanceur,
    selectedFinanceurMonthlyTimelineYears,
    selectedFinanceurTimelineKey,
  ]);
  const selectedFinanceurTimelineYear = selectedFinanceur
    ? selectedTimelineYearByFinanceurKey[selectedFinanceurTimelineKey]
      || "all"
      || ""
    : "";
  const selectedFinanceurTimelineFilterOptions = useMemo(() => {
    return ["all", ...selectedFinanceurContractYears];
  }, [selectedFinanceurContractYears]);
  const selectedFinanceurMonthlyTimelineYear = useMemo(() => {
    if (selectedFinanceurTimelineYear && selectedFinanceurTimelineYear !== "all") {
      return selectedFinanceurTimelineYear;
    }

    const storedYear =
      selectedMonthlyTimelineYearByFinanceurKey[selectedFinanceurTimelineKey];
    if (
      storedYear &&
      selectedFinanceurMonthlyTimelineYears.includes(String(storedYear))
    ) {
      return storedYear;
    }

    return (
      selectedFinanceurMonthlyTimelineYears[
        selectedFinanceurMonthlyTimelineYears.length - 1
      ] || getCurrentTimelineYearString()
    );
  }, [
    selectedFinanceurMonthlyTimelineYears,
    selectedFinanceurTimelineKey,
    selectedFinanceurTimelineYear,
    selectedMonthlyTimelineYearByFinanceurKey,
  ]);
  const selectedFinanceurTimelineSummary = useMemo(() => {
    if (!selectedFinanceur) return null;
    return (
      visibleFinanceurTimelineYears.find(
        (year) => year.year === selectedFinanceurMonthlyTimelineYear,
      ) || createFinanceurTimelineYearSummary(selectedFinanceurMonthlyTimelineYear)
    );
  }, [
    selectedFinanceur,
    selectedFinanceurMonthlyTimelineYear,
    visibleFinanceurTimelineYears,
  ]);
  const selectedFinanceurHeaderYear = useMemo(() => {
    if (!selectedFinanceurTimelineYear || selectedFinanceurTimelineYear === "all") {
      return null;
    }

    const numericYear = Number(selectedFinanceurTimelineYear);
    return Number.isFinite(numericYear) ? numericYear : null;
  }, [selectedFinanceurTimelineYear]);
  const selectedFinanceurHeaderTotals = useMemo(() => {
    if (!selectedFinanceur) {
      return {
        contractAmountEuro: 0,
        expectedPaymentsEuro: 0,
        receivedPaymentsEuro: 0,
        landPaymentsEuro: 0,
        contractsCount: 0,
      };
    }

    const visibleContractsForHeader =
      selectedFinanceurHeaderYear === null
        ? selectedFinanceur.contracts
        : selectedFinanceur.contracts.filter(
            (contract) =>
              buildContractDeclaredYears(contract).includes(
                selectedFinanceurHeaderYear,
              ),
          );
    const allContractsForPayments = selectedFinanceur.contracts;

    const baseTotals = visibleContractsForHeader.reduce(
      (acc, contract) => {
        acc.contractAmountEuro +=
          selectedFinanceurHeaderYear === null
            ? Number(contract.amountEuro || 0)
            : getContractAmountEuroForYear(contract, selectedFinanceurHeaderYear);
        acc.contractsCount += 1;

        return acc;
      },
      {
        contractAmountEuro: 0,
        contractsCount: 0,
      },
    );

    const paymentTotals = allContractsForPayments.reduce(
      (acc, contract) => {
        const realizedLandPayments = contract.landPayments.filter(
          (payment) => Number(payment.status || 0) === 2,
        );

        acc.expectedPaymentsEuro +=
          selectedFinanceurHeaderYear === null
            ? Number(contract.totals.expectedPaymentsEuro || 0)
            : sumRowsAmountForYear(
                contract.expectedPayments,
                selectedFinanceurHeaderYear,
              );
        acc.receivedPaymentsEuro +=
          selectedFinanceurHeaderYear === null
            ? Number(contract.totals.receivedPaymentsEuro || 0)
            : sumRowsAmountForYear(
                contract.paidPayments,
                selectedFinanceurHeaderYear,
              );
        acc.landPaymentsEuro +=
          selectedFinanceurHeaderYear === null
            ? Number(contract.totals.landPaymentsEuro || 0)
            : sumRowsAmountForYear(
                realizedLandPayments,
                selectedFinanceurHeaderYear,
              );

        return acc;
      },
      {
        expectedPaymentsEuro: 0,
        receivedPaymentsEuro: 0,
        landPaymentsEuro: 0,
      },
    );

    return {
      contractAmountEuro: Number(baseTotals.contractAmountEuro.toFixed(2)),
      expectedPaymentsEuro: Number(paymentTotals.expectedPaymentsEuro.toFixed(2)),
      receivedPaymentsEuro: Number(paymentTotals.receivedPaymentsEuro.toFixed(2)),
      landPaymentsEuro: Number(paymentTotals.landPaymentsEuro.toFixed(2)),
      contractsCount: baseTotals.contractsCount,
    };
  }, [selectedFinanceur, selectedFinanceurHeaderYear]);
  const selectedFinanceurPendingPaymentsEuro = useMemo(
    () =>
      Number(
        (
          Number(selectedFinanceurHeaderTotals.expectedPaymentsEuro || 0) -
          Number(selectedFinanceurHeaderTotals.receivedPaymentsEuro || 0)
        ).toFixed(2),
      ),
    [
      selectedFinanceurHeaderTotals.expectedPaymentsEuro,
      selectedFinanceurHeaderTotals.receivedPaymentsEuro,
    ],
  );

  const {
    value: creationDraftState,
    setValue: setCreationDraftState,
    status: creationLocalAutosaveStatus,
    lastSavedAt: creationLastLocalSavedAt,
  } = useLocalDraftAutosave<FinanceurGroupedContractCreationDraft>(
    GROUPED_CONTRACT_CREATION_STORAGE_KEY,
    () => ({
      isCreating: false,
      financeurId: null,
      financeurName: "",
      form: null,
      pending: [],
    }),
  );

  const normalizedCreationDraftState = useMemo(
    () => normalizeCreationDraftState(creationDraftState),
    [creationDraftState],
  );
  const creationDraftNeedsNormalization = useMemo(() => {
    const formNeeds =
      !!creationDraftState.form &&
      !Array.isArray((creationDraftState.form as any)?.yearGroups);
    const pendingNeeds = Array.isArray(creationDraftState.pending)
      ? creationDraftState.pending.some(
          (item: any) => !Array.isArray(item?.yearGroups),
        )
      : false;
    return formNeeds || pendingNeeds;
  }, [creationDraftState]);

  useEffect(() => {
    if (!creationDraftNeedsNormalization) return;
    setCreationDraftState(normalizedCreationDraftState);
  }, [
    creationDraftNeedsNormalization,
    normalizedCreationDraftState,
    setCreationDraftState,
  ]);

  const creationRemoteAutosave = useSynthesesRemoteAutosave({
    view: "financeurs",
    storageKey: GROUPED_CONTRACT_CREATION_STORAGE_KEY,
    draft: normalizedCreationDraftState,
    lastLocalSavedAt: creationLastLocalSavedAt,
  });

  const selectedFinanceurPendingContracts = useMemo(() => {
    return normalizedCreationDraftState.pending.filter(
      (item) =>
        String(item.financeurId ?? "") === String(selectedFinanceur?.id ?? ""),
    );
  }, [normalizedCreationDraftState.pending, selectedFinanceur?.id]);
  const visibleSelectedFinanceurPendingContracts = useMemo(() => {
    if (selectedFinanceurHeaderYear === null) {
      return selectedFinanceurPendingContracts;
    }

    return selectedFinanceurPendingContracts.filter((draftContract) =>
      pendingContractHasYear(draftContract, selectedFinanceurHeaderYear),
    );
  }, [selectedFinanceurHeaderYear, selectedFinanceurPendingContracts]);
  const visibleSelectedFinanceurContracts = useMemo(() => {
    if (!selectedFinanceur) return [];
    if (selectedFinanceurHeaderYear === null) {
      return selectedFinanceur.contracts;
    }

    return selectedFinanceur.contracts.filter((contract) =>
      buildContractDeclaredYears(contract).includes(selectedFinanceurHeaderYear),
    );
  }, [selectedFinanceur, selectedFinanceurHeaderYear]);
  const contractProjectFilterOptions = useMemo(() => {
    const options = new Map<string, ContractProjectFilterOption>();

    visibleSelectedFinanceurContracts.forEach((contract) => {
      collectContractProjectFilterOptions(contract).forEach((option) => {
        if (!options.has(option.key)) {
          options.set(option.key, option);
        }
      });
    });

    visibleSelectedFinanceurPendingContracts.forEach((contract) => {
      collectPendingContractProjectFilterOptions(contract).forEach((option) => {
        if (!options.has(option.key)) {
          options.set(option.key, option);
        }
      });
    });

    return Array.from(options.values()).sort((left, right) =>
      left.label.localeCompare(right.label, "fr"),
    );
  }, [
    visibleSelectedFinanceurContracts,
    visibleSelectedFinanceurPendingContracts,
  ]);
  const selectedContractProjectFilter = useMemo(() => {
    const storedValue =
      selectedContractProjectFilterByFinanceurKey[selectedFinanceurTimelineKey];
    if (!storedValue || storedValue === "all") return "all";

    return contractProjectFilterOptions.some((option) => option.key === storedValue)
      ? storedValue
      : "all";
  }, [
    contractProjectFilterOptions,
    selectedContractProjectFilterByFinanceurKey,
    selectedFinanceurTimelineKey,
  ]);
  const selectedContractProjectFilterLabel = useMemo(() => {
    if (selectedContractProjectFilter === "all") return "Tous les projets";
    return (
      contractProjectFilterOptions.find(
        (option) => option.key === selectedContractProjectFilter,
      )?.label || "Projet"
    );
  }, [contractProjectFilterOptions, selectedContractProjectFilter]);
  const filteredVisibleSelectedFinanceurPendingContracts = useMemo(() => {
    if (selectedContractProjectFilter === "all") {
      return visibleSelectedFinanceurPendingContracts;
    }

    return visibleSelectedFinanceurPendingContracts.filter((draftContract) =>
      pendingContractMatchesProjectFilter(draftContract, selectedContractProjectFilter),
    );
  }, [
    selectedContractProjectFilter,
    visibleSelectedFinanceurPendingContracts,
  ]);
  const filteredVisibleSelectedFinanceurContracts = useMemo(() => {
    if (selectedContractProjectFilter === "all") {
      return visibleSelectedFinanceurContracts;
    }

    return visibleSelectedFinanceurContracts.filter((contract) =>
      contractMatchesProjectFilter(contract, selectedContractProjectFilter),
    );
  }, [selectedContractProjectFilter, visibleSelectedFinanceurContracts]);
  const visibleSelectedFinanceurProjectIds = useMemo(() => {
    const ids = new Set<number>();

    visibleSelectedFinanceurContracts.forEach((contract) => {
      const contractProjectId = Number(contract.project?.id || 0);
      if (contractProjectId > 0) {
        ids.add(contractProjectId);
      }

      (Array.isArray(contract.projectTotals) ? contract.projectTotals : []).forEach(
        (projectTotal) => {
          const projectId = Number(projectTotal?.projectId || 0);
          if (projectId > 0) ids.add(projectId);
        },
      );

      (Array.isArray(contract.lineRows) ? contract.lineRows : []).forEach((row) => {
        const projectId = Number(row?.projectId || 0);
        if (projectId > 0) ids.add(projectId);
      });
    });

    return Array.from(ids);
  }, [visibleSelectedFinanceurContracts]);

  const nextContractNumber = useMemo(() => {
    return (
      (selectedFinanceur?.contracts.length ?? 0) +
      selectedFinanceurPendingContracts.length +
      1
    );
  }, [
    selectedFinanceur?.contracts.length,
    selectedFinanceurPendingContracts.length,
  ]);
  useEffect(() => {
    if (!selectedFinanceur || filteredVisibleSelectedFinanceurContracts.length === 0) return;

    const financeurKey = selectedFinanceurTimelineKey;
    const visibleContractKeys = filteredVisibleSelectedFinanceurContracts.map((contract, index) =>
      getContractCollapseKey(contract, index),
    );

    setExpandedContractKeysByFinanceur((current) => {
      const existing = current[financeurKey] || {};
      let hasChanged = !current[financeurKey];
      const nextFinanceurState = { ...existing };
      const selectedVisibleKey =
        visibleContractKeys.find((contractKey) => existing[contractKey] === true)
        || visibleContractKeys[0]
        || null;

      visibleContractKeys.forEach((contractKey) => {
        const nextValue = contractKey === selectedVisibleKey;
        if (nextFinanceurState[contractKey] !== nextValue) {
          nextFinanceurState[contractKey] = nextValue;
          hasChanged = true;
        }
      });

      if (!hasChanged) {
        return current;
      }

      return {
        ...current,
        [financeurKey]: nextFinanceurState,
      };
    });
  }, [
    filteredVisibleSelectedFinanceurContracts,
    selectedFinanceur,
    selectedFinanceurTimelineKey,
  ]);
  const selectedFinanceurExpandedContracts = useMemo(
    () => expandedContractKeysByFinanceur[selectedFinanceurTimelineKey] || {},
    [expandedContractKeysByFinanceur, selectedFinanceurTimelineKey],
  );
  const selectedVisibleContractIndex = useMemo(() => {
    if (filteredVisibleSelectedFinanceurContracts.length === 0) return -1;

    const activeIndex = filteredVisibleSelectedFinanceurContracts.findIndex((contract, index) => {
      const contractKey = getContractCollapseKey(contract, index);
      return selectedFinanceurExpandedContracts[contractKey] === true;
    });

    return activeIndex >= 0 ? activeIndex : 0;
  }, [filteredVisibleSelectedFinanceurContracts, selectedFinanceurExpandedContracts]);
  const selectedVisibleContract =
    selectedVisibleContractIndex >= 0
      ? filteredVisibleSelectedFinanceurContracts[selectedVisibleContractIndex] || null
      : null;
  const selectContractForFinanceur = useCallback(
    (financeurKey: string, contractKey: string) => {
      setExpandedContractKeysByFinanceur((current) => {
        const financeurState = current[financeurKey] || {};
        const nextFinanceurState: Record<string, boolean> = {};

        Object.keys(financeurState).forEach((key) => {
          nextFinanceurState[key] = false;
        });
        nextFinanceurState[contractKey] = true;

        const hasChanged = Object.keys(nextFinanceurState).some(
          (key) => financeurState[key] !== nextFinanceurState[key],
        );
        if (!hasChanged && current[financeurKey]) {
          return current;
        }

        return {
          ...current,
          [financeurKey]: nextFinanceurState,
        };
      });
    },
    [],
  );
  const toggleContractExpanded = useCallback(
    (contractKey: string) => {
      selectContractForFinanceur(selectedFinanceurTimelineKey, contractKey);
    },
    [selectContractForFinanceur, selectedFinanceurTimelineKey],
  );

  const projectSuggestionOptions = useMemo(() => {
    const options = Array.from(projectVisualCatalog.values())
      .map((project) => ({
        id: Number(project.id || 0),
        name: String(project.name || "").trim(),
      }))
      .filter(
        (
          option,
        ): option is {
          id: number;
          name: string;
        } => option.id > 0 && option.name.length > 0,
      )
      .sort((a, b) => a.name.localeCompare(b.name, "fr"));

    const seenNames = new Set<string>();
    return options.filter((option) => {
      const key = option.name.toLocaleLowerCase();
      if (seenNames.has(key)) return false;
      seenNames.add(key);
      return true;
    });
  }, [projectVisualCatalog]);
  const projectSuggestions = useMemo(
    () => projectSuggestionOptions.map((option) => option.name),
    [projectSuggestionOptions],
  );
  const projectSuggestionIdByName = useMemo(() => {
    const mapping = new Map<string, number>();
    projectSuggestionOptions.forEach((option) => {
      mapping.set(option.name.toLocaleLowerCase(), option.id);
    });
    return mapping;
  }, [projectSuggestionOptions]);

  const [creationError, setCreationError] = useState<string | null>(null);
  const [savePendingError, setSavePendingError] = useState<string | null>(null);
  const [savingPendingDraftId, setSavingPendingDraftId] = useState<
    string | null
  >(null);
  const [savingContractNodeId, setSavingContractNodeId] = useState<string | null>(
    null,
  );
  const [editingPendingDraftId, setEditingPendingDraftId] = useState<
    string | null
  >(null);
  const [editingPersistedContract, setEditingPersistedContract] =
    useState<EditingPersistedContractState | null>(null);
  const [contractFormStep, setContractFormStep] =
    useState<FinanceurContractFormStep>(1);
  const [deletingContractKey, setDeletingContractKey] = useState<string | null>(
    null,
  );
  const [bulkDeletingFinanceurId, setBulkDeletingFinanceurId] = useState<
    string | null
  >(null);
  const [activeLineMove, setActiveLineMove] =
    useState<ActiveContractLineMoveState | null>(null);
  const [moveLineError, setMoveLineError] = useState<string | null>(null);
  const [movingLineRequestKey, setMovingLineRequestKey] = useState<
    string | null
  >(null);
  const [activeContractsMerge, setActiveContractsMerge] =
    useState<ActiveContractsMergeState | null>(null);
  const [mergeContractsError, setMergeContractsError] = useState<string | null>(
    null,
  );
  const [mergingContracts, setMergingContracts] = useState(false);
  const savingPendingDraftRef = useRef<string | null>(null);

  const isContractSaveInProgress = useCallback(
    (contract: SynthesesGroupedContractNode | null | undefined) => {
      if (!contract || !savingContractNodeId) return false;

      const contractNodeId = String(contract.id || "");
      if (contractNodeId && contractNodeId === savingContractNodeId) {
        return true;
      }

      const synthesesContractId = extractSynthesesContractId(contract);
      return (
        Number.isFinite(Number(synthesesContractId)) &&
        Number(synthesesContractId || 0) > 0 &&
        savingContractNodeId === `synth-contract-${synthesesContractId}`
      );
    },
    [savingContractNodeId],
  );

  const scrollToContractNode = useCallback((contractNodeId: string) => {
    let attempt = 0;
    const maxAttempts = 8;

    const tryScroll = () => {
      const selector = `[data-contract-node-id="${contractNodeId}"]`;
      const target = document.querySelector<HTMLElement>(selector);
      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }

      attempt += 1;
      if (attempt < maxAttempts) {
        window.setTimeout(tryScroll, 140);
      }
    };

    window.setTimeout(tryScroll, 120);
  }, []);
  const clearPendingRouteNavigationTarget = useCallback(() => {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem(
        FINANCEUR_NAVIGATION_TARGET_STORAGE_KEY,
      );
    }
    setPendingRouteTarget(null);
  }, []);
  const scrollToCreationTarget = useCallback((selector: string) => {
    let attempt = 0;
    const maxAttempts = 8;

    const tryScroll = () => {
      const target = document.querySelector<HTMLElement>(selector);
      if (target) {
        target.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
        return;
      }

      attempt += 1;
      if (attempt < maxAttempts) {
        window.setTimeout(tryScroll, 120);
      }
    };

    window.setTimeout(tryScroll, 80);
  }, []);
  useEffect(() => {
    if (!pendingRouteTarget || financeursLoading || !selectedFinanceur) return;

    const targetFinanceur = findFinanceurForRouteTarget(
      financeurs,
      pendingRouteTarget,
    );
    if (!targetFinanceur) return;

    const targetFinanceurKey = getFinanceurListKey(
      targetFinanceur.id,
      targetFinanceur.name,
    );
    if (selectedFinanceurKey !== targetFinanceurKey) return;

    const financeurKey = getFinanceurListKey(
      selectedFinanceur.id,
      selectedFinanceur.name,
    );
    const targetProjectFilterKey =
      toTrimmedText(pendingRouteTarget.projectFilterKey) || "all";
    const hasExplicitProjectFilterTarget = targetProjectFilterKey !== "all";

    if (
      hasExplicitProjectFilterTarget &&
      selectedTimelineYearByFinanceurKey[financeurKey] !== "all"
    ) {
      setSelectedTimelineYearByFinanceurKey((current) => ({
        ...current,
        [financeurKey]: "all",
      }));
      return;
    }

    if (
      hasExplicitProjectFilterTarget &&
      selectedContractProjectFilterByFinanceurKey[financeurKey] !==
        targetProjectFilterKey
    ) {
      setSelectedContractProjectFilterByFinanceurKey((current) => ({
        ...current,
        [financeurKey]: targetProjectFilterKey,
      }));
      return;
    }

    const hasContractTarget =
      !!pendingRouteTarget.contractNodeId || !!pendingRouteTarget.synthesesContractId;
    if (!hasContractTarget) {
      clearPendingRouteNavigationTarget();
      return;
    }

    const targetContractIndex = findContractIndexForRouteTarget(
      selectedFinanceur.contracts,
      pendingRouteTarget,
    );
    if (targetContractIndex < 0) return;

    if (
      !hasExplicitProjectFilterTarget &&
      selectedContractProjectFilterByFinanceurKey[financeurKey]
      && selectedContractProjectFilterByFinanceurKey[financeurKey] !== "all"
    ) {
      setSelectedContractProjectFilterByFinanceurKey((current) => ({
        ...current,
        [financeurKey]: "all",
      }));
      return;
    }
    if (selectedTimelineYearByFinanceurKey[financeurKey] !== "all") {
      setSelectedTimelineYearByFinanceurKey((current) => ({
        ...current,
        [financeurKey]: "all",
      }));
      return;
    }

    const targetContract = selectedFinanceur.contracts[targetContractIndex];
    const targetCollapseKey = getContractCollapseKey(
      targetContract,
      targetContractIndex,
    );
    const targetContractNodeId = String(targetContract.id || "").trim();
    if (!targetContractNodeId) return;

    const isExpanded =
      expandedContractKeysByFinanceur[financeurKey]?.[targetCollapseKey] === true;
    if (!isExpanded) {
      selectContractForFinanceur(financeurKey, targetCollapseKey);
      return;
    }

    const selector = `[data-contract-node-id="${targetContractNodeId}"]`;
    const targetNode = document.querySelector<HTMLElement>(selector);
    if (!targetNode) return;

    scrollToContractNode(targetContractNodeId);

    if (financeurRouteTargetRequestsDesiredFundingAllocation(pendingRouteTarget)) {
      return;
    }

    clearPendingRouteNavigationTarget();
  }, [
    clearPendingRouteNavigationTarget,
    expandedContractKeysByFinanceur,
    financeurs,
    financeursLoading,
    pendingRouteTarget,
    selectedContractProjectFilterByFinanceurKey,
    selectContractForFinanceur,
    selectedFinanceur,
    selectedFinanceurKey,
    selectedTimelineYearByFinanceurKey,
    scrollToContractNode,
  ]);

  const editingPendingDraft = useMemo(() => {
    if (!editingPendingDraftId) return null;
    return (
      normalizedCreationDraftState.pending.find(
        (item) => item.id === editingPendingDraftId,
      ) || null
    );
  }, [editingPendingDraftId, normalizedCreationDraftState.pending]);

  const selectedFinanceurDeletableContracts = useMemo(() => {
    return (selectedFinanceur?.contracts || []).filter(
      (contract) => getPersistedContractDeletionInfo(contract).canManage,
    );
  }, [selectedFinanceur?.contracts]);
  const selectedFinanceurMergeableContracts = useMemo(() => {
    return (selectedFinanceur?.contracts || [])
      .map((contract, index) => {
        const contractId = getMergeableSynthesesContractId(contract);
        if (!contractId) return null;

        return {
          contract,
          index,
          contractId,
          label: getContractMergeOptionLabel(contract, index),
        };
      })
      .filter(
        (
          item,
        ): item is {
          contract: SynthesesGroupedContractNode;
          index: number;
          contractId: number;
          label: string;
        } => !!item,
      );
  }, [selectedFinanceur?.contracts]);
  const selectedFinanceurMoveTargetOptionsByContractId = useMemo(() => {
    const contracts = (selectedFinanceur?.contracts || [])
      .map((contract, index) => {
        const contractId = extractSynthesesContractId(contract);
        const deletionInfo = getPersistedContractDeletionInfo(contract);
        if (
          !Number.isFinite(Number(contractId)) ||
          Number(contractId || 0) <= 0 ||
          !deletionInfo.canManage
        ) {
          return null;
        }

        return {
          contractId: Number(contractId),
          label: `${String(contract.title || `Contrat ${index + 1}`).trim()} • ${formatDate(contract.contractDate)}`,
        };
      })
      .filter(
        (
          item,
        ): item is {
          contractId: number;
          label: string;
        } => !!item,
      );

    const byContractId = new Map<number, ContractLineMoveTargetOption[]>();
    contracts.forEach((sourceContract) => {
      byContractId.set(
        sourceContract.contractId,
        contracts
          .filter(
            (targetContract) =>
              targetContract.contractId !== sourceContract.contractId,
          )
          .map((targetContract) => ({
            contractId: targetContract.contractId,
            label: targetContract.label,
          })),
      );
    });

    return byContractId;
  }, [selectedFinanceur?.contracts]);

  const isBulkDeletingSelectedFinanceur =
    String(bulkDeletingFinanceurId || "") === String(selectedFinanceur?.id || "");

  useEffect(() => {
    setActiveLineMove(null);
    setMoveLineError(null);
    setMovingLineRequestKey(null);
    setActiveContractsMerge(null);
    setMergeContractsError(null);
    setMergingContracts(false);
  }, [selectedFinanceur?.id]);

  const updateCreationForm = useCallback(
    (updater: (form: GroupContractFormDraft) => GroupContractFormDraft) => {
      setCreationDraftState((prev) => {
        const normalizedPrev = normalizeCreationDraftState(prev);
        if (!normalizedPrev.form) return normalizedPrev;
        return {
          ...normalizedPrev,
          form: updater(normalizedPrev.form),
        };
      });
    },
    [setCreationDraftState],
  );

  const loadDesiredFundingCatalogForProject = useCallback(
    async (projectId: number | null | undefined) => {
      const normalizedProjectId = Number(projectId || 0);
      if (!Number.isFinite(normalizedProjectId) || normalizedProjectId <= 0) {
        return;
      }

      if (
        desiredFundingCatalogByProjectId[normalizedProjectId] ||
        loadingDesiredFundingProjectIds[normalizedProjectId]
      ) {
        return;
      }

      const apiBase = String(import.meta.env.VITE_API_ENDPOINT || "")
        .trim()
        .replace(/\/+$/, "");
      if (!apiBase) return;

      setLoadingDesiredFundingProjectIds((current) => ({
        ...current,
        [normalizedProjectId]: true,
      }));
      setDesiredFundingErrorsByProjectId((current) => ({
        ...current,
        [normalizedProjectId]: "",
      }));

      try {
        const payload = await fetchDesiredFundingProjectData(
          `${apiBase}/project/${normalizedProjectId}?excludes=indicator,reporting`,
        );
        if (!payload || typeof payload !== "object") {
          throw new Error(
            "Erreur de chargement des financements souhaités",
          );
        }

        const desiredFundings = Array.isArray(payload?.desiredFunding)
          ? payload.desiredFunding
          : [];

        const normalizedCatalog: ProjectDesiredFundingCatalogEntry = {
          projectId: normalizedProjectId,
          desiredFundings: desiredFundings
            .map((item: any) => {
              const desiredFundingId = Number(item?.id || 0);
              if (!Number.isFinite(desiredFundingId) || desiredFundingId <= 0) {
                return null;
              }

              const convertedAmount = Number(item?.convertedAmount ?? 0);
              const amount = Number(item?.amount ?? 0);
              const devise = item?.devise && typeof item.devise === "object"
                ? item.devise
                : null;

              return {
                id: desiredFundingId,
                title: toTrimmedText(item?.title) || `Financement souhaité #${desiredFundingId}`,
                amount: Number.isFinite(amount) ? amount : 0,
                convertedAmount:
                  Number.isFinite(convertedAmount) && convertedAmount > 0
                    ? convertedAmount
                    : null,
                currencyCode: toTrimmedText(
                  devise?.code ?? item?.currencyCode ?? "",
                ) || null,
                currencySymbol: toTrimmedText(
                  devise?.symbol ?? item?.currencySymbol ?? "",
                ) || null,
              } satisfies ProjectDesiredFundingOption;
            })
            .filter(
              (
                item,
              ): item is ProjectDesiredFundingOption => !!item && item.id > 0,
            ),
        };

        setDesiredFundingCatalogByProjectId((current) => ({
          ...current,
          [normalizedProjectId]: normalizedCatalog,
        }));
      } catch (error: unknown) {
        setDesiredFundingErrorsByProjectId((current) => ({
          ...current,
          [normalizedProjectId]:
            error instanceof Error
              ? error.message
              : "Impossible de charger les financements souhaités.",
        }));
      } finally {
        setLoadingDesiredFundingProjectIds((current) => ({
          ...current,
          [normalizedProjectId]: false,
        }));
      }
    },
    [
      desiredFundingCatalogByProjectId,
      fetchDesiredFundingProjectData,
      loadingDesiredFundingProjectIds,
    ],
  );

  const openCreationForm = useCallback(() => {
    if (!selectedFinanceur) return;
    setContractFormStep(1);
    setCreationError(null);
    setSavePendingError(null);
    setActiveLineMove(null);
    setMoveLineError(null);
    setActiveContractsMerge(null);
    setMergeContractsError(null);
    setEditingPendingDraftId(null);
    setEditingPersistedContract(null);
    setCreationDraftState((prev) => {
      const normalizedPrev = normalizeCreationDraftState(prev);
      const isSameFinanceur =
        String(normalizedPrev.financeurId ?? "") ===
        String(selectedFinanceur.id ?? "");
      if (normalizedPrev.isCreating && isSameFinanceur && normalizedPrev.form) {
        return normalizedPrev;
      }
      return {
        ...normalizedPrev,
        isCreating: true,
        financeurId: selectedFinanceur.id ?? null,
        financeurName: String(selectedFinanceur.name || "").trim(),
        form: buildInitialGroupContractForm(
          selectedFinanceur,
          nextContractNumber,
        ),
      };
    });
  }, [nextContractNumber, selectedFinanceur, setCreationDraftState]);

  const closeCreationForm = useCallback(() => {
    setContractFormStep(1);
    setCreationError(null);
    setSavePendingError(null);
    setActiveLineMove(null);
    setMoveLineError(null);
    setEditingPendingDraftId(null);
    setEditingPersistedContract(null);
    setCreationDraftState((prev) => ({
      ...normalizeCreationDraftState(prev),
      isCreating: false,
      form: null,
    }));
  }, [setCreationDraftState]);

  const addYearGroup = useCallback(() => {
    let nextGroupId: string | null = null;
    updateCreationForm((form) => ({
      ...form,
      yearGroups: (() => {
        const nextGroup = buildInitialYearGroup(
          selectedFinanceur,
          getNextYearValue(
            form.yearGroups,
            getYearFromIsoDate(form.contractDate || null),
          ),
        );
        nextGroupId = nextGroup.id;
        return [...form.yearGroups, nextGroup];
      })(),
    }));
    if (nextGroupId) {
      scrollToCreationTarget(
        `[data-creation-year-group-id="${nextGroupId}"]`,
      );
    }
  }, [selectedFinanceur, scrollToCreationTarget, updateCreationForm]);

  const removeYearGroup = useCallback(
    (groupId: string) => {
      updateCreationForm((form) => {
        const nextGroups = form.yearGroups.filter(
          (group) => group.id !== groupId,
        );
        if (nextGroups.length > 0) {
          return {
            ...form,
            yearGroups: nextGroups,
          };
        }
        return {
          ...form,
          yearGroups: [
            buildInitialYearGroup(
              selectedFinanceur,
              getYearFromIsoDate(form.contractDate || null),
            ),
          ],
        };
      });
    },
    [selectedFinanceur, updateCreationForm],
  );

  const addProjectRowToYearGroup = useCallback(
    (groupId: string) => {
      const nextRow = buildInitialProjectRow(selectedFinanceur);
      updateCreationForm((form) => ({
        ...form,
        yearGroups: form.yearGroups.map((group) =>
          group.id === groupId
            ? {
                ...group,
                projectRows: [
                  ...group.projectRows,
                  nextRow,
                ],
              }
            : group,
        ),
      }));
      scrollToCreationTarget(
        `[data-creation-project-row-id="${nextRow.id}"]`,
      );
    },
    [scrollToCreationTarget, selectedFinanceur, updateCreationForm],
  );

  const removeProjectRowFromYearGroup = useCallback(
    (groupId: string, rowId: string) => {
      updateCreationForm((form) => ({
        ...form,
        yearGroups: form.yearGroups.map((group) => {
          if (group.id !== groupId) return group;
          const nextRows = group.projectRows.filter((row) => row.id !== rowId);
          return {
            ...group,
            projectRows: nextRows.length
              ? nextRows
              : [buildInitialProjectRow(selectedFinanceur)],
          };
        }),
      }));
    },
    [selectedFinanceur, updateCreationForm],
  );

  const addExpectedPaymentToContract = useCallback(() => {
    updateCreationForm((form) => ({
      ...form,
      expectedPayments: [
        ...form.expectedPayments,
        buildInitialExpectedPaymentDraft(),
      ],
    }));
  }, [updateCreationForm]);

  const updateExpectedPaymentInContract = useCallback(
    (paymentId: string, patch: Partial<DraftExpectedPayment>) => {
      updateCreationForm((form) => ({
        ...form,
        expectedPayments: form.expectedPayments.map((payment) => {
          if (payment.id !== paymentId) return payment;
          const next = { ...payment, ...patch };
          if (patch.status === 2 && !next.receivedAt) {
            next.receivedAt =
              next.expectedAt || new Date().toISOString().split("T")[0];
          }
          if (patch.status === 1) {
            next.receivedAt = "";
          }
          return next;
        }),
      }));
    },
    [updateCreationForm],
  );

  const removeExpectedPaymentFromContract = useCallback(
    (paymentId: string) => {
      updateCreationForm((form) => ({
        ...form,
        expectedPayments: form.expectedPayments.filter(
          (payment) => payment.id !== paymentId,
        ),
      }));
    },
    [updateCreationForm],
  );

  const buildPendingContractDraftFromForm = useCallback(() => {
    if (!selectedFinanceur) return;
    const form = normalizedCreationDraftState.form;
    if (!form) return;

    const title = String(form.title || "").trim();
    if (!title) {
      setCreationError("Renseignez l’intitulé du contrat.");
      return;
    }

    if (!form.contractDate) {
      setCreationError("Renseignez la date du contrat.");
      return;
    }

    const contractAmount = parseAmountInput(form.amountEuro);
    if (null === contractAmount || contractAmount <= 0) {
      setCreationError("Renseignez un montant de contrat valide.");
      return;
    }

    const cleanedGroups = form.yearGroups
      .map((group) => ({
        ...group,
        year: String(group.year || "").trim(),
        projectRows: group.projectRows
          .map((row) => ({
            ...row,
            lineId:
              Number.isFinite(Number(row.lineId)) && Number(row.lineId) > 0
                ? Number(row.lineId)
                : null,
            realFundingId:
              Number.isFinite(Number(row.realFundingId)) &&
              Number(row.realFundingId) > 0
                ? Number(row.realFundingId)
                : null,
            projectId: (() => {
              const explicitProjectId =
                Number.isFinite(Number(row.projectId)) &&
                Number(row.projectId) > 0
                  ? Number(row.projectId)
                  : null;
              if (explicitProjectId) return explicitProjectId;

              const normalizedProjectName = String(row.projectName || "")
                .trim()
                .toLocaleLowerCase();
              return projectSuggestionIdByName.get(normalizedProjectName) ?? null;
            })(),
            projectName: String(row.projectName || "").trim(),
            title: String(row.title || "").trim(),
            amountEuro: normalizeAmountInput(row.amountEuro),
            desiredFundingIds: Array.isArray(row.desiredFundingIds)
              ? row.desiredFundingIds
                  .map((desiredFundingId) => Number(desiredFundingId))
                  .filter(
                    (desiredFundingId) =>
                      Number.isFinite(desiredFundingId) && desiredFundingId > 0,
                  )
              : [],
            desiredFundingAllocations: Array.isArray(row.desiredFundingAllocations)
              ? row.desiredFundingAllocations
                  .map((allocation) => {
                    const desiredFundingId = Number(
                      allocation?.desiredFundingId,
                    );
                    if (
                      !Number.isFinite(desiredFundingId) ||
                      desiredFundingId <= 0
                    ) {
                      return null;
                    }

                    return {
                      desiredFundingId,
                      amountEuro: normalizeAmountInput(allocation.amountEuro),
                    } satisfies DesiredFundingAllocationDraft;
                  })
                  .filter(
                    (
                      allocation,
                    ): allocation is DesiredFundingAllocationDraft => !!allocation,
                  )
              : [],
            initialDesiredFundingIds: Array.isArray(row.initialDesiredFundingIds)
              ? row.initialDesiredFundingIds
                  .map((desiredFundingId) => Number(desiredFundingId))
                  .filter(
                    (desiredFundingId) =>
                      Number.isFinite(desiredFundingId) && desiredFundingId > 0,
                  )
              : [],
            desiredFundingSelectionTouched: !!row.desiredFundingSelectionTouched,
            expectedPayments: Array.isArray(row.expectedPayments)
              ? row.expectedPayments.map((payment) => ({
                  ...normalizeExpectedPaymentDraft(payment),
                }))
              : [],
            initialExpectedPaymentIds: Array.isArray(row.initialExpectedPaymentIds)
              ? row.initialExpectedPaymentIds
                  .map((paymentId) => Number(paymentId))
                  .filter((paymentId) => Number.isFinite(paymentId) && paymentId > 0)
              : [],
          }))
          .filter((row) => row.projectName || row.projectId || row.amountEuro),
      }))
      .filter((group) => group.year || group.projectRows.length > 0);

    if (cleanedGroups.length === 0) {
      setCreationError("Ajoutez au moins une année.");
      return;
    }

    const hasInvalidYear = cleanedGroups.some(
      (group) => !/^\d{4}$/.test(group.year),
    );
    if (hasInvalidYear) {
      setCreationError("Chaque année doit etre renseignee au format YYYY.");
      return;
    }

    const hasNoProjectInGroup = cleanedGroups.some(
      (group) => group.projectRows.length === 0,
    );
    if (hasNoProjectInGroup) {
      setCreationError(
        "Chaque année doit contenir au moins un projet finance.",
      );
      return;
    }

    const hasInvalidProjectRow = cleanedGroups.some((group) =>
      group.projectRows.some((row) => {
        const rowAmount = parseAmountInput(row.amountEuro);
        const hasProjectReference =
          !!String(row.projectName || "").trim() ||
          (Number.isFinite(Number(row.projectId)) && Number(row.projectId) > 0);
        return !hasProjectReference || null === rowAmount || rowAmount <= 0;
      }),
    );
    if (hasInvalidProjectRow) {
      setCreationError(
        "Chaque projet financé doit avoir un nom et un montant valide.",
      );
      return;
    }

    const expectedPayments = Array.isArray(form.expectedPayments)
      ? form.expectedPayments.map((payment) => normalizeExpectedPaymentDraft(payment))
      : [];
    const initialExpectedPaymentIds = Array.isArray(form.initialExpectedPaymentIds)
      ? form.initialExpectedPaymentIds
          .map((paymentId) => Number(paymentId))
          .filter((paymentId) => Number.isFinite(paymentId) && paymentId > 0)
      : [];
    const expectedPaymentsError = validateExpectedPaymentsList(expectedPayments);
    if (expectedPaymentsError) {
      setCreationError(expectedPaymentsError);
      return;
    }

    for (const group of cleanedGroups) {
      for (const row of group.projectRows) {
        if (
          row.initialDesiredFundingIds.length > 0 ||
          !row.desiredFundingSelectionTouched ||
          row.desiredFundingIds.length === 0
        ) {
          continue;
        }

        const rowAmountEuro = parseAmountInput(row.amountEuro) ?? 0;
        if (rowAmountEuro <= 0) {
          continue;
        }

        const projectId = Number(row.projectId || 0);
        const desiredFundingCatalog =
          projectId > 0
            ? desiredFundingCatalogByProjectId[projectId]?.desiredFundings || []
            : [];
        const desiredFundingCatalogById = new Map(
          desiredFundingCatalog.map((option) => [option.id, option] as const),
        );
        const selectedDesiredFundingIds = Array.from(
          new Set(
            row.desiredFundingIds.filter(
              (desiredFundingId) =>
                Number.isFinite(desiredFundingId) && desiredFundingId > 0,
            ),
          ),
        );
        const selectedDesiredFundingTotalEuro = selectedDesiredFundingIds.reduce(
          (sum, desiredFundingId) =>
            sum +
            getDesiredFundingOptionAmountEuro(
              desiredFundingCatalogById.get(desiredFundingId) || {
                id: desiredFundingId,
                title: "",
                amount: 0,
                convertedAmount: 0,
                currencyCode: "EUR",
                currencySymbol: "€",
              },
            ),
          0,
        );

        if (selectedDesiredFundingTotalEuro <= rowAmountEuro + 0.0001) {
          continue;
        }

        const automaticAllocationByDesiredFundingId =
          buildAutomaticDesiredFundingAllocationMap(
            selectedDesiredFundingIds
              .map((desiredFundingId) =>
                desiredFundingCatalogById.get(desiredFundingId),
              )
              .filter(
                (
                  option,
                ): option is ProjectDesiredFundingOption => !!option,
              ),
            rowAmountEuro,
          );
        const allocationEntryByDesiredFundingId = new Map(
          row.desiredFundingAllocations
            .map((allocation) => {
              const desiredFundingId = Number(allocation.desiredFundingId || 0);
              if (
                !Number.isFinite(desiredFundingId) ||
                desiredFundingId <= 0
              ) {
                return null;
              }

              return [desiredFundingId, String(allocation.amountEuro || "")] as const;
            })
            .filter(
              (
                entry,
              ): entry is readonly [number, string] => Array.isArray(entry),
            ),
        );

        let manualAllocationTotalEuro = 0;
        for (const desiredFundingId of selectedDesiredFundingIds) {
          const manualAmountEuro = allocationEntryByDesiredFundingId.has(
            desiredFundingId,
          )
            ? parseAmountInput(
                allocationEntryByDesiredFundingId.get(desiredFundingId) || "",
              )
            : automaticAllocationByDesiredFundingId.get(desiredFundingId) ?? null;
          const desiredFundingOption =
            desiredFundingCatalogById.get(desiredFundingId) || null;
          const desiredFundingLabel = desiredFundingOption
            ? getDesiredFundingOptionLabel(desiredFundingOption)
            : `Financement souhaité #${desiredFundingId}`;
          const desiredFundingCapEuro = desiredFundingOption
            ? getDesiredFundingOptionAmountEuro(desiredFundingOption)
            : 0;

          if (!Number.isFinite(manualAmountEuro) || manualAmountEuro <= 0) {
            setCreationError(
              `Renseignez la répartition manuelle pour "${desiredFundingLabel}" sur "${row.projectName || "ce projet"}".`,
            );
            return;
          }

          if (
            desiredFundingCapEuro > 0 &&
            manualAmountEuro > desiredFundingCapEuro + 0.0001
          ) {
            setCreationError(
              `La répartition saisie pour "${desiredFundingLabel}" dépasse le montant disponible.`,
            );
            return;
          }

          manualAllocationTotalEuro += manualAmountEuro;
        }

        manualAllocationTotalEuro = Number(manualAllocationTotalEuro.toFixed(2));
        if (Math.abs(manualAllocationTotalEuro - rowAmountEuro) > 0.01) {
          setCreationError(
            `La répartition manuelle de "${row.projectName || "ce projet"}" doit être égale au montant de la ligne (${formatEuro(rowAmountEuro)}).`,
          );
          return;
        }
      }
    }

    return {
      id: editingPendingDraft?.id || createDraftId("pending-contract"),
      financeurId: selectedFinanceur.id ?? null,
      financeurName: String(selectedFinanceur.name || "").trim() || "Financeur",
      createdAt: editingPendingDraft?.createdAt || new Date().toISOString(),
      title,
      contractDate: form.contractDate,
      amountEuro: contractAmount.toFixed(2),
      notes: String(form.notes || "").trim(),
      expectedPayments,
      initialExpectedPaymentIds,
      yearGroups: cleanedGroups.map((group) => ({
        ...group,
        projectRows: (() => {
          const normalizedRows = group.projectRows.map((row) => ({
            ...row,
            lineId:
              Number.isFinite(Number(row.lineId)) && Number(row.lineId) > 0
                ? Number(row.lineId)
                : null,
            realFundingId:
              Number.isFinite(Number(row.realFundingId)) &&
              Number(row.realFundingId) > 0
                ? Number(row.realFundingId)
                : null,
            projectId: (() => {
              const explicitProjectId =
                Number.isFinite(Number(row.projectId)) &&
                Number(row.projectId) > 0
                  ? Number(row.projectId)
                  : null;
              if (explicitProjectId) return explicitProjectId;

              const normalizedProjectName = String(row.projectName || "")
                .trim()
                .toLocaleLowerCase();
              return projectSuggestionIdByName.get(normalizedProjectName) ?? null;
            })(),
            title: String(row.title || "").trim(),
            amountEuro: String(parseAmountInput(row.amountEuro) ?? 0),
            desiredFundingIds: Array.isArray(row.desiredFundingIds)
              ? row.desiredFundingIds
                  .map((desiredFundingId) => Number(desiredFundingId))
                  .filter(
                    (desiredFundingId) =>
                      Number.isFinite(desiredFundingId) && desiredFundingId > 0,
                  )
              : [],
            desiredFundingAllocations: Array.isArray(row.desiredFundingAllocations)
              ? row.desiredFundingAllocations
                  .map((allocation) => {
                    const desiredFundingId = Number(
                      allocation?.desiredFundingId,
                    );
                    if (
                      !Number.isFinite(desiredFundingId) ||
                      desiredFundingId <= 0
                    ) {
                      return null;
                    }

                    return {
                      desiredFundingId,
                      amountEuro: String(
                        parseAmountInput(allocation.amountEuro) ?? "",
                      ),
                    } satisfies DesiredFundingAllocationDraft;
                  })
                  .filter(
                    (
                      allocation,
                    ): allocation is DesiredFundingAllocationDraft => !!allocation,
                  )
              : [],
            initialDesiredFundingIds: Array.isArray(row.initialDesiredFundingIds)
              ? row.initialDesiredFundingIds
                  .map((desiredFundingId) => Number(desiredFundingId))
                  .filter(
                    (desiredFundingId) =>
                      Number.isFinite(desiredFundingId) && desiredFundingId > 0,
                  )
              : [],
            desiredFundingSelectionTouched: !!row.desiredFundingSelectionTouched,
            expectedPayments: [],
            initialExpectedPaymentIds: [],
          }));

          const seenKeys = new Set<string>();
          return normalizedRows.filter((row) => {
            if (Number.isFinite(Number(row.lineId)) && Number(row.lineId) > 0) {
              const key = `line-${Number(row.lineId)}`;
              if (seenKeys.has(key)) return false;
              seenKeys.add(key);
              return true;
            }
            if (
              Number.isFinite(Number(row.realFundingId)) &&
              Number(row.realFundingId) > 0
            ) {
              const key = `rf-${Number(row.realFundingId)}`;
              if (seenKeys.has(key)) return false;
              seenKeys.add(key);
            }
            return true;
          });
        })(),
      })),
    };
  }, [
    desiredFundingCatalogByProjectId,
    editingPendingDraft?.createdAt,
    editingPendingDraft?.id,
    normalizedCreationDraftState.form,
    projectSuggestionIdByName,
    selectedFinanceur,
  ]);

  const handleSavePendingContractDraft = useCallback(() => {
    if (editingPersistedContract) {
      setCreationError(
        'Vous modifiez un contrat existant. Utilisez "Enregistrer les modifications".',
      );
      return;
    }

    const pendingContract = buildPendingContractDraftFromForm();
    if (!pendingContract) return;

    setCreationError(null);
    setSavePendingError(null);
    setCreationDraftState((prev) => {
      const normalizedPrev = normalizeCreationDraftState(prev);
      const alreadyExists = normalizedPrev.pending.some(
        (item) => item.id === pendingContract.id,
      );
      const nextPending = alreadyExists
        ? normalizedPrev.pending.map((item) =>
            item.id === pendingContract.id ? pendingContract : item,
          )
        : [pendingContract, ...normalizedPrev.pending];

      return {
        ...normalizedPrev,
        isCreating: false,
        form: null,
        pending: nextPending,
      };
    });
    setEditingPendingDraftId(null);
  }, [
    buildPendingContractDraftFromForm,
    editingPersistedContract,
    setCreationDraftState,
  ]);

  const removePendingContract = useCallback(
    (draftId: string) => {
      if (!window.confirm("Supprimer ce brouillon de contrat ?")) return;
      const isEditedDraft = editingPendingDraftId === draftId;
      setCreationDraftState((prev) => {
        const normalizedPrev = normalizeCreationDraftState(prev);
        return {
          ...normalizedPrev,
          pending: normalizedPrev.pending.filter((item) => item.id !== draftId),
          ...(isEditedDraft
            ? {
                isCreating: false,
                form: null,
              }
            : {}),
        };
      });
      if (isEditedDraft) {
        setEditingPendingDraftId(null);
      }
    },
    [editingPendingDraftId, setCreationDraftState],
  );

  const openPendingContractForEdit = useCallback(
    (draftId: string) => {
      setContractFormStep(1);
      setCreationError(null);
      setSavePendingError(null);
      setActiveLineMove(null);
      setMoveLineError(null);
      setEditingPersistedContract(null);
      setCreationDraftState((prev) => {
        const normalizedPrev = normalizeCreationDraftState(prev);
        const draftContract = normalizedPrev.pending.find(
          (item) => item.id === draftId,
        );
        if (!draftContract) return normalizedPrev;

        return {
          ...normalizedPrev,
          isCreating: true,
          financeurId: draftContract.financeurId,
          financeurName: draftContract.financeurName,
          form: {
            title: draftContract.title,
            contractDate: draftContract.contractDate,
            amountEuro: draftContract.amountEuro,
            notes: draftContract.notes,
            expectedPayments: draftContract.expectedPayments,
            initialExpectedPaymentIds: draftContract.initialExpectedPaymentIds,
            yearGroups: draftContract.yearGroups.map((group) => ({
              ...group,
              projectRows: group.projectRows.map((row) => ({
                ...row,
              })),
            })),
          },
        };
      });
      setEditingPendingDraftId(draftId);
    },
    [setCreationDraftState],
  );

  const openSavedContractForEdit = useCallback(
    (contract: SynthesesGroupedContractNode, index: number) => {
      if (!selectedFinanceur) return;
      setContractFormStep(1);
      const sourceType = String(contract.sourceType || "").trim();
      const isSynthesesContract = sourceType === "syntheses_contract";
      const isLegacyContract =
        sourceType === "legacy_real_funding_group" ||
        sourceType === "legacy_real_funding";
      const contractId = isSynthesesContract
        ? extractSynthesesContractId(contract)
        : null;
      const legacyRealFundingIds = isLegacyContract
        ? extractRealFundingIds(contract)
        : [];
      if (!isSynthesesContract && !isLegacyContract) {
        setSavePendingError(
          "Ce contrat ne peut pas être modifié depuis cette vue.",
        );
        return;
      }
      if (isSynthesesContract && !contractId) {
        setSavePendingError("Identifiant contrat manquant.");
        return;
      }
      if (isLegacyContract && legacyRealFundingIds.length === 0) {
        setSavePendingError("Ce contrat legacy ne peut pas être modifié.");
        return;
      }

      setCreationError(null);
      setSavePendingError(null);
      setActiveLineMove(null);
      setMoveLineError(null);
      setEditingPendingDraftId(null);
      setEditingPersistedContract({
        sourceType,
        contractId,
        realFundingIds: legacyRealFundingIds,
        contractNodeId: String(contract.id || ""),
        contractCollapseKey: getContractCollapseKey(contract, index),
        contractLabel: String(contract.title || `Contrat ${index + 1}`).trim(),
      });
      setCreationDraftState((prev) => {
        const normalizedPrev = normalizeCreationDraftState(prev);
        return {
          ...normalizedPrev,
          isCreating: true,
          financeurId: selectedFinanceur.id ?? null,
          financeurName: String(selectedFinanceur.name || "").trim(),
          form: buildFormDraftFromSavedContract(contract, index),
        };
      });
    },
    [selectedFinanceur, setCreationDraftState],
  );

  const saveContractToApi = useCallback(
    async ({
      draftId,
      contractId,
      sourceType,
      realFundingIds,
      financeurId,
      title,
      contractDate,
      amountEuro,
      notes,
      expectedPayments,
      initialExpectedPaymentIds,
      yearGroups,
    }: {
      draftId: string | null;
      contractId?: number | null;
      sourceType?: string | null;
      realFundingIds?: number[];
      financeurId: number;
      title: string;
      contractDate: string;
      amountEuro: string;
      notes: string;
      expectedPayments: DraftExpectedPayment[];
      initialExpectedPaymentIds: number[];
      yearGroups: GroupContractYearGroupDraft[];
    }) => {
      if (savingPendingDraftRef.current || savingPendingDraftId) {
        return { success: false as const, payload: null };
      }

      if (!saveContractEndpoint) {
        setSavePendingError(
          "La synchronisation n'est pas configurée pour enregistrer le contrat.",
        );
        return { success: false as const, payload: null };
      }

      if (!financeurId) {
        setSavePendingError("Financeur invalide pour ce contrat.");
        return { success: false as const, payload: null };
      }

      const requestId =
        draftId ||
        (contractId ? `contract-${contractId}` : createDraftId("save"));
      setSavePendingError(null);
      savingPendingDraftRef.current = requestId;
      setSavingPendingDraftId(requestId);

      const optimisticFinanceur =
        selectedFinanceur ||
        ({
          id: financeurId,
          name: "Financeur",
          email: null,
          isVerdeo: false,
          contracts: [],
          totals: {
            contractAmountEuro: 0,
            allocationsEuro: 0,
            expectedPaymentsEuro: 0,
            receivedPaymentsEuro: 0,
            landPaymentsEuro: 0,
          },
        } satisfies SynthesesGroupedFinanceurItem);
      const existingContract = findContractInGroupedData(data, {
        contractId,
        sourceType,
        realFundingIds,
      });
      const optimisticContract = buildOptimisticContractNode({
        draftId,
        contractId,
        sourceType,
        realFundingIds,
        financeur: optimisticFinanceur,
        title,
        contractDate,
        amountEuro,
        notes,
        expectedPayments,
        yearGroups,
        existingContract,
      });
      setSavingContractNodeId(String(optimisticContract.id || ""));
      const didApplyOptimisticContract = !!onMutateData;
      const optimisticSnapshot = onMutateData?.((current) =>
        upsertSynthesesContractInGroupedData(
          current,
          {
            id: optimisticFinanceur.id ?? financeurId,
            name: optimisticFinanceur.name || "Financeur",
            email: optimisticFinanceur.email,
            isVerdeo: optimisticFinanceur.isVerdeo,
          },
          optimisticContract,
          {
            contractNodeId: existingContract?.id || optimisticContract.id,
            synthesesContractId: contractId,
            realFundingIds,
          },
        ),
      ) ?? null;

      try {
        const token = sessionManager.getToken();
        const response = await fetch(saveContractEndpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          credentials: "include",
          body: JSON.stringify({
            ...(contractId && contractId > 0 ? { contractId } : {}),
            ...(sourceType ? { sourceType } : {}),
            ...(Array.isArray(realFundingIds) && realFundingIds.length > 0
              ? { realFundingIds }
              : {}),
            financeurId,
            title,
            contractDate,
            amountEuro,
            notes,
            expectedPayments: expectedPayments.map((payment) => ({
              id: payment.id,
              amount: payment.amount,
              expectedAt: payment.expectedAt,
              status: payment.status,
              receivedAt: payment.receivedAt,
            })),
            initialExpectedPaymentIds,
            yearGroups: yearGroups.map((group) => ({
              year: group.year,
              projectRows: group.projectRows.map((row) => ({
                lineId: row.lineId,
                realFundingId: row.realFundingId,
                projectId: row.projectId,
                projectName: row.projectName,
                title: String(row.title || "").trim() || title,
                amountEuro: row.amountEuro,
                ...(row.initialDesiredFundingIds.length === 0 &&
                row.desiredFundingSelectionTouched &&
                row.desiredFundingIds.length > 0
                  ? (() => {
                      const desiredFundingIds = Array.from(
                        new Set(
                          row.desiredFundingIds.filter(
                            (desiredFundingId) =>
                              Number.isFinite(Number(desiredFundingId)) &&
                              Number(desiredFundingId) > 0,
                          ),
                        ),
                      );
                      const rowAmountEuroValue =
                        parseAmountInput(row.amountEuro) ?? 0;
                      const projectId = Number(row.projectId || 0);
                      const desiredFundingCatalog =
                        projectId > 0
                          ? desiredFundingCatalogByProjectId[projectId]
                              ?.desiredFundings || []
                          : [];
                      const desiredFundingCatalogById = new Map(
                        desiredFundingCatalog.map((option) => [option.id, option] as const),
                      );
                      const selectedDesiredFundingOptions = desiredFundingIds
                        .map((desiredFundingId) =>
                          desiredFundingCatalogById.get(desiredFundingId),
                        )
                        .filter(
                          (
                            option,
                          ): option is ProjectDesiredFundingOption => !!option,
                        );
                      const selectedDesiredFundingTotalEuro =
                        selectedDesiredFundingOptions.reduce(
                          (sum, option) =>
                            sum + getDesiredFundingOptionAmountEuro(option),
                          0,
                        );
                      const requiresManualDesiredFundingAllocation =
                        selectedDesiredFundingTotalEuro >
                        rowAmountEuroValue + 0.0001;
                      const automaticDesiredFundingAllocationById =
                        buildAutomaticDesiredFundingAllocationMap(
                          selectedDesiredFundingOptions,
                          rowAmountEuroValue,
                        );
                      const desiredFundingAllocationInputById = new Map(
                        Array.isArray(row.desiredFundingAllocations)
                          ? row.desiredFundingAllocations
                              .map((allocation) => {
                                const desiredFundingId = Number(
                                  allocation?.desiredFundingId,
                                );
                                if (
                                  !Number.isFinite(desiredFundingId) ||
                                  desiredFundingId <= 0
                                ) {
                                  return null;
                                }

                                return [
                                  desiredFundingId,
                                  String(allocation?.amountEuro || ""),
                                ] as const;
                              })
                              .filter(
                                (
                                  allocation,
                                ): allocation is readonly [number, string] =>
                                  Array.isArray(allocation),
                              )
                          : [],
                      );
                      const desiredFundingAllocations =
                        requiresManualDesiredFundingAllocation
                          ? desiredFundingIds
                              .map((desiredFundingId) => {
                                const amountEuro =
                                  desiredFundingAllocationInputById.has(
                                    desiredFundingId,
                                  )
                                    ? parseAmountInput(
                                        desiredFundingAllocationInputById.get(
                                          desiredFundingId,
                                        ) || "",
                                      )
                                    : automaticDesiredFundingAllocationById.get(
                                        desiredFundingId,
                                      ) ?? null;

                                if (
                                  !Number.isFinite(amountEuro) ||
                                  null === amountEuro ||
                                  amountEuro <= 0
                                ) {
                                  return null;
                                }

                                return {
                                  desiredFundingId,
                                  amountEuro: Number(amountEuro.toFixed(2)),
                                };
                              })
                              .filter((allocation) => !!allocation)
                          : [];

                      return {
                        desiredFundingIds,
                        ...(desiredFundingAllocations.length > 0
                          ? { desiredFundingAllocations }
                          : {}),
                      };
                    })()
                  : {}),
              })),
            })),
          }),
        });

        const payload = await response.json().catch(() => null);
        if (!response.ok) {
          const message = String(
            payload?.message || payload?.error || `HTTP ${response.status}`,
          );
          throw new Error(message);
        }

        const finalContract = buildOptimisticContractNode({
          draftId,
          contractId,
          sourceType,
          realFundingIds,
          financeur: optimisticFinanceur,
          title,
          contractDate,
          amountEuro,
          notes,
          expectedPayments,
          yearGroups,
          existingContract: optimisticContract,
          responseContract: payload?.contract,
        });
        onMutateData?.((current) =>
          upsertSynthesesContractInGroupedData(
            current,
            {
              id: optimisticFinanceur.id ?? financeurId,
              name: optimisticFinanceur.name || "Financeur",
              email: optimisticFinanceur.email,
              isVerdeo: optimisticFinanceur.isVerdeo,
            },
            finalContract,
            {
              contractNodeId: optimisticContract.id,
              synthesesContractId:
                Number(finalContract.synthesesContractId || 0) > 0
                  ? Number(finalContract.synthesesContractId)
                  : contractId,
              realFundingIds,
            },
          ),
        );

        const createdContractId = Number(payload?.contract?.id || 0);
        const createdContractNodeId =
          !contractId &&
          Number.isFinite(createdContractId) &&
          createdContractId > 0
            ? `synth-contract-${createdContractId}`
            : null;

        if (draftId) {
          setCreationDraftState((prev) => {
            const normalizedPrev = normalizeCreationDraftState(prev);
            return {
              ...normalizedPrev,
              pending: normalizedPrev.pending.filter(
                (item) => item.id !== draftId,
              ),
            };
          });
        }

        if (onRefreshData) {
          void Promise.resolve(onRefreshData()).catch(() => undefined);
        }
        if (createdContractNodeId) {
          scrollToContractNode(createdContractNodeId);
        }

        return { success: true as const, payload };
      } catch (error: unknown) {
        const message =
          error instanceof Error
            ? error.message
            : "Erreur sauvegarde du contrat";
        if (didApplyOptimisticContract) {
          onRestoreData?.(optimisticSnapshot);
        }
        setSavePendingError(String(message || "Erreur sauvegarde du contrat"));
        return { success: false as const, payload: null };
      } finally {
        savingPendingDraftRef.current = null;
        setSavingPendingDraftId(null);
        setSavingContractNodeId((current) =>
          current === String(optimisticContract.id || "") ? null : current,
        );
      }
    },
    [
      desiredFundingCatalogByProjectId,
      data,
      onMutateData,
      onRefreshData,
      onRestoreData,
      saveContractEndpoint,
      savingPendingDraftId,
      scrollToContractNode,
      selectedFinanceur,
      setCreationDraftState,
    ],
  );

  const savePendingContractToApi = useCallback(
    async (draftContract: PendingGroupContractDraft) => {
      if (!draftContract.financeurId) {
        setSavePendingError("Financeur invalide pour ce brouillon.");
        return false;
      }

      const result = await saveContractToApi({
        draftId: draftContract.id,
        financeurId: draftContract.financeurId,
        title: draftContract.title,
        contractDate: draftContract.contractDate,
        amountEuro: draftContract.amountEuro,
        notes: draftContract.notes,
        expectedPayments: draftContract.expectedPayments,
        initialExpectedPaymentIds: draftContract.initialExpectedPaymentIds,
        yearGroups: draftContract.yearGroups,
      });
      return result.success;
    },
    [saveContractToApi],
  );

  const deletePersistedContractRequest = useCallback(
    async (contract: SynthesesGroupedContractNode) => {
      const deletionInfo = getPersistedContractDeletionInfo(contract);
      if (!deletionInfo.canManage || !deletionInfo.payload) {
        throw new Error("Ce contrat ne peut pas être supprimé depuis cette vue.");
      }
      if (!deleteContractEndpoint) {
        throw new Error("Endpoint de suppression non configuré.");
      }

      const token = sessionManager.getToken();
      const response = await fetch(deleteContractEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify(deletionInfo.payload),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const message = String(
          payload?.message || payload?.error || `HTTP ${response.status}`,
        );
        throw new Error(message);
      }
    },
    [deleteContractEndpoint],
  );

  const handleDeletePersistedContract = useCallback(
    async (contract: SynthesesGroupedContractNode) => {
      const deletionInfo = getPersistedContractDeletionInfo(contract);
      if (!deletionInfo.canManage || !deletionInfo.payload) {
        setSavePendingError("Ce contrat ne peut pas être supprimé depuis cette vue.");
        return;
      }
      if (deletingContractKey || savingPendingDraftId || bulkDeletingFinanceurId)
        return;

      const confirmed = window.confirm(
        "Supprimer ce contrat ? Cette action est définitive.",
      );
      if (!confirmed) return;

      setCreationError(null);
      setSavePendingError(null);
      setActiveLineMove(null);
      setMoveLineError(null);
      setDeletingContractKey(String(contract.id || ""));
      const didApplyOptimisticDelete = !!onMutateData;
      const optimisticDeleteSnapshot = onMutateData?.((current) =>
        removeSynthesesContractsFromGroupedData(current, {
          contractNodeId: contract.id,
          synthesesContractId:
            "contractId" in deletionInfo.payload
              ? deletionInfo.payload.contractId
              : extractSynthesesContractId(contract),
          realFundingIds:
            "realFundingIds" in deletionInfo.payload
              ? deletionInfo.payload.realFundingIds
              : extractRealFundingIds(contract),
        }),
      ) ?? null;
      try {
        await deletePersistedContractRequest(contract);

        const isEditingCurrent =
          !!editingPersistedContract &&
          ((contract.sourceType === "syntheses_contract" &&
            editingPersistedContract.sourceType === "syntheses_contract" &&
            Number(editingPersistedContract.contractId || 0) ===
              Number(
                "contractId" in deletionInfo.payload
                  ? deletionInfo.payload.contractId
                  : 0,
              )) ||
            ((contract.sourceType === "legacy_real_funding_group" ||
              contract.sourceType === "legacy_real_funding") &&
              (editingPersistedContract.sourceType ===
                "legacy_real_funding_group" ||
                editingPersistedContract.sourceType ===
                  "legacy_real_funding") &&
              editingPersistedContract.realFundingIds.some((value) =>
                "realFundingIds" in deletionInfo.payload
                  ? deletionInfo.payload.realFundingIds.includes(value)
                  : false,
              )));
        if (isEditingCurrent) {
          setEditingPersistedContract(null);
          setEditingPendingDraftId(null);
          setCreationDraftState((prev) => {
            const normalizedPrev = normalizeCreationDraftState(prev);
            return {
              ...normalizedPrev,
              isCreating: false,
              form: null,
            };
          });
        }

        if (onRefreshData) {
          void Promise.resolve(onRefreshData()).catch(() => undefined);
        }
      } catch (error: unknown) {
        if (didApplyOptimisticDelete) {
          onRestoreData?.(optimisticDeleteSnapshot);
        }
        const message =
          error instanceof Error
            ? error.message
            : "Erreur suppression du contrat";
        setSavePendingError(String(message || "Erreur suppression du contrat"));
      } finally {
        setDeletingContractKey(null);
      }
    },
    [
      bulkDeletingFinanceurId,
      deletePersistedContractRequest,
      deletingContractKey,
      editingPersistedContract,
      onMutateData,
      onRefreshData,
      onRestoreData,
      savingPendingDraftId,
      setCreationDraftState,
    ],
  );

  const openContractLineMove = useCallback(
    (
      contract: SynthesesGroupedContractNode,
      index: number,
      row: ContractLineDisplayRow,
    ) => {
      if (normalizedCreationDraftState.isCreating) {
        setSavePendingError(
          "Fermez d'abord le formulaire en cours avant de déplacer une ligne.",
        );
        return;
      }

      const sourceContractId = extractSynthesesContractId(contract);
      if (
        !Number.isFinite(Number(sourceContractId)) ||
        Number(sourceContractId || 0) <= 0 ||
        !row.lineId
      ) {
        setSavePendingError("Cette ligne ne peut pas être déplacée.");
        return;
      }

      const moveTargets =
        selectedFinanceurMoveTargetOptionsByContractId.get(
          Number(sourceContractId),
        ) || [];
      if (moveTargets.length === 0) {
        setSavePendingError(
          "Aucun contrat cible disponible pour ce déplacement.",
        );
        return;
      }

      setCreationError(null);
      setSavePendingError(null);
      setMoveLineError(null);
      setActiveLineMove({
        sourceContractId: Number(sourceContractId),
        sourceContractLabel: String(
          contract.title || `Contrat ${index + 1}`,
        ).trim(),
        lineId: row.lineId,
        lineKey: row.key,
        year: row.year,
        projectName: row.projectName,
        amountEuro: row.amountEuro,
        targetContractId:
          moveTargets.length === 1 ? String(moveTargets[0].contractId) : "",
      });
    },
    [
      normalizedCreationDraftState.isCreating,
      selectedFinanceurMoveTargetOptionsByContractId,
    ],
  );

  const cancelContractLineMove = useCallback(() => {
    if (movingLineRequestKey) return;
    setActiveLineMove(null);
    setMoveLineError(null);
  }, [movingLineRequestKey]);

  const updateContractLineMoveTarget = useCallback((targetContractId: string) => {
    setMoveLineError(null);
    setActiveLineMove((prev) =>
      prev
        ? {
            ...prev,
            targetContractId,
          }
        : prev,
    );
  }, []);

  const confirmContractLineMove = useCallback(async () => {
    if (!activeLineMove) return;
    if (normalizedCreationDraftState.isCreating) {
      setMoveLineError(
        "Fermez d'abord le formulaire en cours avant de déplacer une ligne.",
      );
      return;
    }
    if (
      deletingContractKey ||
      savingPendingDraftId ||
      bulkDeletingFinanceurId ||
      movingLineRequestKey
    ) {
      return;
    }
    if (!moveContractLineEndpoint) {
      setMoveLineError("L'endpoint de déplacement n'est pas configuré.");
      return;
    }

    const targetContractId = Number(activeLineMove.targetContractId || 0);
    if (!Number.isFinite(targetContractId) || targetContractId <= 0) {
      setMoveLineError("Choisissez un contrat cible valide.");
      return;
    }

    setMoveLineError(null);
    setMovingLineRequestKey(activeLineMove.lineKey);
    try {
      const token = sessionManager.getToken();
      const response = await fetch(moveContractLineEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          sourceContractId: activeLineMove.sourceContractId,
          targetContractId,
          lineId: activeLineMove.lineId,
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const message = String(
          payload?.message || payload?.error || `HTTP ${response.status}`,
        );
        throw new Error(message);
      }

      setActiveLineMove(null);
      setMoveLineError(null);

      if (onRefreshData) {
        await Promise.resolve(onRefreshData());
      }
      scrollToContractNode(`synth-contract-${targetContractId}`);
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors du déplacement de la ligne";
      setMoveLineError(
        String(message || "Erreur lors du déplacement de la ligne"),
      );
    } finally {
      setMovingLineRequestKey(null);
    }
  }, [
    activeLineMove,
    bulkDeletingFinanceurId,
    deletingContractKey,
    moveContractLineEndpoint,
    movingLineRequestKey,
    normalizedCreationDraftState.isCreating,
    onRefreshData,
    savingPendingDraftId,
    scrollToContractNode,
  ]);

  const openContractsMerge = useCallback(() => {
    if (normalizedCreationDraftState.isCreating) {
      setSavePendingError(
        "Fermez d'abord le formulaire en cours avant de fusionner des contrats.",
      );
      return;
    }

    if (selectedFinanceurMergeableContracts.length < 2) {
      setSavePendingError(
        "Il faut au moins deux contrats modifiables pour lancer une fusion.",
      );
      return;
    }

    const selectedContractId = selectedVisibleContract
      ? getMergeableSynthesesContractId(selectedVisibleContract)
      : null;
    const defaultTargetId =
      selectedContractId ||
      selectedFinanceurMergeableContracts[0]?.contractId ||
      null;
    if (!defaultTargetId) {
      setSavePendingError("Aucun contrat cible disponible pour cette fusion.");
      return;
    }

    setCreationError(null);
    setSavePendingError(null);
    setActiveLineMove(null);
    setMoveLineError(null);
    setMergeContractsError(null);
    setActiveContractsMerge({
      targetContractId: String(defaultTargetId),
      sourceContractIds: [],
    });
  }, [
    normalizedCreationDraftState.isCreating,
    selectedFinanceurMergeableContracts,
    selectedVisibleContract,
  ]);

  const closeContractsMerge = useCallback(() => {
    if (mergingContracts) return;
    setActiveContractsMerge(null);
    setMergeContractsError(null);
  }, [mergingContracts]);

  const updateContractsMergeTarget = useCallback((targetContractId: string) => {
    setMergeContractsError(null);
    setActiveContractsMerge((current) => {
      if (!current) return current;
      return {
        targetContractId,
        sourceContractIds: current.sourceContractIds.filter(
          (sourceContractId) => sourceContractId !== targetContractId,
        ),
      };
    });
  }, []);

  const toggleContractsMergeSource = useCallback((contractId: number) => {
    const normalizedId = String(contractId);
    setMergeContractsError(null);
    setActiveContractsMerge((current) => {
      if (!current || current.targetContractId === normalizedId) return current;
      const alreadySelected =
        current.sourceContractIds.includes(normalizedId);
      return {
        ...current,
        sourceContractIds: alreadySelected
          ? current.sourceContractIds.filter(
              (sourceContractId) => sourceContractId !== normalizedId,
            )
          : [...current.sourceContractIds, normalizedId],
      };
    });
  }, []);

  const confirmContractsMerge = useCallback(async () => {
    if (!activeContractsMerge) return;
    if (normalizedCreationDraftState.isCreating) {
      setMergeContractsError(
        "Fermez d'abord le formulaire en cours avant de fusionner des contrats.",
      );
      return;
    }
    if (
      deletingContractKey ||
      savingPendingDraftId ||
      bulkDeletingFinanceurId ||
      movingLineRequestKey ||
      mergingContracts
    ) {
      return;
    }
    if (!mergeContractsEndpoint) {
      setMergeContractsError("L'endpoint de fusion n'est pas configuré.");
      return;
    }

    const targetContractId = Number(activeContractsMerge.targetContractId || 0);
    const sourceContractIds = Array.from(
      new Set(
        activeContractsMerge.sourceContractIds
          .map((value) => Number(value))
          .filter(
            (value) =>
              Number.isFinite(value) &&
              value > 0 &&
              value !== targetContractId,
          ),
      ),
    );

    if (!Number.isFinite(targetContractId) || targetContractId <= 0) {
      setMergeContractsError("Choisissez le contrat qui doit rester.");
      return;
    }
    if (sourceContractIds.length === 0) {
      setMergeContractsError("Sélectionnez au moins un contrat à absorber.");
      return;
    }

    setMergeContractsError(null);
    setMergingContracts(true);
    const targetOption = selectedFinanceurMergeableContracts.find(
      (option) => option.contractId === targetContractId,
    );
    const optimisticSnapshot = onMutateData?.((current) =>
      mergeSynthesesContractsInGroupedData(
        current,
        targetContractId,
        sourceContractIds,
      ),
    ) ?? null;
    const didApplyOptimisticMerge = !!onMutateData;
    const submittedMergeState = activeContractsMerge;

    if (didApplyOptimisticMerge) {
      setActiveContractsMerge(null);
      setMergeContractsError(null);
      if (targetOption) {
        selectContractForFinanceur(
          selectedFinanceurTimelineKey,
          getContractCollapseKey(targetOption.contract, targetOption.index),
        );
      }
      window.setTimeout(() => {
        scrollToContractNode(`synth-contract-${targetContractId}`);
      }, 120);
    }

    try {
      const token = sessionManager.getToken();
      const response = await fetch(mergeContractsEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({
          targetContractId,
          sourceContractIds,
        }),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        const message = String(
          payload?.message || payload?.error || `HTTP ${response.status}`,
        );
        throw new Error(message);
      }

      setActiveContractsMerge(null);
      setMergeContractsError(null);

      if (targetOption) {
        selectContractForFinanceur(
          selectedFinanceurTimelineKey,
          getContractCollapseKey(targetOption.contract, targetOption.index),
        );
      }

      if (onRefreshData) {
        if (didApplyOptimisticMerge) {
          void Promise.resolve(onRefreshData());
        } else {
          await Promise.resolve(onRefreshData());
        }
      }
      scrollToContractNode(`synth-contract-${targetContractId}`);
    } catch (error: unknown) {
      if (didApplyOptimisticMerge) {
        onRestoreData?.(optimisticSnapshot);
        setActiveContractsMerge(submittedMergeState);
      }
      const message =
        error instanceof Error
          ? error.message
          : "Erreur lors de la fusion des contrats";
      setMergeContractsError(
        String(message || "Erreur lors de la fusion des contrats"),
      );
    } finally {
      setMergingContracts(false);
    }
  }, [
    activeContractsMerge,
    bulkDeletingFinanceurId,
    deletingContractKey,
    mergeContractsEndpoint,
    mergingContracts,
    movingLineRequestKey,
    normalizedCreationDraftState.isCreating,
    onMutateData,
    onRefreshData,
    onRestoreData,
    savingPendingDraftId,
    scrollToContractNode,
    selectContractForFinanceur,
    selectedFinanceurMergeableContracts,
    selectedFinanceurTimelineKey,
  ]);

  const handleDeleteAllContractsForSelectedFinanceur = useCallback(async () => {
    if (!selectedFinanceur) return;
    if (
      deletingContractKey ||
      savingPendingDraftId ||
      isBulkDeletingSelectedFinanceur
    ) {
      return;
    }

    const deletableContracts = selectedFinanceurDeletableContracts;
    if (deletableContracts.length === 0) {
      setSavePendingError("Aucun contrat supprimable pour ce financeur.");
      return;
    }

    const readonlyContractsCount =
      (selectedFinanceur.contracts?.length || 0) - deletableContracts.length;
    const confirmed = window.confirm(
      `Supprimer ${deletableContracts.length} contrat(s) pour ce financeur ? Cette action est définitive.${
        readonlyContractsCount > 0
          ? ` ${readonlyContractsCount} contrat(s) en lecture seule ne seront pas supprimé(s).`
          : ""
      }`,
    );
    if (!confirmed) return;

    setCreationError(null);
    setSavePendingError(null);
    setActiveLineMove(null);
    setMoveLineError(null);
    setBulkDeletingFinanceurId(String(selectedFinanceur.id || ""));

    let deletedCount = 0;
    try {
      for (const contract of deletableContracts) {
        await deletePersistedContractRequest(contract);
        deletedCount += 1;
      }

      if (editingPersistedContract) {
        setEditingPersistedContract(null);
        setEditingPendingDraftId(null);
        setCreationDraftState((prev) => {
          const normalizedPrev = normalizeCreationDraftState(prev);
          return {
            ...normalizedPrev,
            isCreating: false,
            form: null,
          };
        });
      }

      if (onRefreshData) {
        await Promise.resolve(onRefreshData());
      }
    } catch (error: unknown) {
      const message =
        error instanceof Error
          ? error.message
          : "Erreur suppression des contrats";
      if (onRefreshData) {
        await Promise.resolve(onRefreshData());
      }
      setSavePendingError(
        deletedCount > 0
          ? `Suppression interrompue après ${deletedCount} contrat(s): ${String(
              message || "Erreur suppression des contrats",
            )}`
          : String(message || "Erreur suppression des contrats"),
      );
      return;
    } finally {
      setBulkDeletingFinanceurId(null);
    }
  }, [
    deletePersistedContractRequest,
    deletingContractKey,
    editingPersistedContract,
    isBulkDeletingSelectedFinanceur,
    onRefreshData,
    savingPendingDraftId,
    selectedFinanceur,
    selectedFinanceurDeletableContracts,
    setCreationDraftState,
  ]);

  const handleSaveContractDirectly = useCallback(async () => {
    if (savingPendingDraftRef.current || savingPendingDraftId) return;
    if (!saveContractEndpoint) {
      setSavePendingError(
        "La synchronisation n'est pas configurée pour enregistrer le contrat.",
      );
      return;
    }

    const pendingContract = buildPendingContractDraftFromForm();
    if (!pendingContract) return;

    const saveRequest = {
      draftId: editingPersistedContract ? null : pendingContract.id,
      contractId: editingPersistedContract?.contractId,
      sourceType: editingPersistedContract?.sourceType,
      realFundingIds: editingPersistedContract?.realFundingIds,
      financeurId: Number(pendingContract.financeurId || 0),
      title: pendingContract.title,
      contractDate: pendingContract.contractDate,
      amountEuro: pendingContract.amountEuro,
      notes: pendingContract.notes,
      expectedPayments: pendingContract.expectedPayments,
      initialExpectedPaymentIds: pendingContract.initialExpectedPaymentIds,
      yearGroups: pendingContract.yearGroups,
    };

    const backgroundSave = saveContractToApi(saveRequest);
    const isEditingExistingContract = !!editingPersistedContract;

    if (!isEditingExistingContract) {
      const optimisticContractNodeId = `optimistic-contract-${pendingContract.id}`;
      const optimisticContractCollapseKey = [
        "fallback",
        0,
        toTrimmedText(pendingContract.title),
        toTrimmedText(pendingContract.contractDate),
      ].join(":");

      setCreationError(null);
      setSavePendingError(null);
      setCreationDraftState((prev) => {
        const normalizedPrev = normalizeCreationDraftState(prev);
        return {
          ...normalizedPrev,
          isCreating: false,
          form: null,
        };
      });
      setEditingPendingDraftId(null);
      setEditingPersistedContract(null);
      setSelectedTimelineYearByFinanceurKey((current) => ({
        ...current,
        [selectedFinanceurTimelineKey]: "all",
      }));
      setSelectedContractProjectFilterByFinanceurKey((current) => ({
        ...current,
        [selectedFinanceurTimelineKey]: "all",
      }));
      selectContractForFinanceur(
        selectedFinanceurTimelineKey,
        optimisticContractCollapseKey,
      );
      scrollToContractNode(optimisticContractNodeId);

      void backgroundSave
        .then((result) => {
          if (result.success) return;
          toast({
            title: "La sauvegarde du contrat a echoue.",
            description:
              "L'ajout affiche a ete annule. Verifiez le contrat puis reessayez.",
          });
        })
        .catch(() => {
          toast({
            title: "La sauvegarde du contrat a echoue.",
            description:
              "L'ajout affiche a ete annule. Verifiez le contrat puis reessayez.",
          });
        });
      return;
    }

    setCreationError(null);
    setSavePendingError(null);
    setCreationDraftState((prev) => {
      const normalizedPrev = normalizeCreationDraftState(prev);
      return {
        ...normalizedPrev,
        isCreating: false,
        form: null,
      };
    });
    setEditingPendingDraftId(null);
    setEditingPersistedContract(null);
    selectContractForFinanceur(
      selectedFinanceurTimelineKey,
      editingPersistedContract.contractCollapseKey,
    );
    scrollToContractNode(editingPersistedContract.contractNodeId);

    void backgroundSave
      .then((result) => {
        if (result.success) return;
        toast({
          title: "La sauvegarde du contrat a echoue.",
          description:
            "La modification affichee a ete annulee. Verifiez le contrat puis reessayez.",
        });
      })
      .catch(() => {
        toast({
          title: "La sauvegarde du contrat a echoue.",
          description:
            "La modification affichee a ete annulee. Verifiez le contrat puis reessayez.",
        });
      });
  }, [
    buildPendingContractDraftFromForm,
    editingPersistedContract,
    saveContractToApi,
    saveContractEndpoint,
    scrollToContractNode,
    selectContractForFinanceur,
    selectedFinanceur,
    selectedFinanceurTimelineKey,
    savingPendingDraftId,
    setCreationDraftState,
  ]);

  const isCreationFormOpen =
    normalizedCreationDraftState.isCreating &&
    String(normalizedCreationDraftState.financeurId ?? "") ===
      String(selectedFinanceur?.id ?? "") &&
    !!normalizedCreationDraftState.form;

  const activeCreationForm =
    isCreationFormOpen && normalizedCreationDraftState.form
      ? normalizedCreationDraftState.form
      : null;
  const isEditingPersistedContract = !!editingPersistedContract;

  useEffect(() => {
    if (
      !pendingRouteTarget ||
      !selectedFinanceur ||
      !financeurRouteTargetRequestsDesiredFundingAllocation(pendingRouteTarget)
    ) {
      return;
    }

    const targetFinanceur = findFinanceurForRouteTarget(
      financeurs,
      pendingRouteTarget,
    );
    if (!targetFinanceur) return;

    const targetFinanceurKey = getFinanceurListKey(
      targetFinanceur.id,
      targetFinanceur.name,
    );
    if (selectedFinanceurKey !== targetFinanceurKey) return;

    const financeurKey = getFinanceurListKey(
      selectedFinanceur.id,
      selectedFinanceur.name,
    );
    const targetContractIndex = findContractIndexForRouteTarget(
      selectedFinanceur.contracts,
      pendingRouteTarget,
    );
    if (targetContractIndex < 0) return;

    const targetContract = selectedFinanceur.contracts[targetContractIndex];
    const targetCollapseKey = getContractCollapseKey(
      targetContract,
      targetContractIndex,
    );
    const targetContractNodeId = String(targetContract.id || "").trim();
    if (!targetContractNodeId) return;

    const isExpanded =
      expandedContractKeysByFinanceur[financeurKey]?.[targetCollapseKey] === true;
    if (!isExpanded) return;

    const targetNode = document.querySelector<HTMLElement>(
      `[data-contract-node-id="${targetContractNodeId}"]`,
    );
    if (!targetNode) return;

    if (!isPersistedContractBeingEdited(targetContract, editingPersistedContract)) {
      scrollToContractNode(targetContractNodeId);
      openSavedContractForEdit(targetContract, targetContractIndex);
      return;
    }

    if (!activeCreationForm) return;

    if (contractFormStep !== 2) {
      setContractFormStep(2);
      return;
    }

    const targetSelector =
      buildDesiredFundingRouteTargetSelector(pendingRouteTarget) ||
      '[data-creation-contract-form-root="true"]';

    scrollToCreationTarget(targetSelector);
    clearPendingRouteNavigationTarget();
  }, [
    activeCreationForm,
    clearPendingRouteNavigationTarget,
    contractFormStep,
    editingPersistedContract,
    expandedContractKeysByFinanceur,
    financeurs,
    openSavedContractForEdit,
    pendingRouteTarget,
    scrollToContractNode,
    scrollToCreationTarget,
    selectedFinanceur,
    selectedFinanceurKey,
  ]);

  useEffect(() => {
    if (!activeCreationForm) return;

    const projectIds = new Set<number>();
    activeCreationForm.yearGroups.forEach((group) => {
      group.projectRows.forEach((row) => {
        const projectId = Number(row.projectId || 0);
        if (projectId > 0) {
          projectIds.add(projectId);
        }
      });
    });

    projectIds.forEach((projectId) => {
      void loadDesiredFundingCatalogForProject(projectId);
    });
  }, [activeCreationForm, loadDesiredFundingCatalogForProject]);

  const totalProjectRowsAmountEuro = useMemo(() => {
    if (!activeCreationForm) return 0;
    return activeCreationForm.yearGroups.reduce((sum, group) => {
      return (
        sum +
        group.projectRows.reduce((groupSum, row) => {
          return groupSum + (parseAmountInput(row.amountEuro) ?? 0);
        }, 0)
      );
    }, 0);
  }, [activeCreationForm]);

  const totalExpectedPaymentsAmountEuro = useMemo(() => {
    if (!activeCreationForm) return 0;

    return activeCreationForm.expectedPayments.reduce(
      (sum, payment) => sum + (parseAmountInput(payment.amount) ?? 0),
      0,
    );
  }, [activeCreationForm]);

  const validateContractFormStep = useCallback(
    (step: FinanceurContractFormStep) => {
      if (!activeCreationForm) return false;

      if (step === 1) {
        const title = String(activeCreationForm.title || "").trim();
        if (!title) {
          setCreationError("Renseignez l’intitulé du contrat.");
          return false;
        }

        if (!activeCreationForm.contractDate) {
          setCreationError("Renseignez la date du contrat.");
          return false;
        }

        const contractAmount = parseAmountInput(activeCreationForm.amountEuro);
        if (null === contractAmount || contractAmount <= 0) {
          setCreationError("Renseignez un montant de contrat valide.");
          return false;
        }
      }

      if (step === 2) {
        const cleanedGroups = activeCreationForm.yearGroups
          .map((group) => ({
            ...group,
            year: String(group.year || "").trim(),
            projectRows: group.projectRows.filter(
              (row) =>
                String(row.projectName || "").trim() ||
                Number(row.projectId || 0) > 0 ||
                String(row.amountEuro || "").trim(),
            ),
          }))
          .filter((group) => group.year || group.projectRows.length > 0);

        if (cleanedGroups.length === 0) {
          setCreationError("Ajoutez au moins une année.");
          return false;
        }

        if (cleanedGroups.some((group) => !/^\d{4}$/.test(group.year))) {
          setCreationError("Chaque année doit etre renseignee au format YYYY.");
          return false;
        }

        if (cleanedGroups.some((group) => group.projectRows.length === 0)) {
          setCreationError(
            "Chaque année doit contenir au moins une action financée.",
          );
          return false;
        }

        const hasInvalidProjectRow = cleanedGroups.some((group) =>
          group.projectRows.some((row) => {
            const rowAmount = parseAmountInput(row.amountEuro);
            const normalizedProjectName = String(row.projectName || "")
              .trim()
              .toLocaleLowerCase();
            const hasProjectReference =
              !!normalizedProjectName ||
              (Number.isFinite(Number(row.projectId)) && Number(row.projectId) > 0) ||
              projectSuggestionIdByName.has(normalizedProjectName);

            return !hasProjectReference || null === rowAmount || rowAmount <= 0;
          }),
        );

        if (hasInvalidProjectRow) {
          setCreationError(
            "Chaque action financée doit avoir un projet et un montant valide.",
          );
          return false;
        }

        for (const group of cleanedGroups) {
          for (const row of group.projectRows) {
            if (
              row.initialDesiredFundingIds.length > 0 ||
              !row.desiredFundingSelectionTouched ||
              row.desiredFundingIds.length === 0
            ) {
              continue;
            }

            const rowAmountEuro = parseAmountInput(row.amountEuro) ?? 0;
            if (rowAmountEuro <= 0) continue;

            const projectId =
              Number(row.projectId || 0) > 0
                ? Number(row.projectId)
                : projectSuggestionIdByName.get(
                    String(row.projectName || "").trim().toLocaleLowerCase(),
                  ) ?? 0;
            const desiredFundingCatalog =
              projectId > 0
                ? desiredFundingCatalogByProjectId[projectId]?.desiredFundings || []
                : [];
            const desiredFundingCatalogById = new Map(
              desiredFundingCatalog.map((option) => [option.id, option] as const),
            );
            const selectedDesiredFundingIds = Array.from(
              new Set(
                row.desiredFundingIds.filter(
                  (desiredFundingId) =>
                    Number.isFinite(desiredFundingId) && desiredFundingId > 0,
                ),
              ),
            );
            const selectedDesiredFundingOptions = selectedDesiredFundingIds
              .map((desiredFundingId) =>
                desiredFundingCatalogById.get(desiredFundingId),
              )
              .filter(
                (option): option is ProjectDesiredFundingOption => !!option,
              );
            const selectedDesiredFundingTotalEuro =
              selectedDesiredFundingOptions.reduce(
                (sum, option) => sum + getDesiredFundingOptionAmountEuro(option),
                0,
              );

            if (selectedDesiredFundingTotalEuro <= rowAmountEuro + 0.0001) {
              continue;
            }

            const automaticAllocationByDesiredFundingId =
              buildAutomaticDesiredFundingAllocationMap(
                selectedDesiredFundingOptions,
                rowAmountEuro,
              );
            const allocationEntryByDesiredFundingId = new Map(
              row.desiredFundingAllocations
                .map((allocation) => {
                  const desiredFundingId = Number(allocation.desiredFundingId || 0);
                  if (
                    !Number.isFinite(desiredFundingId) ||
                    desiredFundingId <= 0
                  ) {
                    return null;
                  }

                  return [desiredFundingId, allocation.amountEuro] as const;
                })
                .filter(
                  (
                    entry,
                  ): entry is readonly [number, string] => Array.isArray(entry),
                ),
            );

            const manualAllocationTotalEuro = selectedDesiredFundingIds.reduce(
              (sum, desiredFundingId) => {
                const manualAmountEuro = allocationEntryByDesiredFundingId.has(
                  desiredFundingId,
                )
                  ? parseAmountInput(
                      allocationEntryByDesiredFundingId.get(desiredFundingId) || "",
                    )
                  : automaticAllocationByDesiredFundingId.get(desiredFundingId) ??
                    null;

                return sum + (manualAmountEuro ?? 0);
              },
              0,
            );

            if (Math.abs(manualAllocationTotalEuro - rowAmountEuro) > 0.01) {
              setCreationError(
                `Le montant de l'action financée "${row.projectName || "ce projet"}" est insuffisant. Ajustez la répartition manuelle des financements souhaités avant de continuer.`,
              );
              return false;
            }
          }
        }
      }

      if (step === 3) {
        const expectedPaymentsError = validateExpectedPaymentsList(
          activeCreationForm.expectedPayments,
        );
        if (expectedPaymentsError) {
          setCreationError(expectedPaymentsError);
          return false;
        }

        const contractAmount = parseAmountInput(activeCreationForm.amountEuro) ?? 0;
        if (
          contractAmount > 0 &&
          totalExpectedPaymentsAmountEuro > contractAmount + 0.0001
        ) {
          setCreationError(
            `Le total des versements (${formatEuro(totalExpectedPaymentsAmountEuro)}) dépasse le montant du contrat (${formatEuro(contractAmount)}). Corrigez les versements ou le montant du contrat avant de passer au récapitulatif.`,
          );
          return false;
        }
      }

      setCreationError(null);
      return true;
    },
    [
      activeCreationForm,
      projectSuggestionIdByName,
      totalExpectedPaymentsAmountEuro,
    ],
  );

  const goToContractFormStep = useCallback(
    (targetStep: FinanceurContractFormStep) => {
      if (targetStep === contractFormStep) return;

      if (targetStep < contractFormStep) {
        setCreationError(null);
        setContractFormStep(targetStep);
        return;
      }

      for (let step = contractFormStep; step < targetStep; step += 1) {
        if (!validateContractFormStep(step as FinanceurContractFormStep)) {
          return;
        }
      }

      setCreationError(null);
      setContractFormStep(targetStep);
    },
    [contractFormStep, validateContractFormStep],
  );

  const creationAutosaveLabel = useMemo(() => {
    return formatCreationAutosaveLabel({
      localStatus: creationLocalAutosaveStatus,
      endpointConfigured: creationRemoteAutosave.endpointConfigured,
      remoteStatus: creationRemoteAutosave.status,
    });
  }, [
    creationLocalAutosaveStatus,
    creationRemoteAutosave.endpointConfigured,
    creationRemoteAutosave.status,
  ]);

  const renderContractFormPanel = () => {
    if (!activeCreationForm || !selectedFinanceur) return null;
    const currentStepGuidance =
      FINANCEUR_CONTRACT_FORM_STEP_GUIDANCE[contractFormStep];
    const activeContractAmountEuro =
      parseAmountInput(activeCreationForm.amountEuro) ?? 0;
    const actionsRemainingEuro = Number(
      (activeContractAmountEuro - totalProjectRowsAmountEuro).toFixed(2),
    );
    const expectedPaymentsRemainingEuro = Number(
      (activeContractAmountEuro - totalExpectedPaymentsAmountEuro).toFixed(2),
    );

    return (
      <div
        data-creation-contract-form-root="true"
        className="bg-gradient-to-b from-slate-50 via-white to-slate-50"
      >
        <div className="sticky top-0 z-10 border-b border-emerald-200 bg-gradient-to-r from-emerald-100 via-white to-sky-50 px-5 py-4 backdrop-blur">
          <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
              <div className="inline-flex items-center rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                {isEditingPersistedContract
                  ? "Édition"
                  : editingPendingDraft
                    ? "Brouillon"
                    : "Nouveau contrat"}
              </div>
            <div className="mt-2 text-lg font-semibold text-slate-900">
              {isEditingPersistedContract
                ? "Modifier le contrat"
                : editingPendingDraft
                  ? "Modifier le brouillon"
                  : "Nouveau contrat"}
            </div>
            <div className="mt-1 text-sm text-slate-600">
              {selectedFinanceur.name} •{" "}
              {isEditingPersistedContract
                ? "Édition du contrat"
                : "Saisie du contrat"}
            </div>
            {isEditingPersistedContract ? (
              <div className="mt-2 inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-xs font-medium text-emerald-800">
                Contrat ciblé: {editingPersistedContract?.contractLabel || "Contrat"}
              </div>
            ) : null}
          </div>
            <div className="max-w-xs text-right text-xs text-slate-500">
              Renseignez d’abord les informations du contrat, puis les actions financées, puis les versements du contrat.
            </div>
          </div>
        </div>

        <div className="space-y-4 p-5">
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {FINANCEUR_CONTRACT_FORM_STEPS.map((step) => {
            const isActive = contractFormStep === step.id;
            const isDone = contractFormStep > step.id;

            return (
              <button
                key={step.id}
                type="button"
                className={cn(
                  "rounded-xl border px-3 py-2 text-left transition",
                  isActive
                    ? "border-emerald-300 bg-emerald-50 text-emerald-950 shadow-sm"
                    : isDone
                      ? "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
                      : "border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100",
                )}
                onClick={() => goToContractFormStep(step.id)}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={cn(
                      "inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-semibold",
                      isActive || isDone
                        ? "bg-emerald-600 text-white"
                        : "bg-slate-200 text-slate-600",
                    )}
                  >
                    {step.id}
                  </span>
                  <span className="text-sm font-semibold">{step.label}</span>
                </div>
                <div className="mt-1 text-[11px] leading-snug">
                  {step.description}
                </div>
              </button>
            );
          })}
        </div>

        <div className="rounded-lg border border-sky-100 bg-sky-50/70 p-3">
          <div className="text-xs font-semibold uppercase tracking-wide text-sky-700">
            {currentStepGuidance.title}
          </div>
          <div className="mt-1 text-xs leading-relaxed text-slate-600">
            {currentStepGuidance.description}
          </div>
          <ul className="mt-2 list-disc space-y-1 pl-4 text-xs leading-relaxed text-slate-600">
            {currentStepGuidance.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>

        {contractFormStep === 1 ? (
        <div className="rounded-2xl border border-sky-200 bg-sky-50/70 p-4">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-sky-700">
              Contrat
            </div>
            <SectionHint className="shrink-0">
              <div className="space-y-1.5">
                <p>Commencez par le contrat signé, puis répartissez sa part par année et par action financée.</p>
                <ul className="list-disc space-y-1 pl-4">
                  <li>le montant du contrat correspond au total signé avec le financeur ;</li>
                  <li>chaque action financée porte uniquement sa part dans ce contrat ;</li>
                  <li>les versements se saisissent ensuite au niveau du contrat.</li>
                </ul>
              </div>
            </SectionHint>
          </div>
        <div className="grid gap-3 md:grid-cols-3">
          <label className="space-y-1 text-xs text-slate-700">
            <span className="font-medium">Intitulé</span>
            <input
              type="text"
              value={activeCreationForm.title}
              onChange={(event) => {
                const value = event.target.value;
                updateCreationForm((form) => ({ ...form, title: value }));
              }}
              className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
              placeholder="Ajoutez ici le nom du contrat signé entre vous et votre financeur"
            />
            <p className="text-[11px] leading-relaxed text-slate-500">
              Exemple : Convention 2026 - restauration mangrove.
            </p>
          </label>
          <label className="space-y-1 text-xs text-slate-700">
            <span className="font-medium">Date du contrat</span>
            <input
              type="date"
              value={activeCreationForm.contractDate}
              onChange={(event) => {
                const value = event.target.value;
                const firstYear = getYearFromIsoDate(value || null);
                updateCreationForm((form) => ({
                  ...form,
                  contractDate: value,
                  yearGroups: isEditingPersistedContract
                    ? form.yearGroups
                    : form.yearGroups.map((group, index) =>
                        index === 0 ? { ...group, year: firstYear } : group,
                      ),
                }));
              }}
              className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
            />
            <p className="text-[11px] leading-relaxed text-slate-500">
              Date de signature ou d&apos;obtention du financement.
            </p>
          </label>
          <label className="space-y-1 text-xs text-slate-700">
            <span className="font-medium">Montant contrat (€)</span>
            <input
              type="text"
              inputMode="decimal"
              value={activeCreationForm.amountEuro}
              onChange={(event) => {
                const value = event.target.value;
                updateCreationForm((form) => ({ ...form, amountEuro: value }));
              }}
              className="w-full rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
              placeholder="Ex : 10000 - montant total du contrat"
            />
            <p className="text-[11px] leading-relaxed text-slate-500">
              Montant total signé avec ce financeur, avant répartition par action.
            </p>
          </label>
        </div>
        </div>
        ) : null}

        {contractFormStep === 2 ? (
        <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
          <div className="mb-3 flex items-center justify-between">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700">
                Actions financées
              </div>
              <div className="mt-1 text-sm font-medium text-slate-900">
                Actions financées par année
              </div>
              <div className="mt-1 max-w-2xl text-xs leading-relaxed text-slate-600">
                Si le contrat couvre plusieurs usages, saisissez une action par
                usage avec sa part du montant du contrat. Ces actions seront
                ensuite visibles dans les contributions déjà obtenues.
              </div>
            </div>
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-50"
              onClick={addYearGroup}
            >
              <Plus className="h-3.5 w-3.5" />
	              Ajouter une année au contrat
	            </button>
	          </div>

	          <div className="mb-3 grid gap-3 md:grid-cols-3">
	            <div className="rounded-xl border border-sky-100 bg-sky-50/80 p-3">
	              <div className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">
	                Montant du contrat
	              </div>
	              <div className="mt-1 text-lg font-semibold text-sky-950">
	                {formatEuro(activeContractAmountEuro)}
	              </div>
	              <div className="mt-1 text-[11px] leading-relaxed text-sky-800/75">
	                Montant total signé, saisi à l&apos;étape contrat.
	              </div>
	            </div>
	            <div className="rounded-xl border border-indigo-100 bg-indigo-50/80 p-3">
	              <div className="text-[11px] font-semibold uppercase tracking-wide text-indigo-700">
	                Actions affichées
	              </div>
	              <div className="mt-1 text-lg font-semibold text-indigo-950">
	                {formatEuro(totalProjectRowsAmountEuro)}
	              </div>
	              <div className="mt-1 text-[11px] leading-relaxed text-indigo-800/75">
	                Total des actions financées renseignées dans ce contrat.
	              </div>
	            </div>
	            <div
	              className={cn(
	                "rounded-xl border p-3",
	                actionsRemainingEuro < 0
	                  ? "border-rose-100 bg-rose-50/80"
	                  : "border-emerald-100 bg-emerald-50/80",
	              )}
	            >
	              <div
	                className={cn(
	                  "text-[11px] font-semibold uppercase tracking-wide",
	                  actionsRemainingEuro < 0
	                    ? "text-rose-700"
	                    : "text-emerald-700",
	                )}
	              >
	                Écart affiché
	              </div>
	              <div
	                className={cn(
	                  "mt-1 text-lg font-semibold",
	                  actionsRemainingEuro < 0
	                    ? "text-rose-950"
	                    : "text-emerald-950",
	                )}
	              >
	                {formatEuro(actionsRemainingEuro)}
	              </div>
	              <div
	                className={cn(
	                  "mt-1 text-[11px] leading-relaxed",
	                  actionsRemainingEuro < 0
	                    ? "text-rose-800/75"
	                    : "text-emerald-800/75",
	                )}
	              >
	                Montant contrat moins total des actions affichées.
	              </div>
	            </div>
	          </div>

	          <div className="space-y-3">
            {activeCreationForm.yearGroups.map((yearGroup, yearIndex) => (
              <div
                key={yearGroup.id}
                data-creation-year-group-id={yearGroup.id}
                className="rounded-2xl border border-sky-200 bg-white p-3 shadow-sm"
              >
                {(() => {
                  const canRemoveYearGroup = true;

                  return (
                    <>
                      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                        <label className="flex items-center gap-2 text-xs text-slate-700">
                          <span className="rounded-full bg-sky-100 px-2 py-0.5 font-semibold text-sky-800">
                            Année {yearIndex + 1}
                          </span>
                          <input
                            type="text"
                            inputMode="numeric"
                            value={yearGroup.year}
                            onChange={(event) => {
                              const value = event.target.value;
                              updateCreationForm((form) => ({
                                ...form,
                                yearGroups: form.yearGroups.map((item) =>
                                  item.id === yearGroup.id
                                    ? { ...item, year: value }
                                    : item,
                                ),
                              }));
                            }}
                            className="w-[120px] rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
                            placeholder="2026"
                          />
                        </label>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            className={cn(
                              "rounded-full border px-3 py-1.5 text-xs font-medium",
                              canRemoveYearGroup
                                ? "border-rose-300 bg-white text-rose-700 hover:bg-rose-50"
                                : "cursor-not-allowed border-rose-200 text-rose-300",
                            )}
                            onClick={() => removeYearGroup(yearGroup.id)}
                            disabled={!canRemoveYearGroup}
                          >
                            Supprimer année
                          </button>
                        </div>
                      </div>

                      <div className="rounded-xl border border-sky-100 bg-sky-50/60 p-3">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div>
                            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-700">
                              Projets de l'année
                            </div>
                            <div className="mt-1 text-xs text-slate-600">
                              Ajoutez ici les actions financées sur cette année,
                              avec leur projet de rattachement et leur montant.
                            </div>
                          </div>
                          <button
                            type="button"
                            className="inline-flex items-center gap-1 rounded-full border border-sky-200 bg-white px-3 py-1.5 text-xs font-medium text-sky-800 hover:bg-sky-100"
                            onClick={() =>
                              addProjectRowToYearGroup(yearGroup.id)
                            }
                          >
                            <Plus className="h-3.5 w-3.5" />
                            Ajouter un projet dans l'année
                          </button>
                        </div>

                      <div className="mt-3 space-y-3">
                        {yearGroup.projectRows.map((row) => {
                          const normalizedProjectId = Number(row.projectId || 0);
                          const hasProjectSelection =
                            Number.isFinite(normalizedProjectId) &&
                            normalizedProjectId > 0;
                          const desiredFundingCatalog = hasProjectSelection
                            ? desiredFundingCatalogByProjectId[normalizedProjectId]
                                ?.desiredFundings || []
                            : [];
                          const desiredFundingError = hasProjectSelection
                            ? desiredFundingErrorsByProjectId[normalizedProjectId] || ""
                            : "";
                          const isDesiredFundingLoading = hasProjectSelection
                            ? !!loadingDesiredFundingProjectIds[normalizedProjectId]
                            : false;
                          const isDesiredFundingSelectionLocked =
                            row.initialDesiredFundingIds.length > 0;
                          const selectedDesiredFundingIds = Array.from(
                            new Set(
                              (
                                row.desiredFundingSelectionTouched
                                  ? row.desiredFundingIds
                                  : row.initialDesiredFundingIds
                              ).filter(
                                (desiredFundingId) =>
                                  Number.isFinite(Number(desiredFundingId)) &&
                                  Number(desiredFundingId) > 0,
                              ),
                            ),
                          );
                          const selectedDesiredFundingOptions =
                            desiredFundingCatalog.filter((option) =>
                              selectedDesiredFundingIds.includes(option.id),
                            );
                          const selectedDesiredFundingTotalEuro =
                            selectedDesiredFundingOptions.reduce(
                              (sum, option) =>
                                sum + getDesiredFundingOptionAmountEuro(option),
                              0,
                            );
                          const rowAmountEuro =
                            parseAmountInput(row.amountEuro) ?? 0;
                          const desiredFundingAllocationById = new Map(
                            Array.isArray(row.desiredFundingAllocations)
                              ? row.desiredFundingAllocations
                                  .map((allocation) => {
                                    const desiredFundingId = Number(
                                      allocation?.desiredFundingId ?? 0,
                                    );
                                    if (
                                      !Number.isFinite(desiredFundingId) ||
                                      desiredFundingId <= 0
                                    ) {
                                      return null;
                                    }

                                    return [
                                      desiredFundingId,
                                      String(allocation?.amountEuro ?? ""),
                                    ] as const;
                                  })
                                  .filter(
                                    (
                                      entry,
                                    ): entry is readonly [number, string] =>
                                      Array.isArray(entry),
                                  )
                              : [],
                          );
                          const requiresManualDesiredFundingAllocation =
                            selectedDesiredFundingTotalEuro >
                            rowAmountEuro + 0.0001;
                          const automaticDesiredFundingAllocationById =
                            buildAutomaticDesiredFundingAllocationMap(
                              selectedDesiredFundingOptions,
                              rowAmountEuro,
                            );
                          const manualDesiredFundingTotalEuro =
                            selectedDesiredFundingIds.reduce((sum, desiredFundingId) => {
                              const amountEuro = desiredFundingAllocationById.has(
                                desiredFundingId,
                              )
                                ? parseAmountInput(
                                    desiredFundingAllocationById.get(
                                      desiredFundingId,
                                    ) || "",
                                  )
                                : automaticDesiredFundingAllocationById.get(
                                    desiredFundingId,
                                  ) ?? null;
                              return sum + (amountEuro ?? 0);
                            }, 0);
                          const manualDesiredFundingRemainingEuro = Number(
                            (rowAmountEuro - manualDesiredFundingTotalEuro).toFixed(
                              2,
                            ),
                          );

                          return (
                            <div
                              key={row.id}
                              data-creation-project-row-id={row.id}
                              className="rounded-xl border border-slate-200 bg-slate-50/80 p-3"
                            >
                            <div className="mb-3 flex items-center justify-between gap-2">
                              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                                Action financée
                              </div>
                              <button
                                type="button"
                                className={cn(
                                  "rounded-full border px-3 py-1.5 text-xs font-medium",
                                  isDesiredFundingSelectionLocked
                                    ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400"
                                    : "border-rose-300 bg-white text-rose-700 hover:bg-rose-50",
                                )}
                                onClick={() =>
                                  removeProjectRowFromYearGroup(
                                    yearGroup.id,
                                    row.id,
                                  )
                                }
                                disabled={isDesiredFundingSelectionLocked}
                              >
                                Supprimer
                              </button>
                            </div>
                            <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_140px]">
                              <label className="space-y-1 text-xs text-slate-700">
                                <span className="font-medium">Projet</span>
                                <input
                                  list={`project-suggestions-${selectedFinanceur.id ?? "x"}`}
                                  type="text"
                                  value={row.projectName}
                                  disabled={isDesiredFundingSelectionLocked}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    const normalizedValue = value
                                      .trim()
                                      .toLocaleLowerCase();
                                    const matchedProjectId =
                                      projectSuggestionIdByName.get(normalizedValue) ?? null;
                                    const currentProjectId =
                                      Number.isFinite(Number(row.projectId)) &&
                                      Number(row.projectId) > 0
                                        ? Number(row.projectId)
                                        : null;
                                    const shouldResetDesiredFundingSelection =
                                      value.trim() === "" ||
                                      (null !== matchedProjectId &&
                                        matchedProjectId !== currentProjectId);
                                    updateCreationForm((form) => ({
                                      ...form,
                                      yearGroups: form.yearGroups.map((group) =>
                                        group.id === yearGroup.id
                                          ? {
                                              ...group,
                                              projectRows: group.projectRows.map(
                                                (item) =>
                                                  item.id === row.id
                                                    ? {
                                                        ...item,
                                                        projectId: matchedProjectId,
                                                        projectName: value,
                                                        ...(shouldResetDesiredFundingSelection
                                                          ? {
                                                              desiredFundingIds: [],
                                                              desiredFundingAllocations:
                                                                [],
                                                              initialDesiredFundingIds: [],
                                                              desiredFundingSelectionTouched: false,
                                                            }
                                                          : {}),
                                                      }
                                                    : item,
                                              ),
                                            }
                                          : group,
                                      ),
                                    }));
                                  }}
                                  className={cn(
                                    "hide-datalist-picker w-full rounded-md border px-2 py-1.5 text-sm outline-none ring-emerald-200 focus:ring-2",
                                    isDesiredFundingSelectionLocked
                                      ? "border-slate-200 bg-slate-100 text-slate-500"
                                      : "border-slate-300 bg-white text-slate-900",
                                  )}
                                  placeholder="Recherchez le projet concerné par cette action financée"
                                />
                                <p className="text-[11px] leading-relaxed text-slate-500">
                                  Sélectionnez le projet où cette action sera
                                  rattachée.
                                </p>
                              </label>
                              <label className="space-y-1 text-xs text-slate-700">
                                <span className="font-medium">Intitulé du financement obtenu</span>
                                <input
                                  type="text"
                                  value={row.title}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    updateCreationForm((form) => ({
                                      ...form,
                                      yearGroups: form.yearGroups.map((group) =>
                                        group.id === yearGroup.id
                                          ? {
                                              ...group,
                                              projectRows: group.projectRows.map(
                                                (item) =>
                                                  item.id === row.id
                                                    ? { ...item, title: value }
                                                    : item,
                                              ),
                                            }
                                          : group,
                                      ),
                                    }));
                                  }}
                                  className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
                                  placeholder="Ex : Achat matériel, travaux, mission terrain..."
                                />
                                <p className="text-[11px] leading-relaxed text-slate-500">
                                  Décrivez ce que cette part du contrat finance.
                                </p>
                              </label>
                              <label className="space-y-1 text-xs text-slate-700">
                                <span className="font-medium">Montant €</span>
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  value={row.amountEuro}
                                  onChange={(event) => {
                                    const value = event.target.value;
                                    updateCreationForm((form) => ({
                                      ...form,
                                      yearGroups: form.yearGroups.map((group) =>
                                        group.id === yearGroup.id
                                          ? {
                                              ...group,
                                              projectRows: group.projectRows.map(
                                                (item) =>
                                                  item.id === row.id
                                                    ? {
                                                        ...item,
                                                        amountEuro: value,
                                                      }
                                                    : item,
                                              ),
                                            }
                                          : group,
                                      ),
                                    }));
                                  }}
                                  className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
                                  placeholder="Ex : 5000 - part du contrat pour cette action"
                                />
                                <p className="text-[11px] leading-relaxed text-slate-500">
                                  Montant du contrat affecté à cette action.
                                </p>
                              </label>
                            </div>

                            <div
                              className="mt-3 rounded-xl border border-violet-200 bg-violet-50/70 p-3"
                              data-creation-desired-funding-line-id={
                                Number.isFinite(Number(row.lineId || 0)) &&
                                Number(row.lineId || 0) > 0
                                  ? String(Number(row.lineId || 0))
                                  : undefined
                              }
                              data-creation-desired-funding-project-id={
                                Number.isFinite(Number(row.projectId || 0)) &&
                                Number(row.projectId || 0) > 0
                                  ? String(Number(row.projectId || 0))
                                  : undefined
                              }
                            >
                              <div className="flex items-start justify-between gap-3">
                                <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-violet-700">
                                  Associer aux financements souhaités
                                </div>
                                <SectionHint className="shrink-0">
                                  <div className="space-y-1.5">
                                    <p>Utilisez ce bloc pour rattacher la part du contrat aux financements souhaités du projet.</p>
                                    <ul className="list-disc space-y-1 pl-4">
                                      <li>si la somme sélectionnée tient dans le montant du projet, la répartition suit automatiquement les montants choisis ;</li>
                                      <li>si elle dépasse, vous pouvez ajuster manuellement la part affectée à chaque financement souhaité ;</li>
                                      <li>quand une ligne est déjà liée, la sélection reste verrouillée pour préserver les rattachements existants.</li>
                                    </ul>
                                  </div>
                                </SectionHint>
                              </div>
                              <div className="mt-1 text-xs text-slate-600">
                                Vous avez renseigné précédemment des financements
                                que vous souhaiteriez obtenir. Si cette action
                                financée correspond à un ou plusieurs de ces
                                financements souhaités, sélectionnez-les ci-dessous
                                pour garder le lien entre le besoin initial et le
                                financement obtenu.
                              </div>

                              {!hasProjectSelection ? (
                                <div className="mt-3 rounded-lg border border-dashed border-violet-200 bg-white/80 px-3 py-2 text-xs text-slate-500">
                                  Sélectionnez d&apos;abord un projet pour afficher ses financements souhaités.
                                </div>
                              ) : isDesiredFundingSelectionLocked ? (
                                <div className="mt-3 rounded-lg border border-violet-200 bg-white/90 px-3 py-2">
                                  <div className="text-xs text-slate-700">
                                    {selectedDesiredFundingIds.length > 0
                                      ? `${selectedDesiredFundingIds.length} financement${selectedDesiredFundingIds.length > 1 ? "s" : ""} souhaité${selectedDesiredFundingIds.length > 1 ? "s sont déjà associés" : " est déjà associé"} à ce projet du contrat.`
                                      : "Des financements souhaités sont déjà associés à ce projet du contrat."}
                                  </div>
                                  {selectedDesiredFundingOptions.length > 0 ? (
                                    <div className="mt-3 flex flex-wrap gap-2">
                                      {selectedDesiredFundingOptions.map((option) => (
                                        <div
                                          key={option.id}
                                          className="rounded-xl border border-violet-200 bg-violet-100/80 px-3 py-2 text-left text-xs text-violet-900"
                                        >
                                          <div className="font-medium">
                                            {getDesiredFundingOptionLabel(option)}
                                          </div>
                                          <div className="mt-1 text-violet-800/80">
                                            {formatEuro(
                                              getDesiredFundingOptionAmountEuro(option),
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : null}
                                </div>
                              ) : isDesiredFundingLoading ? (
                                <div className="mt-3 rounded-lg border border-dashed border-violet-200 bg-white/80 px-3 py-2 text-xs text-slate-500">
                                  Chargement des financements souhaités...
                                </div>
                              ) : desiredFundingError ? (
                                <div className="mt-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                                  {desiredFundingError}
                                </div>
                              ) : desiredFundingCatalog.length === 0 ? (
                                <div className="mt-3 rounded-lg border border-dashed border-violet-200 bg-white/80 px-3 py-2 text-xs text-slate-500">
                                  Aucun financement souhaité disponible sur ce projet.
                                </div>
                              ) : (
                                <>
                                  <div className="mt-3 flex flex-wrap gap-2">
                                    {desiredFundingCatalog.map((option) => {
                                      const isSelected = selectedDesiredFundingIds.includes(
                                        option.id,
                                      );

                                      return (
                                        <button
                                          key={option.id}
                                          type="button"
                                          className={cn(
                                            "min-w-[220px] rounded-xl border px-3 py-2 text-left transition",
                                            isSelected
                                              ? "border-violet-400 bg-violet-100 text-violet-950 shadow-sm"
                                              : "border-violet-200 bg-white text-slate-700 hover:border-violet-300 hover:bg-violet-50",
                                          )}
                                          onClick={() => {
                                            updateCreationForm((form) => ({
                                              ...form,
                                              yearGroups: form.yearGroups.map((group) =>
                                                group.id === yearGroup.id
                                                  ? {
                                                      ...group,
                                                      projectRows: group.projectRows.map(
                                                        (item) => {
                                                          if (item.id !== row.id) return item;

                                                          const nextIds = isSelected
                                                            ? item.desiredFundingIds.filter(
                                                                (desiredFundingId) =>
                                                                  desiredFundingId !== option.id,
                                                              )
                                                            : [
                                                                ...item.desiredFundingIds,
                                                                option.id,
                                                              ];
                                                          const nextAllocations = isSelected
                                                            ? item.desiredFundingAllocations.filter(
                                                                (allocation) =>
                                                                  allocation.desiredFundingId !==
                                                                  option.id,
                                                              )
                                                            : item.desiredFundingAllocations;

                                                          return {
                                                            ...item,
                                                            desiredFundingIds: Array.from(
                                                              new Set(nextIds),
                                                            ),
                                                            desiredFundingAllocations:
                                                              nextAllocations,
                                                            desiredFundingSelectionTouched: true,
                                                          };
                                                        },
                                                      ),
                                                    }
                                                  : group,
                                              ),
                                            }));
                                          }}
                                        >
                                          <div className="flex items-start justify-between gap-3">
                                            <div className="min-w-0">
                                              <div className="text-sm font-medium">
                                                {getDesiredFundingOptionLabel(option)}
                                              </div>
                                              <div className="mt-1 text-xs text-slate-500">
                                                {Number(option.amount || 0).toLocaleString(
                                                  "fr-FR",
                                                  {
                                                    minimumFractionDigits: 2,
                                                    maximumFractionDigits: 2,
                                                  },
                                                )}{" "}
                                                {getDesiredFundingOptionCurrencyLabel(option)}
                                              </div>
                                            </div>
                                            <div
                                              className={cn(
                                                "shrink-0 rounded-full px-2 py-1 text-[11px] font-medium",
                                                isSelected
                                                  ? "bg-violet-600 text-white"
                                                  : "bg-violet-100 text-violet-800",
                                              )}
                                            >
                                              {formatEuro(
                                                getDesiredFundingOptionAmountEuro(option),
                                              )}
                                            </div>
                                          </div>
                                        </button>
                                      );
                                    })}
                                  </div>

                                  {selectedDesiredFundingIds.length > 0 ? (
                                    <div className="mt-3 rounded-lg border border-violet-200 bg-white/90 px-3 py-2 text-xs text-slate-700">
                                      {selectedDesiredFundingIds.length} financement
                                      {selectedDesiredFundingIds.length > 1 ? "s" : ""} souhaité
                                      {selectedDesiredFundingIds.length > 1 ? "s" : ""} sélectionné
                                      {selectedDesiredFundingIds.length > 1 ? "s" : ""} •{" "}
                                      {formatEuro(selectedDesiredFundingTotalEuro)}
                                    </div>
                                  ) : null}

                                  {requiresManualDesiredFundingAllocation ? (
                                    <div className="mt-3 rounded-xl border border-amber-300 bg-amber-50/90 p-3">
                                      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-amber-700">
                                        Répartition manuelle requise
                                      </div>
                                      <div className="mt-1 text-xs text-slate-700">
                                        Le total des financements souhaités sélectionnés ({formatEuro(
                                          selectedDesiredFundingTotalEuro,
                                        )}) dépasse le montant de cette ligne ({formatEuro(
                                          rowAmountEuro,
                                        )}). Indiquez la part à affecter à chacun.
                                      </div>

                                      <div className="mt-3 space-y-2">
                                        {selectedDesiredFundingOptions.map((option) => {
                                          const currentAllocationValue =
                                            desiredFundingAllocationById.has(
                                              option.id,
                                            )
                                              ? desiredFundingAllocationById.get(
                                                  option.id,
                                                ) || ""
                                              : (() => {
                                                  const automaticAmountEuro =
                                                    automaticDesiredFundingAllocationById.get(
                                                      option.id,
                                                    );
                                                  return Number.isFinite(
                                                    automaticAmountEuro,
                                                  ) &&
                                                    Number(
                                                      automaticAmountEuro || 0,
                                                    ) > 0
                                                    ? Number(
                                                        automaticAmountEuro,
                                                      ).toFixed(2)
                                                    : "";
                                                })();

                                          return (
                                            <div
                                              key={`manual-allocation-${option.id}`}
                                              className="grid gap-2 rounded-xl border border-amber-200 bg-white/90 p-3 md:grid-cols-[minmax(0,1fr)_160px] md:items-end"
                                            >
                                              <div className="min-w-0">
                                                <div className="text-sm font-medium text-slate-900">
                                                  {getDesiredFundingOptionLabel(option)}
                                                </div>
                                                <div className="mt-1 text-xs text-slate-500">
                                                  Disponible :{" "}
                                                  {formatEuro(
                                                    getDesiredFundingOptionAmountEuro(
                                                      option,
                                                    ),
                                                  )}
                                                </div>
                                              </div>
                                              <label className="space-y-1 text-xs text-slate-700">
                                                <span className="font-medium">
                                                  Montant à affecter (€)
                                                </span>
                                                <input
                                                  type="text"
                                                  inputMode="decimal"
                                                  value={currentAllocationValue}
                                                  onChange={(event) => {
                                                    const value =
                                                      event.target.value;
                                                    updateCreationForm((form) => ({
                                                      ...form,
                                                      yearGroups:
                                                        form.yearGroups.map(
                                                          (group) =>
                                                            group.id ===
                                                            yearGroup.id
                                                              ? {
                                                                  ...group,
                                                                  projectRows:
                                                                    group.projectRows.map(
                                                                      (item) =>
                                                                        item.id ===
                                                                        row.id
                                                                          ? {
                                                                              ...item,
                                                                              desiredFundingAllocations:
                                                                                (() => {
                                                                                  const existing =
                                                                                    item.desiredFundingAllocations.filter(
                                                                                      (
                                                                                        allocation,
                                                                                      ) =>
                                                                                        allocation.desiredFundingId !==
                                                                                        option.id,
                                                                                    );

                                                                                  return [
                                                                                    ...existing,
                                                                                    {
                                                                                      desiredFundingId:
                                                                                        option.id,
                                                                                      amountEuro:
                                                                                        value,
                                                                                    },
                                                                                  ];
                                                                                })(),
                                                                            }
                                                                          : item,
                                                                    ),
                                                                }
                                                              : group,
                                                        ),
                                                    }));
                                                  }}
                                                  className="w-full rounded-md border border-amber-200 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none ring-amber-200 focus:ring-2"
                                                  placeholder="0,00"
                                                />
                                              </label>
                                            </div>
                                          );
                                        })}
                                      </div>

                                      <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
                                        <div className="rounded-full bg-white px-2.5 py-1 text-slate-700">
                                          Total saisi :{" "}
                                          {formatEuro(
                                            manualDesiredFundingTotalEuro,
                                          )}
                                        </div>
                                        <div
                                          className={cn(
                                            "rounded-full px-2.5 py-1",
                                            Math.abs(
                                              manualDesiredFundingRemainingEuro,
                                            ) <= 0.01
                                              ? "bg-emerald-100 text-emerald-800"
                                              : "bg-amber-100 text-amber-800",
                                          )}
                                        >
                                          {Math.abs(
                                            manualDesiredFundingRemainingEuro,
                                          ) <= 0.01
                                            ? "Répartition complète"
                                            : manualDesiredFundingRemainingEuro > 0
                                              ? `Reste à répartir : ${formatEuro(
                                                  manualDesiredFundingRemainingEuro,
                                                )}`
                                              : `Dépassement : ${formatEuro(
                                                  Math.abs(
                                                    manualDesiredFundingRemainingEuro,
                                                  ),
                                                )}`}
                                        </div>
                                      </div>
                                    </div>
                                  ) : null}
                                </>
                              )}
                            </div>
                          </div>
                          );
                        })}
                      </div>
                      </div>
                    </>
                  );
                })()}
              </div>
            ))}
          </div>
          <datalist id={`project-suggestions-${selectedFinanceur.id ?? "x"}`}>
            {projectSuggestions.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>
        </div>
        ) : null}

        {contractFormStep === 3 ? (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-emerald-700">
                Versements du contrat
              </div>
              <div className="mt-1 max-w-3xl text-xs leading-relaxed text-slate-600">
                Avez-vous convenu un ou plusieurs versements programmés avec
                votre financeur ? Renseignez chaque échéance prévue, puis marquez-la
                comme reçue lorsque l&apos;argent est encaissé. Exemple : 10 000 €
                prévus le 15 mars 2026, puis 5 000 € prévus le 15 septembre 2026.
              </div>
	            </div>
	          </div>

	          <div className="mt-3 grid gap-3 md:grid-cols-3">
	            <div className="rounded-xl border border-sky-100 bg-sky-50/80 p-3">
	              <div className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">
	                Montant du contrat
	              </div>
	              <div className="mt-1 text-lg font-semibold text-sky-950">
	                {formatEuro(activeContractAmountEuro)}
	              </div>
	              <div className="mt-1 text-[11px] leading-relaxed text-sky-800/75">
	                Référence pour vérifier l&apos;échéancier saisi.
	              </div>
	            </div>
	            <div className="rounded-xl border border-violet-100 bg-violet-50/80 p-3">
	              <div className="text-[11px] font-semibold uppercase tracking-wide text-violet-700">
	                Versements saisis
	              </div>
	              <div className="mt-1 text-lg font-semibold text-violet-950">
	                {formatEuro(totalExpectedPaymentsAmountEuro)}
	              </div>
	              <div className="mt-1 text-[11px] leading-relaxed text-violet-800/75">
	                Total des montants renseignés dans cette étape.
	              </div>
	            </div>
	            <div
	              className={cn(
	                "rounded-xl border p-3",
	                expectedPaymentsRemainingEuro < 0
	                  ? "border-rose-100 bg-rose-50/80"
	                  : "border-emerald-100 bg-emerald-50/80",
	              )}
	            >
	              <div
	                className={cn(
	                  "text-[11px] font-semibold uppercase tracking-wide",
	                  expectedPaymentsRemainingEuro < 0
	                    ? "text-rose-700"
	                    : "text-emerald-700",
	                )}
	              >
	                Écart versements
	              </div>
	              <div
	                className={cn(
	                  "mt-1 text-lg font-semibold",
	                  expectedPaymentsRemainingEuro < 0
	                    ? "text-rose-950"
	                    : "text-emerald-950",
	                )}
	              >
	                {formatEuro(expectedPaymentsRemainingEuro)}
	              </div>
	              <div
	                className={cn(
	                  "mt-1 text-[11px] leading-relaxed",
	                  expectedPaymentsRemainingEuro < 0
	                    ? "text-rose-800/75"
	                    : "text-emerald-800/75",
	                )}
	              >
	                Montant contrat moins total des versements affichés.
	              </div>
	            </div>
	          </div>

	          {activeCreationForm.expectedPayments.length > 0 ? (
            <div className="mt-3 space-y-2">
              {activeCreationForm.expectedPayments.map((payment) => (
                <div
                  key={payment.id}
                  className={cn(
                    "rounded-xl border p-3",
                    payment.status === 2
                      ? "border-violet-200 bg-violet-50/70"
                      : "border-sky-200 bg-sky-50/70",
                  )}
                >
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div
                      className={cn(
                        "text-[11px] font-semibold uppercase tracking-wide",
                        payment.status === 2
                          ? "text-violet-700"
                          : "text-sky-700",
                      )}
                    >
                      {payment.status === 2
                        ? "Versement reçu"
                        : "Versement prévu"}
                    </div>
                    <button
                      type="button"
                      className="rounded-full border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-50"
                      onClick={() => removeExpectedPaymentFromContract(payment.id)}
                    >
                      Supprimer
                    </button>
                  </div>
                  <div className="grid gap-2 md:grid-cols-[140px_170px_140px_170px] md:items-end">
                    <label className="space-y-1 text-xs text-slate-700">
                      <span className="font-medium">Montant €</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={payment.amount}
                        onChange={(event) =>
                          updateExpectedPaymentInContract(payment.id, {
                            amount: event.target.value,
                          })
	                        }
	                        className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
	                        placeholder="Ex : 10000 - montant du versement"
	                      />
	                      <p className="text-[11px] leading-relaxed text-slate-500">
	                        Montant prévu ou reçu pour cette échéance.
	                      </p>
	                    </label>
                    <label className="space-y-1 text-xs text-slate-700">
                      <span className="font-medium">Date prévue</span>
                      <input
                        type="date"
                        value={payment.expectedAt}
                        onChange={(event) =>
                          updateExpectedPaymentInContract(payment.id, {
                            expectedAt: event.target.value,
                          })
	                        }
	                        className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
	                      />
	                      <p className="text-[11px] leading-relaxed text-slate-500">
	                        Date à laquelle le versement est attendu.
	                      </p>
	                    </label>
                    <label className="space-y-1 text-xs text-slate-700">
                      <span className="font-medium">Statut</span>
                      <select
                        value={String(payment.status)}
                        onChange={(event) =>
                          updateExpectedPaymentInContract(payment.id, {
                            status:
                              Number(event.target.value) === 2 ? 2 : 1,
                          })
                        }
                        className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
	                      >
	                        <option value="1">Non reçu</option>
	                        <option value="2">Reçu</option>
	                      </select>
	                      <p className="text-[11px] leading-relaxed text-slate-500">
	                        Gardez Non reçu tant que l&apos;argent n&apos;est pas encaissé.
	                      </p>
	                    </label>
                    <label className="space-y-1 text-xs text-slate-700">
                      <span className="font-medium">Date de réception</span>
                      <input
                        type="date"
                        value={payment.receivedAt}
                        onChange={(event) =>
                          updateExpectedPaymentInContract(payment.id, {
                            receivedAt: event.target.value,
                          })
                        }
                        disabled={payment.status !== 2}
                        className={cn(
                          "w-full rounded-md border px-2 py-1.5 text-sm outline-none ring-emerald-200 focus:ring-2",
                          payment.status === 2
                            ? "border-slate-300 bg-white text-slate-900"
	                          : "border-slate-200 bg-slate-100 text-slate-400",
	                      )}
	                    />
	                      <p className="text-[11px] leading-relaxed text-slate-500">
	                        À remplir uniquement quand le versement est marqué comme reçu.
	                      </p>
	                    </label>
                  </div>
                </div>
              ))}
            </div>
	          ) : (
	            <div className="mt-3 rounded-xl border border-dashed border-emerald-200 bg-white/80 px-3 py-3 text-xs text-slate-500">
	              Aucun versement déclaré sur ce contrat. Ajoutez un versement si un
	              échéancier a été convenu avec le financeur.
	            </div>
	          )}
	          <div className="mt-4 flex justify-center">
	            <button
	              type="button"
	              className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white px-3 py-1.5 text-xs font-medium text-emerald-800 hover:bg-emerald-100"
	              onClick={addExpectedPaymentToContract}
	            >
	              <Plus className="h-3.5 w-3.5" />
	              Ajouter un versement
	            </button>
	          </div>
	        </div>
        ) : null}

        {contractFormStep === 1 ? (
        <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4">
        <div className="grid gap-3 md:grid-cols-[minmax(0,1fr)_auto] md:items-end">
          <label className="space-y-1 text-xs text-slate-700">
            <span className="font-medium">Notes</span>
            <textarea
              value={activeCreationForm.notes}
              onChange={(event) => {
                const value = event.target.value;
                updateCreationForm((form) => ({ ...form, notes: value }));
	              }}
	              className="min-h-[78px] w-full resize-y rounded-md border border-slate-300 bg-white px-2.5 py-2 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
	              placeholder="Ajoutez une précision utile : numéro de contrat, contexte, condition particulière, commentaire interne..."
	            />
	            <p className="text-[11px] leading-relaxed text-slate-500">
	              Champ optionnel. Ces notes servent uniquement à mieux comprendre
	              ou retrouver le contrat plus tard.
	            </p>
	          </label>
	          <div className="text-right text-xs text-slate-600">
	            <div>
	              Total des actions financées: {formatEuro(totalProjectRowsAmountEuro)}
	            </div>
	          </div>
        </div>
        </div>
        ) : null}

        {contractFormStep === 4 ? (
          <div className="space-y-4">
            <div className="rounded-2xl border border-sky-100 bg-sky-50/80 p-4">
              <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-sky-700">
                Récapitulatif
              </div>
              <div className="mt-1 text-sm text-slate-700">
                Vérifiez le contrat, les actions financées et les versements avant
                d&apos;enregistrer. Les données seront synchronisées dans le suivi
                de financements.
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Contrat
                </div>
                <div className="mt-1 text-sm font-semibold text-slate-900">
                  {activeCreationForm.title || "Contrat sans intitulé"}
                </div>
                <div className="mt-1 text-xs text-slate-500">
                  {activeCreationForm.contractDate
                    ? formatDate(activeCreationForm.contractDate)
                    : "Date non renseignée"}
                </div>
              </div>

              <div className="rounded-xl border border-sky-100 bg-sky-50/80 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-sky-700">
                  Montant contrat
                </div>
                <div className="mt-1 text-lg font-semibold text-sky-950">
                  {formatEuro(parseAmountInput(activeCreationForm.amountEuro) ?? 0)}
                </div>
              </div>

              <div className="rounded-xl border border-amber-100 bg-amber-50/80 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-amber-700">
                  Actions financées
                </div>
                <div className="mt-1 text-lg font-semibold text-amber-950">
                  {formatEuro(totalProjectRowsAmountEuro)}
                </div>
                <div className="mt-1 text-xs text-amber-800/80">
                  {activeCreationForm.yearGroups.reduce(
                    (count, group) => count + group.projectRows.length,
                    0,
                  )}{" "}
                  action(s)
                </div>
              </div>

              <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 p-3">
                <div className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">
                  Versements
                </div>
                <div className="mt-1 text-lg font-semibold text-emerald-950">
                  {formatEuro(totalExpectedPaymentsAmountEuro)}
                </div>
                <div className="mt-1 text-xs text-emerald-800/80">
                  {activeCreationForm.expectedPayments.length} versement(s)
                </div>
              </div>
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="grid gap-3 text-sm md:grid-cols-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Financeur
                  </div>
                  <div className="mt-1 font-medium text-slate-900">
                    {selectedFinanceur.name || "Financeur"}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Années
                  </div>
                  <div className="mt-1 font-medium text-slate-900">
                    {activeCreationForm.yearGroups
                      .map((group) => String(group.year || "").trim())
                      .filter(Boolean)
                      .join(", ") || "Non renseignées"}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    Écart actions / contrat
                  </div>
                  <div className="mt-1 font-medium text-slate-900">
                    {formatEuro(
                      totalProjectRowsAmountEuro -
                        (parseAmountInput(activeCreationForm.amountEuro) ?? 0),
                    )}
                  </div>
                </div>
              </div>
            </div>

            {activeCreationForm.notes ? (
              <div className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-700">
                <div className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                  Notes
                </div>
                {activeCreationForm.notes}
              </div>
            ) : null}
          </div>
        ) : null}

        {creationError ? (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {creationError}
          </div>
        ) : null}
        {savePendingError ? (
          <div className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
            {savePendingError}
          </div>
        ) : null}

        <div className="sticky bottom-0 -mx-5 mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-white/95 px-5 py-3 backdrop-blur">
          <div className="text-xs text-slate-500">
            Étape {contractFormStep} sur {FINANCEUR_CONTRACT_FORM_STEPS.length} •{" "}
            {FINANCEUR_CONTRACT_FORM_STEPS.find((step) => step.id === contractFormStep)
              ?.description || "Vérifiez les informations du contrat."}
          </div>
          <div className="flex flex-wrap justify-end gap-2">
          {contractFormStep > 1 ? (
            <button
              type="button"
              className="inline-flex items-center rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => {
                setCreationError(null);
                setContractFormStep(
                  Math.max(1, contractFormStep - 1) as FinanceurContractFormStep,
                );
              }}
              disabled={!!savingPendingDraftId}
            >
              Précédent
            </button>
          ) : null}
          {contractFormStep < 4 ? (
            <button
              type="button"
              className={cn(
                "inline-flex items-center rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white",
                savingPendingDraftId
                  ? "cursor-not-allowed opacity-70"
                  : "hover:bg-emerald-700",
              )}
              onClick={() =>
                goToContractFormStep(
                  Math.min(4, contractFormStep + 1) as FinanceurContractFormStep,
                )
              }
              disabled={!!savingPendingDraftId}
            >
              Suivant
            </button>
          ) : (
            <>
              {!isEditingPersistedContract ? (
                <button
                  type="button"
                  className={cn(
                    "inline-flex items-center rounded-md border px-3 py-1.5 text-xs font-medium",
                    savingPendingDraftId
                      ? "cursor-not-allowed border-slate-300 bg-slate-100 text-slate-400"
                      : "border-slate-300 bg-white text-slate-700 hover:bg-slate-50",
                  )}
                  onClick={handleSavePendingContractDraft}
                  disabled={!!savingPendingDraftId}
                >
                  {editingPendingDraft
                    ? "Mettre à jour le brouillon"
                    : "Ajouter au brouillon"}
                </button>
              ) : null}
              <button
                type="button"
                className={cn(
                  "inline-flex items-center rounded-md bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white",
                  savingPendingDraftId
                    ? "cursor-not-allowed opacity-70"
                    : "hover:bg-emerald-700",
                )}
                onClick={handleSaveContractDirectly}
                disabled={!!savingPendingDraftId}
              >
                {savingPendingDraftId
                  ? "Enregistrement..."
                  : isEditingPersistedContract
                    ? "Enregistrer les modifications"
                    : "Enregistrer le contrat"}
              </button>
            </>
          )}
        </div>
        </div>
        </div>
      </div>
    );
  };

  return (
    <TooltipProvider delayDuration={120}>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-[260px_minmax(0,1fr)]">
      <aside className="rounded-xl border bg-white p-4 xl:sticky xl:top-6 xl:flex xl:h-[calc(100vh-3rem)] xl:flex-col">
        <div className="mb-3 flex items-center justify-between">
          <div className="text-xs uppercase tracking-wide text-slate-500">
            Financeurs
          </div>
          <div className="rounded-full bg-slate-900 px-2 py-0.5 text-[11px] font-semibold text-white">
            {normalizedFinanceurSearch
              ? `${filteredFinanceurs.length}/${financeurs.length}`
              : financeurs.length}
          </div>
        </div>

        <label className="mb-3 block">
          <span className="sr-only">Rechercher un financeur</span>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={financeurSearch}
              onChange={(event) => setFinanceurSearch(event.target.value)}
              placeholder="Rechercher un financeur ou un email"
              className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:ring-2 focus:ring-slate-200"
            />
          </div>
        </label>

        {financeursLoading ? (
          <div className="space-y-2">
            {[1, 2, 3].map((item) => (
              <div
                key={`financeur-skeleton-${item}`}
                className="h-14 animate-pulse rounded-lg border bg-slate-100"
              />
            ))}
          </div>
        ) : financeurs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
            Aucun financeur trouvé.
          </div>
        ) : filteredFinanceurs.length === 0 ? (
          <div className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-3 text-xs text-slate-500">
            Aucun financeur ne correspond à votre recherche.
          </div>
        ) : (
          <div className="space-y-2 xl:min-h-0 xl:flex-1 xl:overflow-y-auto xl:pr-1">
            {filteredFinanceurs.map((financeur) => {
              const active =
                getFinanceurListKey(financeur.id, financeur.name)
                === selectedFinanceurKey;
              return (
                <button
                  key={`financeur-${financeur.id ?? financeur.name}`}
                  type="button"
                  className={cn(
                    "w-full rounded-lg border p-2 text-left transition",
                    active
                      ? "border-emerald-300 bg-emerald-50/70 shadow-sm"
                      : "border-slate-200 bg-white hover:bg-slate-50",
                  )}
                  onClick={() =>
                    setSelectedFinanceurKey(
                      getFinanceurListKey(financeur.id, financeur.name),
                    )
                  }
                >
                  <div className="truncate text-sm font-medium text-slate-900">
                    {financeur.name || "Financeur"}
                  </div>
                  <div className="mt-1 text-xs text-slate-500">
                    {formatEuro(financeur.totals.contractAmountEuro)}
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </aside>

      <section className="min-w-0 space-y-4">
        {error ? (
          <div className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
            Impossible de charger les contrats: {error}
          </div>
        ) : null}

        {loading ? (
          <div className="space-y-3">
            {[1, 2].map((item) => (
              <div
                key={`contract-skeleton-${item}`}
                className="h-56 animate-pulse rounded-xl border bg-slate-100"
              />
            ))}
          </div>
        ) : financeurs.length === 0 ? (
          <div className="rounded-xl border bg-white p-4 text-sm text-slate-500">
            Aucun financeur disponible.
          </div>
        ) : financeurs.length > 0 && filteredFinanceurs.length === 0 ? (
          <div className="rounded-xl border bg-white p-4 text-sm text-slate-500">
            Aucun financeur ne correspond à votre recherche.
          </div>
        ) : !selectedFinanceur ? (
          <div className="rounded-xl border bg-white p-4 text-sm text-slate-500">
            Aucun financeur sélectionné.
          </div>
        ) : (
          <>
            <Dialog
              open={!!activeCreationForm}
              onOpenChange={(open) => {
                if (!open) {
                  closeCreationForm();
                }
              }}
            >
              <DialogContent className="max-h-[90vh] max-w-5xl overflow-y-auto p-0">
                <DialogTitle className="sr-only">
                  {isEditingPersistedContract
                    ? "Modifier le contrat"
                    : editingPendingDraft
                      ? "Modifier le brouillon"
                      : "Ajouter un contrat"}
                </DialogTitle>
                {renderContractFormPanel()}
              </DialogContent>
            </Dialog>

            <Dialog
              open={!!activeContractsMerge}
              onOpenChange={(open) => {
                if (!open) {
                  closeContractsMerge();
                }
              }}
            >
              <DialogContent className="max-h-[88vh] max-w-3xl overflow-y-auto p-0">
                <DialogTitle className="sr-only">
                  Fusionner des contrats
                </DialogTitle>
                <div className="border-b border-slate-200 bg-gradient-to-r from-amber-50 via-white to-emerald-50 px-5 py-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-amber-700/80">
                        Fusion
                      </div>
                      <div className="mt-1 text-lg font-semibold text-slate-900">
                        Fusionner plusieurs contrats
                      </div>
                      <div className="mt-1 max-w-2xl text-xs leading-relaxed text-slate-600">
                        Choisissez le contrat qui doit rester. Les projets, versements
                        et lignes des autres contrats seront déplacés dedans, puis les
                        contrats absorbés seront supprimés.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-4 p-5">
                  <div className="rounded-2xl border border-emerald-200 bg-emerald-50/60 p-3">
                    <label className="text-xs font-semibold uppercase tracking-wide text-emerald-800">
                      Contrat à conserver
                    </label>
                    <select
                      value={activeContractsMerge?.targetContractId || ""}
                      onChange={(event) =>
                        updateContractsMergeTarget(event.target.value)
                      }
                      disabled={mergingContracts}
                      className="mt-2 w-full rounded-xl border border-emerald-200 bg-white px-3 py-2 text-sm text-slate-900 outline-none ring-emerald-200 focus:ring-2"
                    >
                      {selectedFinanceurMergeableContracts.map((option) => (
                        <option
                          key={`merge-target-${option.contractId}`}
                          value={String(option.contractId)}
                        >
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="rounded-2xl border border-amber-200 bg-amber-50/45 p-3">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div>
                        <div className="text-xs font-semibold uppercase tracking-wide text-amber-800">
                          Contrats à absorber
                        </div>
                        <div className="mt-0.5 text-xs text-slate-600">
                          Vous pouvez en sélectionner un ou plusieurs.
                        </div>
                      </div>
                      <div className="rounded-full border border-amber-200 bg-white px-2.5 py-1 text-[11px] font-medium text-amber-800">
                        {activeContractsMerge?.sourceContractIds.length || 0} sélectionné(s)
                      </div>
                    </div>

                    <div className="mt-3 space-y-2">
                      {selectedFinanceurMergeableContracts
                        .filter(
                          (option) =>
                            String(option.contractId) !==
                            String(activeContractsMerge?.targetContractId || ""),
                        )
                        .map((option) => {
                          const selected =
                            activeContractsMerge?.sourceContractIds.includes(
                              String(option.contractId),
                            ) === true;
                          const lineCount = getContractLineRowsCount(
                            option.contract,
                          );

                          return (
                            <button
                              key={`merge-source-${option.contractId}`}
                              type="button"
                              onClick={() =>
                                toggleContractsMergeSource(option.contractId)
                              }
                              disabled={mergingContracts}
                              className={cn(
                                "flex w-full items-center justify-between gap-3 rounded-xl border bg-white px-3 py-2 text-left text-sm transition",
                                selected
                                  ? "border-amber-300 ring-2 ring-amber-100"
                                  : "border-slate-200 hover:border-amber-200 hover:bg-amber-50/40",
                              )}
                            >
                              <span className="flex min-w-0 items-center gap-2">
                                <span
                                  className={cn(
                                    "flex h-4 w-4 shrink-0 items-center justify-center rounded border",
                                    selected
                                      ? "border-amber-500 bg-amber-500"
                                      : "border-slate-300 bg-white",
                                  )}
                                >
                                  {selected ? (
                                    <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                  ) : null}
                                </span>
                                <span className="min-w-0">
                                  <span className="block truncate font-medium text-slate-900">
                                    {option.label}
                                  </span>
                                  <span className="block text-xs text-slate-500">
                                    {formatEuro(option.contract.amountEuro)} • {lineCount} projet{lineCount > 1 ? "s" : ""}
                                  </span>
                                </span>
                              </span>
                              <span className="shrink-0 text-[11px] font-medium text-amber-700">
                                Absorber
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  </div>

                  {mergeContractsError ? (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                      {mergeContractsError}
                    </div>
                  ) : null}

                  <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 pt-4">
                    <button
                      type="button"
                      className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      onClick={closeContractsMerge}
                      disabled={mergingContracts}
                    >
                      Annuler
                    </button>
                    <button
                      type="button"
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-medium text-white",
                        mergingContracts
                          ? "cursor-not-allowed bg-slate-400"
                          : "bg-amber-600 hover:bg-amber-700",
                      )}
                      onClick={confirmContractsMerge}
                      disabled={mergingContracts}
                    >
                      <Link2 className="h-3.5 w-3.5" />
                      {mergingContracts
                        ? "Fusion en cours..."
                        : "Fusionner les contrats sélectionnés"}
                    </button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
              <div className="border-b border-slate-200 bg-gradient-to-r from-emerald-50/90 via-white to-sky-50/60 px-4 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0 space-y-2">
                    <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                      Financeur
                    </div>
                    <div className="text-xl font-semibold text-slate-900">
                      {selectedFinanceur.name}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-500">
                      {selectedFinanceur.email ? (
                        <span className="inline-flex items-center rounded-full border border-slate-200 bg-white px-2.5 py-1">
                          {selectedFinanceur.email}
                        </span>
                      ) : null}
                      {selectedFinanceur.isVerdeo ? (
                        <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 font-medium text-emerald-700">
                          Verdeo
                        </span>
                      ) : null}
                    </div>
                  </div>
                  <div className="flex min-w-[150px] flex-col items-end gap-1 text-right">
                    {data?.generatedAt ? (
                      <div className="text-[11px] text-slate-500">
                        Mise à jour le {formatDate(data.generatedAt)}
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className="mt-0.5 inline-flex items-center rounded-md bg-emerald-600 px-2.5 py-1 text-[11px] font-medium text-white shadow-sm hover:bg-emerald-700"
                      onClick={openCreationForm}
                      disabled={isBulkDeletingSelectedFinanceur}
                    >
                      Ajouter contrat
                    </button>
                  </div>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {selectedFinanceurTimelineFilterOptions.map((yearOption) => {
                    const active = yearOption === selectedFinanceurTimelineYear;
                    const label = yearOption === "all" ? "Tout" : yearOption;
                    return (
                      <button
                        key={`financeur-header-year-${yearOption}`}
                        type="button"
                        onClick={() =>
                          setSelectedTimelineYearByFinanceurKey((current) => ({
                            ...current,
                            [selectedFinanceurTimelineKey]: yearOption,
                          }))
                        }
                        className={cn(
                          "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition",
                          active
                            ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                            : yearOption === "all"
                              ? "border-transparent bg-transparent text-slate-400 hover:border-slate-200 hover:bg-white/70"
                              : "border-slate-200 bg-white/90 text-slate-600 hover:bg-white",
                        )}
                      >
                        {label}
                      </button>
                    );
                  })}
                  <SectionHint className="ml-1">
                    <div className="space-y-1.5">
                      <div>Le filtre année suit les années trouvées dans les contrats de ce financeur.</div>
                      <ul className="list-disc space-y-1 pl-4">
                        <li>les cartes du haut se mettent à jour avec cette année</li>
                        <li>la liste des contrats se limite aux contrats de cette année</li>
                        <li>avec Tout, la timeline garde son propre choix d&apos;année mois par mois</li>
                      </ul>
                    </div>
                  </SectionHint>
                </div>
              </div>

              <div className="grid gap-2.5 p-4 sm:grid-cols-2 xl:grid-cols-5">
                <TopAmountCell
                  title="Contrats signés"
                  value={formatEuro(
                    selectedFinanceurHeaderTotals.contractAmountEuro,
                  )}
                  subtitle={
                    selectedFinanceurHeaderYear === null
                      ? `${selectedFinanceurHeaderTotals.contractsCount} contrat(s)`
                      : `${selectedFinanceurHeaderTotals.contractsCount} contrat(s) sur ${selectedFinanceurHeaderYear}`
                  }
                  tone="blue"
                />
                <TopAmountCell
                  title="Versements prévus"
                  value={formatEuro(
                    selectedFinanceurHeaderTotals.expectedPaymentsEuro,
                  )}
                  subtitle={
                    selectedFinanceurHeaderYear === null
                      ? "Planning déclaré"
                      : `Planning déclaré sur ${selectedFinanceurHeaderYear}`
                  }
                  tone="teal"
                />
                <TopAmountCell
                  title="Versements reçus"
                  value={formatEuro(
                    selectedFinanceurHeaderTotals.receivedPaymentsEuro,
                  )}
                  subtitle={
                    selectedFinanceurHeaderYear === null
                      ? "Encaissements confirmés"
                      : `Encaissements confirmés sur ${selectedFinanceurHeaderYear}`
                  }
                  tone="violet"
                />
                <TopAmountCell
                  title="Versements non reçus"
                  value={formatEuro(selectedFinanceurPendingPaymentsEuro)}
                  subtitle={
                    selectedFinanceurHeaderYear === null
                      ? "Montants prévus non encore reçus"
                      : `Montants prévus non encore reçus sur ${selectedFinanceurHeaderYear}`
                  }
                  tone="sand"
                />
                <TopAmountCell
                  title="Versements à l'intervenant"
                  value={formatEuro(
                    selectedFinanceurHeaderTotals.landPaymentsEuro,
                  )}
                  subtitle={
                    selectedFinanceurHeaderYear === null
                      ? "Versements réalisés"
                      : `Versements réalisés sur ${selectedFinanceurHeaderYear}`
                  }
                  tone="rose"
                />
              </div>

              <div className="border-t border-slate-100 bg-slate-100/80 px-4 py-3.5">
                <SynthesesProjectImpactIndicators
                  projectIds={visibleSelectedFinanceurProjectIds}
                  title="Impact des projets financés"
                  description={
                    selectedFinanceurHeaderYear === null
                      ? "Totaux des indicateurs sur l’ensemble des projets financés par ce financeur, avec le prévu et le déjà réalisé."
                      : `Totaux des indicateurs sur les projets financés pour ${selectedFinanceurHeaderYear}, avec le prévu et le déjà réalisé.`
                  }
                  compact
                  embedded
                  yearFilter={selectedFinanceurTimelineYear || "all"}
                />
              </div>
              <div className="border-t border-slate-100 bg-white px-4 py-3.5">
                <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-start gap-2">
                    <div>
                      <div className="text-sm font-semibold text-slate-900">
                        Timeline des versements
                      </div>
                      <div className="text-xs text-black/65">
                        Prévu et reçu agrégés par mois.
                      </div>
                    </div>
                    <SectionHint className="mt-0.5">
                      <div className="space-y-1.5">
                        <div>Cette timeline agrège tous les versements financeur visibles pour ce financeur.</div>
                        <ul className="list-disc space-y-1 pl-4">
                          <li>prévu = planning déclaré</li>
                          <li>reçu = encaissements déjà confirmés</li>
                          <li>si Tout est sélectionné en haut, choisissez ici l&apos;année à afficher mois par mois</li>
                        </ul>
                      </div>
                    </SectionHint>
                  </div>
                  {selectedFinanceurTimelineYear === "all" ? (
                    <div className="flex flex-wrap gap-1.5">
                      {selectedFinanceurMonthlyTimelineYears.map((year) => {
                        const isActive = year === selectedFinanceurMonthlyTimelineYear;

                        return (
                          <button
                            key={`financeur-monthly-timeline-year-${year}`}
                            type="button"
                            onClick={() =>
                              setSelectedMonthlyTimelineYearByFinanceurKey((current) => ({
                                ...current,
                                [selectedFinanceurTimelineKey]: year,
                              }))}
                            className={cn(
                              "rounded-full border px-2.5 py-0.5 text-[11px] font-medium transition",
                              isActive
                                ? "border-emerald-300 bg-emerald-50 text-emerald-700"
                                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
                            )}
                          >
                            {year}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="rounded-full border border-slate-200 bg-white px-2.5 py-0.5 text-[11px] font-medium text-slate-600">
                      {selectedFinanceurMonthlyTimelineYear}
                    </span>
                  )}
                </div>

                <FinanceurMonthlyTimeline
                  yearSummary={selectedFinanceurTimelineSummary}
                  embedded
                />
              </div>
            </div>
            {filteredVisibleSelectedFinanceurPendingContracts.length > 0 ? (
              <div className="space-y-3">
                {savePendingError ? (
                  <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                    {savePendingError}
                  </div>
                ) : null}
                {filteredVisibleSelectedFinanceurPendingContracts.map(
                  (draftContract, index) => {
                    const isSavingThisDraft =
                      savingPendingDraftId === draftContract.id;
                    const rowsTotal = draftContract.yearGroups.reduce(
                      (sum, group) => {
                        return (
                          sum +
                          group.projectRows.reduce((groupSum, row) => {
                            return (
                              groupSum + (parseAmountInput(row.amountEuro) ?? 0)
                            );
                          }, 0)
                        );
                      },
                      0,
                    );
                    return (
                      <div
                        key={`${draftContract.id}-${index}`}
                        className="rounded-xl border border-amber-200 bg-amber-50/40 p-4"
                      >
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <div>
                            <div className="text-sm font-semibold text-slate-900">
                              {draftContract.title}
                            </div>
                            <div className="text-xs text-slate-600">
                              Date du contrat:{" "}
                              {formatDate(draftContract.contractDate)}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="rounded-full border border-amber-300 bg-white px-2 py-0.5 text-[11px] font-semibold uppercase text-amber-700">
                              Brouillon
                            </span>
                            <button
                              type="button"
                              className={cn(
                                "rounded-md border bg-white px-2 py-1 text-[11px]",
                                isSavingThisDraft
                                  ? "cursor-not-allowed border-slate-300 text-slate-400"
                                  : "border-slate-300 text-slate-700 hover:bg-slate-50",
                              )}
                              onClick={() =>
                                openPendingContractForEdit(draftContract.id)
                              }
                              disabled={!!savingPendingDraftId}
                            >
                              Modifier
                            </button>
                            <button
                              type="button"
                              className={cn(
                                "rounded-md border bg-white px-2 py-1 text-[11px]",
                                isSavingThisDraft
                                  ? "cursor-not-allowed border-slate-300 text-slate-400"
                                  : "border-emerald-300 text-emerald-700 hover:bg-emerald-50",
                              )}
                              onClick={() =>
                                savePendingContractToApi(draftContract)
                              }
                              disabled={
                                isSavingThisDraft || !!savingPendingDraftId
                              }
                            >
                              {isSavingThisDraft
                                ? "Enregistrement..."
                                : "Enregistrer le contrat"}
                            </button>
                            <button
                              type="button"
                              className="rounded-md border border-rose-300 bg-white px-2 py-1 text-[11px] text-rose-700 hover:bg-rose-50"
                              onClick={() =>
                                removePendingContract(draftContract.id)
                              }
                              disabled={isSavingThisDraft}
                            >
                              Supprimer
                            </button>
                          </div>
                        </div>
                        <div className="mt-2 grid gap-2 md:grid-cols-2">
                          <div className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700">
                            <div className="font-semibold text-slate-900">
                              Montant contrat
                            </div>
                            <div className="mt-1">
                              {formatEuro(
                                parseAmountInput(draftContract.amountEuro) ?? 0,
                              )}
                            </div>
                          </div>
                          <div className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700">
                            <div className="font-semibold text-slate-900">
                              Total des montants projet
                            </div>
                            <div className="mt-1">{formatEuro(rowsTotal)}</div>
                          </div>
                        </div>
                        <div className="mt-2 space-y-1">
                          {draftContract.yearGroups.map((group) => (
                            <div
                              key={group.id}
                              className="rounded-md border border-slate-200 bg-white p-2 text-xs text-slate-700"
                            >
                              <div className="mb-1 font-semibold text-slate-900">
                                Année {group.year}
                              </div>
                              <div className="space-y-1">
                                {group.projectRows.map((row) => (
                                  <div
                                    key={row.id}
                                    className="flex flex-wrap items-center justify-between rounded border border-slate-100 px-2 py-1"
                                  >
                                    <span className="truncate font-medium text-slate-900">
                                      {row.projectName}
                                    </span>
                                    <span className="text-slate-600">
                                      {formatEuro(
                                        parseAmountInput(row.amountEuro) ?? 0,
                                      )}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  },
                )}
              </div>
            ) : null}

            {filteredVisibleSelectedFinanceurContracts.length === 0 &&
            filteredVisibleSelectedFinanceurPendingContracts.length === 0 ? (
              <div className="rounded-xl border bg-white p-4 text-sm text-slate-500">
                {selectedContractProjectFilter !== "all"
                  ? (
                    selectedFinanceurHeaderYear === null
                      ? `Aucun contrat pour ${selectedContractProjectFilterLabel}.`
                      : `Aucun contrat pour ${selectedContractProjectFilterLabel} en ${selectedFinanceurHeaderYear}.`
                  )
                  : (
                    selectedFinanceurHeaderYear === null
                      ? "Aucun contrat pour ce financeur."
                      : `Aucun contrat pour ${selectedFinanceurHeaderYear}.`
                  )}
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <div className="mb-2 flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
                        Contrats
                      </div>
                      <SectionHint>
                        <div className="space-y-1.5">
                          <div>Chaque carte correspond à un contrat du financeur.</div>
                          <ul className="list-disc space-y-1 pl-4">
                            <li>cliquez sur une carte pour afficher le détail en dessous</li>
                            <li>le filtre projet aide à retrouver plus vite les contrats concernés</li>
                            <li>les brouillons non enregistrés restent affichés au-dessus de la liste</li>
                          </ul>
                        </div>
                      </SectionHint>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-2">
                      {savingPendingDraftId ? (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-[11px] font-medium text-blue-700">
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Sauvegarde en cours...
                        </span>
                      ) : null}
                      {selectedFinanceurMergeableContracts.length > 1 ? (
                        <button
                          type="button"
                          className={cn(
                            "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition",
                            mergingContracts
                              ? "cursor-not-allowed border-slate-300 bg-slate-100 text-slate-400"
                              : "border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100",
                          )}
                          onClick={openContractsMerge}
                          disabled={isBulkDeletingSelectedFinanceur || mergingContracts}
                        >
                          <Link2 className="h-3.5 w-3.5" />
                          Fusionner des contrats
                        </button>
                      ) : null}
                      <div className="text-[11px] text-slate-500">
                        {filteredVisibleSelectedFinanceurContracts.length}
                        {filteredVisibleSelectedFinanceurContracts.length !==
                        visibleSelectedFinanceurContracts.length
                          ? `/${visibleSelectedFinanceurContracts.length}`
                          : ""}
                        {" "}contrat
                        {filteredVisibleSelectedFinanceurContracts.length > 1 ? "s" : ""}
                      </div>
                    </div>
                  </div>

                  <div className="mb-3 rounded-2xl border border-slate-200/80 bg-gradient-to-r from-slate-50 via-white to-sky-50/60 p-2.5">
                    <div className="flex items-center justify-between gap-2 px-1">
                      <div className="flex items-center gap-2">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
                          Filtrer par projet
                        </div>
                        <SectionHint>
                          <div className="space-y-1.5">
                            <div>Ce filtre sert surtout à retrouver rapidement les contrats d&apos;un projet.</div>
                            <ul className="list-disc space-y-1 pl-4">
                              <li>Tous remet l&apos;ensemble des contrats du financeur</li>
                              <li>les couleurs aident à repérer un projet dans la liste</li>
                            </ul>
                          </div>
                        </SectionHint>
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {selectedContractProjectFilter === "all"
                          ? "Tous les projets"
                          : selectedContractProjectFilterLabel}
                      </div>
                    </div>

                    <CustomHorizontalScroller compact>
                      <div className="flex min-w-max gap-2 px-1 pb-1 pt-2">
                        <button
                          type="button"
                          onClick={() =>
                            setSelectedContractProjectFilterByFinanceurKey((current) => ({
                              ...current,
                              [selectedFinanceurTimelineKey]: "all",
                            }))}
                          className={cn(
                            "inline-flex h-8 items-center gap-2 rounded-full border px-3 text-[11px] font-medium transition",
                            selectedContractProjectFilter === "all"
                              ? "border-slate-900 bg-slate-900 text-white shadow-sm shadow-slate-200"
                              : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50",
                          )}
                        >
                          <span
                            className={cn(
                              "h-2 w-2 rounded-full",
                              selectedContractProjectFilter === "all"
                                ? "bg-white/90"
                                : "bg-slate-400",
                            )}
                          />
                          Tous
                        </button>

                        {contractProjectFilterOptions.map((option) => {
                          const tone = getProjectFilterTone(option.key);
                          const isActive = selectedContractProjectFilter === option.key;

                          return (
                            <button
                              key={option.key}
                              type="button"
                              onClick={() =>
                                setSelectedContractProjectFilterByFinanceurKey((current) => ({
                                  ...current,
                                  [selectedFinanceurTimelineKey]: option.key,
                                }))}
                              className={cn(
                                "inline-flex h-8 items-center gap-2 rounded-full border px-3 text-[11px] font-medium transition",
                                isActive
                                  ? tone.activeClassName
                                  : tone.inactiveClassName,
                              )}
                              title={option.label}
                            >
                              <span
                                className={cn(
                                  "h-2 w-2 rounded-full",
                                  isActive ? "bg-white/90" : tone.dotClassName,
                                )}
                              />
                              <span className="max-w-[220px] truncate">{option.label}</span>
                            </button>
                          );
                        })}
                      </div>
                    </CustomHorizontalScroller>
                  </div>

                  <CustomHorizontalScroller compact>
                    <div className="flex min-w-max gap-3 pb-1">
                      {filteredVisibleSelectedFinanceurContracts.map((contract, index) => {
                        const contractCollapseKey = getContractCollapseKey(contract, index);
                        const isSelected = selectedVisibleContractIndex === index;
                        const isSavingContract = isContractSaveInProgress(contract);

                        return (
                          <div
                            key={`${contract.id}-${contract.realFundingId ?? index}`}
                            data-contract-node-id={String(contract.id || "")}
                          >
                            <FinanceurContractSelectorCard
                              contract={contract}
                              index={index}
                              selectedHeaderYear={selectedFinanceurHeaderYear}
                              isSelected={isSelected}
                              isEditing={isPersistedContractBeingEdited(
                                contract,
                                editingPersistedContract,
                              )}
                              isSaving={isSavingContract}
                              onSelect={() => toggleContractExpanded(contractCollapseKey)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </CustomHorizontalScroller>
                </div>

                {selectedVisibleContract ? (() => {
                  const contract = selectedVisibleContract;
                  const index = selectedVisibleContractIndex;
                  const canManage =
                    getPersistedContractDeletionInfo(contract).canManage;
                  const contractYears = buildContractYears(contract);
                  const contractKey = String(contract.id || `contract-${index}`);
                  const storedContractYear = selectedYearByContractId[contractKey];
                  const selectedContractYear = contractYears.includes(storedContractYear)
                    ? storedContractYear
                    : contractYears[contractYears.length - 1] ?? null;
                  const isEditingContractCard = isPersistedContractBeingEdited(
                    contract,
                    editingPersistedContract,
                  );
                  const isSavingContractCard = isContractSaveInProgress(contract);
                  const contractId = extractSynthesesContractId(contract);
                  const moveTargets =
                    Number.isFinite(Number(contractId)) &&
                    Number(contractId || 0) > 0
                      ? selectedFinanceurMoveTargetOptionsByContractId.get(
                          Number(contractId),
                        ) || []
                      : [];

                  return (
                    <div
                      className="-mt-1"
                      data-contract-node-id={String(contract.id || "")}
                    >
                      <ContractCard
                        contract={contract}
                        financeurContracts={selectedFinanceur?.contracts || []}
                        index={index}
                        isExpanded
                        selectedYear={selectedContractYear}
                        projectVisualCatalog={projectVisualCatalog}
                        onSelectYear={(year) =>
                          setSelectedYearByContractId((current) => ({
                            ...current,
                            [contractKey]: year,
                          }))
                        }
                        canManage={canManage}
                        actionsDisabled={
                          isBulkDeletingSelectedFinanceur || isSavingContractCard
                        }
                        isEditing={isEditingContractCard}
                        isSaving={isSavingContractCard}
                        moveTargets={moveTargets}
                        activeLineMove={activeLineMove}
                        movePendingLineKey={movingLineRequestKey}
                        moveError={moveLineError}
                        isDeleting={
                          canManage &&
                          String(deletingContractKey || "") ===
                            String(contract.id || "")
                        }
                        onEdit={openSavedContractForEdit}
                        onDelete={handleDeletePersistedContract}
                        onStartMoveLine={openContractLineMove}
                        onChangeMoveTarget={updateContractLineMoveTarget}
                        onCancelMoveLine={cancelContractLineMove}
                        onConfirmMoveLine={confirmContractLineMove}
                        onRefreshContracts={onRefreshData}
                        onMutateData={onMutateData}
                        onRestoreData={onRestoreData}
                        collapsible={false}
                      />
                    </div>
                  );
                })() : null}
              </div>
            )}
          </>
        )}
      </section>
      </div>
    </TooltipProvider>
  );
}

export default SynthesesFinanceursReadOnlyBoard;
